import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  InputGroup,
  Row,
  Col,
  Spinner,
  Accordion,
  Tabs,
  Tab,
  Dropdown,
} from "react-bootstrap";
import api from "../../../services/api"; // same service you use elsewhere
import {
  calculateAdminTripQuote,
  fetchTripPrices,
  fetchExtensionRoomAvailability,
  fetchTripRoomAvailability,
} from "../../../services/priceService";
import {
  fetchBookingTransportRating,
  listTransportMaster,
  saveBookingTransportRating,
} from "../../../services/transportMasterApi";
import StarRating from "../../components/StarRating";
import BookingPickupStatus from "./BookingPickupStatus";
import PhoneLink from "../../components/PhoneLink";
import {
  getBookingTransportPayments,
  recordBookingTransportPayment,
  saveBookingTransportPayable,
  followQuoteBookingTransportPayable,
  editBookingTransportPayment,
  deleteBookingTransportPayment,
} from "../../../services/bookingTransportPaymentApi";
import { listExternalVendors } from "../../../services/externalVendorApi";
import {
  DRIVER_APP_DOWNLOAD_URL,
  DRIVER_APP_VIDEO_LABELS,
  DRIVER_GUIDE_LANGUAGES,
  listDriverGuideVideos,
} from "../../../services/driverGuideVideoApi";
import HotelVendorPayments from "./HotelVendorPayments";
import { DeletedPayments, EditedMark, PaymentRowActions, usePaymentCorrections } from "./PaymentCorrections";
import MiscVendorPayments from "./MiscVendorPayments";
import CustomerPayments from "./CustomerPayments";
import BookingEpassChecklist, { bookingEpassesPending, bookingEpassFilterValues } from "./BookingEpasses";
import BookingDocuments, { CUSTOMER_ID, ITINERARY, bookingDocumentsOfKind } from "./BookingDocuments";
import BookingReviewCell, { reviewDecided, reviewFilterValues } from "./BookingReview";
import { SilenceAlertPanel, SilenceAlertsSwitch, SilenceBadge, SilenceCheckOffNote, useSilenceAlerts } from "./SilenceAlerts";
import { NotReadyAlertPanel, useNotReadyAlerts } from "./NotReadyAlerts";
import { PaymentAlertPanel, usePaymentAlerts } from "./PaymentAlerts";
import {
  PLACE_CIRCLE_HINT,
  PLACE_CIRCLE_MAX,
  PLACE_CIRCLE_MIN,
  placeCircleInvalid,
  withPlaceCircle,
} from "../../../utils/placeCircle";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY, formatDateTimeDMY, formatDateTimeDMY12 } from "../../../utils/dateFormat";
import { describeBrowser } from "../../../utils/browserInfo";
import { generatePaymentReceipt } from "../../../utils/generatePaymentReceipt";
import { generateTravelPolicy } from "../../../utils/generateTravelPolicy";
import { tripStayDates, vehicleExtensionDays, vehicleTripDates } from "../../../utils/vehicleTripDates";
import { combinedVehicle, vehicleNamesLabel, vehiclesForIds } from "../../../utils/quoteVehicles";
import { bookingPaymentSchedule, paymentSchedule } from "../../../utils/paymentSchedule";
import { stayInclusionLines, tripDayLabel } from "../../../utils/stayInclusions";
import { listTripTrackingRows } from "../../../services/tripTrackingApi";
import { getBookingHotelPayments } from "../../../services/bookingHotelPaymentApi";
import { companyPaymentLines, fetchCompanyPaymentQrBlob, getCompanyPaymentDetails } from "../../../services/companyPaymentApi";
import { buildBookingRows, CopyDayWiseImageButton, CopyWhatsAppButton, TimelineModal, TrackingSummary } from "../tracking/TripTrackingNew";

// Browsers only accept image/png on the clipboard, so a JPEG or WebP QR is redrawn once through a
// canvas. The white fill matters: a transparent QR pasted onto a dark chat background is unreadable.
const pngBlobFrom = (blob) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);
    URL.revokeObjectURL(objectUrl);
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("QR could not be converted to PNG"))),
      "image/png",
    );
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("QR image could not be decoded"));
  };
  image.src = objectUrl;
});

const STATUS_OPTIONS = ["PENDING", "UPCOMING", "ONGOING", "COMPLETED", "CANCELLED"];
const BOOKING_FILTER_STORAGE_KEY = "adminBookingFiltersV2";


// eslint-disable-next-line no-unused-vars
function SortableBookingTh({ label, sortKey, sort, onSort, className = "" }) {
  const active = sort.key === sortKey;
  return (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 fw-semibold text-decoration-none text-reset text-nowrap"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}${active ? `, currently ${sort.dir}ending` : ""}`}
      >
        {label}
        <span className="ms-1" aria-hidden="true">{active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span>
      </button>
    </th>
  );
}

function BookingFilterMenu({ label, toggleLabel = "Filter", type = "values", filter, options, resultCount, openRequest, onOpened, onChange }) {
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState(filter || { type });
  const selected = draft?.values || [];
  const appliedSelected = filter?.values || [];
  const active = appliedSelected.length > 0 || Boolean(filter && Object.entries(filter).some(([key, value]) => key !== "type" && key !== "values" && value));
  const visibleOptions = options.filter((option) => `${option.value} ${option.label || ""}`.toLowerCase().includes(search.trim().toLowerCase()));
  const commit = (next) => { setDraft(next); onChange(next); };
  const toggleValue = (value) => commit({ ...(draft || {}), type, values: selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value] });
  const setField = (field, value) => commit({ ...(draft || {}), type, [field]: value });
  const optionRows = (items) => items.map((option) => {
    const checked = selected.includes(option.value);
    return <button key={option.value} type="button" className={`booking-filter-option ${checked ? "is-selected" : ""}`} onClick={() => toggleValue(option.value)}>
      <span className="booking-filter-check" aria-hidden="true">{checked ? "✓" : ""}</span><span className="booking-filter-option-label">{option.label || option.value}</span><small>{option.count}</small>
    </button>;
  });
  const handleToggle = (nextShow) => {
    if (nextShow) { setDraft(filter || { type }); setSearch(""); }
    setShow(nextShow);
  };
  useEffect(() => {
    if (!openRequest) return;
    setDraft(filter || { type });
    setSearch("");
    setShow(true);
    onOpened?.();
    // The request flag is the event; filter values are intentionally read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);
  const clear = () => { setDraft({ type }); onChange(null); setShow(false); };
  return <Dropdown show={show} onToggle={handleToggle} autoClose="outside" align="end">
    <Dropdown.Toggle className={`booking-filter-toggle ${active ? "is-active" : ""}`} variant="light" size="sm" title={`Filter ${label}`} aria-label={`Filter ${label}`}>
      <i className="bi bi-funnel" aria-hidden="true" /><span className="booking-filter-toggle-label">{active ? `${toggleLabel} (${appliedSelected.length || 1})` : toggleLabel}</span>
    </Dropdown.Toggle>
    <Dropdown.Menu className="booking-filter-menu">
      <div className="booking-filter-menu-header"><strong>{label}</strong>{active && <Button size="sm" variant="link" onClick={clear}>Clear</Button>}</div>
      {type === "values" && <>
        <Form.Control size="sm" autoFocus placeholder="Search options..." value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="booking-filter-options">
          {optionRows([...visibleOptions].sort((a, b) => Number(selected.includes(b.value)) - Number(selected.includes(a.value))))}
          {!visibleOptions.length && <div className="small text-muted py-2">No matching options</div>}
        </div>
      </>}
      {type === "text" && <Form.Control size="sm" autoFocus placeholder="Contains..." value={draft?.contains || ""} onChange={(event) => setField("contains", event.target.value)} />}
      {type === "number" && <div className="d-flex gap-2"><Form.Control size="sm" type="number" placeholder="Min" value={draft?.min || ""} onChange={(event) => setField("min", event.target.value)} /><Form.Control size="sm" type="number" placeholder="Max" value={draft?.max || ""} onChange={(event) => setField("max", event.target.value)} /></div>}
      {type === "payment" && <>
        <div className="booking-filter-options mb-2">{optionRows(options)}</div>
        <div className="small fw-semibold mb-1">Balance range</div>
        <div className="d-flex gap-2"><Form.Control size="sm" type="number" placeholder="Min INR" value={draft?.min || ""} onChange={(event) => setField("min", event.target.value)} /><Form.Control size="sm" type="number" placeholder="Max INR" value={draft?.max || ""} onChange={(event) => setField("max", event.target.value)} /></div>
      </>}
      {type === "date" && <>
        <div className="booking-date-presets">{["Today", "Tomorrow", "Next 7 days"].map((preset) => <Button key={preset} size="sm" variant={draft?.preset === preset ? "primary" : "outline-secondary"} onClick={() => commit({ type: "date", preset })}>{preset}</Button>)}</div>
        <Form.Select size="sm" className="mt-2" value={draft?.from && draft.from === draft.to ? draft.from : ""} onChange={(event) => commit(event.target.value ? { type: "date", from: event.target.value, to: event.target.value } : { type: "date" })}>
          <option value="">Exact available date...</option>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label || option.value} ({option.count})</option>)}
        </Form.Select>
        <div className="small fw-semibold mt-2 mb-1">Custom range</div>
        <Form.Control size="sm" type="date" className="mb-2" value={draft?.from || ""} onChange={(event) => commit({ type: "date", from: event.target.value, to: draft?.to || "" })} />
        <Form.Control size="sm" type="date" value={draft?.to || ""} onChange={(event) => commit({ type: "date", from: draft?.from || "", to: event.target.value })} />
      </>}
      <div className="booking-filter-live-note">{resultCount} bookings match | selections apply immediately</div>
    </Dropdown.Menu>
  </Dropdown>;
}

function BookingFilterableHeader({ label, sortKey, sort, onSort, filterMenu }) {
  const active = sortKey && sort.key === sortKey;
  return <th><div className="booking-column-heading">
    {sortKey ? <button type="button" className="btn btn-link btn-sm p-0 fw-semibold text-decoration-none text-reset text-nowrap" onClick={() => onSort(sortKey)}>{label}<span className="ms-1" aria-hidden="true">{active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span></button> : <span className="fw-semibold text-nowrap">{label}</span>}
    {filterMenu}
  </div></th>;
}

function sortBookingRows(rows, key, dir) {
  const direction = dir === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const result = compareBookingValues(bookingSortValue(left, key), bookingSortValue(right, key));
    if (result !== 0) return result * direction;
    return Number(right.id || 0) - Number(left.id || 0);
  });
}

function ConversionCreditEditor({ value = [], members = [], onChange, disabled = false }) {
  const rows = Array.isArray(value) ? value : [];
  const activeMembers = (members || []).filter((member) => member?.active !== false);
  const total = conversionCreditTotal(rows);
  const updateRow = (index, changes) => onChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...changes } : row));
  const addRow = () => onChange([...rows, { memberId: "", contributionPercent: "" }]);
  const removeRow = (index) => onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  return (
    <Col xs={12}>
      <div className="border rounded bg-white p-3 mt-2">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <Form.Label className="mb-0 fw-semibold">Converted By</Form.Label>
          {rows.length ? <Badge bg={total === 100 ? "success" : "danger"}>{total}% / 100%</Badge> : <Badge bg="secondary">Not captured</Badge>}
        </div>
        {rows.length ? (
          <div className="d-flex flex-column gap-2">
            {rows.map((row, index) => (
              <Row className="g-2 align-items-end" key={`${row.memberId || "new"}-${index}`}>
                <Col md={7}>
                  <Form.Label className="small mb-1">Team member</Form.Label>
                  <Form.Select value={row.memberId || ""} disabled={disabled} onChange={(event) => updateRow(index, { memberId: event.target.value })}>
                    <option value="">Select team member</option>
                    {activeMembers.map((member) => <option key={member.id} value={member.id}>{conversionMemberLabel(member)}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">Contribution %</Form.Label>
                  <Form.Control type="number" min="1" max="100" step="1" value={row.contributionPercent ?? ""} disabled={disabled} onChange={(event) => updateRow(index, { contributionPercent: event.target.value })} />
                </Col>
                <Col md={2} className="d-grid">
                  <Button type="button" variant="outline-danger" disabled={disabled} onClick={() => removeRow(index)}>Remove</Button>
                </Col>
              </Row>
            ))}
          </div>
        ) : (
          <div className="small text-muted">No conversion attribution captured for this booking yet.</div>
        )}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
          <Button type="button" size="sm" variant="outline-primary" disabled={disabled} onClick={addRow}>Add Member</Button>
          {rows.length > 0 && total !== 100 && <span className="small text-danger">Contribution total must be exactly 100%.</span>}
        </div>
      </div>
    </Col>
  );
}

function normalizeConversionCredits(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      memberId: row?.memberId != null ? String(row.memberId) : "",
      contributionPercent: row?.contributionPercent ?? "",
    }))
    .filter((row) => row.memberId || row.contributionPercent !== "");
}

function normalizeConversionCreditPayload(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      memberId: Number(row?.memberId || 0),
      contributionPercent: Number(row?.contributionPercent || 0),
    }));
}

function conversionCreditTotal(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + Number(row?.contributionPercent || 0), 0);
}

function conversionMemberLabel(member) {
  const name = member?.name || member?.username || `Member ${member?.id || ""}`;
  return member?.team ? `${name} (${member.team})` : name;
}

function bookingSortValue(row, key) {
  if (key === "balance") {
    const total = adjustedBookingTotal(row);
    return total == null ? null : Math.max(0, Number(total) - Number(row.advancePaid || 0));
  }
  if (key === "tripPreparation") {
    return Number(Boolean(row.allDriversAssigned)) + Number(Boolean(row.hotelsBlocked)) + Number(!bookingEpassesPending(row));
  }
  if (key === "driverLanguages") return preferredLanguageText(row);
  if (key === "travelDate") return row?.vehicleStartDate || row?.travelDate;
  return row?.[key];
}

// The languages are stored as an ordered list; searching and sorting both want one string.
function preferredLanguageText(row) {
  return Array.isArray(row?.driverLanguages) ? row.driverLanguages.join(", ") : "";
}

function paymentFilterValue(payable, paid) {
  const total = Number(payable || 0);
  const received = Number(paid || 0);
  if (total <= 0) return "No payable";
  if (received <= 0) return "Unpaid";
  if (received >= total) return "Paid";
  return "Partially paid";
}

function bookingColumnFilterValues(row, key) {
  if (key === "id") return [String(row.id)];
  if (key === "tripStatus") return [String(row.tripStatus || "PENDING")];
  if (key === "tripPreparation") {
    const values = [bookingPreparationReady(row) ? "Ready" : "Not ready"];
    values.push(row.allDriversAssigned ? "Driver assigned" : "Driver pending");
    values.push(row.hotelsBlocked ? "Hotels blocked" : "Hotels pending");
    values.push(...bookingEpassFilterValues(row));
    return values;
  }
  if (key === "driverName") return [row.driverName || "Not assigned", ...(row.extraVehicles || []).map((extra) => extra.driverName || "Not assigned")];
  if (key === "vehicleOwnerName") return [row.vehicleOwnerName || "Not assigned", ...(row.extraVehicles || []).map((extra) => extra.vehicleOwnerName || "Not assigned")];
  if (key === "userName") return [row.userName || "No user"];
  if (key === "driverLanguages") return row.driverLanguages?.length ? row.driverLanguages : ["Not specified"];
  if (key === "bookingNotes") {
    return [row.bookingNotes ? "Has notes" : "No notes", ...(bookingDocumentsOfKind(row, ITINERARY).length ? ["Has itinerary"] : [])];
  }
  if (key === "tripTitle") {
    const title = row.tripTitle || "No trip";
    const duration = bookingDurationLabel(parseQuoteSnapshot(row.quoteSnapshot));
    return [duration ? `${title} - ${duration}` : title];
  }
  if (key === "travelDate") return [row.vehicleStartDate || row.travelDate || "No date"];
  if (key === "noOfTravellers") return [String(row.noOfTravellers ?? 0)];
  if (key === "customerPayment") {
    const total = adjustedBookingTotal(row);
    const schedule = bookingPaymentSchedule(row, total);
    return [
      paymentFilterValue(total, row.advancePaid),
      ...(schedule?.afterBoardingDue > 0 ? ["After-boarding payment due"] : []),
      ...(schedule?.dayOneDue > 0 ? ["Day 1 payment due"] : []),
    ];
  }
  if (key === "transportPayment") return [paymentFilterValue(row.transportVendorPayable, row.transportVendorPaid)];
  if (key === "hotelPayment") return [paymentFilterValue(row.hotelVendorPayable, row.hotelVendorPaid)];
  if (key === "couponCode") return [row.couponCode || "No coupon"];
  if (key === "tracking") return [row.trackingPublicToken ? "Tracking available" : "Tracking pending"];
  if (key === "driverWhatsapp") return [row.driverWhatsappText ? "Message available" : "No message"];
  if (key === "hotelWhatsapp") {
    const hotelNames = [...new Set(hotelWhatsappMessages(row).map((message) => message.hotelName).filter(Boolean))];
    return hotelNames.length ? hotelNames : ["No hotel message"];
  }
  if (key === "customerWhatsapp") {
    const sent = row.customerWhatsappSent ? "Sent" : "Pending";
    return row.customerPolicy ? [sent, customerPolicyFilterLabel(row.customerPolicy)] : [sent];
  }
  if (key === "review") return reviewFilterValues(row);
  if (key === "createdAt") return [String(row.createdAt || "").slice(0, 10) || "No date"];
  return [];
}

// "+91 97056 64542", "097056-64542" and "9705664542" are the same number - compare digits only,
// without the country code or trunk zero, so it matches however the phone was saved.
function phoneSearchDigits(query) {
  if (!/^[\d\s()+-]+$/.test(query)) return "";
  let digits = query.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits.length >= 4 ? digits : "";
}

function bookingPhoneNumbers(booking) {
  const hotelContacts = (Array.isArray(booking.hotelConfirmations) ? booking.hotelConfirmations : [])
    .flatMap((item) => [item?.caretakerNumber, item?.ownerNumber]);
  const extraContacts = (booking.extraVehicles || []).flatMap((extra) => [extra.driverPhone, extra.vehicleOwnerPhone]);
  return [booking.userPhone, booking.driverPhone, booking.vehicleOwnerPhone, ...extraContacts, ...hotelContacts].filter(Boolean);
}

function bookingMatchesQuery(booking, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const phoneDigits = phoneSearchDigits(q);
  if (phoneDigits && bookingPhoneNumbers(booking).some((phone) => String(phone).replace(/\D/g, "").includes(phoneDigits))) {
    return true;
  }
  return [
    booking.id,
    booking.tripTitle,
    booking.tripCategory,
    booking.path,
    booking.pickupLabel,
    booking.vehicleNameSnapshot,
    booking.hotelSummarySnapshot,
    hotelSearchTextFromQuoteSnapshot(parseQuoteSnapshot(booking.quoteSnapshot)),
    booking.userName,
    booking.userEmail,
    booking.userPhone,
    preferredLanguageText(booking),
    booking.bookingNotes,
    booking.stickyNoteDoneAt ? "" : booking.stickyNote,
    booking.tripStatus,
    booking.driverName,
    booking.driverPhone,
    booking.assignedVehicleRegistrationNumber,
    booking.vehicleOwnerName,
    booking.vehicleOwnerPhone,
    ...(booking.extraVehicles || []).flatMap((extra) => [extra.driverName, extra.assignedVehicleNumber, extra.vehicleOwnerName]),
    booking.transportVendorPayable,
    booking.transportVendorPaid,
    booking.transportVendorBalance,
    booking.hotelVendorPayable,
    booking.hotelVendorPaid,
    booking.hotelVendorBalance,
    booking.allDriversAssigned ? "driver assigned" : "driver pending",
    booking.hotelsBlocked ? "hotels blocked" : "hotels pending",
    hasBookingAddons(booking) ? (booking.addonsArranged ? "addons arranged" : "addons pending") : "no addons",
    bookingPreparationReady(booking) ? "ready" : "not ready",
    booking.couponCode,
    booking.travelDate,
    booking.vehicleStartDate,
    booking.customerWhatsappSent ? "customer message sent" : "customer message pending",
  ].join(" ").toLowerCase().includes(q);
}

function bookingPaymentBalance(row, key) {
  if (key === "customerPayment") return Math.max(0, Number(adjustedBookingTotal(row) || 0) - Number(row.advancePaid || 0));
  if (key === "transportPayment") return Math.max(0, Number(row.transportVendorPayable || 0) - Number(row.transportVendorPaid || 0));
  return Math.max(0, Number(row.hotelVendorPayable || 0) - Number(row.hotelVendorPaid || 0));
}

function localIsoDate(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function bookingMatchesColumnFilter(booking, key, filter) {
  if (!filter) return true;
  if (filter.type === "values") return !filter.values?.length || filter.values.some((value) => bookingColumnFilterValues(booking, key).includes(value));
  if (filter.type === "text") return !filter.contains || bookingColumnFilterValues(booking, key).join(" ").toLowerCase().includes(filter.contains.toLowerCase());
  if (filter.type === "number") {
    const value = key === "noOfTravellers" ? Number(booking.noOfTravellers || 0) : Number(booking.id || 0);
    return (filter.min === "" || filter.min == null || value >= Number(filter.min)) && (filter.max === "" || filter.max == null || value <= Number(filter.max));
  }
  if (filter.type === "payment") {
    const valuesMatch = !filter.values?.length
      || bookingColumnFilterValues(booking, key).some((value) => filter.values.includes(value));
    const balance = bookingPaymentBalance(booking, key);
    return valuesMatch && (filter.min === "" || filter.min == null || balance >= Number(filter.min)) && (filter.max === "" || filter.max == null || balance <= Number(filter.max));
  }
  if (filter.type === "date") {
    const value = key === "createdAt" ? String(booking.createdAt || "").slice(0, 10) : (booking.vehicleStartDate || booking.travelDate);
    let from = filter.from || "";
    let to = filter.to || "";
    if (filter.preset === "Today") from = to = localIsoDate();
    if (filter.preset === "Tomorrow") from = to = localIsoDate(1);
    if (filter.preset === "Next 7 days") { from = localIsoDate(); to = localIsoDate(6); }
    return (!from || value >= from) && (!to || value <= to);
  }
  return true;
}

function bookingFilterSummary(filter) {
  if (!filter) return "";
  if (filter.values?.length > 1) return `${filter.values.length} selected`;
  if (filter.values?.length === 1) return filter.values[0];
  if (filter.contains) return `contains ${filter.contains}`;
  if (filter.preset) return filter.preset;
  if (filter.from || filter.to) return `${filter.from || "any"} to ${filter.to || "any"}`;
  if (filter.min || filter.max) return `${filter.min || "any"} to ${filter.max || "any"}`;
  return "Filtered";
}

function friendlyBookingFilterDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return value;
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function compareBookingValues(left, right) {
  const leftEmpty = left === null || left === undefined || left === "";
  const rightEmpty = right === null || right === undefined || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  const leftText = String(left);
  const rightText = String(right);
  const datePattern = /^\d{4}-\d{2}-\d{2}(?:T|\s|$)/;
  if (datePattern.test(leftText) && datePattern.test(rightText)) {
    return new Date(leftText).getTime() - new Date(rightText).getTime();
  }
  return leftText.localeCompare(rightText, undefined, { numeric: true, sensitivity: "base" });
}

export default function BookingsManager() {
  const savedFilters = useMemo(() => {
    let saved;
    try { saved = JSON.parse(localStorage.getItem(BOOKING_FILTER_STORAGE_KEY) || "{}"); } catch { return {}; }
    // The Coupon column is gone and Preferred Language lives in the User column without its own sort,
    // so a filter or sort saved on them would hide or reorder rows with nothing on screen showing why.
    const { couponCode, ...columnFilters } = saved.columnFilters || {};
    const sort = ["couponCode", "driverLanguages"].includes(saved.sort?.key) ? undefined : saved.sort;
    return { ...saved, columnFilters, sort };
  }, []);
  const [query, setQuery] = useState(savedFilters.query || "");
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter || "ALL");
  const [fullScreen, setFullScreen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);

  // Loaded once for the whole table: every reminder quotes the same account, and it is edited
  // under Payment Details in the admin nav rather than being baked into this file.
  useEffect(() => {
    let cancelled = false;
    getCompanyPaymentDetails()
      .then((data) => { if (!cancelled) setPaymentDetails(data); })
      .catch(() => { if (!cancelled) setPaymentDetails(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!fullScreen) return undefined;
    const onKeyDown = (event) => {
      // Escape belongs to whatever dialog is open first; only the bare table answers to it here.
      if (event.key !== "Escape" || document.querySelector(".modal.show")) return;
      setFullScreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fullScreen]);
  const [rows, setRows] = useState([]);
  // Completed bookings are most of the list and rarely needed, so they load only when the
  // All or Completed view is opened. Tracking events load the same way, in two halves.
  const [activeTrackingRows, setActiveTrackingRows] = useState([]);
  const [completedTrackingRows, setCompletedTrackingRows] = useState([]);
  const [completedLoad, setCompletedLoad] = useState("idle"); // idle | loading | loaded | error
  const [serverStatusCounts, setServerStatusCounts] = useState(null);
  const loadGeneration = useRef(0);
  const [selectedTrackingBooking, setSelectedTrackingBooking] = useState(null);
  const [trackingRefreshing, setTrackingRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openingBookingId, setOpeningBookingId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState({}); // { [id]: true }
  const [reminderUpdating, setReminderUpdating] = useState({});
  const [customerWhatsappUpdating, setCustomerWhatsappUpdating] = useState({});
  const [driverCallUpdating, setDriverCallUpdating] = useState({});
  const [preparationUpdating, setPreparationUpdating] = useState({});
  const [reminderSnoozeHours, setReminderSnoozeHours] = useState({});
  const [driverAmountChecked, setDriverAmountChecked] = useState({}); // { [bookingId]: true }
  // Driver guide video language per booking for the Copy Driver Message; English unless changed.
  const [driverVideoLanguage, setDriverVideoLanguage] = useState({}); // { [bookingId]: "EN" | "KN" | "TA" | "HI" }
  const [driverGuideVideos, setDriverGuideVideos] = useState({}); // { [language]: youtubeUrl }
  // Every saved Driver App video goes into the message, so the driver picks their own language.
  const [driverAppVideos, setDriverAppVideos] = useState([]); // [{ label, url }] in language order
  useEffect(() => {
    listDriverGuideVideos()
      .then((rows) => {
        setDriverGuideVideos(Object.fromEntries(
          rows.filter((row) => row.youtubeUrl).map((row) => [row.language, row.youtubeUrl]),
        ));
        const appByLanguage = Object.fromEntries(rows.map((row) => [row.language, row.appYoutubeUrl]));
        setDriverAppVideos(DRIVER_GUIDE_LANGUAGES
          .filter(({ code }) => appByLanguage[code])
          .map(({ code }) => ({ label: DRIVER_APP_VIDEO_LABELS[code] || code, url: appByLanguage[code] })));
      })
      // Without the links the message is copied without videos, as before.
      .catch(() => {
        setDriverGuideVideos({});
        setDriverAppVideos([]);
      });
  }, []);
  const [hotelAmountChecked, setHotelAmountChecked] = useState({});
  const [hotelPaymentsByBooking, setHotelPaymentsByBooking] = useState({}); // { [bookingId]: Summary[] }
  const [hotelPaymentsLoading, setHotelPaymentsLoading] = useState({});
  const [sort, setSort] = useState(savedFilters.sort || { key: "createdAt", dir: "desc" });
  const [columnFilters, setColumnFilters] = useState(savedFilters.columnFilters || {});
  const [filterOpenRequest, setFilterOpenRequest] = useState(null);
  const [filtersRestored, setFiltersRestored] = useState(Boolean(
    savedFilters.query || (savedFilters.statusFilter && savedFilters.statusFilter !== "ALL") || Object.keys(savedFilters.columnFilters || {}).length
  ));
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const silence = useSilenceAlerts();
  const silenceByBookingId = useMemo(
    () => new Map(silence.alerts.map((entry) => [entry.bookingId, entry])),
    [silence.alerts]
  );
  const notReady = useNotReadyAlerts();
  const notReadyBookingIds = useMemo(
    () => new Set(notReady.alerts.map((entry) => entry.bookingId)),
    [notReady.alerts]
  );
  const paymentAlerts = usePaymentAlerts();

  // A refresh starts a new generation; anything still arriving from an older one is dropped.
  const load = async () => {
    const generation = ++loadGeneration.current;
    const isCurrent = () => generation === loadGeneration.current;
    setLoading(true);
    setCompletedLoad("idle");
    setCompletedTrackingRows([]);
    api.get("/api/admin/bookings/status-counts")
      .then(({ data }) => { if (isCurrent()) setServerStatusCounts(data || null); })
      .catch(() => { if (isCurrent()) setServerStatusCounts(null); });
    // The table shows as soon as the bookings arrive; the tracking column fills in after.
    listTripTrackingRows({ excludeStatus: "COMPLETED" })
      .then((data) => { if (isCurrent()) setActiveTrackingRows(data || []); })
      .catch(() => {});
    try {
      const { data } = await api.get("/api/admin/bookings", { params: { excludeStatus: "COMPLETED" } });
      if (isCurrent()) setRows(data || []);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const loadCompleted = async () => {
    const generation = loadGeneration.current;
    const isCurrent = () => generation === loadGeneration.current;
    setCompletedLoad("loading");
    listTripTrackingRows({ status: "COMPLETED" })
      .then((data) => { if (isCurrent()) setCompletedTrackingRows(data || []); })
      .catch(() => {});
    try {
      const { data } = await api.get("/api/admin/bookings", { params: { status: "COMPLETED" } });
      if (!isCurrent()) return;
      const fetched = data || [];
      const fetchedIds = new Set(fetched.map((booking) => booking.id));
      // A booking marked completed on this screen is already in the list; the fetched copy replaces it.
      setRows((prev) => [...prev.filter((row) => !fetchedIds.has(row.id)), ...fetched]);
      setCompletedLoad("loaded");
    } catch {
      if (isCurrent()) setCompletedLoad("error");
    }
  };

  const needsCompleted = statusFilter === "ALL" || statusFilter === "COMPLETED";
  const tableLoading = loading || (needsCompleted && (completedLoad === "idle" || completedLoad === "loading"));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (needsCompleted && !loading && completedLoad === "idle") loadCompleted();
  }, [needsCompleted, loading, completedLoad]);

  // Events of a booking in the completed half come from there, even if the active half has an
  // older copy (a booking marked completed on this screen, or a full timeline refresh).
  const trackingRows = useMemo(() => {
    if (!completedTrackingRows.length) return activeTrackingRows;
    const completedIds = new Set(completedTrackingRows.map((event) => event.bookingId));
    return [...activeTrackingRows.filter((event) => !completedIds.has(event.bookingId)), ...completedTrackingRows];
  }, [activeTrackingRows, completedTrackingRows]);

  useEffect(() => {
    localStorage.setItem(BOOKING_FILTER_STORAGE_KEY, JSON.stringify({ query, statusFilter, columnFilters, sort }));
  }, [query, statusFilter, columnFilters, sort]);

  useEffect(() => {
    const updateWidth = () => setTableScrollWidth(tableRef.current?.scrollWidth || 0);
    updateWidth();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateWidth);
    if (tableRef.current) observer?.observe(tableRef.current);
    window.addEventListener("resize", updateWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [tableLoading, rows]);

  const syncTableScroll = (event) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const syncTopScroll = (event) => {
    if (topScrollRef.current) topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const normalized = useMemo(
    () =>
      (rows || []).map((b) => ({
        id: b.id,
        tripId: b.tripId ?? b.trip?.id ?? null,
        tripTitle: b.tripTitle ?? b.trip?.title ?? "",
        tripCategory: b.tripCategory ?? b.trip_category ?? b.trip?.category?.title ?? "",
        path: b.tripPath ?? b.trip_path ?? b.trip?.path ?? "",
        pickupLabel: b.pickupLabel ?? b.pickup_label_snapshot ?? "",
        dynamicPricing: Boolean(b.dynamicPricing ?? b.dynamic_pricing),
        vehicleNameSnapshot:
          b.vehicleNameSnapshot ?? b.vehicle_name_snapshot ?? "",
        acTypeSnapshot: b.acTypeSnapshot ?? b.ac_type_snapshot ?? "",
        hotelSummarySnapshot:
          b.hotelSummarySnapshot ?? b.hotel_summary_snapshot ?? "",
        addonSummarySnapshot:
          b.addonSummarySnapshot ?? b.addon_summary_snapshot ?? "",
        quoteSnapshot: b.quoteSnapshot ?? b.quote_snapshot ?? "",
        totalPrice:
          b.totalPrice != null
            ? Number(b.totalPrice)
            : b.total_price != null
            ? Number(b.total_price)
            : null,
        userId: b.userId ?? b.user?.id ?? null,
        userName:
          b.userName ??
          b.user?.name ??
          b.user?.fullName ??
          (b.user
            ? `${b.user.firstName || ""} ${b.user.lastName || ""}`.trim()
            : ""),
        userEmail: b.userEmail ?? b.user?.email ?? "",
        userPhone: b.userPhone ?? b.user?.phone ?? "",
        driverLanguages: Array.isArray(b.driverLanguages ?? b.driver_languages)
          ? (b.driverLanguages ?? b.driver_languages)
          : [],
        travelDate: b.travelDate, // "YYYY-MM-DD" - package Day 1
        // When the vehicle runs: earlier/later than the package when an extra night includes it.
        ...bookingVehicleDates(b),
        createdAt: b.createdAt, // ISO or DB timestamp
        noOfTravellers: b.noOfTravellers,
        tripStatus: b.tripStatus,
        driverAssignedToTrip: Boolean(b.driverAssignedToTrip),
        // Vehicles added on request (2, 3 ...); "Driver: Assigned" needs every vehicle's driver.
        extraVehicles: Array.isArray(b.extraVehicles) ? b.extraVehicles : [],
        allDriversAssigned: Boolean(b.allDriversAssigned ?? b.driverAssignedToTrip),
        hotelsBlocked: Boolean(b.hotelsBlocked),
        addonsArranged: Boolean(b.addonsArranged ?? b.addons_arranged),
        addonDates: b.addonDates && typeof b.addonDates === "object" ? { ...b.addonDates } : {},
        breakfastArranged: Boolean(b.breakfastArranged ?? b.breakfast_arranged),
        facilityChecks: Array.isArray(b.facilityChecks ?? b.facility_checks) ? (b.facilityChecks ?? b.facility_checks) : [],
        cashbackReviewRequestAllowed: Boolean(b.cashbackReviewRequestAllowed ?? b.cashback_review_request_allowed),
        cashbackReviewRequestNote: b.cashbackReviewRequestNote ?? b.cashback_review_request_note ?? "",
        paymentReminderSent: Boolean(b.paymentReminderSent ?? b.payment_reminder_sent),
        paymentReminderSentAt: b.paymentReminderSentAt ?? b.payment_reminder_sent_at ?? null,
        paymentReminderSnoozedUntil: b.paymentReminderSnoozedUntil ?? b.payment_reminder_snoozed_until ?? null,
        paymentReminderDisabled: Boolean(b.paymentReminderDisabled ?? b.payment_reminder_disabled ?? false),
        currentRevisionNumber: Number(b.currentRevisionNumber ?? b.current_revision_number ?? 0),
        receiptReshareRequired: Boolean(b.receiptReshareRequired ?? b.receipt_reshare_required),
        policyReshareRequired: Boolean(b.policyReshareRequired ?? b.policy_reshare_required),
        latestReceiptNumber: b.latestReceiptNumber ?? b.latest_receipt_number ?? "",
        latestReceiptSharedAt: b.latestReceiptSharedAt ?? b.latest_receipt_shared_at ?? null,
        latestPolicySharedAt: b.latestPolicySharedAt ?? b.latest_policy_shared_at ?? null,
        bookingType: (b.bookingType || b.booking_type || "PACKAGE").toUpperCase(),
        description: b.description ?? "",
        bookingNotes: b.bookingNotes ?? b.booking_notes ?? "",
        travellingContactName: b.travellingContactName ?? "",
        travellingContactPhone: b.travellingContactPhone ?? "",
        couponId: b.couponId ?? b.coupon?.id ?? null,
        couponCode: b.couponCode ?? b.coupon?.code ?? "",
        advancePaid:
          b.advancePaid != null
            ? Number(b.advancePaid)
            : b.advance_paid != null
            ? Number(b.advance_paid)
            : 0,
        bookingAdvanceAmount: b.bookingAdvanceAmount ?? b.booking_advance_amount ?? null,
        driverId: b.driverId ?? b.driver_id ?? null,
        driverName: b.driverName ?? b.driver_name ?? "",
        driverPhone: b.driverPhone ?? b.driver_phone ?? "",
        driverPortalUrl: b.driverPortalUrl ?? b.driver_portal_url ?? "",
        assignedDriverLanguages: Array.isArray(b.assignedDriverLanguages ?? b.assigned_driver_languages)
          ? (b.assignedDriverLanguages ?? b.assigned_driver_languages)
          : [],
        vehicleOwnerId: b.vehicleOwnerId ?? b.vehicle_owner_id ?? null,
        vehicleOwnerName: b.vehicleOwnerName ?? b.vehicle_owner_name ?? "",
        vehicleOwnerPhone: b.vehicleOwnerPhone ?? b.vehicle_owner_phone ?? "",
        vehicleOwnerLanguages: Array.isArray(b.vehicleOwnerLanguages ?? b.vehicle_owner_languages)
          ? (b.vehicleOwnerLanguages ?? b.vehicle_owner_languages)
          : [],
        assignedVehicleName: b.assignedVehicleName ?? b.assigned_vehicle_name ?? "",
        assignedVehicleRegistrationNumber: b.assignedVehicleRegistrationNumber ?? b.assigned_vehicle_registration_number ?? "",
        trackingPublicToken: b.trackingPublicToken ?? b.tracking_public_token ?? "",
        driverGpsTestDone: Boolean(b.driverGpsTestDone ?? b.driver_gps_test_done),
        driverGpsLatitude: b.driverGpsLatitude ?? b.driver_gps_latitude ?? null,
        driverGpsLongitude: b.driverGpsLongitude ?? b.driver_gps_longitude ?? null,
        driverGpsAccuracy: b.driverGpsAccuracy ?? b.driver_gps_accuracy ?? null,
        driverGpsTestedAt: b.driverGpsTestedAt ?? b.driver_gps_tested_at ?? null,
        driverPracticeCheckDone: Boolean(b.driverPracticeCheckDone ?? b.driver_practice_check_done),
        driverPracticeCheckedAt: b.driverPracticeCheckedAt ?? b.driver_practice_checked_at ?? null,
        driverReadinessDeviceInfo: b.driverReadinessDeviceInfo ?? b.driver_readiness_device_info ?? "",
        driverWhatsappText: b.driverWhatsappText ?? b.driver_whatsapp_text ?? "",
        customerWhatsappText: b.customerWhatsappText ?? b.customer_whatsapp_text ?? "",
        customerWhatsappSent: Boolean(b.customerWhatsappSent ?? b.customer_whatsapp_sent),
        customerWhatsappSentAt: b.customerWhatsappSentAt ?? b.customer_whatsapp_sent_at ?? null,
        customerPolicy: b.customerPolicy ?? b.customer_policy ?? null,
        pickupPoint: b.pickupPoint ?? b.pickup_point ?? null,
        driverCallConfirmed: Boolean(b.driverCallConfirmed ?? b.driver_call_confirmed),
        driverCallConfirmedAt: b.driverCallConfirmedAt ?? b.driver_call_confirmed_at ?? null,
        stickyNote: b.stickyNote ?? b.sticky_note ?? "",
        stickyNoteUpdatedAt: b.stickyNoteUpdatedAt ?? b.sticky_note_updated_at ?? null,
        stickyNoteDoneAt: b.stickyNoteDoneAt ?? b.sticky_note_done_at ?? null,
        silenceAlertsOffAt: b.silenceAlertsOffAt ?? b.silence_alerts_off_at ?? null,
        silenceAlertsOffBy: b.silenceAlertsOffBy ?? b.silence_alerts_off_by ?? "",
        hotelConfirmations: b.hotelConfirmations ?? b.hotel_confirmations ?? [],
        epasses: Array.isArray(b.epasses) ? b.epasses : [],
        documents: Array.isArray(b.documents) ? b.documents : [],
        reviewRequestSentAt: b.reviewRequestSentAt ?? b.review_request_sent_at ?? null,
        reviewNotAsked: Boolean(b.reviewNotAsked ?? b.review_not_asked),
        reviewNotAskedReason: b.reviewNotAskedReason ?? b.review_not_asked_reason ?? "",
        cashbackGoogleReviewReceived: Boolean(b.cashbackGoogleReviewReceived ?? b.cashback_google_review_received),
        cashbackReviewVideoReceived: Boolean(b.cashbackReviewVideoReceived ?? b.cashback_review_video_received),
        cashbackTravelPhotosReceived: Boolean(b.cashbackTravelPhotosReceived ?? b.cashback_travel_photos_received),
        totalCashbackAmount: Number(b.totalCashbackAmount ?? b.total_cashback_amount ?? 0),
        cashbackPaid: Boolean(b.cashbackPaid ?? b.cashback_paid),
        dayWisePlan: b.dayWisePlan ?? b.day_wise_plan ?? null,
        refreshmentRoomName: b.refreshmentRoomName ?? "",
        // Vehicle 1's own payable, for its driver's message; the totals below add every vehicle.
        vehicleOneTransportPayable: Number(b.transportVendorPayable ?? b.transport_vendor_payable ?? 0),
        transportVendorPayable: Number(b.transportVendorPayable ?? b.transport_vendor_payable ?? 0) + extraVehicleTotal(b, "transportPayable"),
        transportVendorPaid: Number(b.transportVendorPaid ?? b.transport_vendor_paid ?? 0) + extraVehicleTotal(b, "transportPaid"),
        transportVendorBalance: Number(b.transportVendorBalance ?? b.transport_vendor_balance ?? 0) + extraVehicleTotal(b, "transportBalance"),
        hotelVendorPayable: Number(b.hotelVendorPayable ?? b.hotel_vendor_payable ?? 0),
        hotelVendorPaid: Number(b.hotelVendorPaid ?? b.hotel_vendor_paid ?? 0),
        hotelVendorBalance: Number(b.hotelVendorBalance ?? b.hotel_vendor_balance ?? 0),
        travellers: b.travellers || [],
        raw: b,
      })),
    [rows]
  );


  const trackingBookings = useMemo(
    () => buildBookingRows(rows || [], trackingRows || []),
    [rows, trackingRows]
  );

  const trackingByBookingId = useMemo(() => {
    const map = new Map();
    trackingBookings.forEach((booking) => map.set(booking.bookingId, booking));
    return map;
  }, [trackingBookings]);

  const refreshTrackingTimeline = async () => {
    setTrackingRefreshing(true);
    try {
      const data = await listTripTrackingRows();
      setActiveTrackingRows(data || []);
      if (selectedTrackingBooking) {
        const next = buildBookingRows(rows || [], data || []).find((booking) => booking.bookingId === selectedTrackingBooking.bookingId) || selectedTrackingBooking;
        setSelectedTrackingBooking(next);
      }
    } catch (error) {
      alert(error?.response?.data?.message || "Could not refresh tracking timeline.");
    } finally {
      setTrackingRefreshing(false);
    }
  };
  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(STATUS_OPTIONS.map((status) => [status, 0]));
    normalized.forEach((booking) => {
      const status = String(booking.tripStatus || "PENDING").toUpperCase();
      counts[status] = (counts[status] || 0) + 1;
    });
    // Until the completed bookings are loaded, their count comes from the server. Any booking
    // marked completed here since the page loaded is in the local list and added on top.
    if (completedLoad !== "loaded" && serverStatusCounts) {
      counts.COMPLETED = (counts.COMPLETED || 0) + Number(serverStatusCounts.COMPLETED || 0);
    }
    return counts;
  }, [normalized, completedLoad, serverStatusCounts]);

  const totalBookingCount = completedLoad !== "loaded" && serverStatusCounts
    ? normalized.length + Number(serverStatusCounts.COMPLETED || 0)
    : normalized.length;

  const filterColumns = useMemo(() => ([
    ["id", "ID", "text"], ["tripStatus", "Status", "values"], ["tripPreparation", "Trip Preparation", "values"], ["review", "Review", "values"],
    ["driverName", "Driver Details", "values"], ["vehicleOwnerName", "Vehicle Owner", "values"], ["userName", "User", "values"],
    ["driverLanguages", "Preferred Language", "values"], ["bookingNotes", "Notes", "values"], ["tripTitle", "Trip", "values"],
    ["travelDate", "Travel Date", "date"], ["noOfTravellers", "Travellers", "number"], ["customerPayment", "Customer Payments", "payment"],
    ["transportPayment", "Transport Payments", "payment"], ["hotelPayment", "Hotel Payments", "payment"],
    ["tracking", "Tracking", "values"], ["driverWhatsapp", "Driver WhatsApp", "values"], ["hotelWhatsapp", "Hotel WhatsApp", "values"],
    ["customerWhatsapp", "Customer WhatsApp", "values"], ["createdAt", "Created", "date"],
  ]), []);

  // Review is asked while the trip is on and followed up once it is over, so its column shows on the
  // Ongoing and Completed tabs. A Review filter left over from those tabs is dropped elsewhere,
  // where the column is not there to clear it.
  const showReviewColumn = statusFilter === "ONGOING" || statusFilter === "COMPLETED";
  useEffect(() => {
    if (!showReviewColumn && columnFilters.review) {
      setColumnFilters((current) => {
        const next = { ...current };
        delete next.review;
        return next;
      });
    }
  }, [showReviewColumn, columnFilters.review]);

  const filterBaseRows = useMemo(() => normalized.filter((booking) => (
    (statusFilter === "ALL" || booking.tripStatus === statusFilter)
    && bookingMatchesQuery(booking, query)
  )), [normalized, query, statusFilter]);

  const columnFilterOptions = useMemo(() => Object.fromEntries(
    filterColumns.map(([key]) => {
      const rowsForColumn = filterBaseRows.filter((booking) => Object.entries(columnFilters).every(
        ([activeKey, filter]) => activeKey === key || bookingMatchesColumnFilter(booking, activeKey, filter)
      ));
      const counts = new Map();
      rowsForColumn.flatMap((row) => bookingColumnFilterValues(row, key)).filter(Boolean).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
      (columnFilters[key]?.values || []).forEach((value) => { if (!counts.has(value)) counts.set(value, 0); });
      return [key, [...counts.entries()].map(([value, count]) => ({
        value,
        count,
        label: key === "travelDate" || key === "createdAt" ? friendlyBookingFilterDate(value) : value,
      })).sort((left, right) => left.value.localeCompare(right.value, undefined, { numeric: true, sensitivity: "base" }))];
    })
  ), [filterColumns, filterBaseRows, columnFilters]);

  const updateColumnFilter = (key, value) => {
    setColumnFilters((current) => {
      const next = { ...current };
      const hasValue = value && Object.entries(value).some(([field, fieldValue]) => field !== "type" && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== "" && fieldValue != null));
      if (hasValue) next[key] = value;
      else delete next[key];
      return next;
    });
  };

  const filtered = useMemo(() => {
    const columnMatched = filterBaseRows.filter((booking) => Object.entries(columnFilters).every(
      ([key, filter]) => bookingMatchesColumnFilter(booking, key, filter)
    ));
    return sortBookingRows(columnMatched, sort.key, sort.dir);
  }, [filterBaseRows, columnFilters, sort]);

  // Cancelled bookings are left out: whatever they still show as payable is not money we expect to send.
  const vendorDueTotals = useMemo(() => {
    const totals = { transport: 0, hotel: 0, bookings: 0 };
    filtered.forEach((booking) => {
      if (booking.tripStatus === "CANCELLED") return;
      const transport = Math.max(0, booking.transportVendorBalance);
      const hotel = Math.max(0, booking.hotelVendorBalance);
      totals.transport += transport;
      totals.hotel += hotel;
      if (transport > 0 || hotel > 0) totals.bookings += 1;
    });
    return totals;
  }, [filtered]);

  const clearAllBookingFilters = () => {
    setQuery("");
    setStatusFilter("ALL");
    setColumnFilters({});
    setFiltersRestored(false);
  };

  const applySavedBookingView = (view) => {
    setQuery("");
    setFiltersRestored(false);
    if (view === "upcoming-preparation") {
      setStatusFilter("UPCOMING");
      setColumnFilters({ tripPreparation: { type: "values", values: ["Not ready"] } });
    } else if (view === "ongoing-payment") {
      setStatusFilter("ONGOING");
      setColumnFilters({ customerPayment: { type: "payment", values: ["Unpaid", "Partially paid"] } });
    } else if (view === "hotels-pending") {
      setStatusFilter("ALL");
      setColumnFilters({ tripPreparation: { type: "values", values: ["Hotels pending"] } });
    } else if (view === "driver-pending") {
      setStatusFilter("ALL");
      setColumnFilters({ tripPreparation: { type: "values", values: ["Driver pending"] } });
    }
  };

  const requestSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === "asc" ? "desc" : "asc",
    }));
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    // TODO: adjust if your endpoint differs
    await api.delete(`/api/admin/bookings/${id}`);
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const onPatched = (updated) => {
    if (!updated?.id) return;
    setRows((prev) => {
      const exists = prev.some((r) => r.id === updated.id);
      if (!exists) return [updated, ...prev];
      return prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r));
    });
  };

  const copyBalanceReminder = async (booking) => {
    const paymentLines = companyPaymentLines(paymentDetails);
    const total = adjustedBookingTotal(booking);
    const balance = Math.max(0, Number(total || 0) - Number(booking.advancePaid || 0));
    const message = [
      `Dear ${booking.userName || "Customer"},`,
      "",
      `This is a reminder that ₹${balance.toLocaleString("en-IN")} is pending for Booking #${booking.id}.`,
      `Trip: ${booking.tripTitle || "-"}`,
      `Travel Date: ${formatDate(booking.travelDate)}`,
      "",
      "We kindly request you to complete the pending payment today, soon after you check in to your stay.",
      "This helps us keep the rest of your trip smooth and hassle-free. Thank you for your cooperation!",
      "",
      // Nothing saved yet means no payment block at all, rather than an empty heading.
      ...(paymentLines.length ? ["Payment Details", ...paymentLines, ""] : []),
      "Regards,",
      "IO Trips",
    ].join("\n");
    await navigator.clipboard.writeText(message);
    alert(paymentLines.length
      ? "Payment reminder copied."
      : "Payment reminder copied, but no payment details are saved yet - add them under Payment Details.");
  };

  // Sent the night before the trip: the after-boarding payment, still owed, paid to IO Trips. The
  // driver makes sure it is paid once the customers board.
  const copyAfterBoardingReminder = async (booking) => {
    const schedule = bookingPaymentSchedule(booking, adjustedBookingTotal(booking));
    const due = Number(schedule?.afterBoardingDue || 0);
    if (due <= 0) return;
    const paymentLines = companyPaymentLines(paymentDetails);
    // Boarding is the vehicle start date, which comes before package Day 1 when there are extra nights.
    const boardingDate = String(booking.vehicleStartDate || booking.travelDate || "").slice(0, 10);
    const when = boardingDate === localIsoDate(1) ? "tomorrow, " : boardingDate === localIsoDate(0) ? "today, " : "on ";
    const message = [
      `Dear ${booking.userName || "Customer"},`,
      "",
      `Your trip starts ${when}${formatDate(boardingDate)}. We look forward to having you with us!`,
      "",
      `Booking #${booking.id}`,
      `Trip: ${booking.tripTitle || "-"}`,
      "",
      `Right after you board the vehicle, please pay ₹${due.toLocaleString("en-IN")} to IO Trips using the payment details below.`,
      "",
      ...(paymentLines.length ? ["Payment Details", ...paymentLines, ""] : []),
      "Regards,",
      "IO Trips",
    ].join("\n");
    await navigator.clipboard.writeText(message);
    alert(paymentLines.length
      ? "After-boarding reminder copied."
      : "After-boarding reminder copied, but no payment details are saved yet - add them under Payment Details.");
  };

  // The QR cannot travel with the text: a clipboard write carries either text or an image, and
  // WhatsApp takes them as two separate pastes anyway. So it gets its own button.
  const copyPaymentQr = async () => {
    const url = paymentDetails?.qrImageUrl;
    if (!url) {
      alert("No QR code uploaded yet. Add one under Payment Details in the admin menu.");
      return;
    }
    let blob;
    try {
      // Through our own API, not the CDN: the CDN will show the image but refuses a cross-origin
      // fetch, which is what the clipboard needs.
      blob = await fetchCompanyPaymentQrBlob();
      if (blob.type !== "image/png") blob = await pngBlobFrom(blob);
      if (!window.ClipboardItem || !navigator.clipboard?.write) throw new Error("Image clipboard unsupported");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      alert("Payment QR copied. Paste it into the chat.");
    } catch (error) {
      // Falling back to a download keeps the QR reachable when the clipboard refuses it.
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = "payment-qr.png";
        link.click();
        // Revoked on a delay, matching the day-wise plan download: revoking straight away can
        // cancel the save before the browser has read the blob.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        alert("QR could not be copied, so it was downloaded instead. Attach it from your downloads.");
        return;
      }
      // Nothing was fetched at all, so say why rather than sending everyone to a new tab and
      // leaving them to guess. The status is the useful part: 404 from this route means the
      // server is running a build without it, which a restart fixes.
      console.error("Payment QR copy failed", error);
      const status = error?.response?.status;
      const reason = status === 404
        ? "The server has no QR to give (404). Either no QR is saved, or the backend needs restarting to pick up this feature."
        : status === 401 || status === 403
          ? "Your admin session has expired (" + status + "). Sign in again."
          : status
            ? `The server returned ${status}.`
            : `${error?.message || "The request failed"}.`;
      window.open(url, "_blank", "noopener");
      alert(`QR could not be copied. ${reason}

It has been opened in a new tab so you can copy it from there.`);
    }
  };

  const updateReminderSent = async (booking, sent) => {
    setReminderUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/payment-reminder`, { sent });
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update payment reminder status.");
    } finally {
      setReminderUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const updateCustomerWhatsappSent = async (booking, sent) => {
    setCustomerWhatsappUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/customer-whatsapp-sent`, { sent });
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the customer message status.");
    } finally {
      setCustomerWhatsappUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const updateDriverCallConfirmed = async (booking, confirmed) => {
    setDriverCallUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/driver-call-confirmed`, { confirmed });
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the driver call check.");
    } finally {
      setDriverCallUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const updatePreparationChecks = async (booking, patch) => {
    setPreparationUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/preparation-checks`, patch);
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update the trip checklist.");
    } finally {
      setPreparationUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const updateReminderDisabled = async (booking, disabled) => {
    setReminderUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/payment-reminder-disabled`, { disabled });
      onPatched(data);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update payment reminder setting.");
    } finally {
      setReminderUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const updateReminderSnooze = async (booking, hours) => {
    setReminderUpdating((old) => ({ ...old, [booking.id]: true }));
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/payment-reminder-snooze`, { hours });
      onPatched(data);
      setReminderSnoozeHours((old) => ({ ...old, [booking.id]: "" }));
    } catch (error) {
      alert(error?.response?.data?.message || "Could not snooze payment reminder.");
    } finally {
      setReminderUpdating((old) => ({ ...old, [booking.id]: false }));
    }
  };

  const quickStatusChange = async (id, value) => {
    try {
      setStatusUpdating((m) => ({ ...m, [id]: true }));
      const current = rows.find((r) => r.id === id);
      if ((value === "ONGOING" || value === "COMPLETED")
          && !bookingPreparationReady(current)) {
        alert("Confirm all required Trip Preparation items before changing this status.");
        setStatusUpdating((m) => ({ ...m, [id]: false }));
        return;
      }
      if (value === "COMPLETED" && !reviewDecided(current)) {
        alert("Send the review request or tick Do not ask for review (Review column) before completing the trip.");
        setStatusUpdating((m) => ({ ...m, [id]: false }));
        return;
      }
      // Enforce advance payment before moving to UPCOMING
      if (value === "UPCOMING") {
        const adv = Number(
          current?.advancePaid != null
            ? current.advancePaid
            : current?.raw?.advancePaid ?? current?.raw?.advance_paid ?? 0
        ) || 0;
        if (adv <= 0) {
          alert("Cannot set to UPCOMING: please record an advance payment first.");
          setStatusUpdating((m) => ({ ...m, [id]: false }));
          return;
        }
      }
      // For COMPLETED, rely on modal/back-end validation (discounts can apply)
      // Try PATCH first
      const body = { tripStatus: value };
      try {
        const { data } = await api.patch(`/api/admin/bookings/${id}`, body);
        onPatched(data || { id, tripStatus: value });
      } catch {
        // Fallback to PUT with full payload (fetch current row)
        const current = rows.find((r) => r.id === id);
        if (!current) throw new Error("Row not found");
        const payload = {
          id,
          userId: current.userId,
          tripId: current.tripId,
          tripTitle: current.tripTitle,
          travelDate: current.travelDate,
          noOfTravellers: current.noOfTravellers,
          tripStatus: value,
          couponId: current.couponId ?? null,
          bookingType: current.bookingType,
          description: current.description,
          pricePerPerson:
            current.bookingType === 'CUSTOM' && (current.pricePerPerson != null || current.price_per_person != null)
              ? Number(current.pricePerPerson ?? current.price_per_person)
              : null,
          priceListId:
            current.bookingType === 'PACKAGE' ? (current.priceListId ?? current.price_list_id ?? null) : null,
          advancePaid:
            current.advancePaid != null
              ? Number(current.advancePaid)
              : current.advance_paid != null
              ? Number(current.advance_paid)
              : null,
          dynamicPricing: Boolean(current.dynamicPricing ?? current.dynamic_pricing),
          pickupLabelSnapshot: current.pickupLabel ?? current.pickup_label_snapshot ?? null,
          vehicleNameSnapshot: current.vehicleNameSnapshot ?? current.vehicle_name_snapshot ?? null,
          acTypeSnapshot: current.acTypeSnapshot ?? current.ac_type_snapshot ?? null,
          hotelSummarySnapshot: current.hotelSummarySnapshot ?? current.hotel_summary_snapshot ?? null,
          addonSummarySnapshot: current.addonSummarySnapshot ?? current.addon_summary_snapshot ?? null,
          quoteSnapshot: current.quoteSnapshot ?? current.quote_snapshot ?? null,
          totalPrice: current.totalPrice ?? current.total_price ?? null,
          cashbackReviewRequestAllowed: Boolean(current.cashbackReviewRequestAllowed ?? current.cashback_review_request_allowed),
          cashbackReviewRequestNote: current.cashbackReviewRequestNote ?? current.cashback_review_request_note ?? "",
        };
        const { data } = await api.put(`/api/admin/bookings/${id}`, payload);
        onPatched(data || payload);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to update status.");
    } finally {
      setStatusUpdating((m) => ({ ...m, [id]: false }));
    }
  };

  const columnFilterMenu = (key, label, type = "values", toggleLabel) => (
    <BookingFilterMenu
      label={label}
      toggleLabel={toggleLabel}
      type={type}
      filter={columnFilters[key]}
      options={columnFilterOptions[key] || []}
      resultCount={filtered.length}
      openRequest={filterOpenRequest === key}
      onOpened={() => setFilterOpenRequest(null)}
      onChange={(value) => updateColumnFilter(key, value)}
    />
  );

  return (
    <>
      <Card className={`booking-manager-card shadow-sm mb-3${fullScreen ? " booking-manager-fullscreen" : ""}`}>
        <Card.Header className="booking-manager-header d-flex flex-wrap gap-3 justify-content-between align-items-center">
          <div>
            <div className="booking-manager-title">Manage Bookings</div>
            <div className="booking-manager-subtitle">Track trips, preparation, vendors and customer payments</div>
          </div>
          <div className="d-flex gap-2 flex-wrap booking-manager-toolbar">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setEditing(null);
                setShow(true);
              }}
            >
              + New Booking
            </Button>
            <InputGroup size="sm" className="booking-manager-filter">
              <InputGroup.Text>Status</InputGroup.Text>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All ({totalBookingCount})</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s} ({statusCounts[s] || 0})
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
            <InputGroup size="sm" className="booking-manager-search">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="id, user, email, trip, coupon, date…"
              />
            </InputGroup>
            <Button size="sm" variant="outline-secondary" onClick={load}>
              Refresh
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setFullScreen((value) => !value)}
              title={fullScreen ? "Exit full screen (Esc)" : "Fill the window with the bookings table"}
            >
              <i
                className={`bi ${fullScreen ? "bi-fullscreen-exit" : "bi-arrows-fullscreen"} me-1`}
                aria-hidden="true"
              />
              {fullScreen ? "Exit full screen" : "Full screen"}
            </Button>
          </div>
        </Card.Header>
        <div className="booking-status-overview">
          <button type="button" className={`booking-stat booking-stat-all ${statusFilter === "ALL" ? "active" : ""}`} onClick={() => setStatusFilter("ALL")}>
            <span>All Bookings</span><strong>{totalBookingCount}</strong>
          </button>
          {STATUS_OPTIONS.map((status) => {
            const silent = status === "ONGOING" ? silence.alerts : [];
            const unready = notReady.alerts.filter((entry) => entry.tripStatus === status);
            const alarms = [...silent, ...unready];
            const silentClass = !alarms.length ? "" : alarms.some((entry) => !entry.seenAtIst) ? " booking-stat-silent booking-stat-silent-unseen" : " booking-stat-silent";
            return (
              <button type="button" key={status} className={`booking-stat booking-stat-${status.toLowerCase()}${silentClass} ${statusFilter === status ? "active" : ""}`} onClick={() => setStatusFilter(status)}>
                <span>{status}</span>
                {silent.length > 0 && <span className="booking-stat-alarm">{silent.length} no location</span>}
                {unready.length > 0 && <span className="booking-stat-alarm">{unready.length} not ready</span>}
                {status === "ONGOING" && paymentAlerts.alerts.length > 0 && (
                  <span className="booking-stat-payment">{paymentAlerts.alerts.length} ask payment</span>
                )}
                <strong>{statusCounts[status] || 0}</strong>
              </button>
            );
          })}
        </div>

        <SilenceAlertPanel
          alerts={silence.alerts}
          now={silence.now}
          soundBlocked={silence.soundBlocked}
          seenBusy={silence.seenBusy}
          onSeen={silence.markSeen}
          onEnableSound={silence.enableSound}
          canOpenTimeline={(bookingId) => trackingByBookingId.has(bookingId)}
          onOpenTimeline={(bookingId) => setSelectedTrackingBooking(trackingByBookingId.get(bookingId))}
        />

        <NotReadyAlertPanel
          alerts={notReady.alerts}
          soundBlocked={notReady.soundBlocked}
          seenBusy={notReady.seenBusy}
          onSeen={notReady.markSeen}
          onEnableSound={notReady.enableSound}
          onShowBooking={(bookingId) => {
            clearAllBookingFilters();
            setQuery(String(bookingId));
          }}
        />

        <PaymentAlertPanel
          alerts={paymentAlerts.alerts}
          soundBlocked={paymentAlerts.soundBlocked}
          askedBusy={paymentAlerts.askedBusy}
          onAsked={paymentAlerts.markAsked}
          onEnableSound={paymentAlerts.enableSound}
          onCopyReminder={(bookingId) => {
            const booking = rows.find((row) => row.id === bookingId);
            if (booking) {
              void copyBalanceReminder(booking);
            } else {
              alert(`Booking #${bookingId} is not loaded yet - use Show booking, then copy the reminder from its row.`);
            }
          }}
          onShowBooking={(bookingId) => {
            clearAllBookingFilters();
            setQuery(String(bookingId));
          }}
        />

        <div className="booking-saved-views">
          <span className="small fw-semibold">Quick views</span>
          <Form.Select size="sm" aria-label="Open a saved booking view" defaultValue="" onChange={(event) => { applySavedBookingView(event.target.value); event.target.value = ""; }}>
            <option value="" disabled>Select a view...</option>
            <option value="upcoming-preparation">Upcoming preparation pending</option>
            <option value="ongoing-payment">Ongoing payment pending</option>
            <option value="hotels-pending">Hotels pending</option>
            <option value="driver-pending">Driver pending</option>
          </Form.Select>
          {filtersRestored && <Badge bg="info" text="dark">Filters restored</Badge>}
          {!tableLoading && (
            <Dropdown align="end" className="booking-vendor-due">
              <Dropdown.Toggle size="sm" variant="light" title="Still to pay vendors for the bookings shown">
                Vendor due <strong>₹{(vendorDueTotals.transport + vendorDueTotals.hotel).toLocaleString("en-IN")}</strong>
              </Dropdown.Toggle>
              <Dropdown.Menu className="booking-vendor-due-menu">
                <div className="booking-vendor-due-line"><span>Transport</span><strong>₹{vendorDueTotals.transport.toLocaleString("en-IN")}</strong></div>
                <div className="booking-vendor-due-line"><span>Hotel</span><strong>₹{vendorDueTotals.hotel.toLocaleString("en-IN")}</strong></div>
                <div className="booking-vendor-due-line booking-vendor-due-sum"><span>Total</span><strong>₹{(vendorDueTotals.transport + vendorDueTotals.hotel).toLocaleString("en-IN")}</strong></div>
                <div className="booking-vendor-due-note">
                  Across {vendorDueTotals.bookings} of {filtered.length} shown booking{filtered.length === 1 ? "" : "s"}. Cancelled and misc vendors not included.
                </div>
              </Dropdown.Menu>
            </Dropdown>
          )}
          <SilenceCheckOffNote check={silence.check} />
        </div>

        {(Object.keys(columnFilters).length > 0 || query || statusFilter !== "ALL") && (
          <div className="booking-active-filters">
            <span className="small fw-semibold">Showing {filtered.length} of {totalBookingCount}</span>
            {filterColumns.filter(([key]) => columnFilters[key]).map(([key, label]) => (
              <span key={key} className="booking-filter-chip">
                <button type="button" onClick={() => setFilterOpenRequest(key)}>{label}: {bookingFilterSummary(columnFilters[key])}</button>
                <button type="button" className="booking-filter-chip-remove" aria-label={`Clear ${label} filter`} onClick={() => updateColumnFilter(key, "")}>x</button>
              </span>
            ))}
            <Button size="sm" variant="outline-secondary" onClick={clearAllBookingFilters}>Clear all filters</Button>
          </div>
        )}

        {needsCompleted && completedLoad === "error" && !loading && (
          <div className="booking-active-filters text-danger">
            <span className="small fw-semibold">Completed bookings could not be loaded, so they are missing from this list.</span>
            <Button size="sm" variant="outline-danger" onClick={() => setCompletedLoad("idle")}>Retry</Button>
          </div>
        )}

        {tableLoading ? (
          <div className="py-5 text-center">
            <Spinner />
            <div className="small text-muted mt-2">{loading ? "Loading…" : "Loading completed bookings…"}</div>
          </div>
        ) : (
          <>
          <div
            ref={topScrollRef}
            className="booking-table-top-scroll"
            onScroll={syncTableScroll}
            aria-label="Bookings table horizontal scroll"
          >
            <div style={{ width: tableScrollWidth, height: 1 }} />
          </div>
          <div ref={tableScrollRef} className="table-responsive sticky-table-scroll" onScroll={syncTopScroll}>
            <Table ref={tableRef} hover className="booking-manager-table mb-0 align-middle">
              <thead>
                <tr><th style={{ width: 44 }}>#</th>
                  <BookingFilterableHeader label="ID" sortKey="id" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("id", "ID", "text")} />
                  <BookingFilterableHeader label="Status" sortKey="tripStatus" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("tripStatus", "Status")} />
                  <BookingFilterableHeader label="Trip Preparation" sortKey="tripPreparation" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("tripPreparation", "Trip Preparation")} />
                  {showReviewColumn && <BookingFilterableHeader label="Review" filterMenu={columnFilterMenu("review", "Review")} />}
                  <BookingFilterableHeader label="Driver Details" sortKey="driverName" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("driverName", "Driver Details")} />
                  <BookingFilterableHeader label="Vehicle Owner" sortKey="vehicleOwnerName" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("vehicleOwnerName", "Vehicle Owner")} />
                  <BookingFilterableHeader
                    label="User"
                    sortKey="userName"
                    sort={sort}
                    onSort={requestSort}
                    filterMenu={<>{columnFilterMenu("userName", "User")}{columnFilterMenu("driverLanguages", "Preferred Language", "values", "Language")}</>}
                  />
                  <BookingFilterableHeader label="Notes" sortKey="bookingNotes" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("bookingNotes", "Notes")} />
                  <BookingFilterableHeader label="Trip" sortKey="tripTitle" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("tripTitle", "Trip")} />
                  <BookingFilterableHeader label="Travel Date" sortKey="travelDate" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("travelDate", "Travel Date", "date")} />
                  <BookingFilterableHeader label="Travellers" sortKey="noOfTravellers" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("noOfTravellers", "Travellers", "number")} />
                  <BookingFilterableHeader label="Customer Payments" sortKey="balance" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("customerPayment", "Customer Payments", "payment")} />
                  <BookingFilterableHeader label="Transport Payments" sortKey="transportVendorBalance" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("transportPayment", "Transport Payments", "payment")} />
                  <BookingFilterableHeader label="Hotel Payments" sortKey="hotelVendorBalance" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("hotelPayment", "Hotel Payments", "payment")} />
                  <BookingFilterableHeader label="Driver WhatsApp" filterMenu={columnFilterMenu("driverWhatsapp", "Driver WhatsApp")} />
                  <BookingFilterableHeader label="Hotel WhatsApp" filterMenu={columnFilterMenu("hotelWhatsapp", "Hotel WhatsApp")} />
                  <BookingFilterableHeader label="Customer WhatsApp" filterMenu={columnFilterMenu("customerWhatsapp", "Customer WhatsApp")} />
                  <BookingFilterableHeader label="Created" sortKey="createdAt" sort={sort} onSort={requestSort} filterMenu={columnFilterMenu("createdAt", "Created", "date")} />
                  <BookingFilterableHeader label="Tracking & Actions" filterMenu={columnFilterMenu("tracking", "Tracking")} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, index) => {
                  const customerTotal = adjustedBookingTotal(b);
                  const customerPaid = Number(b.advancePaid || 0);
                  const customerBalance = customerTotal == null ? null : Math.max(0, Number(customerTotal || 0) - customerPaid);
                  const customerSchedule = bookingPaymentSchedule(b, customerTotal);
                  const hotelMessages = hotelWhatsappMessages(b);
                  // Derive from the quote so trip extensions are reflected even on bookings whose
                  // stored snapshot was written before extension nights were included.
                  const rowQuote = parseQuoteSnapshot(b.quoteSnapshot ?? b.quote_snapshot);
                  const rowDuration = bookingDurationLabel(rowQuote);
                  const rowHotelSummary = hotelSummaryFromQuoteSnapshot(rowQuote) || b.hotelSummarySnapshot || "";
                  const rowRoomUnits = roomUnitsLabel(stayRoomEntries(rowQuote));
                  return (
                  <tr key={b.id} className={`booking-row booking-row-${String(b.tripStatus || "PENDING").toLowerCase()}${silenceByBookingId.has(b.id) || notReadyBookingIds.has(b.id) ? " booking-row-silent" : ""}`}><td className="small fw-semibold">{index + 1}</td>
                    <td className="text-nowrap">
                      <div className="d-flex flex-column align-items-start gap-1">
                        <span className="booking-id-chip">#{b.id}</span>
                      </div>
                    </td>
                    <td className="booking-status-cell">
                      <Form.Select
                        size="sm"
                        className={`booking-status-select booking-status-${String(b.tripStatus || "PENDING").toLowerCase()}`}
                        aria-label={`Status for booking ${b.id}`}
                        value={b.tripStatus || "PENDING"}
                        onChange={(e) => quickStatusChange(b.id, e.target.value)}
                        disabled={!!statusUpdating[b.id]}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Form.Select>
                      {statusUpdating[b.id] && <div className="small text-muted mt-1">Updating...</div>}
                    </td>
                    <td style={{ minWidth: 190 }}>
                      <div className="d-flex flex-column align-items-start gap-1">
                        <Badge bg={bookingPreparationReady(b) ? "success" : "danger"}>
                          {bookingPreparationReady(b) ? "Ready" : "Not ready"}
                        </Badge>
                        <Badge bg={b.allDriversAssigned ? "success" : "warning"} text={b.allDriversAssigned ? undefined : "dark"}>
                          {b.extraVehicles.length ? "Drivers" : "Driver"}: {b.allDriversAssigned ? "Assigned" : "Pending"}
                        </Badge>
                        <Badge bg={b.hotelsBlocked ? "success" : "warning"} text={b.hotelsBlocked ? undefined : "dark"}>
                          Hotels: {b.hotelsBlocked ? "Blocked" : "Pending"}
                        </Badge>
                        <BookingEpassChecklist booking={b} onPatched={onPatched} />
                      </div>
                    </td>
                    {showReviewColumn && (
                      <td>
                        <BookingReviewCell booking={b} onPatched={onPatched} />
                      </td>
                    )}
                    <td style={{ minWidth: 180 }}>
                      <div className="fw-semibold">{b.driverName || "Not assigned"}</div>
                      <PhoneLink phone={b.driverPhone} />
                      {b.assignedVehicleRegistrationNumber && (
                        <div className="small mt-1">
                          <i className="bi bi-car-front me-1" aria-hidden="true" />
                          <span className="fw-semibold text-nowrap">{b.assignedVehicleRegistrationNumber}</span>
                          {b.assignedVehicleName ? <span className="text-muted"> · {b.assignedVehicleName}</span> : null}
                        </div>
                      )}
                      <SpokenLanguages languages={b.assignedDriverLanguages} />
                      <DriverReadinessBadge booking={b} />
                      {b.extraVehicles.map((extra) => (
                        <div key={extra.position} className="mt-2 pt-2 border-top">
                          <div className="small text-muted">Vehicle {extra.position}{extra.quotedVehicleName ? ` · ${extra.quotedVehicleName}` : ""}</div>
                          <div className="fw-semibold">{extra.driverName || "Not assigned"}</div>
                          <PhoneLink phone={extra.driverPhone} />
                          {extra.assignedVehicleNumber && (
                            <div className="small mt-1">
                              <i className="bi bi-car-front me-1" aria-hidden="true" />
                              <span className="fw-semibold text-nowrap">{extra.assignedVehicleNumber}</span>
                              {extra.assignedVehicleName ? <span className="text-muted"> · {extra.assignedVehicleName}</span> : null}
                            </div>
                          )}
                          <SpokenLanguages languages={extra.driverLanguages} />
                        </div>
                      ))}
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <div className="fw-semibold">{b.vehicleOwnerName || "Not assigned"}</div>
                      <PhoneLink phone={b.vehicleOwnerPhone} />
                      <SpokenLanguages languages={b.vehicleOwnerLanguages} />
                      {b.extraVehicles.map((extra) => (
                        <div key={extra.position} className="mt-2 pt-2 border-top">
                          <div className="small text-muted">Vehicle {extra.position}</div>
                          <div className="fw-semibold">{extra.vehicleOwnerName || "Not assigned"}</div>
                          <PhoneLink phone={extra.vehicleOwnerPhone} />
                        </div>
                      ))}
                    </td>
                    <td style={{ minWidth: 200 }}>
                      {(() => {
                        // With no separate travelling contact, the planner is the one travelling too.
                        const separateTraveller = Boolean(b.travellingContactName || b.travellingContactPhone);
                        return (
                          <>
                            <div className="small text-muted">{separateTraveller ? "Planner" : "Planner & Traveller"}</div>
                            <div className="fw-semibold text-truncate">
                              {b.userName || "-"}
                            </div>
                            {/* Lead-converted customers get a placeholder email; the phone is what the team uses. */}
                            <PhoneLink phone={b.userPhone} empty={customerContactEmail(b.userEmail) || "-"} />
                            <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                              <span className="small text-muted">Language:</span>
                              {b.driverLanguages?.length ? (
                                b.driverLanguages.map((language) => (
                                  <Badge bg="light" text="dark" className="border fw-normal" key={language}>{language}</Badge>
                                ))
                              ) : (
                                <span className="small text-muted">-</span>
                              )}
                            </div>
                            {separateTraveller && (
                              <div className="mt-1 pt-1 border-top">
                                <div className="small text-muted">Traveller</div>
                                <div className="fw-semibold text-truncate">{b.travellingContactName || "Travelling guest"}</div>
                                <PhoneLink phone={b.travellingContactPhone} empty="-" />
                              </div>
                            )}
                            <BookingDocuments booking={b} kind={CUSTOMER_ID} label="ID card" compact />
                            {!bookingDocumentsOfKind(b, CUSTOMER_ID).length && ["UPCOMING", "ONGOING"].includes(b.tripStatus) && (
                              <div className="small text-muted mt-1">ID card: not uploaded by customer yet</div>
                            )}
                            <BookingStickyNote booking={b} onPatched={onPatched} />
                            {(() => {
                              // A day before travel the office asks the customer whether the driver has called.
                              const open = !["CANCELLED", "COMPLETED"].includes(b.tripStatus);
                              const startDate = b.vehicleStartDate || b.travelDate;
                              const dueNow = open && !b.driverCallConfirmed && startDate
                                && startDate >= localIsoDate(0) && startDate <= localIsoDate(1);
                              return (
                                <div className="mt-1 pt-1 border-top">
                                  <Form.Check
                                    type="checkbox"
                                    id={`booking-${b.id}-driver-call-confirmed`}
                                    label="Driver called (confirmed with customer)"
                                    className="small"
                                    checked={Boolean(b.driverCallConfirmed)}
                                    disabled={Boolean(driverCallUpdating[b.id])}
                                    onChange={(event) => updateDriverCallConfirmed(b, event.target.checked)}
                                  />
                                  {b.driverCallConfirmedAt ? (
                                    <div className="small text-muted">Confirmed on {formatDateTime(b.driverCallConfirmedAt)}</div>
                                  ) : dueNow ? (
                                    <Badge bg="danger">Check with customer today</Badge>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ minWidth: 220, maxWidth: 280 }} title={b.bookingNotes || ""}>
                      {b.bookingNotes ? (
                        <div className="small text-wrap">{b.bookingNotes}</div>
                      ) : !bookingDocumentsOfKind(b, ITINERARY).length ? (
                        <span className="text-muted">-</span>
                      ) : null}
                      <BookingDocuments booking={b} kind={ITINERARY} label="Itinerary" />
                    </td>
                    <td className="booking-trip-cell">
                      <div className="booking-trip-title">{b.tripTitle || "-"}</div>
                      <div className="booking-trip-badges">
                        {rowDuration && <Badge bg="primary">{rowDuration}</Badge>}
                      </div>
                      {b.pickupLabel && <div className="booking-trip-line"><i className="bi bi-geo-alt" aria-hidden="true" /><span>{b.pickupLabel}</span></div>}
                      {b.vehicleNameSnapshot && <div className="booking-trip-line"><i className="bi bi-car-front" aria-hidden="true" /><span>Vehicle: {b.vehicleNameSnapshot}</span></div>}
                      <TripArrangementChecklist
                        booking={b}
                        busy={Boolean(preparationUpdating[b.id])}
                        onChange={(patch) => updatePreparationChecks(b, patch)}
                      />
                      {false && <>
                      {(() => {
                        const category = b.tripCategory && b.tripCategory !== rowDuration ? b.tripCategory : "";
                        const line = [rowDuration, category, b.pickupLabel, b.dynamicPricing ? b.vehicleNameSnapshot : ""]
                          .filter(Boolean)
                          .join(" · ");
                        return line ? <div className="small text-muted text-truncate">{line}</div> : null;
                      })()}
                      {(() => {
                        const stay = [rowRoomUnits, b.dynamicPricing ? rowHotelSummary : ""]
                          .filter(Boolean)
                          .join(" · ");
                        return stay ? <div className="small text-muted text-truncate">{stay}</div> : null;
                      })()}
                      </>}
                    </td>
                    <td className="text-nowrap">
                      {(() => {
                        const pickup = b.raw?.pickupDateTime ?? b.raw?.pickup_date_time;
                        const drop = bookingDropDateTime(b);
                        const endDate = bookingTravelEndDate(b);
                        // Hotel-only extra nights: the customer is at the hotel before pickup or after drop.
                        const pickupDay = String(pickup || b.vehicleStartDate || b.travelDate || "").slice(0, 10);
                        const dropDay = String(drop || endDate || "").slice(0, 10);
                        return (
                          <>
                            <div className="small text-muted">Pickup</div>
                            <div>{pickup ? formatDateTimeDMY12(pickup, "—") : formatDate(b.vehicleStartDate || b.travelDate)}</div>
                            {pickupDay && b.stayStartDate && b.stayStartDate < pickupDay ? (
                              <div className="small text-muted">Hotel check-in {formatDate(b.stayStartDate)}</div>
                            ) : null}
                            {drop || endDate ? (
                              <>
                                <div className="small text-muted mt-1">Drop</div>
                                <div>{drop ? formatDateTimeDMY12(drop, "—") : formatDate(endDate)}</div>
                              </>
                            ) : null}
                            {dropDay && b.stayEndDate && b.stayEndDate > dropDay ? (
                              <div className="small text-muted">Hotel checkout {formatDate(b.stayEndDate)}</div>
                            ) : null}
                          </>
                        );
                      })()}
                      {b.vehicleStartDate && b.travelDate && b.vehicleStartDate !== b.travelDate ? (
                        <div className="small text-muted">Package Day 1: {formatDate(b.travelDate)}</div>
                      ) : null}
                    </td>
                    <td className="text-center" style={{ minWidth: 120 }}>
                      <div className="fw-semibold">{b.noOfTravellers}</div>
                      {(() => {
                        const breakdown = bookingTravellerBreakdown(b);
                        if (!breakdown) return null;
                        const { adults, kids, kidAges, payable } = breakdown;
                        return (
                          <>
                            <div className="small text-nowrap">
                              {adults} adult{adults === 1 ? "" : "s"}
                              {kids > 0 ? ` · ${kids} kid${kids === 1 ? "" : "s"}` : ""}
                            </div>
                            {kidAges.length > 0 && (
                              <div className="small text-muted text-nowrap">
                                Kid age{kidAges.length === 1 ? "" : "s"}: {kidAges.join(", ")}
                              </div>
                            )}
                            {payable != null && (
                              <div className="small fw-semibold text-primary text-nowrap" title="Travellers charged per person - adults plus kids above the free age">
                                Payable: {payable}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <div className="small text-muted">
                        Payable {customerTotal != null ? `INR ${Number(customerTotal).toLocaleString("en-IN")}` : "-"}
                      </div>
                      <div className="small text-success">
                        Paid INR {customerPaid.toLocaleString("en-IN")}
                      </div>
                      <div className={`fw-semibold ${customerBalance != null && customerBalance > 0 ? "text-danger" : "text-success"}`}>
                        Balance {customerBalance != null ? `INR ${customerBalance.toLocaleString("en-IN")}` : "-"}
                      </div>
                      <PaymentScheduleLines schedule={customerSchedule} />
                      {customerBalance != null && customerBalance > 0 ? (
                        <div className="d-flex flex-column gap-2 align-items-start mt-2">
                          <div className="d-flex flex-wrap gap-2 align-items-start">
                            {customerSchedule?.afterBoardingDue > 0 && (
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => copyAfterBoardingReminder(b)}
                                title="Send the night before the trip"
                              >
                                Copy After-Boarding Reminder
                              </Button>
                            )}
                            <Button size="sm" variant="outline-success" onClick={() => copyBalanceReminder(b)}>
                              Copy Payment Reminder Message
                            </Button>
                            {paymentDetails?.qrImageUrl && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={copyPaymentQr}
                                title="Copy the payment QR image, to paste after the message"
                              >
                                <i className="bi bi-qr-code me-1" aria-hidden="true" />
                                QR
                              </Button>
                            )}
                          </div>
                          <Form.Check
                            type="checkbox"
                            label="Reminder sent"
                            checked={b.paymentReminderSent}
                            disabled={Boolean(reminderUpdating[b.id])}
                            onChange={(event) => updateReminderSent(b, event.target.checked)}
                          />
                          <InputGroup size="sm">
                            <Form.Control
                              type="number"
                              min="1"
                              max="720"
                              placeholder="Hours"
                              aria-label={`Snooze payment reminder hours for booking ${b.id}`}
                              value={reminderSnoozeHours[b.id] || ""}
                              onChange={(event) => setReminderSnoozeHours((old) => ({ ...old, [b.id]: event.target.value }))}
                            />
                            <Button
                              variant="outline-warning"
                              disabled={Boolean(reminderUpdating[b.id]) || !Number(reminderSnoozeHours[b.id])}
                              onClick={() => updateReminderSnooze(b, Number(reminderSnoozeHours[b.id]))}
                            >
                              Snooze
                            </Button>
                          </InputGroup>
                          {b.paymentReminderSnoozedUntil && new Date(b.paymentReminderSnoozedUntil) > new Date() && (
                            <div className="small text-warning fw-semibold">
                              Snoozed until {formatDateTime(b.paymentReminderSnoozedUntil)}
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 ms-2"
                                disabled={Boolean(reminderUpdating[b.id])}
                                onClick={() => updateReminderSnooze(b, 0)}
                              >
                                Clear
                              </Button>
                            </div>
                          )}
                          <Form.Check
                            type="checkbox"
                            className="small mt-1"
                            id={`payment-reminder-disabled-${b.id}`}
                            label="Never email for this booking"
                            checked={Boolean(b.paymentReminderDisabled)}
                            disabled={Boolean(reminderUpdating[b.id])}
                            onChange={(event) => updateReminderDisabled(b, event.target.checked)}
                          />
                        </div>
                      ) : (
                        <div className="mt-2">
                          <Badge bg="success">Fully Paid</Badge>
                        </div>
                      )}
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <div className="small text-muted">Payable ₹{b.transportVendorPayable.toLocaleString("en-IN")}</div>
                      <div className="small text-success">Paid ₹{b.transportVendorPaid.toLocaleString("en-IN")}</div>
                      <div className={`fw-semibold ${b.transportVendorBalance > 0 ? "text-danger" : "text-success"}`}>
                        Balance ₹{b.transportVendorBalance.toLocaleString("en-IN")}
                      </div>
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <div className="small text-muted">Payable ₹{b.hotelVendorPayable.toLocaleString("en-IN")}</div>
                      <div className="small text-success">Paid ₹{b.hotelVendorPaid.toLocaleString("en-IN")}</div>
                      <div className={`fw-semibold ${b.hotelVendorBalance > 0 ? "text-danger" : "text-success"}`}>
                        Balance ₹{b.hotelVendorBalance.toLocaleString("en-IN")}
                      </div>
                    </td>
                    <td className="text-nowrap" style={{ minWidth: 150 }}>
                      <div className="d-flex flex-column gap-2 align-items-start">
                        <div
                          className="fw-semibold text-truncate"
                          style={{ maxWidth: 220 }}
                          title={b.vehicleOwnerName || "Vehicle owner not assigned"}
                        >
                          {b.vehicleOwnerName || "Vehicle owner not assigned"}
                        </div>
                        <CopyWhatsAppButton
                          text={(() => {
                            const withVideo = withDriverGuideVideo(
                              b.driverWhatsappText,
                              driverGuideVideos[driverVideoLanguage[b.id] || "EN"],
                            );
                            const body = driverAmountChecked[b.id]
                              ? withPayableAmountLine(withVideo, b.vehicleOneTransportPayable)
                              : withVideo;
                            return withDriverAppSection(body, { appVideos: driverAppVideos, tripUrl: b.driverPortalUrl });
                          })()}
                          label={b.extraVehicles.length ? "Copy Vehicle 1 Driver Message" : "Copy Driver Message"}
                          successMessage="Driver WhatsApp text copied"
                        />
                        {b.extraVehicles.map((extra) => {
                          const amountKey = `${b.id}:${extra.position}`;
                          const withVideo = withDriverGuideVideo(
                            extra.driverWhatsappText,
                            driverGuideVideos[driverVideoLanguage[b.id] || "EN"],
                          );
                          return (
                            <div key={extra.position} className="d-flex flex-column align-items-start gap-1">
                              <CopyWhatsAppButton
                                text={withDriverAppSection(
                                  driverAmountChecked[amountKey]
                                    ? withPayableAmountLine(withVideo, Number(extra.transportPayable || 0))
                                    : withVideo,
                                  // No trip link: this vehicle's driver is not tracked in the app yet.
                                  { appVideos: driverAppVideos, tripUrl: "" },
                                )}
                                label={`Copy Vehicle ${extra.position} Driver Message`}
                                successMessage="Driver WhatsApp text copied"
                              />
                              <Form.Check
                                type="checkbox"
                                id={`driver-amount-${amountKey}`}
                                label={`Include vehicle ${extra.position} amount`}
                                checked={Boolean(driverAmountChecked[amountKey])}
                                onChange={(event) =>
                                  setDriverAmountChecked((prev) => ({ ...prev, [amountKey]: event.target.checked }))
                                }
                              />
                            </div>
                          );
                        })}
                        <div className="small">
                          <div className="text-muted">Guide video</div>
                          <div className="d-flex flex-wrap gap-2">
                            {DRIVER_GUIDE_LANGUAGES.map(({ code, label }) => (
                              <Form.Check
                                key={code}
                                type="radio"
                                id={`driver-video-${b.id}-${code}`}
                                name={`driver-video-${b.id}`}
                                label={label}
                                title={driverGuideVideos[code] ? "" : "No video link set in Driver Guide Videos"}
                                disabled={!driverGuideVideos[code]}
                                checked={(driverVideoLanguage[b.id] || "EN") === code}
                                onChange={() => setDriverVideoLanguage((prev) => ({ ...prev, [b.id]: code }))}
                              />
                            ))}
                          </div>
                        </div>
                        <Form.Check
                          type="checkbox"
                          label="Include amount"
                          checked={Boolean(driverAmountChecked[b.id])}
                          onChange={(event) =>
                            setDriverAmountChecked((prev) => ({ ...prev, [b.id]: event.target.checked }))
                          }
                        />
                        {b.driverPortalUrl ? (
                          <CopyWhatsAppButton text={b.driverPortalUrl} label="Copy Portal Link" successMessage="Driver portal link copied" />
                        ) : (
                          <div className="small text-muted">No driver URL</div>
                        )}
                        <CopyDayWiseImageButton booking={trackingByBookingId.get(b.id) || b} />
                      </div>
                    </td>
                    <td className="text-nowrap" style={{ minWidth: 170 }}>
                      <div className="d-flex flex-column gap-2 align-items-start">
                        {hotelMessages.length > 0 ? (
                          hotelMessages.map((item, index) => {
                            const amountKey = `${b.id}:${item.key}`;
                            const hotelAmountOn = Boolean(hotelAmountChecked[amountKey]);
                            const hotelAmountBusy = hotelAmountOn && Boolean(hotelPaymentsLoading[b.id]);
                            const payable = hotelAmountOn
                              ? hotelPayableForKey(item.key, hotelPaymentsByBooking[b.id])
                              : null;
                            const hotelRoomUnits = roomUnitsLabel(
                              (item.rooms || []).map((room) => ({ ...room, night: formatDate(room.stayDate) })),
                            );
                            return (
                              <div key={`${item.key}-${index}`} className="d-flex flex-column align-items-start gap-1 border-bottom pb-2">
                                <div
                                  className="fw-semibold text-truncate"
                                  style={{ maxWidth: 220 }}
                                  title={`${item.hotelName || "Hotel"}${item.city ? ` (${item.city})` : ""}`}
                                >
                                  {item.hotelName || "Hotel"}
                                  {item.city ? ` (${item.city})` : ""}
                                </div>
                                {hotelRoomUnits ? (
                                  <div className="small text-muted" title={`Rooms: ${hotelRoomUnits}`}>
                                    Rooms: {hotelRoomUnits}
                                  </div>
                                ) : null}
                                <PhoneLink
                                  phone={item.caretakerNumber}
                                  label={item.caretakerName ? `Caretaker (${item.caretakerName})` : "Caretaker"}
                                  empty="Caretaker: -"
                                />
                                <PhoneLink
                                  phone={item.ownerNumber}
                                  label={item.ownerName ? `Owner (${item.ownerName})` : "Owner"}
                                  empty="Owner: -"
                                />
                                <div className="d-flex align-items-center gap-2">
                                  <span className="small text-muted">Date: {item.dateLabel}</span>
                                <CopyWhatsAppButton
                                  text={payable != null ? withPayableAmountLine(item.text, payable) : item.text}
                                  label="Copy Reservation Message"
                                  successMessage="Hotel WhatsApp text copied"
                                  disabled={hotelAmountBusy}
                                />
                                <CopyWhatsAppButton
                                  text={
                                    payable != null ? withPayableAmountLine(item.thankYouText, payable) : item.thankYouText
                                  }
                                  label="Copy Thank You Message"
                                  successMessage="Hotel thank you text copied"
                                  disabled={!item.accepted || hotelAmountBusy}
                                  title={!item.accepted ? "Available after hotel accepts the booking" : ""}
                                />
                                </div>
                                <Form.Check
                                  type="checkbox"
                                  label="Include amount"
                                  checked={hotelAmountOn}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setHotelAmountChecked((prev) => ({ ...prev, [amountKey]: checked }));
                                    if (checked && !hotelPaymentsByBooking[b.id] && !hotelPaymentsLoading[b.id]) {
                                      setHotelPaymentsLoading((prev) => ({ ...prev, [b.id]: true }));
                                      getBookingHotelPayments(b.id)
                                        .then((data) =>
                                          setHotelPaymentsByBooking((prev) => ({ ...prev, [b.id]: data || [] }))
                                        )
                                        .catch(() => {})
                                        .finally(() =>
                                          setHotelPaymentsLoading((prev) => ({ ...prev, [b.id]: false }))
                                        );
                                    }
                                  }}
                                />
                                {hotelAmountBusy ? (
                                  <span className="small text-muted">Loading amount...</span>
                                ) : null}
                                <Form.Check
                                  type="checkbox"
                                  label={item.accepted ? `Accepted${item.acceptedAt ? ` at ${formatHotelAcceptedAt(item.acceptedAt)}` : ""}` : "Accepted"}
                                  checked={Boolean(item.accepted)}
                                  readOnly
                                  disabled
                                />
                                {item.accepted && item.acceptedByName && (
                                  <HotelAcceptedBy
                                    name={item.acceptedByName}
                                    via={item.acceptedVia}
                                    latitude={item.acceptedLatitude}
                                    longitude={item.acceptedLongitude}
                                  />
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <span className="small text-muted">No hotel details</span>
                        )}
                      </div>
                    </td>
                    <td style={{ minWidth: 190 }}>
                      <div className="d-flex flex-column gap-2 align-items-start">
                        <CopyWhatsAppButton text={b.customerWhatsappText} label="Copy Customer Message" successMessage="Customer WhatsApp text copied" />
                        <Form.Check
                          type="checkbox"
                          id={`booking-${b.id}-customer-whatsapp-sent`}
                          label="Message sent"
                          checked={b.customerWhatsappSent}
                          disabled={Boolean(customerWhatsappUpdating[b.id])}
                          onChange={(event) => updateCustomerWhatsappSent(b, event.target.checked)}
                        />
                        <div className="small text-muted">
                          {b.customerWhatsappSentAt ? `Sent on ${formatDateTime(b.customerWhatsappSentAt)}` : "Not sent yet"}
                        </div>
                        <CustomerPolicyStatus policy={b.customerPolicy} />
                        <BookingPickupStatus booking={b} onPatched={onPatched} />
                      </div>
                    </td>
                    <td className="text-nowrap">
                      {formatDateTime(b.createdAt)}
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => setSelectedTrackingBooking(trackingByBookingId.get(b.id) || b)}
                        >
                          View Timeline
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setOpeningBookingId(b.id);
                            setEditing(b.raw); // pass original booking object
                            setShow(true);
                          }}
                          disabled={openingBookingId === b.id}
                        >
                          {openingBookingId === b.id ? <><Spinner size="sm" className="me-1" />Opening</> : "Edit"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => onDelete(b.id)}
                        >
                          Delete
                        </Button>
                      </div>
                      <SilenceAlertsSwitch
                        booking={b}
                        onChanged={(data) => {
                          if (data.silenceAlertsOffAt) silence.dropBooking(data.bookingId);
                          onPatched({ id: data.bookingId, silenceAlertsOffAt: data.silenceAlertsOffAt, silenceAlertsOffBy: data.silenceAlertsOffBy || "" });
                        }}
                      />
                      <SilenceBadge entry={silenceByBookingId.get(b.id)} now={silence.now} />
                      <TrackingSummary booking={trackingByBookingId.get(b.id) || b} />
                    </td>
                  </tr>
                  );
                })}
                {!filtered.length && (
                  <tr>
                    <td colSpan={showReviewColumn ? 20 : 19} className="text-center py-5 booking-empty-results">
                      <div className="fw-semibold text-dark mb-1">No bookings match the current filters</div>
                      <div className="small text-muted mb-3">Try removing one filter or clear all filters to return to the complete booking list.</div>
                      <Button size="sm" variant="outline-primary" onClick={clearAllBookingFilters}>Clear all filters</Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          </>
        )}
      </Card>

      <TimelineModal
        booking={selectedTrackingBooking}
        onHide={() => setSelectedTrackingBooking(null)}
        onRefresh={refreshTrackingTimeline}
        refreshing={trackingRefreshing}
      />

      <BookingModal
        show={show}
        onHide={() => { setShow(false); setOpeningBookingId(null); }}
        onEntered={() => setOpeningBookingId(null)}
        initial={editing}
        onPatched={onPatched}
      />
    </>
  );
}

// Languages the assigned person actually speaks. The "Preferred Language" column is a
// different thing - that is what the customer asked for.
function SpokenLanguages({ languages }) {
  if (!languages?.length) return null;
  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {languages.map((language) => (
        <Badge bg="light" text="dark" className="border fw-normal" key={language}>{language}</Badge>
      ))}
    </div>
  );
}

function DriverReadinessBadge({ booking }) {
  const gpsDone = Boolean(booking?.driverGpsTestDone);
  const practiceDone = Boolean(booking?.driverPracticeCheckDone);
  const ready = gpsDone && practiceDone;
  const label = ready ? "Driver Ready" : gpsDone ? "Practice Pending" : "GPS Pending";
  return (
    <div className="mt-1">
      <Badge
        bg={ready ? "success" : "warning"}
        text={ready ? undefined : "dark"}
        title={[
          booking?.driverGpsTestedAt ? `GPS tested: ${formatDateTime(booking.driverGpsTestedAt)}` : "GPS test pending",
          booking?.driverPracticeCheckedAt ? `Practice done: ${formatDateTime(booking.driverPracticeCheckedAt)}` : "Practice checkbox pending",
          booking?.driverReadinessDeviceInfo || "",
        ].filter(Boolean).join("\n")}
      >
        {label}
      </Badge>
    </div>
  );
}

// Internal sticky note in the User column - one per booking, saved straight away, never shown to the
// customer. Done hides it from the table; the text is kept and can be brought back. It stays a yellow
// note in the dark theme too, so it is marked light (data-bs-theme) for Bootstrap's link colours.
const STICKY_NOTE_STYLE = { background: "#fff3b0", border: "1px solid #e9cf5f", color: "#3d3200" };

function BookingStickyNote({ booking, onPatched }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const active = Boolean(booking.stickyNote) && !booking.stickyNoteDoneAt;
  const doneNote = Boolean(booking.stickyNote) && Boolean(booking.stickyNoteDoneAt);

  const save = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/admin/bookings/${booking.id}/sticky-note`, body);
      onPatched(data);
      setEditing(false);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not save the sticky note.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-1 pt-1 border-top">
        <Form.Control
          as="textarea"
          rows={3}
          size="sm"
          maxLength={2000}
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Internal note - not shown to the customer"
          style={STICKY_NOTE_STYLE}
          data-bs-theme="light"
          disabled={saving}
        />
        {doneNote && (
          <div className="small text-muted mt-1 text-wrap">
            Last note (done {formatDateTime(booking.stickyNoteDoneAt)}): {booking.stickyNote}
          </div>
        )}
        <div className="d-flex gap-1 mt-1">
          <Button size="sm" variant="primary" disabled={saving || !draft.trim()} onClick={() => save({ note: draft })}>
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="outline-secondary" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
          {doneNote && (
            <Button size="sm" variant="link" className="p-0 ms-auto" disabled={saving} onClick={() => save({ done: false })}>
              Bring back
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="mt-1">
        <Button
          size="sm"
          variant="link"
          className="p-0 small"
          onClick={() => { setDraft(""); setEditing(true); }}
        >
          + Sticky note
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1 p-2 rounded small" style={STICKY_NOTE_STYLE} data-bs-theme="light">
      <div className="text-wrap" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{booking.stickyNote}</div>
      <div className="d-flex align-items-center gap-2 mt-1" style={{ fontSize: "0.75rem" }}>
        {booking.stickyNoteUpdatedAt && <span>{formatDateTime(booking.stickyNoteUpdatedAt)}</span>}
        <Button
          size="sm"
          variant="link"
          className="p-0 ms-auto"
          style={{ fontSize: "0.75rem" }}
          disabled={saving}
          onClick={() => { setDraft(booking.stickyNote || ""); setEditing(true); }}
        >
          Edit
        </Button>
        <Button
          size="sm"
          variant="link"
          className="p-0"
          style={{ fontSize: "0.75rem" }}
          disabled={saving}
          onClick={() => save({ done: true })}
        >
          Done
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Modal ---------------- */
function BookingModal({ show, onHide, onEntered, initial, onPatched }) {
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [priceOptions, setPriceOptions] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [externalVendors, setExternalVendors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [bookingOptionsLoading, setBookingOptionsLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [driverLoading, setDriverLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [driverQuery, setDriverQuery] = useState("");
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [savingCustomerName, setSavingCustomerName] = useState(false);
  const [quoteOptions, setQuoteOptions] = useState(null);
  const [quoteOptionsLoading, setQuoteOptionsLoading] = useState(false);
  const [draftQuote, setDraftQuote] = useState(null);
  const [draftQuoteLoading, setDraftQuoteLoading] = useState(false);
  const [draftQuoteError, setDraftQuoteError] = useState("");
  const [changeJustification, setChangeJustification] = useState("");
  const [roomAvailabilityByKey, setRoomAvailabilityByKey] = useState({});
  const [transportRating, setTransportRating] = useState(null);
  const [transportRatingSaving, setTransportRatingSaving] = useState(false);
  const [extensionAvailabilityByKey, setExtensionAvailabilityByKey] = useState({});

  const [transportPaymentSummary, setTransportPaymentSummary] = useState(null);
  // 1 is the booking's own vehicle; 2 and up the vehicles added on request.
  const [transportPaymentVehicle, setTransportPaymentVehicle] = useState(1);
  // The extra vehicles' latest payable figures, so updating one does not undo another in the list.
  const extraVehicleFiguresRef = useRef([]);
  const [transportPayableDraft, setTransportPayableDraft] = useState("");
  const [transportPaymentLoading, setTransportPaymentLoading] = useState(false);
  const [transportPaymentSaving, setTransportPaymentSaving] = useState(false);
  const [transportPaymentError, setTransportPaymentError] = useState("");
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [revisionHistoryLoading, setRevisionHistoryLoading] = useState(false);
  const [revisionHistoryError, setRevisionHistoryError] = useState("");
  const [transportPaymentForm, setTransportPaymentForm] = useState({
    amount: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: "UPI",
    referenceNumber: "",
    note: "",
  });
  // The server's figure comes from the saved quote - the one the payable actually follows.
  const calculatedTransportPayable = Number(transportPaymentSummary?.calculatedPayable ?? draftQuote?.vehicleCost ?? 0);
  const savedTransportPayable = Number(transportPaymentSummary?.totalPayable || 0);
  const transportPayableDifference = Math.round((calculatedTransportPayable - savedTransportPayable) * 100) / 100;
  const hasTransportPayableMismatch =
    Boolean(transportPaymentSummary?.vehicle)
    && calculatedTransportPayable > 0
    && Math.abs(transportPayableDifference) >= 0.01;
  const calculatedTransportPayableBelowPaid =
    calculatedTransportPayable + 0.009 < Number(transportPaymentSummary?.totalPaid || 0);

  // Normalize incoming object to a flat form the UI can edit safely
  const normalize = (b) => {
    const snapshot = parseQuoteSnapshot(b?.quoteSnapshot ?? b?.quote_snapshot);
    const snapshotPickup = snapshot?.selectedPickup?.pickupName || "";
    const snapshotVehicle = snapshot?.selectedVehicle?.vehicleName || "";
    const snapshotVehicleId = snapshot?.quoteRequest?.vehicleId || snapshot?.selectedVehicle?.id || "";
    const snapshotAcType =
      snapshot?.quoteRequest?.acType || snapshot?.selectedVehicle?.acType || "";
    const snapshotAddons = (snapshot?.selectedAddons || [])
      .map((addon) => addon?.name)
      .filter(Boolean)
      .join(", ");
    const snapshotTravellers = snapshot?.travellers || snapshot?.quoteRequest || {};
    const roomUnitsByRate = Object.fromEntries([
      ...(snapshot?.selectedRoomRateIds || []).map((roomRateId, index) => [
        String(roomRateId),
        Number(snapshot?.selectedRoomUnits?.[index] || 1),
      ]),
      ...(snapshot?.roomStayCosts || []).map((stay) => [
        String(stay.roomRateId),
        roomUnitsFromAllocation(stay.roomAllocation) || 1,
      ]),
      ...(snapshot?.selectedRoomCategorySelections || []).map((selection) => [
        String(selection.roomRateId),
        Number(selection.units || 0),
      ]),
    ]);
    const roomCategoryIdsByRate = Object.fromEntries([
      ...(snapshot?.roomStayCosts || []).map((stay) => [
        String(stay.roomRateId),
        String(stay.roomCategoryId),
      ]),
      ...(snapshot?.selectedRoomCategorySelections || []).map((selection) => [
        String(selection.roomRateId),
        String(selection.roomCategoryId),
      ]),
    ]);
    const roomCategoryUnitsByKey = roomCategoryUnitsFromSnapshot(snapshot);
    if (!b) {
      return {
        id: undefined,
        userId: null,
        userName: "",
        userEmail: "",
        userPhone: "",
        newCustomerName: "",
        newCustomerPhone: "",
        tripCategoryFilter: "",
        tripId: null,
        tripTitle: "",
        noOfTravellers: 1,
        travelDate: "",
        pickupDateTime: "",
        dropDateTime: "",
        createdAt: "",
        tripStatus: "PENDING",
        couponId: null,
        couponCode: "",
        travellers: [],
        priceListId: null,
        priceList: null,
        bookingType: "PACKAGE",
        description: "",
        bookingNotes: "",
        travellingContactName: "",
        travellingContactPhone: "",
        pricePerPerson: "",
        advancePaid: "",
        driverId: "",
        assignedVehicleId: "",
        pickupOptionId: "",
        pickupLabel: "",
        pickupGoogleMapLink: "",
        refreshmentGoogleMapLink: "",
        dynamicPricing: false,
        dynamicVehicleId: "",
        additionalVehicles: [],
        vehicleNameSnapshot: "",
        acTypeSnapshot: "",
        hotelSummarySnapshot: "",
        addonSummarySnapshot: "",
        quoteSnapshot: "",
        totalPrice: "",
        customizationRequired: false,
        customizationDescription: "",
        customizationPrice: "",
        customizationPriceAdjustment: "ADD",
        cashbackAmountPerPerson: "",
        totalCashbackAmount: "",
        cashbackReviewVideoReceived: false,
        cashbackTravelPhotosReceived: false,
        cashbackGoogleReviewReceived: false,
        cashbackPaid: false,
        cashbackPaidAmount: "",
        cashbackReviewRequestAllowed: false,
        cashbackReviewRequestNote: "",
        currentRevisionNumber: 0,
        receiptReshareRequired: false,
        policyReshareRequired: false,
        latestReceiptNumber: "",
        latestReceiptSharedAt: null,
        latestPolicySharedAt: null,
        addonsArranged: false, addonDates: {},
        breakfastArranged: false,
        dayWisePlanDays: [],
        bookingDayWisePlanDays: [],
        tripDayWisePlanDays: [],
        useBookingDayWisePlan: false,
        adults: 1,
        kids: 0,
        kidAges: [],
        kidVehicleSeatNeeded: [],
        roomUnitsByRate: {},
        roomCategoryIdsByRate: {},
        roomCategoryUnitsByKey: {},
        selectedRoomRateIdsByNight: {},
        noStayNights: [],
        selectedRoomCityByNight: {},
        selectedHotelFacilityKeys: {},
        tripExtensionRequired: false,
        extraPreNights: "",
        extraPostNights: "",
        extensionStays: [],
        tripExtensionNote: "",
        conversionCredits: [],
      };
    }
    const tripDayWisePlanDays = normalizeDayWisePlan(
      b.tripDayWisePlan ?? b.trip_day_wise_plan ?? []
    );
    const tripPickupGoogleMapLink = pickupGoogleMapLinkFromPlan(
      b.tripDayWisePlan ?? b.trip_day_wise_plan ?? []
    );
    const tripRefreshmentGoogleMapLink = refreshmentGoogleMapLinkFromPlan(
      b.tripDayWisePlan ?? b.trip_day_wise_plan ?? []
    );
    const bookingDayWisePlanDays = normalizeDayWisePlan(
      b.bookingDayWisePlan ?? b.booking_day_wise_plan ?? []
    );
    const bookingPickupGoogleMapLink = pickupGoogleMapLinkFromPlan(
      b.bookingDayWisePlan ?? b.booking_day_wise_plan ?? []
    );
    const bookingRefreshmentGoogleMapLink = refreshmentGoogleMapLinkFromPlan(
      b.bookingDayWisePlan ?? b.booking_day_wise_plan ?? []
    );
    const hasBookingDayWisePlan =
      Boolean(b.dayWisePlanOverride ?? b.day_wise_plan_override) ||
      bookingDayWisePlanDays.length > 0;
    const effectiveDayWisePlanDays = hasBookingDayWisePlan
      ? bookingDayWisePlanDays
      : (tripDayWisePlanDays.length
          ? tripDayWisePlanDays
          : normalizeDayWisePlan(b.dayWisePlan ?? b.day_wise_plan));
    const savedHotelSummary = hotelSummaryFromQuoteSnapshot(snapshot)
      || b.hotelSummarySnapshot
      || b.hotel_summary_snapshot
      || "";
    return {
      id: b.id,
      userId: b.userId ?? b.user?.id ?? null,
      userName:
        b.userName ??
        b.user?.name ??
        b.user?.fullName ??
        (b.user
          ? `${b.user.firstName || ""} ${b.user.lastName || ""}`.trim()
          : ""),
      userEmail: b.userEmail ?? b.user?.email ?? "",
      userPhone: b.userPhone ?? b.user?.phone ?? "",
      tripId: b.tripId ?? b.trip?.id ?? null,
      tripTitle: b.tripTitle ?? b.trip?.title ?? "",
      noOfTravellers: b.noOfTravellers ?? 1,
      travelDate: toDateInputValue(b.travelDate),
      pickupDateTime: toDateTimeInputValue(b.pickupDateTime ?? b.pickup_date_time),
      dropDateTime: toDateTimeInputValue(b.dropDateTime ?? b.drop_date_time),
      createdAt: b.createdAt,
      tripStatus: b.tripStatus || "PENDING",
      driverAssignedToTrip: Boolean(b.driverAssignedToTrip),
      // Driver and vehicle of each vehicle added on request, by its place on the booking (2, 3 ...).
      extraVehicleAssignments: Object.fromEntries((Array.isArray(b.extraVehicles) ? b.extraVehicles : []).map((extra) => [
        Number(extra.position),
        {
          driverId: extra.driverId ?? null,
          assignedVehicleId: extra.assignedVehicleId ?? null,
          driverAssignedToTrip: Boolean(extra.driverAssignedToTrip),
        },
      ])),
      hotelsBlocked: Boolean(b.hotelsBlocked),
        addonsArranged: Boolean(b.addonsArranged ?? b.addons_arranged),
        addonDates: b.addonDates && typeof b.addonDates === "object" ? { ...b.addonDates } : {},
        breakfastArranged: Boolean(b.breakfastArranged ?? b.breakfast_arranged),
      couponId: b.couponId ?? b.coupon?.id ?? null,
      couponCode: b.couponCode ?? b.coupon?.code ?? "",
      travellers: b.travellers || [],
      priceListId: b.priceListId ?? b.priceList?.id ?? null,
      priceList: b.priceList
        ? {
            id: b.priceList.id,
            durationNights: b.priceList.durationNights,
            durationDays: b.priceList.durationDays,
            vehicleType: b.priceList.vehicleType,
            price: b.priceList.price,
            originalPrice: b.priceList.originalPrice,
            currencyCode: b.priceList.currencyCode,
            validFrom: b.priceList.validFrom,
            validTo: b.priceList.validTo,
            isActive: b.priceList.isActive,
          }
        : null,
      bookingType: (b.bookingType || b.booking_type || "PACKAGE").toUpperCase(),
      description: b.description ?? "",
      bookingNotes: b.bookingNotes ?? b.booking_notes ?? "",
      travellingContactName: b.travellingContactName ?? "",
      travellingContactPhone: b.travellingContactPhone ?? "",
      pricePerPerson:
        b.pricePerPerson != null
          ? String(b.pricePerPerson)
          : b.price_per_person != null
          ? String(b.price_per_person)
          : "",
      advancePaid:
        b.advancePaid != null
          ? String(b.advancePaid)
          : b.advance_paid != null
          ? String(b.advance_paid)
          : "",
      bookingAdvanceAmount: b.bookingAdvanceAmount ?? b.booking_advance_amount ?? null,
      driverId:
        b.driverId != null
          ? Number(b.driverId)
          : b.driver_id != null
          ? Number(b.driver_id)
          : null,
      assignedVehicleId:
        b.assignedVehicleId != null
          ? Number(b.assignedVehicleId)
          : b.assigned_vehicle_id != null
          ? Number(b.assigned_vehicle_id)
          : null,
      pickupOptionId: b.pickupOptionId ?? b.pickup_option_id ?? "",
      pickupLabel:
        b.pickupLabel || b.pickupLabelSnapshot ||
        b.pickup_label_snapshot || snapshotPickup,
      pickupGoogleMapLink: bookingPickupGoogleMapLink || tripPickupGoogleMapLink,
      refreshmentGoogleMapLink: bookingRefreshmentGoogleMapLink || tripRefreshmentGoogleMapLink,
      dynamicPricing: Boolean(b.dynamicPricing ?? b.dynamic_pricing),
      dynamicVehicleId: snapshotVehicleId ? String(snapshotVehicleId) : "",
      additionalVehicles: (snapshot?.quoteRequest?.additionalVehicles || [])
        .filter((item) => item?.vehicleId != null)
        .map((item) => ({
          vehicleId: String(item.vehicleId),
          extensionVehicleAmount: Number(item.extensionVehicleAmount || 0),
        })),
      // What the transport vendor charges for extra vehicle days on a fixed price pickup. It has to
      // go back with every re-price, or the agreed amount drops out of the new total.
      extensionVehicleAmount: Number(snapshot?.quoteRequest?.extensionVehicleAmount || 0),
      vehicleNameSnapshot:
        b.vehicleNameSnapshot || b.vehicle_name_snapshot || snapshotVehicle,
      acTypeSnapshot:
        b.acTypeSnapshot || b.ac_type_snapshot || snapshotAcType,
      hotelSummarySnapshot: savedHotelSummary,
      addonSummarySnapshot:
        b.addonSummarySnapshot || b.addon_summary_snapshot ||
        snapshotAddons || "None",
      quoteSnapshot: b.quoteSnapshot ?? b.quote_snapshot ?? "",
      totalPrice:
        b.totalPrice != null
          ? String(b.totalPrice)
          : b.total_price != null
          ? String(b.total_price)
          : "",
      customizationRequired: Boolean(snapshot?.customizationRequired ?? Number(snapshot?.customizationPrice || 0) > 0),
      customizationDescription: snapshot?.customizationDescription || customizationDescriptionFromBookingDescription(b.description || b.bookingDescription || ""),
      customizationPrice:
        snapshot?.customizationPrice != null
          ? String(snapshot.customizationPrice)
          : "",
      customizationPriceAdjustment:
        String(snapshot?.customizationPriceAdjustment || "ADD").toUpperCase() === "REDUCE"
          ? "REDUCE"
          : "ADD",
      cashbackAmountPerPerson:
        b.cashbackAmountPerPerson != null
          ? String(b.cashbackAmountPerPerson)
          : b.cashback_amount_per_person != null
          ? String(b.cashback_amount_per_person)
          : "",
      totalCashbackAmount:
        b.totalCashbackAmount != null
          ? String(b.totalCashbackAmount)
          : b.total_cashback_amount != null
          ? String(b.total_cashback_amount)
          : "",
      cashbackReviewVideoReceived: Boolean(b.cashbackReviewVideoReceived ?? b.cashback_review_video_received),
      cashbackTravelPhotosReceived: Boolean(b.cashbackTravelPhotosReceived ?? b.cashback_travel_photos_received),
      cashbackGoogleReviewReceived: Boolean(b.cashbackGoogleReviewReceived ?? b.cashback_google_review_received),
      cashbackPaid: Boolean(b.cashbackPaid ?? b.cashback_paid),
      cashbackPaidAmount:
        b.cashbackPaidAmount != null
          ? String(b.cashbackPaidAmount)
          : b.cashback_paid_amount != null
          ? String(b.cashback_paid_amount)
          : "",
      cashbackReviewRequestAllowed: Boolean(b.cashbackReviewRequestAllowed ?? b.cashback_review_request_allowed),
      cashbackReviewRequestNote: b.cashbackReviewRequestNote ?? b.cashback_review_request_note ?? "",
      currentRevisionNumber: Number(b.currentRevisionNumber ?? b.current_revision_number ?? 0),
      receiptReshareRequired: Boolean(b.receiptReshareRequired ?? b.receipt_reshare_required),
      policyReshareRequired: Boolean(b.policyReshareRequired ?? b.policy_reshare_required),
      latestReceiptNumber: b.latestReceiptNumber ?? b.latest_receipt_number ?? "",
      latestReceiptSharedAt: b.latestReceiptSharedAt ?? b.latest_receipt_shared_at ?? null,
      latestPolicySharedAt: b.latestPolicySharedAt ?? b.latest_policy_shared_at ?? null,
      dayWisePlanDays: effectiveDayWisePlanDays,
      bookingDayWisePlanDays,
      tripDayWisePlanDays,
      bookingPickupGoogleMapLink,
      tripPickupGoogleMapLink,
      bookingRefreshmentGoogleMapLink,
      tripRefreshmentGoogleMapLink,
      useBookingDayWisePlan: hasBookingDayWisePlan,
      adults: Number(snapshotTravellers.adults ?? b.noOfTravellers ?? 1) || 1,
      kids: Number(snapshotTravellers.kids ?? 0) || 0,
      kidAges: Array.isArray(snapshotTravellers.kidAges) ? snapshotTravellers.kidAges : [],
      kidVehicleSeatNeeded: Array.isArray(snapshotTravellers.kidVehicleSeatNeeded)
        ? snapshotTravellers.kidVehicleSeatNeeded
        : [],
      roomUnitsByRate,
      roomCategoryIdsByRate,
      roomCategoryUnitsByKey,
      selectedRoomRateIdsByNight: selectedRoomRateIdsByNightFromSnapshot(snapshot),
      noStayNights: noStayNightsFromSnapshot(snapshot),
      selectedRoomCityByNight: {},
      selectedHotelFacilityKeys: hotelFacilityKeysFromSnapshot(snapshot),
      tripExtensionRequired: Boolean(snapshot?.quoteRequest?.tripExtensionRequired),
      extraPreNights:
        snapshot?.quoteRequest?.extraPreNights != null
          ? String(snapshot.quoteRequest.extraPreNights)
          : "",
      extraPostNights:
        snapshot?.quoteRequest?.extraPostNights != null
          ? String(snapshot.quoteRequest.extraPostNights)
          : "",
      extensionStays: extensionStaysFromSnapshot(snapshot),
      tripExtensionNote: snapshot?.quoteRequest?.tripExtensionNote || "",
      conversionCredits: normalizeConversionCredits(b.conversionCredits ?? b.conversion_credits ?? []),
    };
  };

  const [form, setForm] = useState(normalize(initial));

  // A trip can only be rated once it has actually run, so the section stays hidden until then.
  const transportRatingAllowed = ["ONGOING", "COMPLETED"].includes(String(form.tripStatus || "").toUpperCase());

  useEffect(() => {
    if (!show || !form.id || !transportRatingAllowed) {
      setTransportRating(null);
      return undefined;
    }
    let cancelled = false;
    fetchBookingTransportRating(form.id)
      .then((data) => {
        if (!cancelled) setTransportRating(data || null);
      })
      .catch(() => {
        if (!cancelled) setTransportRating(null);
      });
    return () => { cancelled = true; };
  }, [show, form.id, transportRatingAllowed]);

  const patchTransportRating = (changes) =>
    setTransportRating((previous) => ({ ...(previous || {}), ...changes }));

  const submitTransportRating = async () => {
    if (!form.id) return;
    setTransportRatingSaving(true);
    try {
      const saved = await saveBookingTransportRating(form.id, {
        driverRating: transportRating?.driverRating ?? null,
        driverNote: transportRating?.driverNote || null,
        vehicleRating: transportRating?.vehicleRating ?? null,
        vehicleNote: transportRating?.vehicleNote || null,
        ratedBy: localStorage.getItem("adminName") || "Admin",
      });
      setTransportRating(saved || null);
      alert("Trip rating saved.");
    } catch (error) {
      alert(error?.response?.data?.message || "Could not save the trip rating.");
    } finally {
      setTransportRatingSaving(false);
    }
  };
  const [documentSharing, setDocumentSharing] = useState("");
  const [documentDownloading, setDocumentDownloading] = useState("");
  const [changeTripMode, setChangeTripMode] = useState(false);
  const [pendingChangedTripId, setPendingChangedTripId] = useState(null);
  const [manuallyToggledAddonNames, setManuallyToggledAddonNames] = useState({});
  const restoredDynamicSelectionKeyRef = useRef("");
  // A saved booking already carries the total the customer was quoted. This tracks whether the
  // user changed anything in this session, so a merely restored selection can never silently
  // reprice the booking.
  const dynamicTotalEditedRef = useRef(false);
  const isSavedBooking = Boolean(initial?.id);

  useEffect(() => {
    if (show) {
      setForm(normalize(initial));
      setDraftQuote(null);
      setDraftQuoteError("");
      setChangeJustification("");
      setDocumentSharing("");
      setDocumentDownloading("");
      setChangeTripMode(false);
      setPendingChangedTripId(null);
      dynamicTotalEditedRef.current = false;
      loadCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, show]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setBookingOptionsLoading(true);
    Promise.all([
      api.get("/api/admin/bookings/customers"),
      api.get("/api/admin/trips"),
      listExternalVendors({ activeOnly: true }),
      api.get("/api/admin/lead-tracker/team-members"),
    ])
      .then(([customersRes, tripsRes, vendorRows, teamMembersRes]) => {
        if (cancelled) return;
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        setTrips(Array.isArray(tripsRes.data) ? tripsRes.data : []);
        setExternalVendors(Array.isArray(vendorRows) ? vendorRows : []);
        setTeamMembers(Array.isArray(teamMembersRes.data) ? teamMembersRes.data.filter((member) => member?.active !== false) : []);
      })
      .catch(() => {
        if (cancelled) return;
        setCustomers([]);
        setTrips([]);
        setExternalVendors([]);
        setTeamMembers([]);
      })
      .finally(() => {
        if (!cancelled) setBookingOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Load price options for the selected trip when modal opens or trip changes
  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    // Clear previous options immediately to avoid showing stale data
    setPriceOptions([]);

    const tId = form?.tripId;
    if (!tId) {
      // No trip selected for this booking; also clear selected price
      if (form.priceListId || form.priceList) {
        update("priceListId", null);
        update("priceList", null);
      }
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setPriceLoading(true);
      try {
        const prices = await fetchTripPrices(tId, true);
        if (cancelled) return;
        const list = Array.isArray(prices) ? prices : [];
        setPriceOptions(list);

        // If the currently selected price doesn't belong to this list, clear it
        const selId = form.priceListId || form.priceList?.id || null;
        const found = selId ? list.find((p) => p.id === selId) : null;
        if (!found) {
          if (selId != null) {
            update("priceListId", null);
          }
          if (form.priceList) {
            update("priceList", null);
          }
        } else if (!form.priceList) {
          // If we have a valid id but no object, hydrate it
          update("priceList", found);
        }
      } catch (e) {
        console.error("Failed to load price options", e);
        if (!cancelled) {
          setPriceOptions([]);
        }
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, form?.tripId]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const tId = form?.tripId;
    setQuoteOptions(null);
    if (!tId) return () => {
      cancelled = true;
    };

    (async () => {
      setQuoteOptionsLoading(true);
      try {
        const { data: options } = await api.get(`/api/admin/dynamic-pricing/${tId}`);
        if (!cancelled) setQuoteOptions(options || null);
      } catch (e) {
        console.error("Failed to load dynamic booking options", e);
        if (!cancelled) setQuoteOptions(null);
      } finally {
        if (!cancelled) setQuoteOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, form?.tripId]);

  useEffect(() => {
    if (!show || !quoteOptions) return;
    const restoreKey = [
      initial?.id || "new",
      form.tripId || "",
      quoteOptions?.id || "",
    ].join(":");
    if (restoredDynamicSelectionKeyRef.current === restoreKey) return;
    const snapshot = parseQuoteSnapshot(initial?.quoteSnapshot ?? initial?.quote_snapshot);
    const restoredSummary = hotelSummaryFromRoomRateIds(snapshot, quoteOptions);
    const restoredSelection = roomSelectionStateFromSnapshot(snapshot);
    const restoredRoomCityByNight = selectedRoomCityByNightFromSnapshot(snapshot, quoteOptions);
    const savedSummary =
      restoredSummary ||
      hotelSummaryFromQuoteSnapshot(snapshot) ||
      initial?.hotelSummarySnapshot ||
      initial?.hotel_summary_snapshot ||
      form.hotelSummarySnapshot ||
      "";
    const restoredFromSummary = roomSelectionStateFromSummary(
      savedSummary,
      quoteOptions,
      Number(form.adults || form.noOfTravellers || 1) + Number(form.kids || 0),
      !initial?.id
    );
    if (
      !restoredSummary &&
      !Object.keys(restoredSelection.roomUnitsByRate).length &&
      !Object.keys(restoredFromSummary.selectedRoomRateIdsByNight).length
    ) return;
    restoredDynamicSelectionKeyRef.current = restoreKey;
    setForm((previous) => ({
      ...previous,
      hotelSummarySnapshot: restoredSummary || savedSummary || previous.hotelSummarySnapshot,
      selectedRoomRateIdsByNight: {
        ...(previous.selectedRoomRateIdsByNight || {}),
        ...restoredFromSummary.selectedRoomRateIdsByNight,
        ...selectedRoomRateIdsByNightFromSnapshot(snapshot),
      },
      selectedRoomCityByNight: {
        ...(previous.selectedRoomCityByNight || {}),
        ...restoredFromSummary.selectedRoomCityByNight,
        ...restoredRoomCityByNight,
      },
      roomUnitsByRate: {
        ...(previous.roomUnitsByRate || {}),
        ...restoredFromSummary.roomUnitsByRate,
        ...restoredSelection.roomUnitsByRate,
      },
      roomCategoryIdsByRate: {
        ...(previous.roomCategoryIdsByRate || {}),
        ...restoredFromSummary.roomCategoryIdsByRate,
        ...restoredSelection.roomCategoryIdsByRate,
      },
      roomCategoryUnitsByKey: {
        ...(previous.roomCategoryUnitsByKey || {}),
        ...restoredFromSummary.roomCategoryUnitsByKey,
        ...restoredSelection.roomCategoryUnitsByKey,
      },
    }));
  }, [show, quoteOptions, initial, form.tripId]);

  useEffect(() => {
    const changedTripPending = pendingChangedTripId != null && Number(pendingChangedTripId) === Number(form.tripId);
    if (!show || (!changedTripPending && initial?.id) || !form.tripId || !hasDynamicQuoteOptions(quoteOptions)) return;
    setForm((previous) => {
      if (previous.dynamicPricing) return previous;
      const firstPickup = (quoteOptions.pickups || []).find((pickup) => pickup.active !== false);
      return {
        ...previous,
        dynamicPricing: true,
        bookingType: "CUSTOM",
        priceListId: null,
        priceList: null,
        pickupLabel: previous.pickupLabel || firstPickup?.pickupName || "",
        acTypeSnapshot: previous.acTypeSnapshot || "NON_AC",
        adults: Number(previous.adults || previous.noOfTravellers || 1) || 1,
        kids: Number(previous.kids || 0) || 0,
      };
    });
    if (changedTripPending) {
      setPendingChangedTripId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initial?.id, form.tripId, quoteOptions, trips, pendingChangedTripId]);

  useEffect(() => {
    if (!show || !quoteOptions || !form.tripId || !form.travelDate) {
      setRoomAvailabilityByKey({});
      return undefined;
    }
    let cancelled = false;
    fetchTripRoomAvailability(form.tripId, form.travelDate, form.id || null)
      .then((items) => {
        if (cancelled) return;
        setRoomAvailabilityByKey(
          Object.fromEntries(items.map((item) => [`${item.roomRateId}:${item.roomCategoryId}`, item]))
        );
      })
      .catch(() => {
        if (!cancelled) setRoomAvailabilityByKey({});
      });
    return () => {
      cancelled = true;
    };
  }, [show, quoteOptions, form.tripId, form.travelDate, form.id]);

  // Load operational vehicles and drivers from Transport Master when modal opens
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      setDriverLoading(true);
      setVehicleLoading(true);
      try {
        const [driverList, vehicleList] = await Promise.all([
          listTransportMaster("drivers"),
          listTransportMaster("vehicles"),
        ]);
        if (!cancelled) {
          setDrivers(Array.isArray(driverList) ? driverList.filter((item) => item.active !== false) : []);
          setVehicles(Array.isArray(vehicleList) ? vehicleList.filter((item) => item.active !== false) : []);
        }
      } catch (e) {
        if (!cancelled) {
          setDrivers([]);
          setVehicles([]);
        }
      } finally {
        if (!cancelled) {
          setDriverLoading(false);
          setVehicleLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    setTransportPaymentVehicle(1);
    extraVehicleFiguresRef.current = Array.isArray(initial?.extraVehicles) ? initial.extraVehicles : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initial?.id]);

  useEffect(() => {
    if (!show || !initial?.id) {
      setTransportPaymentSummary(null);
      setTransportPayableDraft("");
      return;
    }
    let cancelled = false;
    setTransportPaymentLoading(true);
    setTransportPaymentError("");
    getBookingTransportPayments(initial.id, transportPaymentVehicle)
      .then((summary) => {
        if (cancelled) return;
        setTransportPaymentSummary(summary);
        setTransportPayableDraft(String(summary?.totalPayable ?? ""));
      })
      .catch((error) => {
        if (!cancelled) setTransportPaymentError(error?.response?.data?.message || "Could not load transport payments.");
      })
      .finally(() => {
        if (!cancelled) setTransportPaymentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, initial?.id, transportPaymentVehicle]);

  useEffect(() => {
    if (!show || !initial?.id) {
      setRevisionHistory([]);
      setRevisionHistoryError("");
      return;
    }
    let cancelled = false;
    setRevisionHistoryLoading(true);
    setRevisionHistoryError("");
    api.get(`/api/admin/bookings/${initial.id}/revisions`)
      .then(({ data }) => {
        if (!cancelled) setRevisionHistory(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (!cancelled) {
          setRevisionHistory([]);
          setRevisionHistoryError(error?.response?.data?.message || "Could not load booking change history.");
        }
      })
      .finally(() => {
        if (!cancelled) setRevisionHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, initial?.id]);

  const loadCoupons = async () => {
    try {
      // Optional: adjust endpoint if needed
      const { data } = await api.get("/api/admin/coupons");
      setCoupons(data || []);
    } catch {
      setCoupons([]);
    }
  };

  const markDynamicQuoteDirty = () => {
    if (!form?.dynamicPricing) return;
    dynamicTotalEditedRef.current = true;
    setDraftQuote((current) =>
      current || (hasSavedCalculationDetails(parseQuoteSnapshot(form.quoteSnapshot)) ? parseQuoteSnapshot(form.quoteSnapshot) : null)
    );
    setDraftQuoteError("Recalculating dynamic booking price...");
  };

  const update = (k, v) => {
    if ([
      "adults",
      "kids",
      "kidAges",
      "kidVehicleSeatNeeded",
      "travelDate",
      "acTypeSnapshot",
      "pickupLabel",
      "dynamicVehicleId",
      "vehicleNameSnapshot",
      "hotelSummarySnapshot",
      "addonSummarySnapshot",
      "tripExtensionRequired",
      "extraPreNights",
      "extraPostNights",
      "extensionVehicleAmount",
      "tripExtensionNote",
    ].includes(k)) {
      markDynamicQuoteDirty();
    }
    if (["customizationPrice", "customizationPriceAdjustment"].includes(k)) {
      dynamicTotalEditedRef.current = true;
    }
    setForm((p) => ({ ...p, [k]: v }));
  };

  useEffect(() => {
    setForm((p) => {
      const perAdult = Math.max(0, Number(p.cashbackAmountPerPerson || 0));
      const adults = Math.max(0, Number(p.adults || 0));
      const nextTotal = perAdult ? String(perAdult * adults) : "";
      return p.totalCashbackAmount === nextTotal ? p : { ...p, totalCashbackAmount: nextTotal };
    });
  }, [form.cashbackAmountPerPerson, form.adults]);

  function planForPickupLocation(tripId, pickupOptionId) {
    const trip = trips.find((item) => Number(item.id) === Number(tripId));
    if (!trip || !pickupOptionId) {
      return { days: [], pickupGoogleMapLink: "", refreshmentGoogleMapLink: "", hasPickupPlan: false };
    }
    const pickupOption = (trip.pickupOptions || trip.pickup_options || []).find(
      (option) => String(option.id) === String(pickupOptionId)
    );
    const pickupPlan = pickupOption?.dayWisePlan ?? pickupOption?.day_wise_plan;
    const pickupDays = normalizeDayWisePlan(pickupPlan);
    return {
      days: pickupDays,
      pickupGoogleMapLink: pickupGoogleMapLinkFromPlan(pickupPlan),
      refreshmentGoogleMapLink: refreshmentGoogleMapLinkFromPlan(pickupPlan),
      hasPickupPlan: pickupDays.length > 0 || Boolean(pickupGoogleMapLinkFromPlan(pickupPlan)),
    };
  }

  const selectCustomer = (idValue) => {
    const id = idValue ? Number(idValue) : null;
    const customer = customers.find((item) => Number(item.id) === id);
    setForm((previous) => ({
      ...previous,
      userId: id,
      userName: customer?.name || "",
      userEmail: customer?.email || "",
      userPhone: customer?.phone || "",
    }));
  };

  const createAndSelectCustomer = async () => {
    const name = (form.newCustomerName || "").trim();
    const phone = (form.newCustomerPhone || "").trim();
    if (!name || !phone) {
      alert("Enter customer name and phone number.");
      return;
    }
    try {
      setBookingOptionsLoading(true);
      const { data } = await api.post("/api/admin/bookings/customers", { name, phone });
      if (!data?.id) throw new Error("Customer was not created.");
      setCustomers((previous) => {
        const exists = previous.some((item) => Number(item.id) === Number(data.id));
        return exists
          ? previous.map((item) => (Number(item.id) === Number(data.id) ? data : item))
          : [data, ...previous];
      });
      setForm((previous) => ({
        ...previous,
        userId: data.id,
        userName: data.name || "",
        userEmail: data.email || "",
        userPhone: data.phone || "",
        newCustomerName: "",
        newCustomerPhone: "",
      }));
    } catch (error) {
      alert(error?.response?.data?.message || error?.response?.data || error?.message || "Could not create customer.");
    } finally {
      setBookingOptionsLoading(false);
    }
  };

  const saveCustomerName = async () => {
    const name = (form.userName || "").trim();
    if (!name || !form.userId) return;
    setSavingCustomerName(true);
    try {
      const { data } = await api.put(`/api/admin/bookings/customers/${form.userId}`, { name });
      setCustomers((previous) => previous.map((item) => (Number(item.id) === Number(form.userId) ? { ...item, name: data.name } : item)));
      setForm((previous) => ({ ...previous, userName: data.name || "" }));
      onPatched?.({ id: form.id, userName: data.name || "" });
    } catch (error) {
      alert(error?.response?.data?.message || error?.response?.data || error?.message || "Could not update customer name.");
    } finally {
      setSavingCustomerName(false);
    }
  };

  const selectTrip = (idValue) => {
    const id = idValue ? Number(idValue) : null;
    const previousTripId = form.tripId ? Number(form.tripId) : null;
    const tripChanged = previousTripId !== id;
    if (form.id && tripChanged) {
      const confirmed = window.confirm(
        "Changing the trip will clear the current pickup, vehicle, hotel, quote, total price, booking plan, driver assignment, and trip preparation flags. Continue?"
      );
      if (!confirmed) return;
    }
    const trip = trips.find((item) => Number(item.id) === id);
    const tripPlanDays = normalizeDayWisePlan(trip?.dayWisePlan ?? trip?.day_wise_plan ?? []);
    const tripPickupGoogleMapLink = pickupGoogleMapLinkFromPlan(trip?.dayWisePlan ?? trip?.day_wise_plan ?? []);
    const tripRefreshmentGoogleMapLink = refreshmentGoogleMapLinkFromPlan(trip?.dayWisePlan ?? trip?.day_wise_plan ?? []);
    const pickupOptions = (trip?.pickupOptions || trip?.pickup_options || []).filter((option) => option.isActive !== false);
    const defaultPickup = pickupOptions.find((option) => option.isDefault) || pickupOptions[0];
    const pickupPlan = planForPickupLocation(id, defaultPickup?.id);
    setManuallyToggledAddonNames({});
    setForm((previous) => ({
      ...previous,
      tripId: id,
      tripTitle: trip?.title || "",
      priceListId: null,
      priceList: null,
      pickupOptionId: defaultPickup?.id || "",
      pickupLabel: "",
      pickupGoogleMapLink: pickupPlan.pickupGoogleMapLink || tripPickupGoogleMapLink,
      refreshmentGoogleMapLink: pickupPlan.refreshmentGoogleMapLink || tripRefreshmentGoogleMapLink,
      dynamicPricing: false,
      dynamicVehicleId: "",
      additionalVehicles: [],
      vehicleNameSnapshot: "",
      acTypeSnapshot: "",
      hotelSummarySnapshot: "",
      addonSummarySnapshot: "",
      quoteSnapshot: "",
      totalPrice: "",
      customizationRequired: false,
      customizationDescription: "",
      customizationPrice: "",
      customizationPriceAdjustment: "ADD",
      selectedRoomRateIdsByNight: {},
      noStayNights: [],
      selectedRoomCityByNight: {},
      selectedHotelFacilityKeys: {},
      ...(previous.id && tripChanged
        ? {
            driverId: null,
            assignedVehicleId: null,
            driverAssignedToTrip: false,
            extraVehicleAssignments: {},
            hotelsBlocked: false,
            addonsArranged: false, addonDates: {},
            breakfastArranged: false,
          }
        : {}),
      useBookingDayWisePlan: pickupPlan.hasPickupPlan,
      bookingDayWisePlanDays: pickupPlan.hasPickupPlan ? pickupPlan.days : [],
      bookingPickupGoogleMapLink: pickupPlan.hasPickupPlan ? pickupPlan.pickupGoogleMapLink : "",
      bookingRefreshmentGoogleMapLink: pickupPlan.hasPickupPlan ? pickupPlan.refreshmentGoogleMapLink : "",
      tripDayWisePlanDays: tripPlanDays,
      tripPickupGoogleMapLink,
      tripRefreshmentGoogleMapLink,
      dayWisePlanDays: pickupPlan.hasPickupPlan ? pickupPlan.days : tripPlanDays,
    }));
    if (form.id && tripChanged) {
      setPendingChangedTripId(id);
      setChangeTripMode(false);
      setDraftQuote(null);
      setDraftQuoteError("Trip changed. Select vehicle and hotel room category for every night to recalculate.");
    }
  };

  const applyTransportPaymentSummary = (summary) => {
    setTransportPaymentSummary(summary);
    setTransportPayableDraft(String(summary?.totalPayable ?? ""));
    if (initial?.id && summary) {
      if (transportPaymentVehicle > 1) {
        extraVehicleFiguresRef.current = extraVehicleFiguresRef.current.map((extra) => (
          Number(extra.position) === transportPaymentVehicle
            ? { ...extra, transportPayable: summary.totalPayable, transportPaid: summary.totalPaid, transportBalance: summary.balance }
            : extra
        ));
        onPatched?.({ id: initial.id, extraVehicles: extraVehicleFiguresRef.current });
        return;
      }
      onPatched?.({
        id: initial.id,
        transportVendorPayable: summary.totalPayable,
        transportVendorPaid: summary.totalPaid,
        transportVendorBalance: summary.balance,
      });
    }
  };

  const saveTransportPayableAmount = async (amount) => {
    if (!initial?.id) return;
    setTransportPaymentSaving(true);
    setTransportPaymentError("");
    try {
      const summary = await saveBookingTransportPayable(initial.id, Number(amount || 0), transportPaymentVehicle);
      applyTransportPaymentSummary(summary);
    } catch (error) {
      setTransportPaymentError(error?.response?.data?.message || "Could not update transport payable.");
    } finally {
      setTransportPaymentSaving(false);
    }
  };

  const updateTransportPayable = async () => {
    await saveTransportPayableAmount(transportPayableDraft);
  };

  // Drops a manual amount so the payable follows the quoted vehicle cost again.
  const updateTransportPayableToCalculated = async () => {
    if (!initial?.id) return;
    setTransportPaymentSaving(true);
    setTransportPaymentError("");
    try {
      applyTransportPaymentSummary(await followQuoteBookingTransportPayable(initial.id, transportPaymentVehicle));
    } catch (error) {
      setTransportPaymentError(error?.response?.data?.message || "Could not switch back to the calculated vehicle cost.");
    } finally {
      setTransportPaymentSaving(false);
    }
  };

  const recordTransportPayment = async () => {
    if (!initial?.id) return;
    setTransportPaymentSaving(true);
    setTransportPaymentError("");
    try {
      const summary = await recordBookingTransportPayment(initial.id, {
        ...transportPaymentForm,
        amount: Number(transportPaymentForm.amount || 0),
      }, transportPaymentVehicle);
      applyTransportPaymentSummary(summary);
      setTransportPaymentForm((previous) => ({
        ...previous,
        amount: "",
        referenceNumber: "",
        note: "",
      }));
    } catch (error) {
      setTransportPaymentError(error?.response?.data?.message || "Could not record transport payment.");
    } finally {
      setTransportPaymentSaving(false);
    }
  };

  const transportCorrections = usePaymentCorrections({
    editPayment: (item, change) => editBookingTransportPayment(initial?.id, item.id, change),
    deletePayment: (item, { reason }) => deleteBookingTransportPayment(initial?.id, item.id, reason),
    onChanged: applyTransportPaymentSummary,
  });

  // Travellers editing helpers
  const setTravellers = (fnOrArray) =>
    setForm((p) => ({
      ...p,
      travellers: typeof fnOrArray === "function" ? fnOrArray(p.travellers || []) : fnOrArray,
    }));

  const addTraveller = () => {
    setTravellers((arr) => {
      const next = [...arr, { id: undefined, fullName: "", age: "", phone: "", idCardUrl: "" }];
      // keep count in sync
      update("noOfTravellers", String(next.length));
      return next;
    });
  };

  const removeTraveller = (idx) => {
    setTravellers((arr) => {
      const next = arr.filter((_, i) => i !== idx);
      update("noOfTravellers", String(next.length));
      return next;
    });
  };

  const editTraveller = (idx, field, value) => {
    setTravellers((arr) => {
      const next = [...arr];
      const cur = { ...(next[idx] || {}) };
      if (field === "fullName" || field === "name") cur.fullName = value;
      else if (field === "age") cur.age = value;
      else if (field === "phone") cur.phone = value;
      else if (field === "idCardUrl") cur.idCardUrl = value;
      next[idx] = cur;
      return next;
    });
  };

  const setDayWisePlanDays = (fnOrArray) => {
    setForm((previous) => {
      const nextDays =
        typeof fnOrArray === "function"
          ? fnOrArray(previous.dayWisePlanDays || [])
          : fnOrArray;
      return {
        ...previous,
        dayWisePlanDays: nextDays,
        bookingDayWisePlanDays: previous.useBookingDayWisePlan
          ? nextDays
          : previous.bookingDayWisePlanDays,
      };
    });
  };

  const setBookingPlanOverride = (enabled) => {
    setForm((previous) => {
      if (!enabled) {
        return {
          ...previous,
          useBookingDayWisePlan: false,
          dayWisePlanDays: clonePlanWithStableKeys(previous.tripDayWisePlanDays || []),
          pickupGoogleMapLink: previous.pickupGoogleMapLink || previous.tripPickupGoogleMapLink || "",
          refreshmentGoogleMapLink: previous.refreshmentGoogleMapLink || previous.tripRefreshmentGoogleMapLink || "",
        };
      }
      const source = (previous.bookingDayWisePlanDays || []).length
        ? previous.bookingDayWisePlanDays
        : (previous.tripDayWisePlanDays || []).length
          ? previous.tripDayWisePlanDays
          : (previous.dayWisePlanDays || []);
      return {
        ...previous,
        useBookingDayWisePlan: true,
        dayWisePlanDays: clonePlanWithStableKeys(source),
        pickupGoogleMapLink: previous.bookingPickupGoogleMapLink || previous.pickupGoogleMapLink || "",
        refreshmentGoogleMapLink: previous.bookingRefreshmentGoogleMapLink || previous.refreshmentGoogleMapLink || "",
      };
    });
  };

  const addBookingPlanDay = () => {
    setDayWisePlanDays((days) => [
      ...days,
      { key: newPlanKey("day"), day: `Day ${days.length + 1}`, title: "", locations: [] },
    ]);
  };

  const updateBookingPlanDay = (dayIdx, field, value) => {
    setDayWisePlanDays((days) =>
      days.map((day, idx) => (idx === dayIdx ? { ...day, [field]: value } : day))
    );
  };

  const removeBookingPlanDay = (dayIdx) => {
    setDayWisePlanDays((days) => days.filter((_, idx) => idx !== dayIdx));
  };

  const moveBookingPlanDay = (dayIdx, dir) => {
    setDayWisePlanDays((days) => {
      const next = [...days];
      const target = dayIdx + dir;
      if (target < 0 || target >= next.length) return days;
      [next[dayIdx], next[target]] = [next[target], next[dayIdx]];
      return next;
    });
  };

  const addBookingPlanLocation = (dayIdx) => {
    setDayWisePlanDays((days) => {
      const next = [...days];
      const day = { ...(next[dayIdx] || {}) };
      day.locations = [...(day.locations || []), { key: newPlanKey("place"), locationName: "", googleMapLink: "" }];
      next[dayIdx] = day;
      return next;
    });
  };

  const updateBookingPlanLocation = (dayIdx, locationIdx, field, value) => {
    setDayWisePlanDays((days) => {
      const next = [...days];
      const day = { ...(next[dayIdx] || {}) };
      const locations = [...(day.locations || [])];
      locations[locationIdx] = { ...(locations[locationIdx] || {}), [field]: value };
      day.locations = locations;
      next[dayIdx] = day;
      return next;
    });
  };

  const removeBookingPlanLocation = (dayIdx, locationIdx) => {
    setDayWisePlanDays((days) => {
      const next = [...days];
      const day = { ...(next[dayIdx] || {}) };
      day.locations = (day.locations || []).filter((_, idx) => idx !== locationIdx);
      next[dayIdx] = day;
      return next;
    });
  };

  const moveBookingPlanLocation = (dayIdx, locationIdx, dir) => {
    setDayWisePlanDays((days) => {
      const next = [...days];
      const day = { ...(next[dayIdx] || {}) };
      const locations = [...(day.locations || [])];
      const target = locationIdx + dir;
      if (target < 0 || target >= locations.length) return days;
      [locations[locationIdx], locations[target]] = [locations[target], locations[locationIdx]];
      day.locations = locations;
      next[dayIdx] = day;
      return next;
    });
  };

  const uploadTravellerIdCard = async (idx, file) => {
    if (!file) return;
    try {
      setSaving(true);
      const payload = new FormData();
      payload.append("file", file);
      if (form.id) payload.append("bookingId", String(form.id));
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${api.defaults.baseURL || ""}/api/admin/bookings/traveller-id/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: payload,
      });
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 404) {
          throw new Error("Traveller ID upload endpoint was not found. Restart or deploy the updated backend, and verify REACT_APP_API_BASE_URL points to that backend.");
        }
        throw new Error(errorText || "ID card upload failed");
      }
      const data = await response.json();
      editTraveller(idx, "idCardUrl", data.publicUrl);
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || error?.message || "Could not upload ID card.");
    } finally {
      setSaving(false);
    }
  };

  const downloadTravellerIdCard = (url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const dynamicPickups = (quoteOptions?.pickups || []).filter(
    (pickup) => pickup.active !== false
  );
  const selectedDynamicPickup = dynamicPickups.find(
    (pickup) => pickup.pickupName === form.pickupLabel
  );

  useEffect(() => {
    if (!show || !form.tripId || !form.pickupOptionId) return;
    setForm((previous) => {
      // Read the trip and pickup from the form being updated, not from this render. On opening a
      // booking this effect still sees the previously opened booking, and that booking's pickup
      // plan was saved into the new one (an Ooty plan on a Wayanad booking).
      const pickupPlan = planForPickupLocation(previous.tripId, previous.pickupOptionId);
      if (!pickupPlan.hasPickupPlan) return previous;
      // A booking that already has its own plan keeps it. This used to swap in the pickup's
      // default plan every time the booking opened, so saved edits looked lost - and the next
      // save wrote the default back over them. Changing the pickup still applies its plan, in
      // the pickup dropdown's own handler.
      if (previous.useBookingDayWisePlan && (previous.bookingDayWisePlanDays || []).length) return previous;
      const sameDays = stableJson(previous.bookingDayWisePlanDays || []) === stableJson(pickupPlan.days || []);
      const sameVisibleDays = stableJson(previous.dayWisePlanDays || []) === stableJson(pickupPlan.days || []);
      const sameLink = (previous.pickupGoogleMapLink || "") === (pickupPlan.pickupGoogleMapLink || "");
      if (previous.useBookingDayWisePlan && sameDays && sameVisibleDays && sameLink) return previous;
      return {
        ...previous,
        useBookingDayWisePlan: true,
        bookingDayWisePlanDays: pickupPlan.days,
        bookingPickupGoogleMapLink: pickupPlan.pickupGoogleMapLink,
        bookingRefreshmentGoogleMapLink: pickupPlan.refreshmentGoogleMapLink,
        dayWisePlanDays: pickupPlan.days,
        pickupGoogleMapLink: pickupPlan.pickupGoogleMapLink,
        refreshmentGoogleMapLink: pickupPlan.refreshmentGoogleMapLink,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, form.tripId, form.pickupOptionId, trips]);

  const dynamicVehicles = quoteOptions?.vehicles || [];
  const vehicleCountPersonsFor = React.useCallback((vehicle) => {
    const adultCount = Math.max(0, Number(form.adults || 0));
    const kidsCount = Math.max(0, Number(form.kids || 0));
    const kidAges = Array.isArray(form.kidAges) ? form.kidAges : [];
    const seatNeeded = Array.isArray(form.kidVehicleSeatNeeded) ? form.kidVehicleSeatNeeded : [];
    const ageLimit = Number(vehicle?.childConcessionAgeLimit ?? 0);
    const maxKidsWithoutSeat = Math.max(0, Number(vehicle?.maxKidsWithoutSeat ?? 0));
    let ignoredKids = 0;
    let countedKids = 0;

    Array.from({ length: kidsCount }, (_, index) => kidAges[index]).forEach((age, index) => {
      const requestedSeat = seatNeeded[index] !== false;
      if (!requestedSeat && age !== "" && age != null && Number(age) <= ageLimit && ignoredKids < maxKidsWithoutSeat) {
        ignoredKids += 1;
      } else {
        countedKids += 1;
      }
    });

    return adultCount + countedKids;
  }, [form.adults, form.kids, form.kidAges, form.kidVehicleSeatNeeded]);
  const pickupDynamicVehicleOptions = dynamicVehicles.filter((vehicle) => {
    if (!selectedDynamicPickup) return true;
    if (vehicle.pickupId && selectedDynamicPickup.id) {
      return String(vehicle.pickupId) === String(selectedDynamicPickup.id);
    }
    if (vehicle.pickupValue && selectedDynamicPickup.pickupValue) {
      return String(vehicle.pickupValue) === String(selectedDynamicPickup.pickupValue);
    }
    return !vehicle.pickupId && !vehicle.pickupValue;
  });
  // Vehicles added on request (Add Vehicle). Once there are any, the group is split across them, so
  // any vehicle can be the first and the seats of all of them are checked together.
  const additionalDynamicVehicles = vehiclesForIds(
    (form.additionalVehicles || []).map((item) => item.vehicleId),
    pickupDynamicVehicleOptions
  );
  const severalDynamicVehicles = additionalDynamicVehicles.length > 0;
  const dynamicVehicleOptions = severalDynamicVehicles ? pickupDynamicVehicleOptions : pickupDynamicVehicleOptions.filter(
    (vehicle) => Number(vehicle.capacityMax || 0) >= vehicleCountPersonsFor(vehicle)
  );
  // The saved vehicle stays selected even when it is too small for the group. This used to blank it
  // the moment the booking opened, so whoever saved next had to pick another vehicle and the price
  // changed without anyone deciding to (booking 327: Innova Crysta became a Tempo, +INR 4,000).
  // Now the admin sees a warning and makes the change themselves.
  const selectedDynamicVehicle = pickupDynamicVehicleOptions.find(
    (vehicle) => String(vehicle.id) === String(form.dynamicVehicleId || "")
  ) || pickupDynamicVehicleOptions.find(
    (vehicle) => vehicle.vehicleName === form.vehicleNameSnapshot
  );
  const dynamicFleet = combinedVehicle([selectedDynamicVehicle, ...additionalDynamicVehicles]);
  const selectedVehicleTooSmall = Boolean(selectedDynamicVehicle) &&
    Number(dynamicFleet?.capacityMax || 0) < vehicleCountPersonsFor(dynamicFleet);
  // Until the trip's options load nothing can be checked, so the saved list is kept as it is.
  const additionalVehiclesPayload = (form.additionalVehicles || [])
    .filter((item) => !quoteOptions || pickupDynamicVehicleOptions.some((vehicle) => String(vehicle.id) === String(item.vehicleId)))
    .map((item) => ({
      vehicleId: Number(item.vehicleId),
      extensionVehicleAmount: Number(item.extensionVehicleAmount || 0),
    }));
  const additionalVehiclesKey = normalizedArrayKey(additionalVehiclesPayload.map((item) => item.vehicleId).sort((a, b) => a - b));
  const currentExtensionVehicleAmountsKey = extensionVehicleAmountsKey(form.extensionVehicleAmount, additionalVehiclesPayload);
  const additionalVehiclesPayloadKey = JSON.stringify(additionalVehiclesPayload);
  // Each added vehicle is run by its own driver: vehicle 2 is the first one added, and so on.
  const extraVehiclePositions = form.dynamicPricing ? additionalVehiclesPayload.map((_, index) => index + 2) : [];
  const extraVehicleAssignment = (position) => (form.extraVehicleAssignments || {})[position] || {};
  const updateExtraVehicleAssignment = (position, changes) => {
    setForm((previous) => ({
      ...previous,
      extraVehicleAssignments: {
        ...(previous.extraVehicleAssignments || {}),
        [position]: { ...((previous.extraVehicleAssignments || {})[position] || {}), ...changes },
      },
    }));
  };
  const selectedNightRooms = parseNightRoomSnapshot(form.hotelSummarySnapshot);
  const configuredRoomRates = (quoteOptions?.roomRates || []).filter((rate) => rate.active !== false);
  // A hotel switched off in Hotel Master must not be offered on new bookings, but a booking that
  // was already saved against it keeps it in the dropdown so the stay is not silently lost.
  const bookingSelectedRoomRateIds = new Set(
    Object.values(form.selectedRoomRateIdsByNight || {})
      .flatMap((ids) => (Array.isArray(ids) ? ids : []))
      .filter((id) => id != null)
      .map((id) => String(id))
  );
  const bookingSelectedHotelIds = new Set(
    configuredRoomRates
      .filter((rate) => bookingSelectedRoomRateIds.has(String(rate.id)))
      .map((rate) => String(rate.hotelId || ""))
      .filter(Boolean)
  );
  // Older bookings have no saved rate ids, so fall back to the night summary - matched on the exact
  // hotel label only. findSelectedRoomRate is deliberately not used here: its fuzzy substring pass
  // would retain an unrelated inactive hotel whose code is contained in another hotel's code.
  const savedHotelLabels = new Set(
    Object.values(selectedNightRooms)
      .flatMap((summary) => String(summary || "").split("|"))
      .map((part) => parseRoomSummary(part.trim()).hotel.toLowerCase())
      .filter(Boolean)
  );
  configuredRoomRates.forEach((rate) => {
    if (!rate.hotelId) return;
    const labels = [rate.hotelCode, rate.hotelName]
      .filter(Boolean)
      .map((label) => String(label).toLowerCase());
    if (labels.some((label) => savedHotelLabels.has(label))) {
      bookingSelectedHotelIds.add(String(rate.hotelId));
    }
  });
  const activeRoomRates = configuredRoomRates.filter(
    (rate) =>
      rate.hotelActive !== false ||
      bookingSelectedRoomRateIds.has(String(rate.id)) ||
      bookingSelectedHotelIds.has(String(rate.hotelId || ""))
  );
  const roomRateCity = (rate) => rate?.hotelCity || "Location not set";
  const roomCityOptions = Array.from(new Set(activeRoomRates.map(roomRateCity).filter(Boolean))).sort();
  const configuredRoomRatesByNight = activeRoomRates
    .reduce((groups, rate) => {
      const night = Number(rate.nightNumber || 1);
      return {
        ...groups,
        [night]: [...(groups[night] || []), rate],
      };
    }, {});
  const defaultRoomCityForNight = (nightNumber) =>
    form.selectedRoomCityByNight?.[String(nightNumber)] ||
    roomRateCity(activeRoomRates.find((rate) => Number(rate.nightNumber || 1) === Number(nightNumber))) ||
    roomCityOptions[0] ||
    "";
  const roomRatesForNight = (nightNumber) => {
    const city = defaultRoomCityForNight(nightNumber);
    const cityRates = activeRoomRates.filter((rate) => String(roomRateCity(rate)) === String(city));
    return cityRates.length ? cityRates : configuredRoomRatesByNight[nightNumber] || [];
  };
  const roomRatesByNight = configuredRoomRatesByNight;
  const baseHotelNightCount = bookingBaseHotelNightCount(
    parseQuoteSnapshot(form.quoteSnapshot),
    quoteOptions
  );
  const hotelNightNumbers = baseHotelNightCount > 0
    ? Array.from({ length: baseHotelNightCount }, (_, index) => index + 1)
    : Array.from(
      new Set([
        ...Object.keys(roomRatesByNight).map(Number),
        ...Object.keys(selectedNightRooms).map(Number),
      ])
    )
      .filter((night) => night > 0)
      .sort((a, b) => a - b);
  // Nights the customer arranges their own stay for: no hotel is picked, priced or held for them.
  const noStayNights = (form.noStayNights || [])
    .map(Number)
    .filter((night) => hotelNightNumbers.includes(night));
  const noStayNightsKey = noStayNights.join(",");
  const isNoStayNight = (night) => noStayNights.includes(Number(night));
  const stayHotelNightNumbers = hotelNightNumbers.filter((night) => !isNoStayNight(night));
  const setNightNoStay = (night, skip) => {
    const current = (form.noStayNights || []).map(Number);
    update(
      "noStayNights",
      skip
        ? Array.from(new Set([...current, Number(night)])).sort((a, b) => a - b)
        : current.filter((item) => item !== Number(night))
    );
  };
  const selectedAddonNames = splitSnapshotList(form.addonSummarySnapshot).filter(
    (name) => name && name !== "None"
  );
  const addonNameOptions = Array.from(
    new Set([
      ...selectedAddonNames,
      ...(quoteOptions?.addons || []).map((addon) => addon.name).filter(Boolean),
    ])
  );
  const selectedTripForBooking = trips.find((trip) => Number(trip.id) === Number(form.tripId));
  const pickupLocationOptions = (selectedTripForBooking?.pickupOptions || selectedTripForBooking?.pickup_options || [])
    .filter((option) => option.isActive !== false);

  const updatePickupLocationFromDropdown = (value) => {
    const pickupPlan = planForPickupLocation(form.tripId, value);
    setForm((previous) => ({
      ...previous,
      pickupOptionId: value || "",
      useBookingDayWisePlan: pickupPlan.hasPickupPlan,
      bookingDayWisePlanDays: pickupPlan.hasPickupPlan ? pickupPlan.days : [],
      bookingPickupGoogleMapLink: pickupPlan.hasPickupPlan ? pickupPlan.pickupGoogleMapLink : "",
      bookingRefreshmentGoogleMapLink: pickupPlan.hasPickupPlan ? pickupPlan.refreshmentGoogleMapLink : "",
      dayWisePlanDays: pickupPlan.hasPickupPlan ? pickupPlan.days : previous.tripDayWisePlanDays,
      pickupGoogleMapLink: pickupPlan.hasPickupPlan
        ? pickupPlan.pickupGoogleMapLink
        : previous.tripPickupGoogleMapLink,
      refreshmentGoogleMapLink: pickupPlan.hasPickupPlan
        ? pickupPlan.refreshmentGoogleMapLink
        : previous.tripRefreshmentGoogleMapLink,
    }));
  };

  const updatePickupFromDropdown = (value) => {
    const pickup = dynamicPickups.find((item) => String(item.id) === value);
    markDynamicQuoteDirty();
    setForm((previous) => ({
      ...previous,
      pickupLabel: pickup?.pickupName || "",
      dynamicVehicleId: "",
      additionalVehicles: [],
      vehicleNameSnapshot: "",
    }));
  };

  const updateVehicleFromDropdown = (value) => {
    const vehicle = pickupDynamicVehicleOptions.find((item) => String(item.id) === value);
    markDynamicQuoteDirty();
    setForm((previous) => ({
      ...previous,
      dynamicVehicleId: vehicle?.id ? String(vehicle.id) : "",
      vehicleNameSnapshot: vehicle?.vehicleName || "",
    }));
  };

  // Same as Add Hotel: a vehicle is only added on request, starting as a copy of the first one.
  const updateAdditionalVehicles = (change) => {
    markDynamicQuoteDirty();
    setForm((previous) => ({ ...previous, additionalVehicles: change(previous.additionalVehicles || []) }));
  };
  const addAdditionalVehicle = () => updateAdditionalVehicles((list) => [
    ...list,
    { vehicleId: String(selectedDynamicVehicle?.id || pickupDynamicVehicleOptions[0]?.id || ""), extensionVehicleAmount: 0 },
  ]);

  const roomRateOptionsForNight = (nightNumber) => {
    const preferredNight = Number(nightNumber || 1);
    const optionsByLabel = new Map();
    roomRatesForNight(nightNumber).forEach((rate) => {
      if (!rate?.id) return;
      const label = roomRateHotelDisplayLabel(rate);
      const option = {
        value: String(rate.id),
        label,
        nightNumber: Number(rate.nightNumber || 1),
      };
      const existing = optionsByLabel.get(label);
      if (
        !existing ||
        (Number(existing.nightNumber || 1) !== preferredNight &&
          option.nightNumber === preferredNight)
      ) {
        optionsByLabel.set(label, option);
      }
    });
    return Array.from(optionsByLabel.values()).filter((item) => item.value);
  };

  const preferredRoomRateIdForNight = (roomRateId, nightNumber) => {
    const rate = activeRoomRates.find((item) => String(item.id) === String(roomRateId));
    if (!rate?.id) return String(roomRateId || "");
    const label = roomRateHotelDisplayLabel(rate);
    const preferred = roomRateOptionsForNight(nightNumber).find((item) => item.label === label);
    return preferred?.value || String(roomRateId);
  };

  const selectedRoomRateIdsForNight = (nightNumber) => {
    const saved = form.selectedRoomRateIdsByNight?.[String(nightNumber)] || form.selectedRoomRateIdsByNight?.[nightNumber];
    if (Array.isArray(saved) && saved.length) {
      const activeIds = new Set(activeRoomRates.map((rate) => String(rate.id)));
      const validSaved = Array.from(new Set(saved
        .map((id) => preferredRoomRateIdForNight(id, nightNumber))
        .filter((id) => activeIds.has(id))));
      if (validSaved.length) return validSaved;
    }
    const rate = findSelectedRoomRate(roomRatesForNight(nightNumber), selectedNightRooms[nightNumber]);
    return rate?.id ? [String(rate.id)] : [];
  };

  const selectedRoomRateIdMap = Object.fromEntries(
    hotelNightNumbers.map((night) => [String(night), selectedRoomRateIdsForNight(night)])
  );
  const roomTravellerCount = Math.max(1, Number(form.adults || 0) + Number(form.kids || 0) || Number(form.noOfTravellers || 1));
  const savedRoomCategoryForRate = (rate, night) => {
    if (!rate?.id) return null;
    const savedCategoryId = form.roomCategoryIdsByRate?.[String(rate.id)];
    const categories = roomCategoriesForRate(quoteOptions, rate);
    if (savedCategoryId) {
      const category = categories.find((option) => String(option.id) === String(savedCategoryId));
      if (category) return category;
    }
    const summary = selectedNightRooms[night];
    if (!summary) return null;
    const rateSummary = String(summary)
      .split(/\s+\|\s+|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .find((item) => {
        const matchedRate = findSelectedRoomRate(roomRatesForNight(night), item);
        return matchedRate?.id && String(matchedRate.id) === String(rate.id);
      });
    if (!rateSummary) return null;
    const selected = parseRoomSummary(rateSummary);
    return categoryForSavedRoomSummary(categories, selected.category);
  };
  const roomCategoryUnitsForSelection = (rate, category) => {
    if (!rate?.id || !category?.id) return 0;
    const key = roomCategoryUnitKey(rate.id, category.id);
    const configuredUnits = form.roomCategoryUnitsByKey || {};
    if (Object.prototype.hasOwnProperty.call(configuredUnits, key)) {
      return Math.max(0, Number(configuredUnits[key] || 0));
    }
    // On a saved booking the units restored from the quote snapshot above are the only trustworthy
    // ones. Falling through to the guesses below would invent a room mix the customer never booked
    // and then reprice the booking from it.
    if (isSavedBooking) return 0;
    const rateUnits = Number(form.roomUnitsByRate?.[String(rate.id)] || 0);
    if (rateUnits > 0 && String(form.roomCategoryIdsByRate?.[String(rate.id)] || "") === String(category.id)) {
      return rateUnits;
    }
    if (String(savedRoomCategoryForRate(rate, rate.nightNumber || 1)?.id || "") === String(category.id)) {
      return suggestedRoomCategoryUnits(category, roomTravellerCount);
    }
    return 0;
  };

  const selectedHotelSummaryFromMap = (map) => Object.entries(map || {})
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([night, ids]) => {
      const summaries = (ids || [])
        .map((id) => activeRoomRates.find((rate) => String(rate.id) === String(id)))
        .map((rate) => roomRateSummary(rate))
        .filter(Boolean);
      return summaries.length ? `Night ${night}: ${summaries.join(" | ")}` : "";
    })
    .filter(Boolean)
    .join(", ");

  const setSelectedRoomRateIdsForNight = (nightNumber, ids) => {
    markDynamicQuoteDirty();
    setForm((previous) => {
      const key = String(nightNumber);
      const nextMap = {
        ...(previous.selectedRoomRateIdsByNight || {}),
        [key]: Array.from(new Set((ids || []).filter(Boolean).map(String))),
      };
      if (!nextMap[key].length) delete nextMap[key];
      const nextRoomUnitsByRate = { ...(previous.roomUnitsByRate || {}) };
      (nextMap[key] || []).forEach((id) => {
        nextRoomUnitsByRate[id] = Math.max(1, Number(nextRoomUnitsByRate[id] || 1));
      });
      const activeRateIds = new Set(
        Object.values(nextMap)
          .flat()
          .map((id) => String(id))
      );
      const nextFacilityKeys = {};
      Object.entries(previous.selectedHotelFacilityKeys || {}).forEach(([facilityKey, selected]) => {
        const [rateId] = facilityKey.split(":");
        if (activeRateIds.has(String(rateId))) {
          nextFacilityKeys[facilityKey] = selected;
        }
      });
      return {
        ...previous,
        selectedRoomRateIdsByNight: nextMap,
        roomUnitsByRate: nextRoomUnitsByRate,
        selectedHotelFacilityKeys: nextFacilityKeys,
        hotelSummarySnapshot: selectedHotelSummaryFromMap(nextMap),
      };
    });
  };

  const updateRoomCityForNight = (nightNumber, city) => {
    markDynamicQuoteDirty();
    setForm((previous) => {
      const key = String(nightNumber);
      const nextMap = { ...(previous.selectedRoomRateIdsByNight || {}) };
      const removedIds = new Set((nextMap[key] || []).map(String));
      delete nextMap[key];
      const activeRateIds = new Set(
        Object.values(nextMap)
          .flat()
          .map((id) => String(id))
      );
      const nextRoomUnitsByRate = {};
      Object.entries(previous.roomUnitsByRate || {}).forEach(([rateId, units]) => {
        if (!removedIds.has(String(rateId))) nextRoomUnitsByRate[rateId] = units;
      });
      const nextRoomCategoryIdsByRate = {};
      Object.entries(previous.roomCategoryIdsByRate || {}).forEach(([rateId, categoryId]) => {
        if (!removedIds.has(String(rateId))) nextRoomCategoryIdsByRate[rateId] = categoryId;
      });
      const nextRoomCategoryUnitsByKey = {};
      Object.entries(previous.roomCategoryUnitsByKey || {}).forEach(([unitKey, units]) => {
        const [rateId] = unitKey.split(":");
        if (!removedIds.has(String(rateId))) nextRoomCategoryUnitsByKey[unitKey] = units;
      });
      const nextFacilityKeys = {};
      Object.entries(previous.selectedHotelFacilityKeys || {}).forEach(([facilityKey, selected]) => {
        const [rateId] = facilityKey.split(":");
        if (activeRateIds.has(String(rateId))) {
          nextFacilityKeys[facilityKey] = selected;
        }
      });
      return {
        ...previous,
        selectedRoomCityByNight: {
          ...(previous.selectedRoomCityByNight || {}),
          [key]: city,
        },
        selectedRoomRateIdsByNight: nextMap,
        roomUnitsByRate: nextRoomUnitsByRate,
        roomCategoryIdsByRate: nextRoomCategoryIdsByRate,
        roomCategoryUnitsByKey: nextRoomCategoryUnitsByKey,
        selectedHotelFacilityKeys: nextFacilityKeys,
        hotelSummarySnapshot: selectedHotelSummaryFromMap(nextMap),
      };
    });
  };

  const addNightHotelSelection = (nightNumber) => {
    const current = selectedRoomRateIdsForNight(nightNumber);
    const nextOption = roomRateOptionsForNight(nightNumber).find(
      (item) => !current.includes(String(item.value))
    );
    if (!nextOption?.value) return;
    setSelectedRoomRateIdsForNight(nightNumber, [...current, String(nextOption.value)]);
  };

  const updateNightHotelSelection = (nightNumber, previousRateId, nextRateId) => {
    const current = selectedRoomRateIdsForNight(nightNumber);
    const next = current.map((id) => String(id) === String(previousRateId) ? String(nextRateId) : id);
    setSelectedRoomRateIdsForNight(nightNumber, next);
  };

  const removeNightHotelSelection = (nightNumber, rateId) => {
    setSelectedRoomRateIdsForNight(
      nightNumber,
      selectedRoomRateIdsForNight(nightNumber).filter((id) => String(id) !== String(rateId))
    );
  };

  const updateRoomCategoryUnits = (rate, category, delta) => {
    if (!rate?.id || !category?.id) return;
    markDynamicQuoteDirty();
    const key = roomCategoryUnitKey(rate.id, category.id);
    const configuredMaximum = Math.max(1, Number(category.maximumUnits || 1));
    const availability = roomAvailabilityByKey[roomCategoryUnitKey(rate.id, category.id)];
    const maximumUnits = availability
      ? Math.max(0, Math.min(configuredMaximum, Number(availability.availableUnits ?? configuredMaximum)))
      : configuredMaximum;
    setForm((previous) => {
      const previousUnits = previous.roomCategoryUnitsByKey || {};
      const hasConfiguredUnits = Object.prototype.hasOwnProperty.call(previousUnits, key);
      let currentUnits = hasConfiguredUnits ? Number(previousUnits[key] || 0) : 0;
      if (!hasConfiguredUnits && !isSavedBooking) {
        const rateUnits = Number(previous.roomUnitsByRate?.[String(rate.id)] || 0);
        if (rateUnits > 0 && String(previous.roomCategoryIdsByRate?.[String(rate.id)] || "") === String(category.id)) {
          currentUnits = rateUnits;
        } else if (String(savedRoomCategoryForRate(rate, rate.nightNumber || 1)?.id || "") === String(category.id)) {
          currentUnits = suggestedRoomCategoryUnits(category, roomTravellerCount);
        }
      }
      return {
        ...previous,
        roomCategoryUnitsByKey: {
          ...previousUnits,
          [key]: Math.min(maximumUnits, Math.max(0, currentUnits + delta)),
        },
      };
    });
  };

  const toggleAddonSelection = (name, checked, manual = true) => {
    if (manual) {
      setManuallyToggledAddonNames((previous) => ({ ...previous, [name]: true }));
    }
    const next = checked
      ? Array.from(new Set([...selectedAddonNames, name]))
      : selectedAddonNames.filter((item) => item !== name);
    update("addonSummarySnapshot", next.length ? next.join(", ") : "None");
  };

  // No-stay nights are left out here, so their hotel is neither priced nor saved - but it stays in
  // selectedRoomRateIdMap, so switching the night back to "Stay needed" restores the pick.
  const selectedRoomRateIds = stayHotelNightNumbers
    .flatMap((night) => selectedRoomRateIdMap[String(night)] || [])
    .filter(Boolean);
  const selectedRoomRateNightSelections = stayHotelNightNumbers
    .flatMap((night) => (selectedRoomRateIdMap[String(night)] || [])
      .filter(Boolean)
      .map((roomRateId) => ({
        roomRateId: Number(roomRateId),
        nightNumber: Number(night),
      })));
  const selectedRoomCategorySelections = Array.from(stayHotelNightNumbers
    .reduce((selectionsByKey, night) => {
      const rates = (selectedRoomRateIdMap[String(night)] || [])
        .map((id) => activeRoomRates.find((rate) => String(rate.id) === String(id)))
        .filter(Boolean);
      rates.forEach((rate) => {
        roomCategoriesForRate(quoteOptions, rate)
          .map((category) => ({
            roomRateId: Number(rate.id),
            roomCategoryId: Number(category.id),
            units: roomCategoryUnitsForSelection(rate, category),
          }))
          .filter((selection) => selection.units > 0)
          .forEach((selection) => {
            selectionsByKey.set(`${selection.roomRateId}:${selection.roomCategoryId}`, selection);
          });
      });
      return selectionsByKey;
    }, new Map())
    .values());
  const selectedRoomCategoryIds = selectedRoomCategorySelections.map((selection) => selection.roomCategoryId);
  const selectedRoomUnits = selectedRoomCategorySelections.map((selection) => selection.units);
  // A saved booking whose snapshot did not yield per-category units now shows zero rooms rather
  // than a guessed mix. Say so, otherwise the empty steppers read as "no rooms booked".
  const unrestoredRoomSelection = isSavedBooking
    && Boolean(form.dynamicPricing)
    && selectedRoomRateIds.length > 0
    && selectedRoomRateIds.some((rateId) =>
      !selectedRoomCategorySelections.some((selection) => String(selection.roomRateId) === String(rateId)));
  // Package nights as picked on screen, each { night, text }; the day label is added when the
  // document is built, once extension nights are in.
  const currentBookingDocumentRoomLines = () => hotelNightNumbers
    .flatMap((night) => {
      if (isNoStayNight(night)) return [{ night, noStay: true, text: DOCUMENT_NO_STAY_TEXT }];
      const rates = (selectedRoomRateIdMap[String(night)] || [])
        .map((id) => activeRoomRates.find((rate) => String(rate.id) === String(id)))
        .filter(Boolean);
      return rates.flatMap((rate) =>
        roomCategoriesForRate(quoteOptions, rate)
          .map((category) => ({
            category,
            units: roomCategoryUnitsForSelection(rate, category),
          }))
          .filter((selection) => selection.units > 0)
          .map(({ category, units }) => {
            // Customer documents only ever carry the hotel's display name (code), never its real name.
            const hotel = rate.hotelCode || "Hotel";
            const room = category.categoryName || rate.roomCategoryName || "Room";
            return {
              night,
              text: withDocumentFacilityDetails(
                `${hotel} - ${room} (${units} unit${units === 1 ? "" : "s"})`,
                liveBookingDocumentFacilityLabels(rate, form.selectedHotelFacilityKeys)
              ),
            };
          })
      );
    })
    .filter(Boolean);
  const selectedAddonIds = selectedAddonNames
    .map(
      (name) =>
        (quoteOptions?.addons || []).find((addon) => addon.name === name)?.id
    )
    .filter(Boolean);
  // An add-on's date for this booking, for the driver's message: the date staff set here, else its
  // package day from Dynamic Pricing counted from the travel date. Only changed dates are saved, so
  // the rest still follow a new travel date.
  const addonDefaultDate = (addon) =>
    form.travelDate && Number(addon?.dayNumber) >= 1 ? addIsoDays(form.travelDate, Number(addon.dayNumber) - 1) : "";
  const addonDateFor = (addon) => (form.addonDates || {})[addon.id] || addonDefaultDate(addon);
  const setAddonDate = (addon, value) => {
    const next = { ...(form.addonDates || {}) };
    if (!value || value === addonDefaultDate(addon)) delete next[addon.id];
    else next[addon.id] = value;
    update("addonDates", next);
  };
  const mandalpattiAddonNames = (quoteOptions?.addons || [])
    .filter((addon) => String(addon?.name || "").toLowerCase().includes("mandalpatti"))
    .map((addon) => addon.name)
    .filter(Boolean);
  const mandalpattiRequired = mandalpattiAddonNames.some((name) => selectedAddonNames.includes(name));
  const selectedRoomRateIdsKey = selectedRoomRateIds.join(",");
  const selectedRoomRateNightSelectionsKey = normalizedRoomRateNightSelectionKey(selectedRoomRateNightSelections);
  const selectedRoomCategoryIdsKey = selectedRoomCategoryIds.join(",");
  const selectedRoomUnitsKey = selectedRoomUnits.join(",");
  const selectedRoomCategorySelectionsKey = normalizedSelectionKey(selectedRoomCategorySelections);
  const selectedAddonIdsKey = selectedAddonIds.join(",");
  const selectedAddonNamesKey = selectedAddonNames.join(",");
  const extensionHotelOptions = React.useMemo(
    () =>
      (quoteOptions?.availableHotels || []).filter(
        (hotel) =>
          hotel?.active !== false &&
          (hotel.roomCategories || []).some((category) => category?.active !== false)
      ),
    [quoteOptions]
  );
  const extensionCityOptions = React.useMemo(
    () =>
      Array.from(
        new Set(extensionHotelOptions.map((hotel) => hotel.city).filter(Boolean))
      ).sort(),
    [extensionHotelOptions]
  );
  const extensionHotelsForCity = React.useCallback(
    (city) =>
      extensionHotelOptions.filter((hotel) => !city || String(hotel.city || "") === String(city)),
    [extensionHotelOptions]
  );
  const extensionCategoriesForHotel = React.useCallback(
    (hotelId) =>
      (
        extensionHotelOptions.find((hotel) => String(hotel.id) === String(hotelId))?.roomCategories || []
      ).filter((category) => category?.active !== false),
    [extensionHotelOptions]
  );
  const extensionStayDateValue = React.useCallback(
    (stay) =>
      form.travelDate
        ? addIsoDays(
            form.travelDate,
            stay?.position === "POST"
              ? Number(quoteOptions?.durationNights || 0) + Number(stay?.nightIndex || 1) - 1
              : -Number(stay?.nightIndex || 1)
          )
        : "",
    [form.travelDate, quoteOptions?.durationNights]
  );
  // Extension nights sit outside the trip's own nights, so they need their own lookup, keyed by
  // room category and stay date rather than by room rate.
  useEffect(() => {
    const preNights = Number(form.extraPreNights || 0);
    const postNights = Number(form.extraPostNights || 0);
    const categoryIds = extensionHotelOptions.flatMap((hotel) =>
      (hotel.roomCategories || []).filter((category) => category?.active !== false).map((category) => category.id));
    if (!show || !form.travelDate || !categoryIds.length || (preNights <= 0 && postNights <= 0)) {
      setExtensionAvailabilityByKey({});
      return undefined;
    }
    const startDate = addIsoDays(form.travelDate, -preNights);
    const endDate = addIsoDays(form.travelDate, Number(quoteOptions?.durationNights || 0) + postNights - 1);
    let cancelled = false;
    fetchExtensionRoomAvailability(categoryIds, startDate, endDate, form.id || null)
      .then((items) => {
        if (cancelled) return;
        setExtensionAvailabilityByKey(
          Object.fromEntries(items.map((item) => [`${item.roomCategoryId}:${item.stayDate}`, item]))
        );
      })
      .catch(() => {
        if (!cancelled) setExtensionAvailabilityByKey({});
      });
    return () => { cancelled = true; };
  }, [show, form.travelDate, form.extraPreNights, form.extraPostNights, form.id, quoteOptions?.durationNights, extensionHotelOptions]);
  const extensionMaxUnits = React.useCallback(
    (category, stay) => {
      const configuredMaximum = Math.max(1, Number(category?.maximumUnits || 1));
      const availability = extensionAvailabilityByKey[`${category?.id}:${extensionStayDateValue(stay)}`];
      if (!availability) return configuredMaximum;
      return Math.max(0, Math.min(configuredMaximum, Number(availability.availableUnits ?? configuredMaximum)));
    },
    [extensionAvailabilityByKey, extensionStayDateValue]
  );
  const suggestedExtensionUnits = React.useCallback(
    (category) => {
      if (!category) return 0;
      const maxPeople = Math.max(1, Number(category?.maxMemberCount || 1));
      const people = Math.max(1, Number(form.adults || 0) + Number(form.kids || 0) || form.noOfTravellers || 1);
      const maxUnits = Math.max(1, Number(category.maximumUnits || 1));
      return Math.min(maxUnits, Math.max(1, Math.ceil(people / maxPeople)));
    },
    [form.adults, form.kids, form.noOfTravellers]
  );
  const defaultExtensionStay = React.useCallback(
    (position, previous = {}, nightIndex = 1) => {
      const city =
        previous.city || extensionCityOptions[0] || extensionHotelOptions[0]?.city || "";
      const hotels = extensionHotelsForCity(city);
      const matchedHotel = hotels.find((item) => String(item.id) === String(previous.hotelId));
      // If this stay already had a hotel (loaded from a saved booking) but that hotel isn't in
      // the currently-loaded options yet (e.g. trip-pricing config still fetching), keep the
      // known hotelId/roomCategoryId instead of silently substituting an unrelated hotel while
      // still carrying over the old room category selections - that mismatch is what the backend
      // rejects as "Extension room category is not available for selected hotel".
      const hotel =
        matchedHotel ||
        (previous.hotelId ? null : hotels[0] || extensionHotelOptions[0] || null);
      const categories = hotel ? extensionCategoriesForHotel(hotel.id) : [];
      const category =
        categories.find((item) => String(item.id) === String(previous.roomCategoryId)) ||
        (previous.roomCategoryId ? null : categories[0]) ||
        null;
      const previousSelections = Array.isArray(previous.roomCategorySelections)
        ? previous.roomCategorySelections
        : [];
      const roomCategorySelections = previousSelections.length
        ? previousSelections
        : category
          ? [{ roomCategoryId: category.id, units: previous.units || suggestedExtensionUnits(category) }]
          : [];
      const primarySelection =
        roomCategorySelections.find((selection) => Number(selection.units || 0) > 0) ||
        roomCategorySelections[0] ||
        {};
      return {
        position,
        nightIndex: Number(previous.nightIndex || nightIndex || 1),
        stayKey:
          previous.stayKey ||
          `ext-${position}-${nightIndex}-${Math.random().toString(36).slice(2, 9)}`,
        scope: previous.scope || "BOTH",
        city: hotel?.city || city,
        hotelId: hotel?.id || previous.hotelId || "",
        roomCategoryId: primarySelection.roomCategoryId || category?.id || previous.roomCategoryId || "",
        units: primarySelection.units || previous.units || suggestedExtensionUnits(category),
        roomCategorySelections,
        selectedFacilityKeys: previous.selectedFacilityKeys || {},
        note: previous.note || "",
      };
    },
    [
      extensionCategoriesForHotel,
      extensionCityOptions,
      extensionHotelOptions,
      extensionHotelsForCity,
      suggestedExtensionUnits,
    ]
  );
  const updateExtensionStayAt = (index, changes) => {
    markDynamicQuoteDirty();
    setForm((previous) => ({
      ...previous,
      extensionStays: (previous.extensionStays || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...changes } : item
      ),
    }));
  };
  const savedQuoteSnapshot = React.useMemo(
    () => parseQuoteSnapshot(form.quoteSnapshot),
    [form.quoteSnapshot]
  );
  const selectedHotelFacilities = React.useMemo(() => {
    const baseNightFacilities = hotelFacilitySelectionsFromKeys(form.selectedHotelFacilityKeys, selectedRoomRateIdsKey.split(",").filter(Boolean));
    // Extension-night facilities (e.g. Campfire on a post-trip home stay) are tracked per-stay in
    // extensionStays[].selectedFacilityKeys since they have no roomRateId. The pricing engine only
    // reads facility selections from this flat list, tagged by extensionStayKey instead - without
    // this, a checked extension facility never reaches the quote calculation.
    const extensionFacilities = (form.tripExtensionRequired ? form.extensionStays || [] : []).flatMap((stay) =>
      Object.entries(stay.selectedFacilityKeys || {})
        .filter(([, selected]) => selected === true)
        .map(([facilityId]) => ({
          extensionStayKey: stay.stayKey,
          facilityId: Number(facilityId),
          selected: true,
        }))
    );
    return [...baseNightFacilities, ...extensionFacilities];
  }, [form.selectedHotelFacilityKeys, selectedRoomRateIdsKey, form.tripExtensionRequired, form.extensionStays]);
  const selectedHotelFacilitiesKey = selectedHotelFacilities
    .map((selection) => `${selection.roomRateId ?? selection.extensionStayKey}:${selection.facilityId}`)
    .join(",");
  // The option of each add-on and how many take it. Until staff change one here it is the booking's
  // own saved choice - sending none would quietly move the booking onto the first option and re-price it.
  const selectedAddonOptions = (form.addonOptions ?? savedQuoteSnapshot?.quoteRequest?.selectedAddonOptions ?? [])
    .filter((choice) => selectedAddonIds.map(String).includes(String(choice.addonId)))
    .filter((choice) => choice.optionId || choice.people);
  const selectedAddonOptionsKey = addonOptionsKey(selectedAddonOptions);
  const chooseAddonOption = (addonId, change) => {
    const rest = selectedAddonOptions.filter((choice) => String(choice.addonId) !== String(addonId));
    const next = { ...(selectedAddonOptions.find((choice) => String(choice.addonId) === String(addonId)) || { addonId }), ...change };
    update("addonOptions", next.optionId || next.people ? [...rest, next] : rest);
  };
  // Which pricing inputs differ from the saved quote - null when there is no saved quote to compare.
  // Also the "reason" shown when a receipted booking's agreed price is about to change.
  const savedQuoteSelectionDifferences = React.useMemo(() => {
    if (!savedQuoteSnapshot) return null;
    const differences = [];
    const differ = (label, changed) => {
      if (changed && !differences.includes(label)) differences.push(label);
    };
    const request = savedQuoteSnapshot.quoteRequest || {};
    const travellers = savedQuoteSnapshot.travellers || request || {};
    const snapshotAdults = Number(travellers.adults ?? request.adults ?? 0);
    const snapshotKids = Number(travellers.kids ?? request.kids ?? 0);
    const currentAdults = Number(form.adults || 0);
    const currentKids = Number(form.kids || 0);
    differ("Travellers", snapshotAdults !== currentAdults || snapshotKids !== currentKids);
    // Saved snapshots can hold ages beyond the kid count (e.g. "0 kids (ages 1, 2)"); only counted kids matter.
    differ("Kids' ages", normalizedArrayKey((travellers.kidAges || request.kidAges || []).slice(0, snapshotKids)) !== normalizedArrayKey((form.kidAges || []).slice(0, currentKids)));
    differ("Kids' vehicle seats", normalizedArrayKey((travellers.kidVehicleSeatNeeded || request.kidVehicleSeatNeeded || []).slice(0, snapshotKids)) !== normalizedArrayKey((form.kidVehicleSeatNeeded || []).slice(0, currentKids)));

    const snapshotPickupId = request.pickupId ?? savedQuoteSnapshot.selectedPickup?.id ?? null;
    differ("Pickup", Boolean(selectedDynamicPickup?.id && snapshotPickupId != null && String(snapshotPickupId) !== String(selectedDynamicPickup.id)));
    const snapshotVehicleId = request.vehicleId ?? savedQuoteSnapshot.selectedVehicle?.id ?? null;
    differ("Vehicle type", Boolean(selectedDynamicVehicle?.id && snapshotVehicleId != null && String(snapshotVehicleId) !== String(selectedDynamicVehicle.id)));
    differ("Vehicle type", normalizedArrayKey((request.additionalVehicles || []).map((item) => Number(item?.vehicleId)).filter(Boolean).sort((a, b) => a - b)) !== additionalVehiclesKey);
    const snapshotAcType = request.acType ?? savedQuoteSnapshot.selectedVehicle?.acType ?? "";
    differ("AC / Non-AC", Boolean(snapshotAcType && form.acTypeSnapshot && String(snapshotAcType) !== String(form.acTypeSnapshot)));
    differ("Travel date", Boolean(savedQuoteSnapshot.travelDate && form.travelDate && String(savedQuoteSnapshot.travelDate) !== String(form.travelDate)));
    differ("Hotel / rooms", normalizedArrayKey(savedQuoteSnapshot.selectedRoomRateIds || []) !== normalizedArrayKey(selectedRoomRateIds));
    differ(
      "Hotel / rooms",
      normalizedRoomRateNightSelectionKey(request.selectedRoomRateNightSelections || savedQuoteSnapshot.selectedRoomRateNightSelections || []) !==
        selectedRoomRateNightSelectionsKey
    );
    differ("Room category / units", normalizedSelectionKey(savedQuoteSnapshot.selectedRoomCategorySelections || []) !== normalizedSelectionKey(selectedRoomCategorySelections));
    differ("No-stay nights", noStayNightsFromSnapshot(savedQuoteSnapshot).join(",") !== noStayNightsKey);
    differ("Add-ons", normalizedArrayKey(request.selectedAddonIds || savedQuoteSnapshot.selectedAddonIds || []) !== normalizedArrayKey(selectedAddonIds));
    differ("Add-on options", addonOptionsKey(request.selectedAddonOptions || []) !== selectedAddonOptionsKey);
    differ("Hotel facilities", normalizedHotelFacilityKey(request.selectedHotelFacilities || savedQuoteSnapshot.selectedHotelFacilities || []) !== normalizedHotelFacilityKey(selectedHotelFacilities));
    differ("Trip extension", Boolean(request.tripExtensionRequired ?? savedQuoteSnapshot.tripExtension?.required) !== Boolean(form.tripExtensionRequired));
    differ("Trip extension", Number(request.extraPreNights ?? savedQuoteSnapshot.tripExtension?.extraPreNights ?? 0) !== Number(form.extraPreNights || 0));
    differ("Trip extension", Number(request.extraPostNights ?? savedQuoteSnapshot.tripExtension?.extraPostNights ?? 0) !== Number(form.extraPostNights || 0));
    differ("Trip extension", normalizedExtensionStayKey(request.extensionStays || []) !== normalizedExtensionStayKey(form.tripExtensionRequired ? extensionStaysRequestPayload(form.extensionStays) : []));
    differ("Trip extension", extensionVehicleAmountsKey(request.extensionVehicleAmount, request.additionalVehicles) !== currentExtensionVehicleAmountsKey);
    return differences;
  }, [
    savedQuoteSnapshot,
    form.adults,
    form.kids,
    form.kidAges,
    form.kidVehicleSeatNeeded,
    form.acTypeSnapshot,
    form.travelDate,
    form.tripExtensionRequired,
    form.extraPreNights,
    form.extraPostNights,
    form.extensionStays,
    currentExtensionVehicleAmountsKey,
    selectedDynamicPickup,
    selectedDynamicVehicle,
    additionalVehiclesKey,
    selectedRoomRateIds,
    selectedRoomRateNightSelectionsKey,
    selectedRoomCategorySelections,
    selectedAddonIds,
    selectedAddonOptionsKey,
    selectedHotelFacilities,
    noStayNightsKey,
  ]);
  const savedQuoteMatchesCurrentSelection = Boolean(savedQuoteSelectionDifferences && savedQuoteSelectionDifferences.length === 0);
  const customizationAdjustment = customizationAdjustmentFromForm(form);
  // Once the payment receipt is generated the agreed price is locked (the server decides, see
  // BookingPriceLockService). While no pricing input differs from the saved quote, the saved quote and
  // agreed total are kept as they are - today's hotel/vehicle rates are never fetched, so saving the
  // booking for anything else (driver, vehicle assignment, notes...) cannot re-price it.
  const priceLocked = isSavedBooking && Boolean(form.dynamicPricing) && Boolean(initial?.priceLocked);
  const agreedTotalPrice = Number(initial?.totalPrice || 0);
  const agreedCustomizationAdjustment = React.useMemo(
    () => customizationAdjustmentFromForm(normalize(initial)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initial]
  );
  const customizationChangedFromAgreed = customizationAdjustment !== agreedCustomizationAdjustment;
  const lockedQuoteInUse = priceLocked && savedQuoteMatchesCurrentSelection && hasSavedCalculationDetails(savedQuoteSnapshot);
  const lockedTotalPrice = Math.max(0, agreedTotalPrice - agreedCustomizationAdjustment + customizationAdjustment);
  const priceChangeReasons = [
    ...(savedQuoteSelectionDifferences || []),
    ...(customizationChangedFromAgreed ? ["Customization"] : []),
  ];
  const visibleCalculationQuote = draftQuote || (hasSavedCalculationDetails(savedQuoteSnapshot) ? savedQuoteSnapshot : null);
  const draftQuoteFinalTotal = correctedBookingCalculationTotal(draftQuote, customizationAdjustment);
  const draftQuoteBaseTotal = correctedBookingCalculationTotal(draftQuote, 0) || Number(draftQuote?.roundedTotalPrice || draftQuote?.finalTripPrice || 0);
  const visibleCalculationTotal = correctedBookingCalculationTotal(visibleCalculationQuote, customizationAdjustment);
  const canRecalculateDynamicQuote =
    Boolean(form.dynamicPricing && quoteOptions && selectedDynamicPickup) &&
    Boolean(selectedDynamicVehicle || !form.vehicleNameSnapshot) &&
    (!hotelNightNumbers.length || stayHotelNightNumbers.length > 0) &&
    stayHotelNightNumbers.every((night) => (selectedRoomRateIdMap[String(night)] || []).length > 0) &&
    stayHotelNightNumbers.every((night) => {
      return (selectedRoomRateIdMap[String(night)] || []).every((rateId) =>
        selectedRoomCategorySelections.some((selection) => String(selection.roomRateId) === String(rateId))
      );
    });
  const extensionStaysPayload = React.useMemo(
    () => form.tripExtensionRequired ? extensionStaysRequestPayload(form.extensionStays) : [],
    [form.tripExtensionRequired, form.extensionStays]
  );
  const extensionStaysPayloadKey = normalizedExtensionStayKey(extensionStaysPayload);
  const dynamicQuoteRequestPayload = React.useMemo(() => {
    const adults = Math.max(1, Number(form.adults || 1));
    const kids = Math.max(0, Number(form.kids || 0));
    return {
      adults,
      kids,
      kidAges: (form.kidAges || []).slice(0, kids).map((age) => Number(age || 0)),
      kidVehicleSeatNeeded: (form.kidVehicleSeatNeeded || []).slice(0, kids),
      pickupId: selectedDynamicPickup?.id || null,
      vehicleId: selectedDynamicVehicle?.id || null,
      additionalVehicles: additionalVehiclesPayload,
      acType: form.acTypeSnapshot || "NON_AC",
      mandalpattiRequired,
      selectedAddonIds,
      selectedAddonOptions,
      selectedRoomRateIds,
      selectedRoomRateNightSelections,
      selectedRoomCityByNight: form.selectedRoomCityByNight || {},
      selectedRoomCategoryIds,
      selectedRoomUnits,
      selectedRoomCategorySelections,
      selectedHotelFacilities,
      noStayNights,
      travelDate: form.travelDate || null,
      bookingId: form.id || null,
      tripExtensionRequired: Boolean(form.tripExtensionRequired),
      extensionScope: "BOTH",
      extraPreNights: Number(form.extraPreNights || 0),
      extraPostNights: Number(form.extraPostNights || 0),
      extensionStays: extensionStaysPayload,
      extensionVehicleAmount: Number(form.extensionVehicleAmount || 0),
      tripExtensionNote: (form.tripExtensionNote || "").trim() || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.adults,
    form.kids,
    form.kidAges,
    form.kidVehicleSeatNeeded,
    form.acTypeSnapshot,
    form.selectedRoomCityByNight,
    form.travelDate,
    form.id,
    form.tripExtensionRequired,
    form.extraPreNights,
    form.extraPostNights,
    form.extensionVehicleAmount,
    form.tripExtensionNote,
    selectedDynamicPickup?.id,
    selectedDynamicVehicle?.id,
    additionalVehiclesPayloadKey,
    mandalpattiRequired,
    selectedAddonIdsKey,
    selectedAddonOptionsKey,
    selectedRoomRateIdsKey,
    selectedRoomRateNightSelectionsKey,
    selectedRoomCategoryIdsKey,
    selectedRoomUnitsKey,
    selectedRoomCategorySelectionsKey,
    selectedHotelFacilitiesKey,
    extensionStaysPayloadKey,
    noStayNightsKey,
  ]);
  const dynamicQuoteRequestKey = JSON.stringify(dynamicQuoteRequestPayload);

  useEffect(() => {
    if (!show || !form.dynamicPricing || !quoteOptions || !form.tripId) return;
    if (lockedQuoteInUse) {
      setDraftQuote(null);
      setDraftQuoteError("");
      setDraftQuoteLoading(false);
      return;
    }
    if (!canRecalculateDynamicQuote) {
      setDraftQuoteLoading(false);
      if (hasSavedCalculationDetails(savedQuoteSnapshot) && savedQuoteMatchesCurrentSelection) {
        setDraftQuote(savedQuoteSnapshot);
        setDraftQuoteError("Showing saved conversion calculation. Select pickup, vehicle, and hotel room category for every night to recalculate.");
      } else {
        setDraftQuote(null);
        setDraftQuoteError("Select pickup, vehicle, and hotel room category for every night to calculate the latest price.");
      }
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setDraftQuoteLoading(true);
      setDraftQuoteError("");
      try {
        const result = await calculateAdminTripQuote(form.tripId, dynamicQuoteRequestPayload);
        if (cancelled) return;
        setDraftQuote(result);
        const total = correctedBookingCalculationTotal(result, customizationAdjustmentFromForm(form));
        if (total > 0) {
          setForm((prev) =>
            String(prev.totalPrice || "") === String(total)
              ? prev
              : { ...prev, totalPrice: String(total) }
          );
        }
      } catch (e) {
        console.error("Failed to recalculate booking quote", e);
        setDraftQuote(null);
        setDraftQuoteError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Unable to calculate price for the selected booking details."
        );
      } finally {
        if (!cancelled) setDraftQuoteLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setDraftQuoteLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    form.dynamicPricing,
    form.tripId,
    savedQuoteMatchesCurrentSelection,
    quoteOptions,
    canRecalculateDynamicQuote,
    dynamicQuoteRequestKey,
    lockedQuoteInUse,
  ]);

  // Agreed price stays as it is; only a customization change moves it, by exactly that change.
  useEffect(() => {
    if (!show || !lockedQuoteInUse || lockedTotalPrice <= 0) return;
    setForm((prev) =>
      String(prev.totalPrice || "") === String(lockedTotalPrice)
        ? prev
        : { ...prev, totalPrice: String(lockedTotalPrice) }
    );
  }, [show, lockedQuoteInUse, lockedTotalPrice]);

  useEffect(() => {
    if (!show || !form.dynamicPricing || lockedQuoteInUse) return;
    if (isSavedBooking && Number(form.totalPrice || 0) > 0 && !dynamicTotalEditedRef.current) return;
    const total = correctedBookingCalculationTotal(
      draftQuote || (savedQuoteMatchesCurrentSelection ? savedQuoteSnapshot : null),
      customizationAdjustmentFromForm(form)
    );
    if (total <= 0) return;
    setForm((prev) =>
      String(prev.totalPrice || "") === String(total)
        ? prev
        : { ...prev, totalPrice: String(total) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    form.dynamicPricing,
    form.customizationRequired,
    form.customizationPrice,
    form.customizationPriceAdjustment,
    draftQuote?.roundedTotalPrice,
    draftQuote?.finalTripPrice,
    savedQuoteSnapshot?.roundedTotalPrice,
    savedQuoteSnapshot?.finalTripPrice,
    savedQuoteMatchesCurrentSelection,
    lockedQuoteInUse,
  ]);

  useEffect(() => {
    if (!show) return;
    if (!form.tripExtensionRequired) {
      if ((form.extensionStays || []).length) {
        setForm((previous) => ({ ...previous, extensionStays: [] }));
      }
      return;
    }
    const preCount = Math.max(0, Number(form.extraPreNights) || 0);
    const postCount = Math.max(0, Number(form.extraPostNights) || 0);
    const desiredPositions = [
      ...Array.from({ length: preCount }, (_, index) => ({ position: "PRE", nightIndex: index + 1 })),
      ...Array.from({ length: postCount }, (_, index) => ({ position: "POST", nightIndex: index + 1 })),
    ];
    setForm((previous) => {
      const grouped = (previous.extensionStays || []).reduce((acc, stay) => {
        const key = `${stay.position || "PRE"}:${Number(stay.nightIndex || 1)}`;
        acc[key] = [...(acc[key] || []), stay];
        return acc;
      }, {});
      const nextExtensionStays = desiredPositions.flatMap(({ position, nightIndex }) => {
        const key = `${position}:${nightIndex}`;
        const existing = grouped[key] || [];
        return existing.length
          ? existing.map((stay) => defaultExtensionStay(position, stay, nightIndex))
          : [defaultExtensionStay(position, {}, nightIndex)];
      });
      return { ...previous, extensionStays: nextExtensionStays };
    });
    // extensionStays is intentionally excluded: it's only read via the setForm updater above,
    // and including it here would rebuild-and-loop on every keystroke inside a stay's fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, form.tripExtensionRequired, form.extraPreNights, form.extraPostNights, defaultExtensionStay]);

  // The trip extension as the form currently has it, in the shape vehicleTripDates reads.
  const extensionDatesQuote = {
    tripExtensionRequired: Boolean(form.tripExtensionRequired),
    extraPreNights: form.extraPreNights,
    extraPostNights: form.extraPostNights,
    extensionScope: "BOTH",
    extensionStays: form.tripExtensionRequired ? form.extensionStays || [] : [],
  };
  const baseDurationNights = quoteOptions?.durationNights ?? form.priceList?.durationNights;
  // Adding, removing or re-scoping an extra night that includes the vehicle moves the vehicle's
  // first/last day. Move the pickup/drop dates with it, keeping the times already set - same as
  // the lead-team portal. Opening a booking changes this key too, but no edit has been made then
  // (dynamicTotalEditedRef is reset on open), so the saved dates are left alone.
  const vehicleExtensionKey = `${vehicleExtensionDays(extensionDatesQuote, "PRE")}:${vehicleExtensionDays(extensionDatesQuote, "POST")}`;
  const previousVehicleExtensionKey = useRef(vehicleExtensionKey);
  useEffect(() => {
    if (previousVehicleExtensionKey.current === vehicleExtensionKey) return;
    previousVehicleExtensionKey.current = vehicleExtensionKey;
    if (!show || !dynamicTotalEditedRef.current || !form.travelDate) return;
    const vehicle = vehicleTripDates(form.travelDate, extensionDatesQuote, baseDurationNights);
    setForm((previous) => ({
      ...previous,
      ...(vehicle.start ? { pickupDateTime: `${vehicle.start}T${timeOfDateTime(previous.pickupDateTime, "06:00")}` } : {}),
      ...(vehicle.end ? { dropDateTime: `${vehicle.end}T${timeOfDateTime(previous.dropDateTime, "22:00")}` } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleExtensionKey]);

  // Chikmagalur (Bababudangiri / Mullayanagiri) route restricts vehicles above
  // 13 seats, so the trip's "Chikmagalur/Chikmangalur Jeep" add-on must track
  // whichever vehicle the quote actually resolves to when staff pick/change it.
  useEffect(() => {
    if (!show || !form.dynamicPricing) return;
    const jeepAddon = (quoteOptions?.addons || []).find(isChikmagalurJeepAddon);
    if (!jeepAddon) return;
    if (manuallyToggledAddonNames[jeepAddon.name]) return;
    // A locked booking keeps its saved quote (no fresh draft), so read the vehicle from that.
    const vehicleQuote = draftQuote || (lockedQuoteInUse ? savedQuoteSnapshot : null);
    if (!vehicleQuote) return;
    // Any one vehicle over 13 seats needs the jeep, vehicles added on request included.
    const quoteFleet = vehicleQuote.vehicleCosts?.length
      ? vehicleQuote.vehicleCosts.map((item) => item.vehicle)
      : [vehicleQuote.selectedVehicle];
    const shouldBeSelected = quoteFleet.some((vehicle) => Number(vehicle?.capacityMax || 0) > 13);
    const isSelected = selectedAddonNames.includes(jeepAddon.name);
    if (shouldBeSelected === isSelected) return;
    toggleAddonSelection(jeepAddon.name, shouldBeSelected, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, form.dynamicPricing, quoteOptions, draftQuote?.selectedVehicle?.capacityMax, draftQuote?.vehicleCosts, lockedQuoteInUse, savedQuoteSnapshot?.selectedVehicle?.capacityMax, savedQuoteSnapshot?.vehicleCosts, selectedAddonNamesKey, manuallyToggledAddonNames]);

  // Compute price summary: unit price from priceList or custom price, subtotal by travellers,
  // minus coupon discount (flat or percentage) if applicable
  const priceSummary = React.useMemo(() => {
    const unit = form?.bookingType === 'CUSTOM'
      ? (Number(form?.pricePerPerson ?? 0) || 0)
      : (Number(form?.priceList?.price ?? 0) || 0);
    const travellers = (form?.travellers?.length ?? 0) || Number(form?.noOfTravellers ?? 0) || 0;
    const dynamicTotal = form?.dynamicPricing ? Number(form?.totalPrice ?? 0) || 0 : 0;
    const subtotal = dynamicTotal > 0 ? dynamicTotal : unit * travellers;
    let discount = 0;
    let note = "";
    const c = coupons.find((x) => x.id === (form?.couponId ?? null));
    if (c && subtotal > 0) {
      const minCart = Number(c.minimumCartValue ?? 0) || 0;
      if (subtotal >= minCart) {
        const flat = Number(c.discountAmount ?? 0) || 0;
        const pct = Number(c.discountPercentage ?? 0) || 0;
        if (flat > 0) discount = flat;
        else if (pct > 0) discount = Math.round((subtotal * pct) / 100);
      } else {
        note = `Min cart ₹${minCart} not met`;
      }
    }
    if (discount > subtotal) discount = subtotal;
    const total = subtotal - discount;
    const advance = Number(form?.advancePaid ?? 0) || 0;
    const balance = Math.max(0, total - advance);
    return { unit, travellers, subtotal, discount, total, advance, balance, note };
  }, [
    form?.bookingType,
    form?.pricePerPerson,
    form?.priceList?.price,
    form?.noOfTravellers,
    form?.travellers?.length,
    form?.dynamicPricing,
    form?.totalPrice,
    form?.couponId,
    form?.advancePaid,
    coupons,
  ]);

  const buildPayload = (changeEntry = null) => {
    const travellers = (form.travellers || []).map((t) => ({
      id: t.id || undefined,
      fullName: t.fullName || t.name || "",
      age: t.age != null && t.age !== "" ? Number(t.age) : null,
      phone: t.phone || "",
      idCardUrl: t.idCardUrl || "",
    }));
    const noOfTravellers = form.dynamicPricing
      ? Number(form.adults || 0) + Number(form.kids || 0)
      : travellers.length || Number(form.noOfTravellers || 0) || 0;
    const effectiveBookingType = form.dynamicPricing ? "CUSTOM" : (form.bookingType || "PACKAGE");
    const dynamicPricePerPerson =
      Number(form.totalPrice || 0) > 0 && noOfTravellers > 0
        ? Number(form.totalPrice || 0) / noOfTravellers
        : Number(form.pricePerPerson || 0);
    const payload = {
      id: form.id,
      userId: form.userId,
      tripId: form.tripId,
      tripTitle: form.tripTitle,
      travelDate: form.travelDate || null, // "YYYY-MM-DD"
      pickupDateTime: form.pickupDateTime || null,
      dropDateTime: form.dropDateTime || null,
      noOfTravellers,
      tripStatus: form.tripStatus,
      driverAssignedToTrip: Boolean(form.driverAssignedToTrip),
      hotelsBlocked: Boolean(form.hotelsBlocked),
      addonsArranged: Boolean(form.addonsArranged),
      breakfastArranged: Boolean(form.breakfastArranged),
      // Only the dates staff changed, and only for add-ons still selected. Null (left as saved) while
      // the trip's add-ons are not loaded, so a save then cannot wipe them.
      addonDates: Array.isArray(quoteOptions?.addons)
        ? Object.fromEntries(
            Object.entries(form.addonDates || {}).filter(([addonId, date]) => date && selectedAddonIds.map(String).includes(String(addonId))),
          )
        : null,
      couponId: form.couponId || null,
      bookingType: effectiveBookingType,
      description: form.description || '',
      bookingNotes: form.bookingNotes || "",
      travellingContactName: form.travellingContactName || "",
      travellingContactPhone: form.travellingContactPhone || "",
      advancePaid:
        form.advancePaid !== '' && form.advancePaid != null
          ? Number(form.advancePaid)
          : null,
      driverId: form.driverId || 0,
      assignedVehicleId: form.assignedVehicleId || 0,
      pricePerPerson:
        effectiveBookingType === 'CUSTOM'
          ? dynamicPricePerPerson
          : null,
      priceListId: effectiveBookingType === 'PACKAGE' ? (form.priceListId || null) : null,
      travellers,
      dynamicPricing: Boolean(form.dynamicPricing),
      pickupOptionId: form.pickupOptionId || null,
      pickupLabelSnapshot: form.pickupLabel || '',
      // With no vehicle picked in the dropdown the quote prices the suggested one, so fall back to
      // the vehicle the saved quote actually used rather than storing a blank name. With vehicles
      // added, or just removed, every vehicle is named again ("2 x Innova Crysta + Swift Dzire").
      vehicleNameSnapshot:
        (severalDynamicVehicles || (savedQuoteSnapshot?.quoteRequest?.additionalVehicles || []).length > 0
          ? vehicleNamesLabel([selectedDynamicVehicle || draftQuote?.selectedVehicle, ...additionalDynamicVehicles])
          : '')
        || form.vehicleNameSnapshot
        || draftQuote?.selectedVehicle?.vehicleName
        || parseQuoteSnapshot(form.quoteSnapshot)?.selectedVehicle?.vehicleName
        || '',
      acTypeSnapshot: form.acTypeSnapshot || '',
      hotelSummarySnapshot: form.hotelSummarySnapshot || '',
      addonSummarySnapshot: form.addonSummarySnapshot || '',
      dayWisePlanOverride: Boolean(form.useBookingDayWisePlan),
      dayWisePlan: form.useBookingDayWisePlan || form.pickupGoogleMapLink || form.refreshmentGoogleMapLink
        ? buildBookingDayWisePlan(
            form.useBookingDayWisePlan ? form.dayWisePlanDays || [] : [],
            form.pickupGoogleMapLink,
            form.refreshmentGoogleMapLink,
          )
        : null,
      quoteSnapshot: JSON.stringify({
        ...(parseQuoteSnapshot(form.quoteSnapshot) || {}),
        ...(draftQuote || {}),
        travelDate: form.travelDate || null,
        travellers: {
          adults: Number(form.adults || 0),
          kids: Number(form.kids || 0),
          kidAges: form.kidAges || [],
          kidVehicleSeatNeeded: form.kidVehicleSeatNeeded || [],
          total: Number(form.adults || 0) + Number(form.kids || 0),
        },
          selectedRoomRateIds,
          selectedRoomRateNightSelections,
          selectedRoomCityByNight: form.selectedRoomCityByNight || {},
          selectedRoomCategoryIds,
        selectedRoomUnits,
        selectedRoomCategorySelections,
        selectedHotelFacilities,
        customizationRequired: Boolean(form.customizationRequired),
        customizationDescription: form.customizationRequired ? (form.customizationDescription || "").trim() : "",
        customizationPrice: form.customizationRequired ? Number(form.customizationPrice || 0) : 0,
        customizationPriceAdjustment: form.customizationPriceAdjustment || "ADD",
        tripExtension: {
          required: Boolean(form.tripExtensionRequired),
          extraPreNights: Number(form.extraPreNights || 0),
          extraPostNights: Number(form.extraPostNights || 0),
          extraTransportDays: draftQuote?.extraTransportDays || 0,
          extensionStays: form.tripExtensionRequired ? (form.extensionStays || []) : [],
          extensionStayCosts: draftQuote?.extensionStayCosts || [],
          tripExtensionCost: draftQuote?.tripExtensionCost || 0,
          finalDurationNights: draftQuote?.finalDurationNights || null,
          finalDurationDays: draftQuote?.finalDurationDays || null,
          note: (form.tripExtensionNote || "").trim(),
        },
        // Same canonical shape LeadBookingConversionService.quoteSnapshot() writes on the backend, and
        // the shape normalize()/hotelFacilitySelectionsFromSnapshot()/extensionStaysFromSnapshot() read
        // back. tripExtension above is this save's own summary, but nothing previously refreshed
        // quoteRequest here - so a booking's extension state would keep reverting to whatever it was
        // at creation every time this form reopened, even after a deliberate edit changed it. Spreading
        // the old quoteRequest first preserves any other fields (adults, pickupId, etc.) it may hold.
        quoteRequest: {
          ...(parseQuoteSnapshot(form.quoteSnapshot)?.quoteRequest || {}),
          adults: Number(form.adults || 0),
          kids: Number(form.kids || 0),
          kidAges: form.kidAges || [],
          kidVehicleSeatNeeded: form.kidVehicleSeatNeeded || [],
          pickupId: selectedDynamicPickup?.id || null,
          vehicleId: selectedDynamicVehicle?.id || null,
          additionalVehicles: additionalVehiclesPayload,
          acType: form.acTypeSnapshot || "NON_AC",
          bookingId: form.id || null,
          tripExtensionRequired: Boolean(form.tripExtensionRequired),
          extraPreNights: Number(form.extraPreNights || 0),
          extraPostNights: Number(form.extraPostNights || 0),
          extensionStays: form.tripExtensionRequired
            ? extensionStaysRequestPayload(form.extensionStays)
            : [],
          extensionVehicleAmount: Number(form.extensionVehicleAmount || 0),
          tripExtensionNote: (form.tripExtensionNote || "").trim() || null,
          selectedHotelFacilities,
          selectedRoomRateNightSelections,
          selectedRoomCityByNight: form.selectedRoomCityByNight || {},
          noStayNights,
          // The room choices too: left as they were at creation, they still pointed at the old hotel
          // after a hotel change, and the server priced the hotel margin on rooms the booking did not
          // have (booking 313). The price lock reads rooms from the top level, so this cannot trip it.
          selectedRoomRateIds,
          selectedRoomCategoryIds,
          selectedRoomUnits,
          selectedRoomCategorySelections,
        },
        adminChangeHistory: [
          ...((parseQuoteSnapshot(form.quoteSnapshot)?.adminChangeHistory || [])),
          ...(changeEntry ? [changeEntry] : []),
        ],
      }),
      totalPrice:
        form.totalPrice !== '' && form.totalPrice != null
          ? Number(form.totalPrice)
          : null,
      cashbackAmountPerPerson:
        form.cashbackAmountPerPerson !== '' && form.cashbackAmountPerPerson != null
          ? Number(form.cashbackAmountPerPerson)
          : null,
      totalCashbackAmount:
        form.totalCashbackAmount !== '' && form.totalCashbackAmount != null
          ? Number(form.totalCashbackAmount)
          : null,
      cashbackReviewVideoReceived: Boolean(form.cashbackReviewVideoReceived),
      cashbackTravelPhotosReceived: Boolean(form.cashbackTravelPhotosReceived),
      cashbackGoogleReviewReceived: Boolean(form.cashbackGoogleReviewReceived),
      cashbackPaid: Boolean(form.cashbackPaid),
      cashbackPaidAmount:
        form.cashbackPaidAmount !== '' && form.cashbackPaidAmount != null
          ? Number(form.cashbackPaidAmount)
          : null,
      cashbackReviewRequestAllowed: Boolean(form.cashbackReviewRequestAllowed),
      cashbackReviewRequestNote: form.cashbackReviewRequestNote || "",
    };
    if ((form.conversionCredits || []).length) {
      payload.conversionCredits = normalizeConversionCreditPayload(form.conversionCredits);
    }
    if (form.id) {
      payload.changeReason = changeJustification.trim() || null;
    }
    // Allow assigning/changing operational vehicle and driver only when status is UPCOMING.
    if (form.tripStatus !== 'UPCOMING') {
      delete payload.driverId;
      delete payload.assignedVehicleId;
    }
    // Vehicles added on request; null leaves a driver or vehicle as saved, as above.
    payload.extraVehicles = extraVehiclePositions.map((position) => {
      const assignment = extraVehicleAssignment(position);
      const upcoming = form.tripStatus === 'UPCOMING';
      return {
        position,
        driverId: upcoming ? (assignment.driverId || 0) : null,
        assignedVehicleId: upcoming ? (assignment.assignedVehicleId || 0) : null,
        driverAssignedToTrip: Boolean(assignment.driverAssignedToTrip),
      };
    });
    return payload;
  };

  const save = async () => {
    try {
      if (form.customizationRequired && !(form.customizationDescription || "").trim()) {
        alert("Please enter customization details.");
        return;
      }
      if ((form.conversionCredits || []).length && conversionCreditTotal(form.conversionCredits) !== 100) {
        alert("Converted By contribution total must be exactly 100%.");
        return;
      }
      setSaving(true);
      const isNewBooking = !form.id;
      const changes = bookingChangeDetails(normalize(initial), form, draftQuote);
      const changeEntry = changes.length > 0
        ? {
            changedAt: new Date().toISOString(),
            justification: changeJustification.trim(),
            changes,
            previousTotalPrice: Number(normalize(initial).totalPrice || 0),
            updatedTotalPrice: Number(form.totalPrice || draftQuoteFinalTotal || draftQuote?.roundedTotalPrice || draftQuote?.finalTripPrice || 0),
          }
        : null;
      const payload = buildPayload(changeEntry);
      if (isNewBooking) {
        if (!payload.userId) {
          alert("Please select a customer.");
          setSaving(false);
          return;
        }
        if (!payload.tripId) {
          alert("Please select a trip.");
          setSaving(false);
          return;
        }
        if (!payload.travelDate) {
          alert("Please select a travel date.");
          setSaving(false);
          return;
        }
        if (form.dynamicPricing && (!draftQuote || !Number(payload.totalPrice || 0))) {
          alert("Please complete pickup, vehicle, hotel room category, and travellers to calculate the dynamic booking price.");
          setSaving(false);
          return;
        }
        if (payload.bookingType === "PACKAGE" && !payload.priceListId) {
          alert("Please select a price option.");
          setSaving(false);
          return;
        }
        if (payload.bookingType === "CUSTOM" && !payload.pricePerPerson) {
          alert("Please enter a custom price.");
          setSaving(false);
          return;
        }
        const { data } = await api.post("/api/admin/bookings", payload);
        onPatched?.(data || payload);
        onHide?.();
        return;
      }
      if (priceLocked && Number(payload.totalPrice || 0) !== agreedTotalPrice) {
        const newTotal = Number(payload.totalPrice || 0);
        const confirmed = window.confirm(
          "The payment receipt for this booking is already generated.\n\n" +
          `Agreed ₹${agreedTotalPrice.toLocaleString("en-IN")} → New ₹${newTotal.toLocaleString("en-IN")}\n` +
          `Reason: ${priceChangeReasons.join(", ") || "Booking price details changed"}\n\n` +
          "The updated receipt must be shared with the customer. Save with the new price?"
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
        payload.priceChangeConfirmed = true;
      }
      // Enforce advance payment before moving to UPCOMING
      const targetStatus = form.tripStatus;
      const adv = Number(form.advancePaid ?? 0) || 0;
      if (targetStatus === 'UPCOMING' && adv <= 0) {
        alert('Cannot set to UPCOMING: please enter an Advance Paid amount first.');
        setSaving(false);
        return;
      }
      if ((targetStatus === 'ONGOING' || targetStatus === 'COMPLETED')
          && !bookingPreparationReady(form)) {
        alert('Confirm all required Trip Preparation items before changing this status.');
        setSaving(false);
        return;
      }
      // Warn before moving to COMPLETED with money still due (price summary total accounts for coupons).
      // A warning, not a block: some customers never pay in full over a dispute, and the trip still has
      // to be closed. Only on the move itself, so an already-completed booking saves without asking.
      if (targetStatus === 'COMPLETED' && initial?.tripStatus !== 'COMPLETED') {
        const netTotal = Number((priceSummary?.total ?? 0));
        // small epsilon to avoid rounding issues
        const EPS = 0.005;
        if (netTotal > 0 && adv + EPS < netTotal) {
          const due = netTotal - adv;
          const proceed = window.confirm(
            "Full payment has not been received from the customer.\n\n" +
            `Total ₹${netTotal.toLocaleString("en-IN")} · Received ₹${adv.toLocaleString("en-IN")} · ` +
            `Balance ₹${due.toLocaleString("en-IN")}\n\n` +
            "Mark this booking as COMPLETED anyway?"
          );
          if (!proceed) {
            setSaving(false);
            return;
          }
        }
      }
      // Try PATCH first (partial)
      try {
        const { data } = await api.patch(
          `/api/admin/bookings/${form.id}`,
          payload
        );
        onPatched?.(data || { id: form.id, ...payload });
      } catch (patchError) {
        if (patchError?.response) {
          // The server received and processed the PATCH (validation error, conflict, etc.) —
          // surface that real failure instead of blindly retrying with PUT, which can double-submit
          // the same save (e.g. duplicate booking revision numbers) if the PATCH actually went through.
          throw patchError;
        }
        // No response reached us at all (network failure) — safe to retry with a full PUT.
        const { data } = await api.put(
          `/api/admin/bookings/${form.id}`,
          payload
        );
        onPatched?.(data || { id: form.id, ...payload });
      }
      onHide?.();
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to save booking. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Marking a document shared saves straight away and loads the saved booking back into the form,
  // so unsaved edits would be lost and the receipt sent would no longer match the saved booking.
  // Rooms and extra-night stays are re-shaped when a booking opens, so those are compared against
  // the saved quote's pricing inputs rather than field by field.
  const unsavedBookingChanges = () => {
    if (!form.id) return [];
    const reshapedOnOpen = ["Hotel / Rooms", "Room units", "Room categories", "Room category units", "Trip extension stays"];
    const saved = normalize(initial);
    const changes = [
      ...bookingChangeDetails(saved, form, draftQuote).filter((label) => !reshapedOnOpen.includes(label)),
      ...(savedQuoteSelectionDifferences || []),
    ];
    if ((saved.travellingContactName || "") !== (form.travellingContactName || "")) changes.push("Travelling contact name");
    if ((saved.travellingContactPhone || "") !== (form.travellingContactPhone || "")) changes.push("Travelling contact phone");
    return Array.from(new Set(changes));
  };

  const markDocumentShared = async (documentType) => {
    if (!form.id) return;
    setDocumentSharing(documentType);
    try {
      const endpoint = documentType === "receipt" ? "payment-receipt" : "travel-policy";
      const body = documentType === "receipt" ? { receiptNumber: form.latestReceiptNumber || "" } : undefined;
      const { data } = await api.post(
        `/api/admin/bookings/${form.id}/documents/${endpoint}/shared`,
        body
      );
      const normalizedBooking = normalize(data || { id: form.id });
      setForm((previous) => ({ ...previous, ...normalizedBooking }));
      onPatched?.(data || { id: form.id, ...normalizedBooking });
    } catch (error) {
      alert(error?.response?.data?.message || "Could not update document sharing status.");
    } finally {
      setDocumentSharing("");
    }
  };

  // 25% after boarding / rest on Day 1, from the live total. A booking being created now counts
  // what is typed as its advance; a saved one uses the advance recorded at creation, and has no
  // schedule if it was created before the schedule existed.
  const bookingFormTotal = Number(form.totalPrice || draftQuoteFinalTotal || priceSummary.total || 0);
  const bookingFormSchedule = form.id
    ? bookingPaymentSchedule(form, bookingFormTotal)
    : paymentSchedule(bookingFormTotal, form.advancePaid, form.advancePaid);

  const bookingDocumentReceiptNumber = () => {
    if (form.latestReceiptNumber) return form.latestReceiptNumber;
    const year = new Date().getFullYear();
    const revision = Number(form.currentRevisionNumber || 0);
    return `IO-${year}-${form.id}${revision > 0 ? `-R${revision}` : ""}`;
  };

  const buildBookingDocumentData = () => {
    if (!form.pickupDateTime || !form.dropDateTime) {
      throw new Error("Please enter Vehicle Pickup Date & Time and Vehicle Drop Date & Time in Booking Details before downloading documents.");
    }
    if (new Date(form.dropDateTime) <= new Date(form.pickupDateTime)) {
      throw new Error("Vehicle Drop Date & Time must be after Vehicle Pickup Date & Time.");
    }
    const snapshot = parseQuoteSnapshot(form.quoteSnapshot) || {};
    const receiptNumber = bookingDocumentReceiptNumber();
    const adults = Math.max(0, Number(form.adults || (form.dynamicPricing ? 0 : form.noOfTravellers) || 0));
    const kids = Math.max(0, Number(form.kids || 0));
    const kidsAge = (form.kidAges || []).map((age) => String(age ?? "").trim()).filter(Boolean).join(", ");
    // An unsaved re-quote is what the documents describe, so it wins over the saved snapshot.
    const inclusionQuote = draftQuote || snapshot;
    const liveRooms = currentBookingDocumentRoomLines();
    const extensionRoomLines = form.tripExtensionRequired
      ? (inclusionQuote.extensionStayCosts || []).map((stay) => stayCostDocumentLine(stay, inclusionQuote))
      : [];
    const rooms = bookingDocumentDayLines(
      liveRooms.length ? [...liveRooms, ...extensionRoomLines] : bookingDocumentRoomLabels(snapshot, form.hotelSummarySnapshot),
      {
        travelDate: form.travelDate,
        preNights: form.tripExtensionRequired ? form.extraPreNights : 0,
        postNights: form.tripExtensionRequired ? form.extraPostNights : 0,
        baseNights: baseDurationNights,
      }
    );
    const addons = selectedAddonNames.length ? selectedAddonNames : splitSnapshotList(form.addonSummarySnapshot);
    // finalDuration* covers trip extensions; the base/config figures are the pre-extension length.
    const durationNights = Number(snapshot.finalDurationNights ?? snapshot.durationNights ?? snapshot.baseDurationNights ?? quoteOptions?.durationNights ?? form.priceList?.durationNights ?? 0);
    const durationDays = Number(snapshot.finalDurationDays ?? snapshot.durationDays ?? snapshot.baseDurationDays ?? quoteOptions?.durationDays ?? form.priceList?.durationDays ?? 0);
    const customization = form.customizationRequired
      ? ((form.customizationDescription || "").trim() || "Yes")
      : "No";
    const total = Number(form.totalPrice || draftQuoteFinalTotal || priceSummary.total || 0);
    const advance = Number(form.advancePaid || 0);
    const tripDates = tripStayDates(form.travelDate, extensionDatesQuote, baseDurationNights);
    // Each night's meals and inclusions, as the customer's Trip Details page lists them.
    const stayInclusions = stayInclusionLines(
      [...(inclusionQuote.roomStayCosts || []), ...(inclusionQuote.extensionStayCosts || [])],
      (hotelId) => (quoteOptions?.availableHotels || []).find((hotel) => String(hotel.id) === String(hotelId)),
      form.travelDate,
      tripDates.start
    );
    return {
      receiptNumber,
      policyNumber: receiptNumber,
      customerName: form.userName,
      customerNumber: form.userPhone,
      pickup: form.pickupLabel,
      trip: form.tripTitle,
      travelDate: form.travelDate,
      pickupDateTime: form.pickupDateTime,
      dropDateTime: form.dropDateTime,
      tripStartDate: tripDates.start,
      tripEndDate: tripDates.end,
      stayInclusions,
      adults,
      kids,
      kidsAge,
      duration: durationNights || durationDays ? `${durationNights} Nights ${durationDays} Days` : "-",
      vehicle: form.vehicleNameSnapshot || form.assignedVehicleName || "-",
      rooms,
      addons,
      packageTotal: total,
      advancePaid: advance,
      balance: Math.max(0, total - advance),
      paymentSchedule: bookingFormSchedule,
      customization,
      bookingNotes: form.bookingNotes || "",
      cashbackAmountPerPerson: Number(form.cashbackAmountPerPerson || 0),
      totalCashbackAmount: Number(form.totalCashbackAmount || 0),
    };
  };

  const downloadBookingReceipt = async () => {
    setDocumentDownloading("receipt");
    try {
      const data = buildBookingDocumentData();
      if (!form.latestReceiptNumber) {
        update("latestReceiptNumber", data.receiptNumber);
      }
      await generatePaymentReceipt(data);
    } catch (error) {
      alert(error?.message || "Could not download payment receipt.");
    } finally {
      setDocumentDownloading("");
    }
  };

  const downloadBookingPolicy = () => {
    setDocumentDownloading("policy");
    try {
      const data = buildBookingDocumentData();
      if (!form.latestReceiptNumber) {
        update("latestReceiptNumber", data.receiptNumber);
      }
      generateTravelPolicy(data);
    } catch (error) {
      alert(error?.message || "Could not download policy.");
    } finally {
      setDocumentDownloading("");
    }
  };

  const removeCoupon = () => {
    update("couponId", null);
    update("couponCode", "");
  };

  const hasDynamicDetails =
    form.dynamicPricing ||
    form.pickupLabel ||
    form.vehicleNameSnapshot ||
    form.hotelSummarySnapshot ||
    form.addonSummarySnapshot ||
    form.totalPrice;
  const tripCategoryOptions = React.useMemo(() => {
    const byKey = new Map();
    (trips || []).forEach((trip) => {
      const key = trip.categoryId ?? trip.category_id ?? trip.categoryName ?? trip.category_name;
      if (key == null || key === "") return;
      byKey.set(String(key), {
        key: String(key),
        label: trip.categoryName || trip.category_name || `Category ${key}`,
      });
    });
    return Array.from(byKey.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [trips]);
  const filteredTrips = React.useMemo(() => {
    const category = String(form.tripCategoryFilter || "");
    if (!category) return trips || [];
    return (trips || []).filter((trip) => {
      const key = trip.categoryId ?? trip.category_id ?? trip.categoryName ?? trip.category_name;
      return String(key || "") === category;
    });
  }, [trips, form.tripCategoryFilter]);
  const pendingChanges = bookingChangeDetails(normalize(initial), form, draftQuote);
  const savedChangeHistory = parseQuoteSnapshot(form.quoteSnapshot)?.adminChangeHistory || [];
  const renderedChangeHistory = revisionHistory.length
    ? revisionHistory
    : [...savedChangeHistory].reverse().map((entry, index) => ({
        ...entry,
        id: `legacy-${index}`,
        legacy: true,
      }));
  const isNewBooking = !form.id;
  const modalLoading = bookingOptionsLoading || quoteOptionsLoading || priceLoading || driverLoading || vehicleLoading || draftQuoteLoading;

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} onEntered={onEntered} size="xl" centered scrollable className="booking-edit-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span>{isNewBooking ? "New Booking" : `Booking #${form.id}`}</span>
            <Badge bg={form.bookingType === "CUSTOM" ? "warning" : "primary"}>
              {form.bookingType || "PACKAGE"}
            </Badge>
            <Badge bg={statusBadgeVariant(form.tripStatus)}>
              {form.tripStatus || "PENDING"}
            </Badge>
            {modalLoading && <Spinner size="sm" />}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="booking-edit-body">
        <div className="booking-edit-summary-grid">
          <div className="booking-edit-summary-item">
            <span>Customer</span>
            <strong>{form.userName || "-"}</strong>
            <small>{form.userPhone || form.userEmail || "-"}</small>
          </div>
          <div className="booking-edit-summary-item">
            <span>Trip</span>
            <strong>{form.tripTitle || "-"}</strong>
            <small>Travel: {formatDate(form.travelDate)}</small>
          </div>
          <div className="booking-edit-summary-item">
            <span>Travellers</span>
            <strong>{form.noOfTravellers || 0}</strong>
            <small>{Number(form.adults || 0)} adults, {Number(form.kids || 0)} kids{kidsAgeSuffix(form.kidAges)}</small>
          </div>
          <div className="booking-edit-summary-item">
            <span>Balance</span>
            <strong>₹{Number(priceSummary.balance || 0).toLocaleString()}</strong>
            <small>Paid ₹{Number(priceSummary.advance || 0).toLocaleString()}</small>
          </div>
        </div>

        <Tabs defaultActiveKey="overview" className="booking-edit-tabs mb-3" fill>
          <Tab eventKey="overview" title="Overview">
            <div className="booking-edit-tab-pane">
        <div className="border rounded p-3 mb-3">
          <div className="fw-semibold mb-2">Trip Preparation</div>
          <Form.Check
            type="checkbox"
            id="booking-driver-assigned-to-trip"
            label={extraVehiclePositions.length ? "Vehicle 1 driver assigned to Trip" : "Driver assigned to Trip"}
            checked={Boolean(form.driverAssignedToTrip)}
            onChange={(e) => update("driverAssignedToTrip", e.target.checked)}
            disabled={saving}
          />
          {extraVehiclePositions.map((position) => (
            <Form.Check
              key={position}
              type="checkbox"
              id={`booking-driver-assigned-to-trip-${position}`}
              label={`Vehicle ${position} driver assigned to Trip`}
              checked={Boolean(extraVehicleAssignment(position).driverAssignedToTrip)}
              onChange={(e) => updateExtraVehicleAssignment(position, { driverAssignedToTrip: e.target.checked })}
              disabled={saving}
            />
          ))}
          <Form.Check
            type="checkbox"
            id="booking-hotels-blocked"
            label="Blocked the Hotels"
            checked={Boolean(form.hotelsBlocked)}
            onChange={(e) => update("hotelsBlocked", e.target.checked)}
            disabled={saving}
          />
          {hasBookingAddons(form) && (
            <Form.Check
              type="checkbox"
              id="booking-addons-arranged"
              label={
                <span>
                  Add-ons arranged
                  {selectedAddonNames.length > 0 && (
                    <span className="text-muted ms-1">
                      ({selectedAddonNames.join(", ")})
                    </span>
                  )}
                </span>
              }
              checked={Boolean(form.addonsArranged)}
              onChange={(e) => update("addonsArranged", e.target.checked)}
              disabled={saving}
            />
          )}
          {!bookingPreparationReady(form) && (
            <div className="small text-warning mt-2">
              Assign the driver and block the hotels before changing the trip to Ongoing or Completed.
            </div>
          )}
        </div>
        {/* User & Trip quick read */}
        <Row>
          <Col md={6} className="mb-2">
            <Form.Label>{isNewBooking ? "User" : "Planned by"}</Form.Label>
            {isNewBooking && (
              <>
                <Form.Select
                  value={form.userId || ""}
                  onChange={(event) => selectCustomer(event.target.value)}
                  disabled={saving || bookingOptionsLoading}
                >
                  <option value="">Select customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name || "-"} - {customer.phone || customer.email || "-"}
                    </option>
                  ))}
                </Form.Select>
                {bookingOptionsLoading && <div className="small text-muted mt-1">Loading customers...</div>}
                <div className="border rounded p-2 mt-2">
                  <div className="small fw-semibold mb-2">Add New Customer</div>
                  <Row className="g-2">
                    <Col md={5}>
                      <Form.Control
                        value={form.newCustomerName || ""}
                        onChange={(event) => update("newCustomerName", event.target.value)}
                        placeholder="Name"
                        disabled={saving || bookingOptionsLoading}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        value={form.newCustomerPhone || ""}
                        onChange={(event) => update("newCustomerPhone", event.target.value)}
                        placeholder="Phone number"
                        disabled={saving || bookingOptionsLoading}
                      />
                    </Col>
                    <Col md={3}>
                      <Button
                        variant="outline-primary"
                        className="w-100"
                        onClick={createAndSelectCustomer}
                        disabled={saving || bookingOptionsLoading || !form.newCustomerName?.trim() || !form.newCustomerPhone?.trim()}
                      >
                        Add
                      </Button>
                    </Col>
                  </Row>
                </div>
              </>
            )}
            <div className={`${isNewBooking ? "d-none" : ""} border rounded p-2`}>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  size="sm"
                  value={form.userName || ""}
                  onChange={(event) => update("userName", event.target.value)}
                  disabled={savingCustomerName || saving}
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={saveCustomerName}
                  disabled={savingCustomerName || saving || !form.userName?.trim() || form.userName === initial?.userName}
                >
                  {savingCustomerName ? <Spinner size="sm" /> : "Save Name"}
                </Button>
              </div>
              <div className="text-muted small">{form.userEmail || "-"}</div>
              <div className="text-muted small">{form.userPhone || "-"}</div>
            </div>
            {/* The planner above is never overwritten; this is who actually travels, when different.
                Driver, vehicle owner and tracking use it, and every change is kept in History. */}
            <div className="border rounded p-2 mt-2">
              <div className="small fw-semibold">Travelling contact (optional)</div>
              <div className="text-muted small mb-1">
                Fill only if someone other than the person above is travelling. Saved with the booking.
              </div>
              <Row className="g-2">
                <Col sm={6}>
                  <Form.Control
                    size="sm"
                    placeholder="Name"
                    value={form.travellingContactName || ""}
                    onChange={(event) => update("travellingContactName", event.target.value)}
                    disabled={saving}
                  />
                </Col>
                <Col sm={6}>
                  <Form.Control
                    size="sm"
                    placeholder="Phone number"
                    inputMode="tel"
                    value={form.travellingContactPhone || ""}
                    onChange={(event) => update("travellingContactPhone", event.target.value)}
                    isInvalid={Boolean(form.travellingContactName?.trim()) && !form.travellingContactPhone?.trim()}
                    disabled={saving}
                  />
                  <Form.Control.Feedback type="invalid">Phone number is needed.</Form.Control.Feedback>
                </Col>
              </Row>
            </div>
          </Col>
          <Col md={6} className="mb-2">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <Form.Label className="mb-0">Trip</Form.Label>
              {!isNewBooking && (
                <div className="d-flex gap-2">
                  {changeTripMode && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setForm(normalize(initial));
                        setChangeTripMode(false);
                        setPendingChangedTripId(null);
                        setDraftQuote(null);
                        setDraftQuoteError("");
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={changeTripMode ? "secondary" : "outline-primary"}
                    onClick={() => setChangeTripMode((value) => !value)}
                    disabled={saving || bookingOptionsLoading}
                  >
                    {changeTripMode ? "Selecting" : "Change Trip"}
                  </Button>
                </div>
              )}
            </div>
            {(isNewBooking || changeTripMode) && (
              <>
                <InputGroup className="mb-2">
                  <InputGroup.Text>Category</InputGroup.Text>
                  <Form.Select
                    value={form.tripCategoryFilter || ""}
                    onChange={(event) => {
                      const category = event.target.value;
                      const selectedTrip = trips.find((trip) => Number(trip.id) === Number(form.tripId));
                      const selectedTripCategory = selectedTrip
                        ? String(selectedTrip.categoryId ?? selectedTrip.category_id ?? selectedTrip.categoryName ?? selectedTrip.category_name ?? "")
                        : "";
                      setForm((previous) => ({
                        ...previous,
                        tripCategoryFilter: category,
                        ...(category && selectedTripCategory !== category
                          ? {
                              tripId: null,
                              tripTitle: "",
                              priceListId: null,
                              priceList: null,
                              pickupOptionId: "",
                              pickupLabel: "",
                              pickupGoogleMapLink: "",
                              refreshmentGoogleMapLink: "",
                              dynamicPricing: false,
                              dynamicVehicleId: "",
                              additionalVehicles: [],
                              vehicleNameSnapshot: "",
                              acTypeSnapshot: "",
                              hotelSummarySnapshot: "",
                              addonSummarySnapshot: "",
                              quoteSnapshot: "",
                              totalPrice: "",
                              selectedHotelFacilityKeys: {},
                              driverId: null,
                              assignedVehicleId: null,
                              driverAssignedToTrip: false,
                              extraVehicleAssignments: {},
                              hotelsBlocked: false,
                              addonsArranged: false, addonDates: {},
                              breakfastArranged: false,
                              useBookingDayWisePlan: false,
                              bookingDayWisePlanDays: [],
                              bookingPickupGoogleMapLink: "",
                              bookingRefreshmentGoogleMapLink: "",
                              tripDayWisePlanDays: [],
                              tripPickupGoogleMapLink: "",
                              tripRefreshmentGoogleMapLink: "",
                              dayWisePlanDays: [],
                            }
                          : {}),
                      }));
                    }}
                    disabled={saving || bookingOptionsLoading}
                  >
                    <option value="">All Categories</option>
                    {tripCategoryOptions.map((category) => (
                      <option key={category.key} value={category.key}>
                        {category.label}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
                <Form.Select
                  value={form.tripId || ""}
                  onChange={(event) => selectTrip(event.target.value)}
                  disabled={saving || bookingOptionsLoading}
                >
                  <option value="">Select trip</option>
                  {filteredTrips.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title || `Trip #${trip.id}`}
                    </option>
                  ))}
                </Form.Select>
                {bookingOptionsLoading && <div className="small text-muted mt-1">Loading trips...</div>}
              </>
            )}
            <div className={`${isNewBooking || changeTripMode ? "d-none" : ""} border rounded p-2 mt-2`}>
              <div className="fw-semibold">{form.tripTitle || "-"}</div>
              <div className="text-muted small">ID: {form.tripId ?? "—"}</div>
            </div>
            {!isNewBooking && changeTripMode && (
              <div className="small text-muted mt-1">
                Changing trip resets pickup, vehicle, hotel selections, quote, total price, booking plan, driver assignment, and preparation flags.
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-2">
            <Form.Label>Pickup Location</Form.Label>
            <Form.Select
              value={form.pickupOptionId || ""}
              onChange={(event) => updatePickupLocationFromDropdown(event.target.value)}
              disabled={saving || !form.tripId || !pickupLocationOptions.length}
            >
              <option value="">Use trip default</option>
              {pickupLocationOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.pickupLabel || option.pickupValue || `Pickup ${option.id}`}
                </option>
              ))}
            </Form.Select>
            <div className="small text-muted mt-1">
              Day-Wise Plan follows this pickup location.
            </div>
          </Col>
        </Row>

        {/* Operational assignment (allowed only when status = UPCOMING) */}
        <Row>
          <Col md={6} className="mb-2">
            <Form.Label>Assign Vehicle</Form.Label>
            <InputGroup className="mb-2">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={vehicleQuery}
                onChange={(e) => setVehicleQuery(e.target.value)}
                placeholder="Search vehicle or registration"
                disabled={vehicleLoading || form.tripStatus !== 'UPCOMING'}
              />
              <Form.Select
                value={form.assignedVehicleId || ""}
                onChange={(e) => update('assignedVehicleId', e.target.value ? Number(e.target.value) : null)}
                disabled={vehicleLoading || form.tripStatus !== 'UPCOMING'}
              >
                <option value="">Unassigned</option>
                {Object.entries(vehicles
                  .filter((vehicle) => {
                    const q = vehicleQuery.trim().toLowerCase();
                    if (!q) return true;
                    return [
                      vehicle.name,
                      vehicle.registrationNumber,
                      vehicle.category,
                      vehicle.ownerName,
                      String(vehicle.id),
                    ].filter(Boolean).join(" ").toLowerCase().includes(q);
                  })
                  .reduce((groups, vehicle) => {
                    const owner = vehicle.ownerName || "Owner not assigned";
                    if (!groups[owner]) groups[owner] = [];
                    groups[owner].push(vehicle);
                    return groups;
                  }, {}))
                  .sort(([ownerA], [ownerB]) => ownerA.localeCompare(ownerB))
                  .map(([owner, ownerVehicles]) => (
                    <optgroup key={owner} label={`${owner} (${ownerVehicles.length})`}>
                      {ownerVehicles
                        .sort((a, b) => String(a.name || a.registrationNumber || "").localeCompare(String(b.name || b.registrationNumber || "")))
                        .map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name || "-"} - {vehicle.registrationNumber || "-"} ({vehicle.seater || "-"} seater, {vehicle.ac ? "AC" : "Non AC"})
                          </option>
                        ))}
                    </optgroup>
                  ))}
              </Form.Select>
            </InputGroup>
            {vehicleLoading && <div className="small text-muted">Loading vehicles...</div>}
          </Col>
          <Col md={6} className="mb-2">
            <Form.Label>Assign Driver</Form.Label>
            <InputGroup className="mb-2">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={driverQuery}
                onChange={(e) => setDriverQuery(e.target.value)}
                placeholder="Search driver by name or phone"
                disabled={driverLoading || form.tripStatus !== 'UPCOMING'}
              />
              <InputGroup.Text>Select</InputGroup.Text>
              <Form.Select
                value={form.driverId || ""}
                onChange={(e) => update('driverId', e.target.value ? Number(e.target.value) : null)}
                disabled={driverLoading || form.tripStatus !== 'UPCOMING'}
              >
                <option value="">Unassigned</option>
                {Object.entries(drivers
                  .filter((d) => {
                    const q = driverQuery.trim().toLowerCase();
                    if (!q) return true;
                    return [d.name, d.phone, d.ownerName, String(d.id)]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase()
                      .includes(q);
                  })
                  .reduce((groups, driver) => {
                    const owner = driver.ownerName || "Individual / Freelance";
                    if (!groups[owner]) groups[owner] = [];
                    groups[owner].push(driver);
                    return groups;
                  }, {}))
                  .sort(([ownerA], [ownerB]) => ownerA.localeCompare(ownerB))
                  .map(([owner, ownerDrivers]) => (
                    <optgroup key={owner} label={`${owner} (${ownerDrivers.length})`}>
                      {ownerDrivers
                        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
                        .map((d) => (
                          <option key={d.id} value={d.id}>
                            #{d.id} - {d.name || '-'} - {d.phone || '-'}
                          </option>
                        ))}
                    </optgroup>
                  ))}
              </Form.Select>
            </InputGroup>
            {form.tripStatus !== 'UPCOMING' && (
              <div className="small text-muted">Vehicle and driver assignment is available only when the booking status is UPCOMING.</div>
            )}
            {driverLoading && <div className="small text-muted">Loading drivers...</div>}
          </Col>
        </Row>
        {extraVehiclePositions.map((position) => {
          const assignment = extraVehicleAssignment(position);
          const quotedName = additionalDynamicVehicles[position - 2]?.vehicleName;
          return (
            <Row key={position} className="border-top pt-2">
              <Col md={6} className="mb-2">
                <Form.Label>Assign Vehicle {position}{quotedName ? ` (${quotedName})` : ""}</Form.Label>
                <Form.Select
                  value={assignment.assignedVehicleId || ""}
                  onChange={(e) => updateExtraVehicleAssignment(position, { assignedVehicleId: e.target.value ? Number(e.target.value) : null })}
                  disabled={vehicleLoading || form.tripStatus !== 'UPCOMING'}
                >
                  <option value="">Unassigned</option>
                  {groupedByOwner(vehicles, (vehicle) => vehicle.ownerName || "Owner not assigned").map(([owner, ownerVehicles]) => (
                    <optgroup key={owner} label={`${owner} (${ownerVehicles.length})`}>
                      {ownerVehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name || "-"} - {vehicle.registrationNumber || "-"} ({vehicle.seater || "-"} seater, {vehicle.ac ? "AC" : "Non AC"})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Label>Assign Driver {position}</Form.Label>
                <Form.Select
                  value={assignment.driverId || ""}
                  onChange={(e) => updateExtraVehicleAssignment(position, { driverId: e.target.value ? Number(e.target.value) : null })}
                  disabled={driverLoading || form.tripStatus !== 'UPCOMING'}
                >
                  <option value="">Unassigned</option>
                  {groupedByOwner(drivers, (driver) => driver.ownerName || "Individual / Freelance").map(([owner, ownerDrivers]) => (
                    <optgroup key={owner} label={`${owner} (${ownerDrivers.length})`}>
                      {ownerDrivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          #{d.id} - {d.name || '-'} - {d.phone || '-'}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          );
        })}
        {extraVehiclePositions.length > 0 && (
          <div className="small text-muted mb-2">
            A vehicle added in the quote above can be assigned here before saving; save to update the drivers' messages.
          </div>
        )}
        {transportRatingAllowed && (
          <Row className="mt-2">
            <Col xs={12}>
              <div className="border rounded p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div>
                    <div className="fw-semibold">Rate this trip</div>
                    <div className="small text-muted">
                      Recorded for the driver's and vehicle's running average. Visible to the transport owner.
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    onClick={submitTransportRating}
                    disabled={transportRatingSaving}
                  >
                    {transportRatingSaving ? <Spinner animation="border" size="sm" /> : "Save Rating"}
                  </Button>
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">
                      Driver{form.driverName ? ` - ${form.driverName}` : ""}
                    </Form.Label>
                    <div className="mb-2">
                      <StarRating
                        value={transportRating?.driverRating}
                        onChange={(value) => patchTransportRating({ driverRating: value })}
                        disabled={transportRatingSaving}
                        size="1.35rem"
                        label="Driver rating"
                      />
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      placeholder="What went well or badly? (optional)"
                      value={transportRating?.driverNote || ""}
                      disabled={transportRatingSaving}
                      onChange={(e) => patchTransportRating({ driverNote: e.target.value })}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">
                      Vehicle{form.vehicleNumber ? ` - ${form.vehicleNumber}` : ""}
                    </Form.Label>
                    <div className="mb-2">
                      <StarRating
                        value={transportRating?.vehicleRating}
                        onChange={(value) => patchTransportRating({ vehicleRating: value })}
                        disabled={transportRatingSaving}
                        size="1.35rem"
                        label="Vehicle rating"
                      />
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      placeholder="Cleanliness, condition, AC... (optional)"
                      value={transportRating?.vehicleNote || ""}
                      disabled={transportRatingSaving}
                      onChange={(e) => patchTransportRating({ vehicleNote: e.target.value })}
                    />
                  </Col>
                </Row>
                {transportRating?.ratedAt && (
                  <div className="small text-muted mt-2">
                    Last saved by {transportRating.ratedBy || "someone"} on {formatDateTime(transportRating.ratedAt)}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}
            </div>
          </Tab>

          <Tab eventKey="payments" title="Payments">
            <div className="booking-edit-tab-pane">

        {form.id && (
          <Card className="mb-3">
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span className="fw-semibold">Updated Documents</span>
              <div className="d-flex flex-wrap gap-2">
                <Badge bg={form.receiptReshareRequired ? "danger" : "success"}>
                  Receipt {form.receiptReshareRequired ? "Pending" : "Shared"}
                </Badge>
                <Badge bg={form.policyReshareRequired ? "danger" : "success"}>
                  Policy {form.policyReshareRequired ? "Pending" : "Shared"}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label>Current Revision</Form.Label>
                  <div className="fw-semibold">
                    Rev {Number(form.currentRevisionNumber || 0)}
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Label>Latest Receipt No</Form.Label>
                  <Form.Control
                    value={form.latestReceiptNumber || ""}
                    placeholder="Receipt number"
                    readOnly
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Customer Sent Status</Form.Label>
                  <div className="d-flex flex-column gap-2">
                    <Form.Check
                      type="checkbox"
                      id={`booking-${form.id}-receipt-sent`}
                      label="Updated receipt sent to customer"
                      checked={!form.receiptReshareRequired}
                      disabled={saving || documentSharing === "receipt" || !form.receiptReshareRequired}
                      onChange={(event) => {
                        if (!event.target.checked) return;
                        const unsavedChanges = unsavedBookingChanges();
                        if (unsavedChanges.length) {
                          alert(`Save your changes first, then download and send the updated receipt.\n\nUnsaved: ${unsavedChanges.join(", ")}`);
                          return;
                        }
                        markDocumentShared("receipt");
                      }}
                    />
                    <div className="small text-muted ms-4">
                      {form.latestReceiptSharedAt ? `Sent on ${formatDateTime(form.latestReceiptSharedAt)}` : "Not sent after latest change"}
                    </div>
                    <Form.Check
                      type="checkbox"
                      id={`booking-${form.id}-policy-sent`}
                      label="Updated policy sent to customer"
                      checked={!form.policyReshareRequired}
                      disabled={saving || documentSharing === "policy" || !form.policyReshareRequired}
                      onChange={(event) => {
                        if (event.target.checked) markDocumentShared("policy");
                      }}
                    />
                    <div className="small text-muted ms-4">
                      {form.latestPolicySharedAt ? `Sent on ${formatDateTime(form.latestPolicySharedAt)}` : "Not sent after latest change"}
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={downloadBookingReceipt}
                  disabled={saving || documentDownloading === "receipt"}
                >
                  {documentDownloading === "receipt" ? "Downloading..." : "Download Updated Payment Receipt"}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={downloadBookingPolicy}
                  disabled={saving || documentDownloading === "policy"}
                >
                  {documentDownloading === "policy" ? "Downloading..." : "Download Updated Policy"}
                </Button>
              </div>
              {(form.receiptReshareRequired || form.policyReshareRequired) && (
                <div className="small text-danger fw-semibold mt-2">
                  Booking details changed after conversion. Resend the updated receipt and policy, then mark them shared here.
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <CustomerPayments
          bookingId={initial?.id}
          currentTotalPrice={Number(form.totalPrice || draftQuoteFinalTotal || 0)}
          onTotalPaidChange={(totalPaid) => update("advancePaid", String(totalPaid))}
        />

        <Accordion>
          <Accordion.Item eventKey="transport-vendor-payments">
            <Accordion.Header>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <span className="fw-semibold">
                  Transport Vendor Payments{(initial?.extraVehicles || []).length > 0 ? ` - Vehicle ${transportPaymentVehicle}` : ""}
                </span>
            {transportPaymentSummary && (
              <div className="d-flex gap-2">
                <Badge bg="secondary">Payable ₹{Number(transportPaymentSummary.totalPayable || 0).toLocaleString()}</Badge>
                <Badge bg="success">Paid ₹{Number(transportPaymentSummary.totalPaid || 0).toLocaleString()}</Badge>
                <Badge bg={Number(transportPaymentSummary.balance || 0) > 0 ? "warning" : "success"} text={Number(transportPaymentSummary.balance || 0) > 0 ? "dark" : undefined}>
                  Balance ₹{Number(transportPaymentSummary.balance || 0).toLocaleString()}
                </Badge>
                {transportPaymentSummary.manualOverride && <Badge bg="info" text="dark">Manual amount</Badge>}
              </div>
            )}
              </div>
            </Accordion.Header>
            <Accordion.Body>

          {(initial?.extraVehicles || []).length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-muted">Each vehicle's vendor is paid separately:</span>
              {[1, ...initial.extraVehicles.map((extra) => Number(extra.position))].map((position) => (
                <Button
                  key={position}
                  type="button"
                  size="sm"
                  variant={transportPaymentVehicle === position ? "primary" : "outline-primary"}
                  onClick={() => setTransportPaymentVehicle(position)}
                  disabled={transportPaymentSaving || transportPaymentLoading}
                >
                  Vehicle {position}
                </Button>
              ))}
            </div>
          )}
          {transportPaymentLoading && <div className="text-muted small">Loading transport payment details...</div>}
          {transportPaymentError && <div className="text-danger small mb-2">{transportPaymentError}</div>}
          {!initial?.id && <div className="text-muted small">Save the booking before recording vendor payments.</div>}
          {initial?.id && !transportPaymentLoading && !transportPaymentSummary?.vehicle && (
            <div className="text-muted small">Assign and save a vehicle before setting the transport payable amount.</div>
          )}

          {transportPaymentSummary?.vehicle && (
            <>
              <div className="bg-light border rounded p-2 mb-3">
                <div className="fw-semibold">
                  {transportPaymentSummary.vehicle.name} - {transportPaymentSummary.vehicle.registrationNumber}
                </div>
                <div className="small fw-semibold">
                  Payable to: {transportPaymentSummary.vehicle.payeeName} ({transportPaymentSummary.vehicle.payeeType === "OWNER" ? "Owner" : "Individual Driver"})
                </div>
                <div className="small">
                  UPI: {transportPaymentSummary.vehicle.upiNumber || "Not provided"}
                  {transportPaymentSummary.vehicle.upiScannerUrl && (
                    <> · <a href={transportPaymentSummary.vehicle.upiScannerUrl} target="_blank" rel="noreferrer">View UPI scanner</a></>
                  )}
                </div>
                <div className="small">
                  Bank: {transportPaymentSummary.vehicle.bankAccountHolder || "-"} · {transportPaymentSummary.vehicle.bankAccountNumber || "-"} · IFSC {transportPaymentSummary.vehicle.bankIfsc || "-"}
                </div>
              </div>

              {hasTransportPayableMismatch && (
                <div className="alert alert-warning py-2 mb-3">
                  <div className="d-flex flex-wrap justify-content-between gap-2 align-items-start">
                    <div>
                      <div className="fw-semibold">Transport payable differs from the latest calculation.</div>
                      <div className="small">
                        Calculated vehicle cost ₹{calculatedTransportPayable.toLocaleString()} · Saved payable ₹{savedTransportPayable.toLocaleString()} · Difference {transportPayableDifference > 0 ? "+" : "-"}₹{Math.abs(transportPayableDifference).toLocaleString()}
                      </div>
                      {transportPaymentSummary.manualOverride && (
                        <div className="small">
                          This is a manually entered amount, so changes to the booking will not update it.
                        </div>
                      )}
                      {calculatedTransportPayableBelowPaid && (
                        <div className="small text-danger">
                          Cannot update automatically because the calculated amount is below the amount already paid.
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={updateTransportPayableToCalculated}
                      disabled={transportPaymentSaving || calculatedTransportPayableBelowPaid}
                    >
                      Use calculated payable
                    </Button>
                  </div>
                </div>
              )}

              <Row className="g-2 mb-3">
                <Col md={5}>
                  <Form.Label>Total Payable to Vehicle Vendor</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={transportPayableDraft}
                      onChange={(event) => setTransportPayableDraft(event.target.value)}
                    />
                    <Button variant="outline-primary" onClick={updateTransportPayable} disabled={transportPaymentSaving}>
                      Save Payable
                    </Button>
                  </InputGroup>
                </Col>
              </Row>

              <div className="fw-semibold mb-2">Record Installment</div>
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Form.Label>Amount *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={transportPaymentForm.amount}
                      onChange={(event) => setTransportPaymentForm((previous) => ({ ...previous, amount: event.target.value }))}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Label>Payment Date *</Form.Label>
                  <IsoDatePicker
                    value={transportPaymentForm.paymentDate}
                    onChange={(value) => setTransportPaymentForm((previous) => ({ ...previous, paymentDate: value }))}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Payment Method *</Form.Label>
                  <Form.Select
                    value={transportPaymentForm.paymentMethod}
                    onChange={(event) => setTransportPaymentForm((previous) => ({ ...previous, paymentMethod: event.target.value }))}
                  >
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CASH">Cash</option>
                    <option value="OTHER">Other</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Reference / UTR</Form.Label>
                  <Form.Control
                    value={transportPaymentForm.referenceNumber}
                    onChange={(event) => setTransportPaymentForm((previous) => ({ ...previous, referenceNumber: event.target.value }))}
                  />
                </Col>
                <Col md={9}>
                  <Form.Label>Payment Note *</Form.Label>
                  <Form.Control
                    value={transportPaymentForm.note}
                    onChange={(event) => setTransportPaymentForm((previous) => ({ ...previous, note: event.target.value }))}
                    placeholder="Reason or installment details"
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    className="w-100"
                    onClick={recordTransportPayment}
                    disabled={transportPaymentSaving || !Number(transportPaymentForm.amount) || !transportPaymentForm.note.trim()}
                  >
                    Record Payment
                  </Button>
                </Col>
              </Row>

              <div className="fw-semibold mb-2">Payment Transactions</div>
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr><th style={{ width: 44 }}>#</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Note</th>
                      <th>Vehicle</th>
                      <th style={{ width: 84 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(transportPaymentSummary.transactions || []).map((transaction, index) => (
                      <tr key={transaction.id}><td className="small fw-semibold">{index + 1}</td>
                        <td>{formatDateDMY(transaction.paymentDate)}</td>
                        <td>₹{Number(transaction.amount || 0).toLocaleString()}<EditedMark payment={transaction} /></td>
                        <td>{transaction.paymentMethod?.replaceAll("_", " ")}</td>
                        <td>{transaction.referenceNumber || "-"}</td>
                        <td>{transaction.note}</td>
                        <td>
                          {transaction.vehicleName} - {transaction.vehicleRegistrationNumber}
                          <div className="small text-muted">Paid to {transaction.payeeName || "-"}</div>
                        </td>
                        <td>
                          <PaymentRowActions payment={transaction} corrections={transportCorrections} disabled={transportPaymentSaving} />
                        </td>
                      </tr>
                    ))}
                    {!transportPaymentSummary.transactions?.length && (
                      <tr><td colSpan={8} className="text-center text-muted">No transport payments recorded.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <DeletedPayments payments={transportPaymentSummary.deletedTransactions} />
              {transportCorrections.modal}
            </>
          )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <HotelVendorPayments bookingId={initial?.id} />

        <Accordion className="mt-3">          <MiscVendorPayments bookingId={initial?.id} category="EXTERNAL_VENDOR" label="External Vendor Payments" details={externalVendorPaymentDetails(form, quoteOptions, externalVendors)} />
          <MiscVendorPayments bookingId={initial?.id} category="ADDONS" label="Add-ons Vendor Payments" />
        </Accordion>
            </div>
          </Tab>

          <Tab eventKey="details" title="Booking Details">
            <div className="booking-edit-tab-pane">

        <div className="border rounded p-3 mb-3">
          <div className="fw-semibold mb-2">Travellers and Customization</div>
          <Row className="g-2 align-items-end">
            {form.dynamicPricing ? (
              <>
                <Col md={3}>
                  <Form.Label>Adults</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.adults}
                    onChange={(event) => {
                      const adults = Math.max(1, Number(event.target.value || 1));
                      markDynamicQuoteDirty();
                      setForm((prev) => ({ ...prev, adults, noOfTravellers: adults + Number(prev.kids || 0) }));
                    }}
                    disabled={saving}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Kids</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.kids}
                    onChange={(event) => {
                      const kids = Math.max(0, Number(event.target.value || 0));
                      markDynamicQuoteDirty();
                      setForm((prev) => ({
                        ...prev,
                        kids,
                        noOfTravellers: Number(prev.adults || 0) + kids,
                        kidAges: Array.from({ length: kids }, (_, index) => prev.kidAges?.[index] ?? ""),
                        kidVehicleSeatNeeded: Array.from({ length: kids }, (_, index) => prev.kidVehicleSeatNeeded?.[index] ?? true),
                      }));
                    }}
                    disabled={saving}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Total Travellers</Form.Label>
                  <Form.Control value={Number(form.adults || 0) + Number(form.kids || 0)} readOnly />
                </Col>
              </>
            ) : (
              <Col md={3}>
                <Form.Label>No. of Travellers</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={form.noOfTravellers || ""}
                  onChange={(event) => update("noOfTravellers", Math.max(1, Number(event.target.value || 1)))}
                  disabled={saving}
                />
              </Col>
            )}
            <Col md={3}>
              <Form.Label>Customization Required</Form.Label>
              <Form.Select
                value={form.customizationRequired ? "YES" : "NO"}
                onChange={(event) => {
                  const required = event.target.value === "YES";
                  dynamicTotalEditedRef.current = true;
                  setForm((prev) => ({
                    ...prev,
                    customizationRequired: required,
                    customizationDescription: required ? prev.customizationDescription : "",
                    customizationPrice: required ? prev.customizationPrice : "",
                  }));
                }}
                disabled={saving}
              >
                <option value="NO">No</option>
                <option value="YES">Yes</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Customization Type</Form.Label>
              <Form.Select
                value={form.customizationPriceAdjustment || "ADD"}
                onChange={(event) => update("customizationPriceAdjustment", event.target.value)}
                disabled={saving || !form.customizationRequired}
              >
                <option value="ADD">Add to trip amount</option>
                <option value="REDUCE">Reduce from trip amount</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Customization Amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={form.customizationPrice || ""}
                  onChange={(event) => update("customizationPrice", event.target.value)}
                  disabled={saving || !form.customizationRequired}
                  placeholder="0"
                />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Label>Customization Details{form.customizationRequired ? " *" : ""}</Form.Label>
              <Form.Control
                value={form.customizationDescription || ""}
                onChange={(event) => update("customizationDescription", event.target.value)}
                disabled={saving || !form.customizationRequired}
                placeholder="Customer-specific customization details"
              />
            </Col>
            <Col md={3}>
              <Form.Label>Vehicle Pickup Date & Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.pickupDateTime || ""}
                onChange={(event) => update("pickupDateTime", event.target.value)}
                disabled={saving}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Vehicle Drop Date & Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.dropDateTime || ""}
                onChange={(event) => update("dropDateTime", event.target.value)}
                disabled={saving}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Dynamic Package Total</Form.Label>
              <Form.Control
                value={draftQuoteBaseTotal || form.totalPrice ? `INR ${Number(draftQuoteBaseTotal || form.totalPrice || 0).toLocaleString("en-IN")}` : ""}
                readOnly
              />
            </Col>
            <Col md={3}>
              <Form.Label>Final Total</Form.Label>
              <Form.Control
                value={form.totalPrice ? `INR ${Number(form.totalPrice).toLocaleString("en-IN")}` : ""}
                readOnly
              />
            </Col>
            <Col md={3}>
              <Form.Label>{isNewBooking ? "Advance Payment" : "Total Customer Payments Received"}</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.advancePaid || ""}
                  onChange={(event) => update("advancePaid", event.target.value)}
                  disabled={saving}
                />
              </InputGroup>
            </Col>
            {bookingFormSchedule ? (
              <Col md={6}>
                <Form.Label>Payment Schedule</Form.Label>
                <div className="border rounded px-2 py-1">
                  <div className="small">Advance at booking: INR {bookingFormSchedule.advanceAmount.toLocaleString("en-IN")}</div>
                  <PaymentScheduleLines schedule={bookingFormSchedule} />
                </div>
              </Col>
            ) : null}
            <Col md={3}>
              <Form.Label>Cashback Amount Per Adult</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cashbackAmountPerPerson || ""}
                  onChange={(event) => {
                    const perAdult = Math.max(0, Number(event.target.value || 0));
                    const adults = Math.max(0, Number(form.adults || form.noOfTravellers || 0));
                    update("cashbackAmountPerPerson", event.target.value);
                    update("totalCashbackAmount", perAdult ? String(perAdult * adults) : "");
                  }}
                  disabled={saving}
                  placeholder="Optional"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label>Total Cashback</Form.Label>
              <Form.Control
                value={form.totalCashbackAmount ? `INR ${Number(form.totalCashbackAmount).toLocaleString("en-IN")}` : ""}
                readOnly
              />
            </Col>
            {form.dynamicPricing && Array.from({ length: Number(form.kids || 0) }, (_, index) => (
              <Col md={4} key={`details-kid-${index}`}>
                <InputGroup>
                  <InputGroup.Text>Kid {index + 1} age</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.kidAges?.[index] ?? ""}
                    onChange={(event) => {
                      markDynamicQuoteDirty();
                      setForm((prev) => {
                        const kidAges = [...(prev.kidAges || [])];
                        kidAges[index] = event.target.value;
                        return { ...prev, kidAges };
                      });
                    }}
                    disabled={saving}
                  />
                </InputGroup>
                <Form.Check
                  type="switch"
                  className="mt-1"
                  label="Vehicle seat needed"
                  checked={form.kidVehicleSeatNeeded?.[index] !== false}
                  onChange={(event) => {
                    markDynamicQuoteDirty();
                    setForm((prev) => {
                      const kidVehicleSeatNeeded = [...(prev.kidVehicleSeatNeeded || [])];
                      kidVehicleSeatNeeded[index] = event.target.checked;
                      return { ...prev, kidVehicleSeatNeeded };
                    });
                  }}
                  disabled={saving}
                />
              </Col>
            ))}
            <ConversionCreditEditor
              value={form.conversionCredits}
              members={teamMembers}
              disabled={saving}
              onChange={(rows) => update("conversionCredits", rows)}
            />
          </Row>
          <div className="small text-muted mt-2">
            Set Customization Required to Yes only when the booking has a customer-specific price change. Customization details are shown on customer documents.
          </div>
        </div>

        {/* Description */}
        <Row>
          <Col md={12} className="mb-2">
            <Form.Label>
              Description <Badge bg="secondary" className="ms-1">Internal only - not shown to customer</Badge>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description || ''}
              onChange={(e) => update('description', e.target.value)}
              disabled={saving}
              placeholder="Enter booking notes / details for internal reference"
            />
          </Col>
          <Col md={12} className="mb-2">
            <Form.Label>
              Notes <Badge bg="warning" text="dark" className="ms-1">Shown to customer on receipt & policy</Badge>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.bookingNotes || ''}
              onChange={(e) => update('bookingNotes', e.target.value)}
              disabled={saving}
              placeholder="Notes shown in payment receipt, policy, and booking table"
            />
          </Col>
        </Row>


        <div className="border rounded p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0 fw-semibold">Cashback Tracking</Form.Label>
            <Badge bg="info" text="dark">Independent</Badge>
          </div>
          <div className="small text-muted mb-2">
            Cashback amount is set above with booking details. It is independent from trip amount and payable only after review video, travel photos, and Google review are received.
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3">
            <Form.Check
              id={`cashback-review-video-${form.id || "new"}`}
              label="Review Video Received"
              checked={Boolean(form.cashbackReviewVideoReceived)}
              onChange={(e) => update("cashbackReviewVideoReceived", e.target.checked)}
              disabled={saving}
            />
            <Form.Check
              id={`cashback-travel-photos-${form.id || "new"}`}
              label="Travel Photos Received"
              checked={Boolean(form.cashbackTravelPhotosReceived)}
              onChange={(e) => update("cashbackTravelPhotosReceived", e.target.checked)}
              disabled={saving}
            />
            <Form.Check
              id={`cashback-google-review-${form.id || "new"}`}
              label="Google Review Received"
              checked={Boolean(form.cashbackGoogleReviewReceived)}
              onChange={(e) => update("cashbackGoogleReviewReceived", e.target.checked)}
              disabled={saving}
            />
            <Form.Check
              id={`cashback-paid-${form.id || "new"}`}
              label="Cashback Paid"
              checked={Boolean(form.cashbackPaid)}
              onChange={(e) => {
                update("cashbackPaid", e.target.checked);
                // Start from the promised total; the team corrects it if a different amount went out.
                if (e.target.checked && !form.cashbackPaidAmount && Number(form.totalCashbackAmount) > 0) {
                  update("cashbackPaidAmount", String(form.totalCashbackAmount));
                }
              }}
              disabled={saving}
            />
          </div>
          <Row className="g-2 mt-2">
            <Col md={4}>
              <Form.Label>Cashback Paid Amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cashbackPaidAmount || ""}
                  onChange={(e) => {
                    update("cashbackPaidAmount", e.target.value);
                    if (Number(e.target.value) > 0) update("cashbackPaid", true);
                  }}
                  disabled={saving}
                  placeholder="Amount paid to customer"
                />
              </InputGroup>
              <Form.Text muted>Deducted from profit once Cashback Paid is ticked.</Form.Text>
            </Col>
          </Row>
          <Row className="g-2 mt-2">
            <Col md={4}>
              <Form.Check
                id={`cashback-review-request-${form.id || "new"}`}
                label="Can ask for review"
                checked={Boolean(form.cashbackReviewRequestAllowed)}
                onChange={(e) => update("cashbackReviewRequestAllowed", e.target.checked)}
                disabled={saving}
              />
            </Col>
            <Col md={8}>
              <Form.Label>Review follow-up note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.cashbackReviewRequestNote || ""}
                onChange={(e) => update("cashbackReviewRequestNote", e.target.value)}
                disabled={saving}
                placeholder="Details for the review video follow-up person"
              />
            </Col>
          </Row>
        </div>
        {hasDynamicDetails && (

          <div className="border rounded p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Dynamic Booking Details</Form.Label>
              <Badge bg="warning" text="dark">Dynamic</Badge>
            </div>
            <Row>
              <Col md={4} className="mb-2">
                <Form.Label>Pickup</Form.Label>
                <Form.Select
                  value={selectedDynamicPickup?.id || ""}
                  onChange={(e) => updatePickupFromDropdown(e.target.value)}
                  disabled={saving || quoteOptionsLoading || !dynamicPickups.length}
                >
                  <option value="">Select pickup</option>
                  {dynamicPickups.map((pickup) => (
                    <option key={pickup.id} value={pickup.id}>
                      {pickup.pickupName}
                    </option>
                  ))}
                </Form.Select>
                {!quoteOptionsLoading && form.pickupLabel && !selectedDynamicPickup && (
                  <div className="small text-muted mt-1">
                    Current saved pickup is not active for this trip.
                  </div>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <Form.Label>Vehicle</Form.Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    className="mb-2"
                    onClick={addAdditionalVehicle}
                    disabled={saving || quoteOptionsLoading || !pickupDynamicVehicleOptions.length}
                  >
                    Add Vehicle
                  </Button>
                </div>
                <Form.Select
                  aria-label="Vehicle 1"
                  value={
                    selectedDynamicVehicle?.id || ""
                  }
                  onChange={(e) => updateVehicleFromDropdown(e.target.value)}
                  disabled={saving || quoteOptionsLoading || !pickupDynamicVehicleOptions.length}
                >
                  <option value="">{form.vehicleNameSnapshot || "Select vehicle"}</option>
                  {pickupDynamicVehicleOptions
                    .filter((vehicle) => dynamicVehicleOptions.includes(vehicle) || vehicle === selectedDynamicVehicle)
                    .map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.vehicleName}
                        {vehicle.capacityMax ? ` (${vehicle.capacityMin || 1}-${vehicle.capacityMax} pax)` : ""}
                        {dynamicVehicleOptions.includes(vehicle) ? "" : " - too small"}
                      </option>
                    ))}
                </Form.Select>
                {(form.additionalVehicles || []).map((item, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mt-2">
                    <Form.Select
                      aria-label={`Vehicle ${index + 2}`}
                      value={item.vehicleId || ""}
                      onChange={(e) => updateAdditionalVehicles((list) =>
                        list.map((row, i) => (i === index ? { ...row, vehicleId: e.target.value } : row))
                      )}
                      disabled={saving || quoteOptionsLoading}
                    >
                      {pickupDynamicVehicleOptions.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.vehicleName}
                          {vehicle.capacityMax ? ` (${vehicle.capacityMin || 1}-${vehicle.capacityMax} pax)` : ""}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      onClick={() => updateAdditionalVehicles((list) => list.filter((_, i) => i !== index))}
                      disabled={saving}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                {!quoteOptionsLoading && selectedVehicleTooSmall && (
                  <div className="small text-danger fw-semibold mt-1">
                    {severalDynamicVehicles ? "The selected vehicles seat" : `${selectedDynamicVehicle.vehicleName} seats`}{" "}
                    {Number(dynamicFleet?.capacityMax || 0)}, but this group
                    needs {vehicleCountPersonsFor(dynamicFleet)} seats. Choose a bigger vehicle{severalDynamicVehicles ? " or add one" : ""} if needed - the price will change.
                  </div>
                )}
                {!quoteOptionsLoading && pickupDynamicVehicleOptions.length > 0 && !dynamicVehicleOptions.length && (
                  <div className="small text-danger mt-1">
                    No single vehicle seats the current traveller count. Use Add Vehicle for a second one.
                  </div>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <Form.Label>AC Type</Form.Label>
                <Form.Select
                  value={form.acTypeSnapshot || ""}
                  onChange={(e) => update("acTypeSnapshot", e.target.value)}
                  disabled={saving}
                >
                  <option value="">Not specified</option>
                  <option value="NON_AC">NON AC</option>
                  <option value="AC">AC</option>
                </Form.Select>
              </Col>
            </Row>
            <Row>
              <Col md={6} className="mb-2">
                <Form.Label>Hotel / Rooms</Form.Label>
                {unrestoredRoomSelection && (
                  <div className="alert alert-warning py-2 px-3 small mb-2">
                    Saved room units could not be restored for this booking. Re-enter the room
                    counts from the hotel reservation message before recalculating - the saved
                    total is kept until you do.
                  </div>
                )}
                <div className="vstack gap-2">
                  {hotelNightNumbers.map((night) => {
                    const nightNoStay = isNoStayNight(night);
                    // At least one night keeps a stay, so the last one can't be switched off.
                    const canSkipStay = nightNoStay || stayHotelNightNumbers.length > 1;
                    const selected = selectedNightRooms[night] || "";
                    const selectedIds = nightNoStay ? [] : selectedRoomRateIdsForNight(night);
                    const selectedHotelRates = selectedIds
                      .map((id) => activeRoomRates.find((rate) => String(rate.id) === String(id)))
                      .filter(Boolean);
                    const hotelOptions = roomRateOptionsForNight(night);
                    return (
                      <div key={`hotel-night-${night}`} className="border rounded p-2">
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                          <div className="small fw-semibold">
                            Night {night}
                            {form.travelDate ? ` - Stay Date: ${formatDate(addIsoDays(form.travelDate, night - 1))}` : ""}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                            {hotelNightNumbers.length > 1 && (
                              <Form.Select
                                size="sm"
                                value={nightNoStay ? "NO_STAY" : "STAY"}
                                onChange={(e) => setNightNoStay(night, e.target.value === "NO_STAY")}
                                disabled={saving || quoteOptionsLoading}
                                aria-label={`Stay needed for night ${night}`}
                                style={{ width: 150 }}
                              >
                                <option value="STAY">Stay needed</option>
                                <option value="NO_STAY" disabled={!canSkipStay}>No stay</option>
                              </Form.Select>
                            )}
                            {!nightNoStay && roomCityOptions.length > 1 && (
                              <Form.Select
                                size="sm"
                                value={defaultRoomCityForNight(night)}
                                onChange={(e) => updateRoomCityForNight(night, e.target.value)}
                                disabled={saving || quoteOptionsLoading}
                                aria-label={`Select city for night ${night}`}
                                style={{ width: 160 }}
                              >
                                {roomCityOptions.map((city) => (
                                  <option key={city} value={city}>{city}</option>
                                ))}
                              </Form.Select>
                            )}
                            {!nightNoStay && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                onClick={() => addNightHotelSelection(night)}
                                disabled={saving || quoteOptionsLoading || selectedIds.length >= hotelOptions.length}
                              >
                                Add Hotel
                              </Button>
                            )}
                          </div>
                        </div>
                        {nightNoStay && (
                          <div className="small text-muted">
                            No stay for this night - the customer arranges their own accommodation. No
                            hotel room is held or paid for it.
                          </div>
                        )}
                        {!nightNoStay && !selectedHotelRates.length && (
                          <InputGroup size="sm">
                            <InputGroup.Text>Hotel</InputGroup.Text>
                            <Form.Select
                              value=""
                              onChange={(e) => setSelectedRoomRateIdsForNight(night, e.target.value ? [e.target.value] : [])}
                              disabled={saving || quoteOptionsLoading || !hotelOptions.length}
                            >
                              <option value="">Select hotel</option>
                              {hotelOptions.map((hotel) => (
                                <option key={hotel.value} value={hotel.value}>
                                  {hotel.label}
                                </option>
                              ))}
                            </Form.Select>
                          </InputGroup>
                        )}
                        {selectedHotelRates.map((selectedHotelRate, hotelIndex) => {
                          const categories = roomCategoriesForRate(quoteOptions, selectedHotelRate);
                          const facilityItems = hotelFacilityItemsForBooking(selectedHotelRate);
                          const includedFacilityItems = facilityItems.filter(
                            (facility) => facilityCostBehavior(facility) !== "OPTIONAL_PAID"
                          );
                          const optionalFacilityItems = facilityItems.filter(
                            (facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID"
                          );
                          return (
                          <div className="d-flex flex-column gap-2 mt-2" key={`selected-hotel-${night}-${selectedHotelRate.id}`}>
                            <InputGroup size="sm">
                              <InputGroup.Text>Hotel {hotelIndex + 1}</InputGroup.Text>
                              <Form.Select
                                value={String(selectedHotelRate.id)}
                                onChange={(e) => updateNightHotelSelection(night, selectedHotelRate.id, e.target.value)}
                                disabled={saving || quoteOptionsLoading || !hotelOptions.length}
                              >
                                {hotelOptions.map((hotel) => (
                                  <option
                                    key={hotel.value}
                                    value={hotel.value}
                                    disabled={selectedIds.includes(String(hotel.value)) && String(hotel.value) !== String(selectedHotelRate.id)}
                                  >
                                    {hotel.label}
                                  </option>
                                ))}
                              </Form.Select>
                              {selectedHotelRates.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  onClick={() => removeNightHotelSelection(night, selectedHotelRate.id)}
                                  disabled={saving}
                                >
                                  Remove
                                </Button>
                              )}
                            </InputGroup>
                            <div className="small">
                              <span className="text-muted">Facilities:</span>{" "}
                              {includedFacilityItems.length ? (
                                includedFacilityItems.map((facility) => (
                                  <Badge
                                    bg={facility.available ? "success" : "danger"}
                                    pill
                                    className="me-1 mb-1"
                                    key={`${selectedHotelRate.id}-${facility.name}`}
                                  >
                                    {facility.name}: {facility.available ? "Yes" : "No"}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted">Not listed</span>
                              )}
                            </div>
                            {optionalFacilityItems.length > 0 && (
                              <div className="border-top pt-2">
                                <div className="small text-muted mb-1">Optional facilities</div>
                                <div className="d-flex flex-column gap-1">
                                  {optionalFacilityItems.map((facility) => {
                                    const facilityKey = `${selectedHotelRate.id}:${facility.id}`;
                                    const amount = Number(facility.extraCost || 0);
                                    const suffix = amount > 0
                                      ? ` (+Rs. ${amount.toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`
                                      : "";
                                    return (
                                      <Form.Check
                                        key={facilityKey}
                                        type="switch"
                                        className="small"
                                        id={`booking-hotel-paid-facility-${night}-${facilityKey}`}
                                        label={`${facility.name}${suffix}`}
                                        checked={form.selectedHotelFacilityKeys?.[facilityKey] === true}
                                        onChange={(event) => {
                                          markDynamicQuoteDirty();
                                          setForm((previous) => ({
                                            ...previous,
                                            selectedHotelFacilityKeys: {
                                              ...(previous.selectedHotelFacilityKeys || {}),
                                              [facilityKey]: event.target.checked,
                                            },
                                          }));
                                        }}
                                        disabled={saving}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {categories.map((category) => {
                              const key = roomCategoryUnitKey(selectedHotelRate.id, category.id);
                              const units = roomCategoryUnitsForSelection(selectedHotelRate, category);
                              const configuredMaximum = Math.max(1, Number(category.maximumUnits || 1));
                              const availability = roomAvailabilityByKey[key];
                              const maximumUnits = availability
                                ? Math.max(0, Math.min(configuredMaximum, Number(availability.availableUnits ?? configuredMaximum)))
                                : configuredMaximum;
                              return (
                                <div className="d-flex justify-content-between align-items-center gap-2 border-top pt-2" key={category.id}>
                                  <div className="small">
                                    <div className="fw-semibold">{category.categoryName}</div>
                                    <div className="text-muted">
                                      Max adults {Number(category.maxMemberCount || 0) || "-"} · Available after bookings {maximumUnits}
                                    </div>
                                  </div>
                                  <InputGroup size="sm" style={{ width: units < maximumUnits ? 118 : 78 }}>
                                    <Button
                                      variant="outline-secondary"
                                      onClick={() => updateRoomCategoryUnits(selectedHotelRate, category, -1)}
                                      disabled={saving || units <= 0}
                                    >
                                      -
                                    </Button>
                                    <InputGroup.Text>{units}</InputGroup.Text>
                                    {units < maximumUnits && (
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => updateRoomCategoryUnits(selectedHotelRate, category, 1)}
                                        disabled={saving}
                                      >
                                        +
                                      </Button>
                                    )}
                                  </InputGroup>
                                </div>
                              );
                            })}
                            {!categories.length && (
                              <div className="small text-muted">No active rooms configured for this hotel.</div>
                            )}
                          </div>
                          );
                        })}
                        {selected && !nightNoStay && !selectedHotelRates.length && (
                          <div className="small text-muted mt-1">Saved room: {selected}</div>
                        )}
                      </div>
                    );
                  })}
                  {!hotelNightNumbers.length && (
                    <div className="small text-muted">
                      No hotel room options available for this trip.
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6} className="mb-2">
                <Form.Label>Add-ons</Form.Label>
                <div className="border rounded p-2">
                  {addonNameOptions.length ? (
                    <div className="d-flex flex-column gap-2">
                      {addonNameOptions.map((name) => {
                        const addon = (quoteOptions?.addons || []).find((item) => item.name === name);
                        const selected = selectedAddonNames.includes(name);
                        const addonOptions = (addon?.options || []).filter((option) => option.active !== false);
                        const choice = selectedAddonOptions.find((entry) => String(entry.addonId) === String(addon?.id)) || {};
                        const chosenOption = addonOptions.find((option) => String(option.id) === String(choice.optionId)) || addonOptions[0] || null;
                        const countable = ["PER_PERSON", "PER_VEHICLE"].includes((chosenOption || addon || {}).pricingType);
                        const bookingTravellers = Number(form.adults || 0) + Number(form.kids || 0);
                        return (
                          <div key={name} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <Form.Check
                              type="switch"
                              id={`booking-addon-${form.id}-${name}`}
                              label={name}
                              checked={selected}
                              onChange={(e) => toggleAddonSelection(name, e.target.checked)}
                              disabled={saving || quoteOptionsLoading}
                            />
                            {selected && addonOptions.length > 0 && (
                              <Form.Select
                                size="sm"
                                style={{ width: "auto" }}
                                aria-label={`${name} option`}
                                value={chosenOption?.id ?? ""}
                                onChange={(e) => chooseAddonOption(addon.id, { optionId: Number(e.target.value) })}
                                disabled={saving}
                              >
                                {addonOptions.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                              </Form.Select>
                            )}
                            {selected && countable && (
                              <InputGroup size="sm" style={{ width: 150 }}>
                                <InputGroup.Text>People</InputGroup.Text>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  max={bookingTravellers || undefined}
                                  placeholder={bookingTravellers ? `All ${bookingTravellers}` : "All"}
                                  aria-label={`How many take ${name}`}
                                  value={choice.people ?? ""}
                                  onChange={(e) => {
                                    const typed = e.target.value === "" ? null : Math.max(1, Number(e.target.value));
                                    chooseAddonOption(addon.id, { people: typed && bookingTravellers && typed >= bookingTravellers ? null : typed });
                                  }}
                                  disabled={saving}
                                />
                              </InputGroup>
                            )}
                            {selected && addon?.id != null && (
                              <Form.Control
                                size="sm"
                                type="date"
                                style={{ width: 160 }}
                                title={addonDefaultDate(addon)
                                  ? "Date for the driver's message. Clear it to go back to the trip's default day."
                                  : "Date for the driver's message. No default day is set for this add-on in Dynamic Pricing."}
                                value={addonDateFor(addon)}
                                onChange={(e) => setAddonDate(addon, e.target.value)}
                                disabled={saving}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="small text-muted">No add-ons available for this trip.</div>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="mb-2">
                <div className="border rounded p-2 bg-light">
                  <div className="small fw-semibold mb-2">Trip Extension</div>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Label className="small text-muted">Trip Extension Required</Form.Label>
                      <Form.Select
                        value={form.tripExtensionRequired ? "YES" : "NO"}
                        onChange={(e) => {
                          const required = e.target.value === "YES";
                          update("tripExtensionRequired", required);
                          if (required && !Number(form.extraPreNights) && !Number(form.extraPostNights)) {
                            update("extraPreNights", "1");
                          }
                        }}
                        disabled={saving || quoteOptionsLoading}
                      >
                        <option value="NO">No</option>
                        <option value="YES">Yes</option>
                      </Form.Select>
                    </Col>
                    {form.tripExtensionRequired && (
                      <>
                        <Col md={4}>
                          <Form.Label className="small text-muted">Extra pre-nights</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={form.extraPreNights}
                            onChange={(e) => update("extraPreNights", e.target.value)}
                            disabled={saving}
                            placeholder="0"
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Label className="small text-muted">Extra post-nights</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={form.extraPostNights}
                            onChange={(e) => update("extraPostNights", e.target.value)}
                            disabled={saving}
                            placeholder="0"
                          />
                        </Col>
                        {visibleCalculationQuote?.extensionVehicleAmountRequired && (
                          <Col xs={12}>
                            <div className="small text-muted mb-1">
                              This pickup charges one flat vehicle price, so enter what the transport vendor charges
                              for the extra days. The customer is charged exactly this amount.
                            </div>
                            <Row className="g-2">
                              <Col md={4}>
                                <Form.Label className="small text-muted">
                                  Extension vehicle amount{severalDynamicVehicles ? " - Vehicle 1" : ""}
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={form.extensionVehicleAmount ?? ""}
                                  onChange={(e) => update("extensionVehicleAmount", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                                  disabled={saving}
                                />
                              </Col>
                              {(form.additionalVehicles || []).map((item, index) => (
                                <Col md={4} key={index}>
                                  <Form.Label className="small text-muted">Extension vehicle amount - Vehicle {index + 2}</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={item.extensionVehicleAmount ?? ""}
                                    onChange={(e) => updateAdditionalVehicles((list) =>
                                      list.map((row, i) => (i === index
                                        ? { ...row, extensionVehicleAmount: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) }
                                        : row))
                                    )}
                                    disabled={saving}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </Col>
                        )}
                        {(form.extensionStays || []).map((stay, index) => {
                          const hotels = extensionHotelsForCity(stay.city);
                          const categories = extensionCategoriesForHotel(stay.hotelId);
                          const extensionSelections = stay.roomCategorySelections || [];
                          const stayIncludesStay = (stay.scope || "BOTH") !== "VEHICLE";
                          const selectedExtensionHotel = extensionHotelOptions.find(
                            (item) => String(item.id) === String(stay.hotelId)
                          );
                          const facilityItems =
                            stayIncludesStay && selectedExtensionHotel
                              ? hotelFacilityItemsForBooking(selectedExtensionHotel)
                              : [];
                          const includedFacilityItems = facilityItems.filter(
                            (facility) => facilityCostBehavior(facility) !== "OPTIONAL_PAID"
                          );
                          const optionalFacilityItems = facilityItems.filter(
                            (facility) => facility.id != null && facility.available && facilityCostBehavior(facility) === "OPTIONAL_PAID"
                          );
                          const categoryUnits = (categoryId) =>
                            Number(
                              extensionSelections.find(
                                (selection) => String(selection.roomCategoryId) === String(categoryId)
                              )?.units || 0
                            );
                          const updateCategoryUnits = (category, units) => {
                            const nextUnits = Math.max(
                              0,
                              Math.min(extensionMaxUnits(category, stay), Number(units || 0))
                            );
                            const nextSelections = extensionSelections.filter(
                              (selection) => String(selection.roomCategoryId) !== String(category.id)
                            );
                            if (nextUnits > 0) {
                              nextSelections.push({ roomCategoryId: category.id, units: nextUnits });
                            }
                            const primary =
                              nextSelections.find((selection) => Number(selection.units || 0) > 0) || {};
                            updateExtensionStayAt(index, {
                              roomCategorySelections: nextSelections,
                              roomCategoryId: primary.roomCategoryId || "",
                              units: primary.units || 0,
                            });
                          };
                          const sameNightCount = (form.extensionStays || []).filter(
                            (item) =>
                              String(item.position || "PRE") === String(stay.position || "PRE") &&
                              Number(item.nightIndex || 1) === Number(stay.nightIndex || 1)
                          ).length;
                          const extensionStayDate = form.travelDate
                            ? addIsoDays(
                                form.travelDate,
                                stay.position === "POST"
                                  ? Number(quoteOptions?.durationNights || 0) + Number(stay.nightIndex || 1) - 1
                                  : -Number(stay.nightIndex || 1)
                              )
                            : "";
                          const addHotelForNight = () => {
                            markDynamicQuoteDirty();
                            setForm((previous) => {
                              const stays = previous.extensionStays || [];
                              const lastIndex = stays.reduce(
                                (found, item, itemIndex) =>
                                  String(item.position || "PRE") === String(stay.position || "PRE") &&
                                  Number(item.nightIndex || 1) === Number(stay.nightIndex || 1)
                                    ? itemIndex
                                    : found,
                                index
                              );
                              const next = [...stays];
                              next.splice(
                                lastIndex + 1,
                                0,
                                defaultExtensionStay(stay.position || "PRE", {}, Number(stay.nightIndex || 1))
                              );
                              return { ...previous, extensionStays: next };
                            });
                          };
                          const removeHotelForNight = () => {
                            if (sameNightCount <= 1) return;
                            markDynamicQuoteDirty();
                            setForm((previous) => ({
                              ...previous,
                              extensionStays: (previous.extensionStays || []).filter((_, itemIndex) => itemIndex !== index),
                            }));
                          };
                          return (
                            <Col xs={12} key={stay.stayKey || `${stay.position}-${index}`}>
                              <div className="border rounded p-2 bg-white">
                                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                  <div className="small fw-semibold">
                                    {stay.position === "POST" ? "Extra post-night" : "Extra pre-night"} {Number(stay.nightIndex || 1)}
                                    {extensionStayDate ? ` - Stay Date: ${formatDate(extensionStayDate)}` : ""}
                                  </div>
                                  <div className="d-flex gap-2">
                                    {sameNightCount > 1 && (
                                      <Button type="button" variant="outline-danger" size="sm" onClick={removeHotelForNight} disabled={saving}>
                                        Remove
                                      </Button>
                                    )}
                                    {stayIncludesStay && (
                                      <Button type="button" variant="outline-primary" size="sm" onClick={addHotelForNight} disabled={saving}>
                                        Add Hotel
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <Row className="g-2">
                                  <Col xs={12} md={4}>
                                    <Form.Label className="small text-muted">This night needs</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={stay.scope || "BOTH"}
                                      onChange={(e) => updateExtensionStayAt(index, { scope: e.target.value })}
                                      disabled={saving}
                                    >
                                      <option value="BOTH">Stay and Vehicle</option>
                                      <option value="STAY">Stay only</option>
                                      <option value="VEHICLE">Vehicle only</option>
                                    </Form.Select>
                                  </Col>
                                  {stayIncludesStay && (
                                    <>
                                      <Col xs={6} md={4}>
                                        <Form.Label className="small text-muted">City</Form.Label>
                                        <Form.Select
                                          value={stay.city || ""}
                                          onChange={(e) => {
                                            const city = e.target.value;
                                            const hotel = extensionHotelsForCity(city)[0] || null;
                                            const category = extensionCategoriesForHotel(hotel?.id)[0] || null;
                                            updateExtensionStayAt(index, {
                                              city,
                                              hotelId: hotel?.id || "",
                                              roomCategoryId: category?.id || "",
                                              units: suggestedExtensionUnits(category),
                                              roomCategorySelections: category
                                                ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }]
                                                : [],
                                              selectedFacilityKeys: {},
                                            });
                                          }}
                                          disabled={saving}
                                        >
                                          <option value="">Select city</option>
                                          {extensionCityOptions.map((city) => (
                                            <option key={city} value={city}>{city}</option>
                                          ))}
                                        </Form.Select>
                                      </Col>
                                      <Col xs={6} md={4}>
                                        <Form.Label className="small text-muted">Hotel</Form.Label>
                                        <Form.Select
                                          value={stay.hotelId || ""}
                                          onChange={(e) => {
                                            const hotelId = e.target.value;
                                            const category = extensionCategoriesForHotel(hotelId)[0] || null;
                                            updateExtensionStayAt(index, {
                                              hotelId,
                                              roomCategoryId: category?.id || "",
                                              units: suggestedExtensionUnits(category),
                                              roomCategorySelections: category
                                                ? [{ roomCategoryId: category.id, units: suggestedExtensionUnits(category) }]
                                                : [],
                                              selectedFacilityKeys: {},
                                            });
                                          }}
                                          disabled={saving}
                                        >
                                          <option value="">Select hotel</option>
                                          {hotels.map((hotel) => (
                                            <option key={hotel.id} value={hotel.id}>
                                              {hotel.hotelCode || hotel.hotelName || `Hotel ${hotel.id}`}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      </Col>
                                      <Col xs={12}>
                                        <div className="small">
                                          <span className="text-muted">Facilities:</span>{" "}
                                          {includedFacilityItems.length ? (
                                            includedFacilityItems.map((facility) => (
                                              <Badge
                                                bg={facility.available ? "success" : "danger"}
                                                pill
                                                className="me-1 mb-1"
                                                key={`${stay.stayKey}-${facility.name}`}
                                              >
                                                {facility.name}: {facility.available ? "Yes" : "No"}
                                              </Badge>
                                            ))
                                          ) : (
                                            <span className="text-muted">Not listed</span>
                                          )}
                                        </div>
                                        {optionalFacilityItems.length > 0 && (
                                          <div className="border-top pt-2 mt-2">
                                            <div className="small text-muted mb-1">Optional facilities</div>
                                            <div className="d-flex flex-column gap-1">
                                              {optionalFacilityItems.map((facility) => {
                                                const amount = Number(facility.extraCost || 0);
                                                const suffix = amount > 0
                                                  ? ` (+Rs. ${amount.toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"})`
                                                  : "";
                                                return (
                                                  <Form.Check
                                                    key={`${stay.stayKey}:${facility.id}`}
                                                    type="switch"
                                                    className="small"
                                                    id={`extension-facility-${form.id}-${stay.stayKey}-${facility.id}`}
                                                    label={`${facility.name}${suffix}`}
                                                    checked={stay.selectedFacilityKeys?.[facility.id] === true}
                                                    onChange={(e) =>
                                                      updateExtensionStayAt(index, {
                                                        selectedFacilityKeys: {
                                                          ...(stay.selectedFacilityKeys || {}),
                                                          [facility.id]: e.target.checked,
                                                        },
                                                      })
                                                    }
                                                    disabled={saving}
                                                  />
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </Col>
                                      <Col xs={12}>
                                        <Form.Label className="small text-muted">Room categories</Form.Label>
                                        <div className="d-flex flex-column gap-1">
                                          {categories.map((category) => {
                                            const units = categoryUnits(category.id);
                                            const maxUnits = extensionMaxUnits(category, stay);
                                            const availabilityKnown = Boolean(extensionAvailabilityByKey[`${category.id}:${extensionStayDateValue(stay)}`]);
                                            return (
                                              <div key={category.id} className="d-flex justify-content-between align-items-center gap-2">
                                                <span className="small">
                                                  {category.categoryName}{" "}
                                                  <span className="text-muted">Max adults {Number(category.maxMemberCount || 0) || "-"}</span>
                                                  {availabilityKnown && (
                                                    <span className="text-muted">
                                                      {maxUnits === 0 ? " · Sold out for this night" : ` · Available after bookings ${maxUnits}`}
                                                    </span>
                                                  )}
                                                  {units > maxUnits && (
                                                    <span className="text-danger"> · Only {maxUnits} left, reduce before saving</span>
                                                  )}
                                                </span>
                                                <InputGroup size="sm" style={{ width: 118 }}>
                                                  <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={saving || units <= 0}
                                                    onClick={() => updateCategoryUnits(category, units - 1)}
                                                  >
                                                    -
                                                  </Button>
                                                  <InputGroup.Text>{units}</InputGroup.Text>
                                                  <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    disabled={saving || units >= maxUnits}
                                                    onClick={() => updateCategoryUnits(category, units + 1)}
                                                  >
                                                    +
                                                  </Button>
                                                </InputGroup>
                                              </div>
                                            );
                                          })}
                                          {!categories.length && (
                                            <div className="small text-danger">No room categories available for this hotel.</div>
                                          )}
                                        </div>
                                      </Col>
                                    </>
                                  )}
                                </Row>
                              </div>
                            </Col>
                          );
                        })}
                        <Col xs={12}>
                          <Form.Label className="small text-muted">Extension note</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={form.tripExtensionNote}
                            onChange={(e) => update("tripExtensionNote", e.target.value)}
                            disabled={saving}
                          />
                        </Col>
                      </>
                    )}
                  </Row>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4} className="mb-2">
                <Form.Label>Calculated Total Price</Form.Label>
                <Form.Control
                  value={Number(form.totalPrice || draftQuoteFinalTotal || 0) > 0
                    ? `₹${Number(form.totalPrice || draftQuoteFinalTotal || 0).toLocaleString("en-IN")}`
                    : "Pending calculation"}
                  readOnly
                />
                {priceLocked && Number(form.totalPrice || 0) !== agreedTotalPrice ? (
                  <div className="small text-danger mt-1">
                    Agreed ₹{agreedTotalPrice.toLocaleString("en-IN")} → New ₹{Number(form.totalPrice || 0).toLocaleString("en-IN")}
                    {priceChangeReasons.length ? ` (${priceChangeReasons.join(", ")})` : ""}. You will be asked to confirm when saving.
                  </div>
                ) : priceLocked ? (
                  <div className="small text-muted mt-1">
                    Locked at the agreed price from the payment receipt. Only changes to travellers, pickup, vehicle type, travel date, rooms, add-ons, facilities, trip extension or customization re-price it.
                  </div>
                ) : (
                  <div className="small text-muted mt-1">
                    Updates from Dynamic Booking Details and customization.
                  </div>
                )}
              </Col>
            </Row>
            <div className="border rounded p-2 mt-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Calculation Details</Form.Label>
                {draftQuoteLoading && <Spinner size="sm" />}
              </div>
              {visibleCalculationQuote ? (
                <div className="small booking-calculation-details">
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Travellers</span>
                    <span>{visibleCalculationQuote.totalTravellers || form.noOfTravellers || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Base Trip Cost</span>
                    <span>₹{Number(correctedBookingCostBeforeProfit(visibleCalculationQuote)).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Hotel / Room Cost</span>
                    <span>₹{Number(totalHotelPayableForCalculation(visibleCalculationQuote)).toLocaleString()}</span>
                  </div>
                  {hotelRoomCostBreakdown(visibleCalculationQuote).map((hotel) => (
                    <div
                      className="d-flex justify-content-between booking-calculation-row booking-calculation-subrow"
                      key={hotel.key}
                    >
                      <span>
                        {hotel.label}
                        {hotel.selectedFacilities.length > 0 && (
                          <span className="d-block text-muted">
                            Selected facilities: {hotel.selectedFacilities.join(", ")}
                          </span>
                        )}
                      </span>
                      <span>₹{Number(hotel.cost || 0).toLocaleString()}</span>
                    </div>
                  ))}
                  {Number(visibleCalculationQuote.externalVendorFacilityCost || 0) > 0 && (
                    <div className="d-flex justify-content-between booking-calculation-row">
                      <span>
                        External Vendor Facilities
                        {externalVendorFacilityBreakdown(visibleCalculationQuote).length > 0 && (
                          <span className="d-block text-muted">
                            {externalVendorFacilityBreakdown(visibleCalculationQuote).join(", ")}
                          </span>
                        )}
                      </span>
                      <span>Rs. {Number(visibleCalculationQuote.externalVendorFacilityCost || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Vehicle KM Cost</span>
                    <span>₹{Number(visibleCalculationQuote.vehicleKmCost || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Driver Beta</span>
                    <span>₹{Number(visibleCalculationQuote.driverBetaCost || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Tolls</span>
                    <span>₹{Number(visibleCalculationQuote.tolls || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Parking</span>
                    <span>₹{Number(visibleCalculationQuote.parking || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between fw-semibold booking-calculation-row">
                    <span>Total Vehicle Cost</span>
                    <span>₹{Number(visibleCalculationQuote.vehicleCost || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Add-ons{addonNamesForCalculation(visibleCalculationQuote, form.addonSummarySnapshot) ? `: ${addonNamesForCalculation(visibleCalculationQuote, form.addonSummarySnapshot)}` : ""}</span>
                    <span>₹{Number(addonTotalFromQuote(visibleCalculationQuote)).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between booking-calculation-row">
                    <span>Profit</span>
                    <span>₹{Number(visibleCalculationQuote.profitAmount || 0).toLocaleString()}</span>
                  </div>
                  {customizationAdjustment !== 0 && (
                    <div className="d-flex justify-content-between booking-calculation-row">
                      <span>Customization</span>
                      <span>{customizationAdjustment < 0 ? "-" : "+"}₹{Math.abs(customizationAdjustment).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between fw-semibold booking-calculation-row booking-calculation-total">
                    <span>Final Total</span>
                    <span>₹{Number(visibleCalculationTotal || visibleCalculationQuote.roundedTotalPrice || visibleCalculationQuote.finalTripPrice || 0).toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between text-muted booking-calculation-row">
                    <span>Per Person</span>
                    <span>₹{Number(correctedBookingPerPersonTotal(visibleCalculationQuote, visibleCalculationTotal, form.noOfTravellers)).toLocaleString()}</span>
                  </div>
                  {[
                    ...(Array.isArray(visibleCalculationQuote.roomStayCosts) ? visibleCalculationQuote.roomStayCosts : []),
                    ...(Array.isArray(visibleCalculationQuote.extensionStayCosts) ? visibleCalculationQuote.extensionStayCosts : []),
                  ].length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Hotel / Room Details</div>
                      {[
                        ...(Array.isArray(visibleCalculationQuote.roomStayCosts) ? visibleCalculationQuote.roomStayCosts : []),
                        ...(Array.isArray(visibleCalculationQuote.extensionStayCosts) ? visibleCalculationQuote.extensionStayCosts : []),
                      ].map((stay) => {
                        const selectedFacilities = selectedHotelFacilityLabels(stay, visibleCalculationQuote);
                        const extensionStay = stay?.extensionStayKey
                          ? (visibleCalculationQuote?.quoteRequest?.extensionStays || []).find((item) => item?.stayKey === stay.extensionStayKey)
                          : null;
                        const extensionNightIndex = Number(extensionStay?.nightIndex || 1);
                        const baseNights = Number(visibleCalculationQuote?.baseDurationNights || quoteOptions?.durationNights || 0);
                        const stayOffset = extensionStay
                          ? (extensionStay.position === "POST" ? baseNights + extensionNightIndex - 1 : -extensionNightIndex)
                          : Number(stay.nightNumber || 1) - 1;
                        const stayLabel = extensionStay
                          ? `${extensionStay.position === "POST" ? "Extra post-night" : "Extra pre-night"} ${extensionNightIndex}`
                          : `Night ${stay.nightNumber}`;
                        return (
                        <div className="border-top py-1" key={`${stay.extensionStayKey || stay.roomRateId || "stay"}-${stay.nightNumber}-${stay.roomCategoryId || ""}`}>
                          <div>
                            {stayLabel}
                            {form.travelDate ? ` (${formatDate(addIsoDays(form.travelDate, stayOffset))})` : ""}
                            : {[stay.hotelCity, stay.hotelName, stay.hotelCode].filter(Boolean).join(" - ")}
                          </div>
                          <div className="text-muted">
                            {[stay.roomCategoryName, formatRoomAllocation(stay), `₹${Number(stay.roomPrice || 0).toLocaleString()}`]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                          {selectedFacilities.length > 0 && (
                            <div className="text-muted">
                              Selected facilities: {selectedFacilities.join(", ")}
                            </div>
                          )}
                          {externalVendorFacilityLabels(stay).length > 0 && (
                            <div className="text-muted">
                              External vendor: {externalVendorFacilityLabels(stay).join(", ")}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="small text-muted">
                  {draftQuoteError || "Change booking details to calculate the latest price."}
                </div>
              )}
              {draftQuoteError && visibleCalculationQuote && (
                <div className="small text-warning mt-1">{draftQuoteError}</div>
              )}
              {/* Allowed here, refused on the customer's page - the hotel has to agree it. */}
              {(draftQuote?.minGuestsWarnings || []).map((warning) => (
                <div className="small text-danger mt-1" key={warning}>
                  <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                  Below the hotel's minimum guests: {warning}
                </div>
              ))}
            </div>
            {quoteOptionsLoading && (
              <div className="small text-muted">Loading dynamic booking options...</div>
            )}
          </div>
        )}

        {/* Booking Type */}
        {!form.dynamicPricing && (
        <Row>
          <Col md={4} className="mb-2">
            <Form.Label>Booking Type</Form.Label>
            <Form.Select
              value={form.bookingType || 'PACKAGE'}
              onChange={(e) => {
                const v = e.target.value;
                update('bookingType', v);
                if (v === 'CUSTOM') {
                  update('priceListId', null);
                  update('priceList', null);
                }
              }}
              disabled={saving}
            >
              <option value="PACKAGE">PACKAGE</option>
              <option value="CUSTOM">CUSTOM</option>
            </Form.Select>
          </Col>
        </Row>
        )}

        {/* Price Option (editable) */}
        {form.dynamicPricing ? (
          <div className="border rounded p-3 mb-2">
            <div className="fw-semibold">Dynamic Quote Pricing</div>
            <div className="small text-muted">
              Price is calculated from selected travellers, pickup, vehicle, hotel rooms, and add-ons above.
            </div>
            {draftQuote ? (
              <div className="mt-2">
                <span className="fw-semibold">Total: </span>
                <span>₹{Number(draftQuoteFinalTotal || draftQuote.roundedTotalPrice || draftQuote.finalTripPrice || form.totalPrice || 0).toLocaleString()}</span>
              </div>
            ) : (
              <div className="small text-muted mt-2">
                {draftQuoteError || "Complete the dynamic selections to calculate price."}
              </div>
            )}
          </div>
        ) : (
        <Row>
          <Col md={12} className="mb-2">
            <Form.Label>{form.bookingType === 'CUSTOM' ? 'Custom Price' : 'Price Option'}</Form.Label>
            <div className="border rounded p-2">
              {form.bookingType === 'PACKAGE' ? (
                <>
                  <InputGroup className="mb-2">
                    <InputGroup.Text>Select</InputGroup.Text>
                    <Form.Select
                      value={form.priceListId || ""}
                      onChange={(e) => {
                        const id = e.target.value ? Number(e.target.value) : null;
                        const found = priceOptions.find((p) => p.id === id) || null;
                        update("priceListId", id);
                        update("priceList", found);
                      }}
                      disabled={priceLoading || !form.tripId}
                    >
                      <option value="">— Select price for trip —</option>
                      {priceOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.durationNights}N {p.durationDays}D · {p.vehicleType} — ₹{Number(p.price).toLocaleString()} {p.currencyCode || ''}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                  {priceLoading && (
                    <div className="small text-muted">Loading prices…</div>
                  )}
                  {!priceLoading && !priceOptions.length && (
                    <div className="small text-muted">No prices available for this trip.</div>
                  )}
                  {form.priceList && (
                    <div className="d-flex flex-wrap gap-3 align-items-center mt-2">
                      <div className="small text-muted">ID: #{form.priceList.id}</div>
                      <div className="fw-semibold">
                        {form.priceList.durationNights}N {form.priceList.durationDays}D
                      </div>
                      <div className="badge bg-light text-dark">
                        {form.priceList.vehicleType}
                      </div>
                      <div className="ms-auto">
                        {form.priceList.originalPrice ? (
                          <>
                            <span className="text-muted text-decoration-line-through small me-2">
                              ₹{Number(form.priceList.originalPrice).toLocaleString()}
                            </span>
                            <span className="fw-semibold">
                              ₹{Number(form.priceList.price).toLocaleString()} {form.priceList.currencyCode || ''}
                            </span>
                          </>
                        ) : (
                          <span className="fw-semibold">
                            ₹{Number(form.priceList.price).toLocaleString()} {form.priceList.currencyCode || ''}
                          </span>
                        )}
                      </div>
                      <div className="w-100 small text-muted">
                        Valid: {formatDateDMY(form.priceList.validFrom, '—')} → {formatDateDMY(form.priceList.validTo, '—')}
                      </div>
                      <div>
                        <Badge bg={form.priceList.isActive ? 'success' : 'secondary'}>
                          {form.priceList.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <InputGroup className="mb-2">
                  <InputGroup.Text>Price / Person</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.pricePerPerson || ''}
                    onChange={(e) => update('pricePerPerson', e.target.value)}
                    placeholder="Enter price per person"
                    disabled={saving}
                  />
                </InputGroup>
              )}
            </div>
          </Col>
        </Row>
        )}

        {/* Customer payments are recorded through the transaction ledger above. */}
        <Row>
          <Col md={6} className="mb-2">
            <Form.Label>Total Customer Payments Received</Form.Label>
            <InputGroup>
              <InputGroup.Text>₹</InputGroup.Text>
              <Form.Control
                type="number"
                min={0}
                step="0.01"
                value={form.advancePaid || ''}
                placeholder="0.00"
                onChange={(event) => update("advancePaid", event.target.value)}
                disabled={saving}
              />
            </InputGroup>
          </Col>
          {bookingFormSchedule ? (
            <Col md={6} className="mb-2">
              <Form.Label>Payment Schedule</Form.Label>
              <div className="border rounded px-2 py-1">
                <div className="small">Advance at booking: INR {bookingFormSchedule.advanceAmount.toLocaleString("en-IN")}</div>
                <PaymentScheduleLines schedule={bookingFormSchedule} />
              </div>
            </Col>
          ) : null}
        </Row>

        {/* Editable core fields */}
        <Row>
          <Col md={4} className="mb-2">
            <Form.Label>Travel Date</Form.Label>
            <IsoDatePicker
              value={form.travelDate || ""}
              onChange={(value) => update("travelDate", value)}
              disabled={saving}
            />
          </Col>
          <Col md={4} className="mb-2">
            <Form.Label>No. of Travellers</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={form.noOfTravellers}
              onChange={(e) => {
                const val = Math.max(0, Number(e.target.value || 0));
                update("noOfTravellers", String(val));
                setTravellers((arr) => {
                  const current = arr || [];
                  if (val > current.length) {
                    return [
                      ...current,
                      ...Array.from({ length: val - current.length }, () => ({ id: undefined, fullName: "", age: "", phone: "", idCardUrl: "" })),
                    ];
                  } else if (val < current.length) {
                    return current.slice(0, val);
                  }
                  return current;
                });
              }}
              disabled={saving}
            />
          </Col>
          <Col md={4} className="mb-2">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.tripStatus}
              onChange={(e) => update("tripStatus", e.target.value)}
              disabled={saving}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* Coupon management */}
        <Row>
          <Col md={8} className="mb-2">
            <Form.Label>Coupon</Form.Label>
            <InputGroup>
              <Form.Select
                value={form.couponId || ""}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  const found = coupons.find((c) => c.id === id);
                  update("couponId", id);
                  update("couponCode", found?.code || "");
                }}
                disabled={saving}
              >
                <option value="">— No coupon —</option>
                {coupons.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} {c.description ? `— ${c.description}` : ""}
                  </option>
                ))}
              </Form.Select>
              {form.couponId && (
                <Button
                  variant="outline-warning"
                  onClick={removeCoupon}
                  disabled={saving}
                  title="Remove coupon"
                >
                  Remove
                </Button>
              )}
            </InputGroup>
            {form.couponCode ? (
              <small className="text-muted">Selected: {form.couponCode}</small>
            ) : (
              <small className="text-muted">No coupon applied.</small>
            )}
          </Col>
          <Col md={4} className="mb-2">
            <Form.Label>Created At</Form.Label>
            <div className="border rounded p-2 text-muted small">
              {formatDateTime(form.createdAt) || "—"}
            </div>
          </Col>
        </Row>
        )}

        {/* Payment Summary */}
        <div className="booking-summary-section">
          <Form.Label className="fw-semibold">Payment Summary</Form.Label>
          <div className="border rounded p-3 booking-summary-card">
            {(form.bookingType === 'PACKAGE' && form.priceList) || form.bookingType === 'CUSTOM' ? (
              <div className="d-flex flex-column gap-1">
                <div className="d-flex justify-content-between">
                  <span>{form.dynamicPricing ? "Per Person (calculated)" : `Unit Price ${form.bookingType === 'CUSTOM' ? '(Custom)' : ''}`}</span>
                  <span>
                    ₹{Number(priceSummary.unit).toLocaleString()} {form.bookingType === 'CUSTOM' ? '' : (form.priceList?.currencyCode || '')}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Travellers</span>
                  <span>{priceSummary.travellers}</span>
                </div>
                <div className="d-flex justify-content-between fw-semibold">
                  <span>Trip Total</span>
                  <span>₹{Number(priceSummary.subtotal).toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Coupon Discount {form.couponCode ? `(${form.couponCode})` : ''}</span>
                  <span className={priceSummary.discount ? 'text-success' : 'text-muted'}>
                    - ₹{Number(priceSummary.discount).toLocaleString()}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Customer Payments Received</span>
                  <span className={priceSummary.advance ? 'text-success' : 'text-muted'}>
                    - ₹{Number(priceSummary.advance).toLocaleString()}
                  </span>
                </div>
                {priceSummary.note && (
                  <div className="small text-muted">{priceSummary.note}</div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Customer Balance Due</span>
                  <span>₹{Number(priceSummary.balance).toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <div className="text-muted small">
                No price option attached. Unable to compute total.
              </div>
            )}
          </div>
        </div>
            </div>
          </Tab>

          <Tab eventKey="tracking-plan" title="Day-Wise Plan">
            <div className="booking-edit-tab-pane">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <div className="fw-semibold">Day-Wise Plan</div>
                  <div className="small text-muted">
                    Driver tracking uses the Trip Default Plan unless booking-specific plan is enabled.
                  </div>
                </div>
                <Button size="sm" variant="outline-primary" onClick={addBookingPlanDay} disabled={saving || !form.useBookingDayWisePlan}>
                  + Add Day
                </Button>
              </div>

              <div className="border rounded p-3 mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div>
                    <div className="fw-semibold">
                      {form.useBookingDayWisePlan ? "Booking Specific Day-Wise Plan" : "Continue with Trip Default Plan"}
                    </div>
                    <div className="small text-muted">
                      {form.useBookingDayWisePlan
                        ? "This booking has its own copied plan and can be edited for tracking."
                        : "No booking override is saved. Tracking falls back to the trip plan."}
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id={`booking-plan-override-${form.id || "new"}`}
                    label="Booking Specific Plan"
                    checked={Boolean(form.useBookingDayWisePlan)}
                    disabled={saving}
                    onChange={(event) => setBookingPlanOverride(event.target.checked)}
                  />
                </div>
              </div>

              <div className="border rounded p-3 mb-3">
                <Form.Label className="small text-muted mb-1">Pickup Location Address Link</Form.Label>
                <Form.Control
                  value={form.pickupGoogleMapLink || ""}
                  onChange={(e) => update("pickupGoogleMapLink", e.target.value)}
                  placeholder="Paste Google Maps pickup address link"
                  disabled={saving}
                />
                <div className="small text-muted mt-1">
                  This link appears in the Driver Portal near Driver Reached the Pickup Location.
                </div>
              </div>

              <div className="border rounded p-3 mb-3">
                <Form.Label className="small text-muted mb-1">Legacy Refreshment Map Link</Form.Label>
                <Form.Control
                  value={form.refreshmentGoogleMapLink || ""}
                  onChange={(e) => update("refreshmentGoogleMapLink", e.target.value)}
                  placeholder="Paste Google Maps refreshment room address link"
                  disabled={saving}
                />
                <div className="small text-muted mt-1">
                  Fallback for older/manual bookings. New confirmed leads use the refreshment room selected in Dynamic Pricing.
                </div>
              </div>

              {(form.dayWisePlanDays || []).length ? (
                <div className="d-flex flex-column gap-3">
                  {(form.dayWisePlanDays || []).map((day, dayIdx) => (
                    <div className={`border rounded p-3 ${form.useBookingDayWisePlan ? "" : "bg-light"}`} key={`booking-plan-day-${day.key || dayIdx}`}>
                      <div className="row g-2 align-items-end mb-3">
                        <div className="col-md-2">
                          <Form.Label className="small text-muted mb-1">Day</Form.Label>
                          <Form.Control
                            value={day.day || ""}
                            onChange={(e) => updateBookingPlanDay(dayIdx, "day", e.target.value)}
                            placeholder={`Day ${dayIdx + 1}`}
                            disabled={saving || !form.useBookingDayWisePlan}
                          />
                        </div>
                        <div className="col-md-6">
                          <Form.Label className="small text-muted mb-1">Title / Route</Form.Label>
                          <Form.Control
                            value={day.title || ""}
                            onChange={(e) => updateBookingPlanDay(dayIdx, "title", e.target.value)}
                            placeholder="Optional title"
                            disabled={saving || !form.useBookingDayWisePlan}
                          />
                        </div>
                        <div className="col-md-4 d-flex flex-wrap justify-content-md-end gap-2">
                          <Button size="sm" variant="outline-secondary" disabled={dayIdx === 0 || saving || !form.useBookingDayWisePlan} onClick={() => moveBookingPlanDay(dayIdx, -1)}>
                            Up
                          </Button>
                          <Button size="sm" variant="outline-secondary" disabled={dayIdx === (form.dayWisePlanDays || []).length - 1 || saving || !form.useBookingDayWisePlan} onClick={() => moveBookingPlanDay(dayIdx, 1)}>
                            Down
                          </Button>
                          <Button size="sm" variant="outline-danger" disabled={saving || !form.useBookingDayWisePlan} onClick={() => removeBookingPlanDay(dayIdx)}>
                            Remove Day
                          </Button>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="small fw-semibold">Locations</div>
                        <Button size="sm" variant="outline-primary" disabled={saving || !form.useBookingDayWisePlan} onClick={() => addBookingPlanLocation(dayIdx)}>
                          + Add Location
                        </Button>
                      </div>

                      {(day.locations || []).length ? (
                        <div className="d-flex flex-column gap-2">
                          {(day.locations || []).map((location, locationIdx) => (
                            <div className="row g-2 align-items-end" key={`booking-plan-location-${location.key || `${dayIdx}-${locationIdx}`}`}>
                              <div className="col-md-4">
                                <Form.Label className="small text-muted mb-1">Location Name</Form.Label>
                                <Form.Control
                                  value={location.locationName || ""}
                                  onChange={(e) => updateBookingPlanLocation(dayIdx, locationIdx, "locationName", e.target.value)}
                                  placeholder="Place name"
                                  disabled={saving || !form.useBookingDayWisePlan}
                                />
                              </div>
                              <div className="col-md-3">
                                <Form.Label className="small text-muted mb-1">Google Map Link</Form.Label>
                                <Form.Control
                                  value={location.googleMapLink || ""}
                                  onChange={(e) => updateBookingPlanLocation(dayIdx, locationIdx, "googleMapLink", e.target.value)}
                                  placeholder="Paste map URL"
                                  disabled={saving || !form.useBookingDayWisePlan}
                                />
                              </div>
                              <div className="col-md-2">
                                <Form.Label className="small text-muted mb-1" title={PLACE_CIRCLE_HINT}>Circle (m)</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={PLACE_CIRCLE_MIN}
                                  max={PLACE_CIRCLE_MAX}
                                  step={50}
                                  value={location.arrivalRadiusM ?? ""}
                                  onChange={(e) => updateBookingPlanLocation(dayIdx, locationIdx, "arrivalRadiusM", e.target.value)}
                                  isInvalid={placeCircleInvalid(location.arrivalRadiusM)}
                                  placeholder="Default"
                                  title={PLACE_CIRCLE_HINT}
                                  disabled={saving || !form.useBookingDayWisePlan}
                                />
                              </div>
                              <div className="col-md-3 d-flex flex-wrap gap-2">
                                <Button size="sm" variant="outline-secondary" disabled={locationIdx === 0 || saving || !form.useBookingDayWisePlan} onClick={() => moveBookingPlanLocation(dayIdx, locationIdx, -1)}>
                                  Up
                                </Button>
                                <Button size="sm" variant="outline-secondary" disabled={locationIdx === (day.locations || []).length - 1 || saving || !form.useBookingDayWisePlan} onClick={() => moveBookingPlanLocation(dayIdx, locationIdx, 1)}>
                                  Down
                                </Button>
                                <Button size="sm" variant="outline-danger" disabled={saving || !form.useBookingDayWisePlan} onClick={() => removeBookingPlanLocation(dayIdx, locationIdx)}>
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted small">No locations added for this day.</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded p-3 text-muted small">
                  No day-wise plan is available from the trip yet.
                </div>
              )}
            </div>
          </Tab>

          <Tab eventKey="travellers" title={`Travellers (${(form.travellers || []).length})`}>
            <div className="booking-edit-tab-pane">

        {/* Travellers (editable) */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="mb-0">Travellers</Form.Label>
            <Button size="sm" variant="outline-primary" onClick={addTraveller} disabled={saving}>
              + Add Traveller
            </Button>
          </div>
          {(form.travellers || []).length ? (
            <div className="vstack gap-2">
              {form.travellers.map((t, idx) => (
                <div key={`trav-edit-${idx}`} className="border rounded p-2">
                  <div className="d-flex gap-2 align-items-end flex-wrap">
                    <Form.Group style={{ minWidth: 220, flex: 1 }}>
                      <Form.Label className="small">Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={t.fullName || t.name || ""}
                        onChange={(e) => editTraveller(idx, "fullName", e.target.value)}
                        disabled={saving}
                      />
                    </Form.Group>
                    <Form.Group style={{ width: 120 }}>
                      <Form.Label className="small">Age</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={t.age ?? ""}
                        onChange={(e) => editTraveller(idx, "age", e.target.value)}
                        disabled={saving}
                      />
                    </Form.Group>
                    <Form.Group style={{ minWidth: 200, flex: 1 }}>
                      <Form.Label className="small">Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={t.phone || ""}
                        onChange={(e) => editTraveller(idx, "phone", e.target.value)}
                        disabled={saving}
                      />
                    </Form.Group>
                    <Form.Group style={{ minWidth: 220 }}>
                      <Form.Label className="small">ID Card</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => uploadTravellerIdCard(idx, e.target.files?.[0])}
                        disabled={saving}
                      />
                      {t.idCardUrl ? (
                        <div className="small mt-1">
                          <a href={t.idCardUrl} target="_blank" rel="noreferrer">View uploaded ID</a>
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 ms-2"
                            onClick={() => downloadTravellerIdCard(t.idCardUrl)}
                            disabled={saving}
                          >
                            Download
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            className="text-danger p-0 ms-2"
                            onClick={() => editTraveller(idx, "idCardUrl", "")}
                            disabled={saving}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="small text-muted mt-1">No ID uploaded</div>
                      )}
                    </Form.Group>
                    <div className="ms-auto">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeTraveller(idx)}
                        disabled={saving}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted small">No travellers. Add at least one if needed.</div>
          )}
        </div>
            </div>
          </Tab>

          <Tab eventKey="history" title={pendingChanges.length ? `History (${pendingChanges.length})` : "History"}>
            <div className="booking-edit-tab-pane">

        <div className="border rounded p-3 booking-history-section">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0 fw-semibold">
              Change Justification <span className="text-muted fw-normal">(Optional)</span>
            </Form.Label>
            {pendingChanges.length > 0 && <Badge bg="warning" text="dark">{pendingChanges.length} pending changes</Badge>}
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            value={changeJustification}
            onChange={(event) => setChangeJustification(event.target.value)}
            placeholder={pendingChanges.length > 0 ? "Optionally explain why this booking is being changed" : "No changes detected"}
            disabled={saving || pendingChanges.length === 0}
          />
          {pendingChanges.length > 0 && (
            <div className="small text-muted mt-2">
              Changed: {pendingChanges.join(", ")}
            </div>
          )}
        </div>

        <div className="border rounded p-3">
          <Form.Label className="fw-semibold">Booking Change History</Form.Label>
          {revisionHistoryLoading && <div className="small text-muted mb-2">Loading detailed change history...</div>}
          {revisionHistoryError && <div className="small text-danger mb-2">{revisionHistoryError}</div>}
          {renderedChangeHistory.length > 0 ? (
            <div className="vstack gap-2">
              {renderedChangeHistory.map((entry, index) => {
                const changeLabels = revisionChangeLabels(entry);
                const diffDetails = bookingRevisionDiffDetails(entry);
                const canShowDetails = !entry.legacy && (entry.previousSnapshot || entry.updatedSnapshot);
                return (
                <div className="border rounded p-2" key={`${entry.changedAt || "change"}-${index}`}>
                  <div className="d-flex justify-content-between gap-2">
                    <span className="fw-semibold">{entry.changeReason || entry.justification || "No justification recorded"}</span>
                    <span className="small text-muted text-nowrap">{formatDateTime(entry.createdAt || entry.changedAt)}</span>
                  </div>
                  {changeLabels.length > 0 && (
                    <div className="small text-muted mt-1">{changeLabels.join(", ")}</div>
                  )}
                  {(entry.previousTotalPrice != null || entry.updatedTotalPrice != null) && (
                    <div className="small mt-1">
                      Price: ₹{Number(entry.previousTotalPrice || 0).toLocaleString()} → ₹{Number(entry.updatedTotalPrice || 0).toLocaleString()}
                    </div>
                  )}
                  {canShowDetails && (
                    <details className="mt-2">
                      <summary className="small text-primary" role="button">View details</summary>
                      {diffDetails.length > 0 ? (
                        <div className="table-responsive mt-2">
                          <Table size="sm" bordered className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>From</th>
                                <th>To</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diffDetails.map((detail) => (
                                <tr key={detail.label}>
                                  <td className="fw-semibold text-nowrap">{detail.label}</td>
                                  <td className="small">{detail.previous}</td>
                                  <td className="small">{detail.updated}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="small text-muted mt-2">
                          This revision has stored snapshots, but no field-level difference could be displayed.
                        </div>
                      )}
                    </details>
                  )}
                  {entry.legacy && (
                    <div className="small text-muted mt-2">
                      Detailed before/after values were not stored for this older history entry.
                    </div>
                  )}
                </div>
              );})}
            </div>
          ) : (
            <div className="small text-muted">No Admin booking changes have been recorded.</div>
          )}
        </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button onClick={save} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" /> Saving…
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------------- helpers ---------------- */
function pad(n) {
  return String(n).padStart(2, "0");
}
function toDateInputValue(d) {
  if (!d) return "";
  // accepts 'YYYY-MM-DD' or ISO string
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const day = pad(dt.getDate());
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}
function toDateTimeInputValue(value) {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:/.test(text)) return text.slice(0, 16);
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)) return text.replace(" ", "T").slice(0, 16);
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function formatDate(d) {
  return formatDateDMY(d, "—");
}
function formatDateTime(d) {
  return formatDateTimeDMY(d, "—");
}
// Who accepted for the hotel, how, and where they were - the proof recorded with the acceptance.
function HotelAcceptedBy({ name, via, latitude, longitude }) {
  const hasLocation = latitude != null && longitude != null;
  return (
    <div className="small">
      By <strong>{name}</strong>
      {via === "PORTAL" ? " via portal" : via === "LINK" ? " via link" : ""}
      {hasLocation && (
        <>
          {" · "}
          <a href={`https://www.google.com/maps?q=${latitude},${longitude}`} target="_blank" rel="noreferrer">
            <i className="bi bi-geo-alt-fill me-1" aria-hidden="true" />Map
          </a>
        </>
      )}
    </div>
  );
}

// What is left after the booking advance, split the way the customer pays it: 25% right after
// boarding, the rest on Day 1 after check-in. Bookings from before the schedule show nothing.
function PaymentScheduleLines({ schedule }) {
  if (!schedule) return null;
  const stage = (label, amount, due) => (
    <div className={`small ${due > 0 ? "text-danger" : "text-success"}`}>
      {label}: INR {Number(amount).toLocaleString("en-IN")}
      {" · "}
      {due <= 0 ? "Paid" : due < amount ? `INR ${Number(due).toLocaleString("en-IN")} due` : "Due"}
    </div>
  );
  return (
    <div className="mt-1 pt-1 border-top">
      {stage("After boarding (25%)", schedule.afterBoardingAmount, schedule.afterBoardingDue)}
      {stage("Day 1 after check-in", schedule.dayOneAmount, schedule.dayOneDue)}
    </div>
  );
}

// The customer's acceptance of the trip policy on the Trip Details link. Bookings created before
// the policy was published have no status and show nothing.
function CustomerPolicyStatus({ policy }) {
  if (!policy) return null;
  if (policy.status === "ACCEPTED") {
    return (
      <div className="small">
        <Badge bg="success">Policy accepted</Badge>
        <div className="mt-1">{formatDateTime(policy.acceptedAt)}</div>
        <HotelAcceptedBy name={policy.acceptedByName} latitude={policy.latitude} longitude={policy.longitude} />
      </div>
    );
  }
  if (policy.status === "OPENED") {
    return (
      <div className="small">
        <Badge bg="warning" text="dark">Policy opened, not accepted</Badge>
        <div className="mt-1">Last opened {formatDateTime(policy.lastOpenedAt)}</div>
        <PolicyLocationFailure policy={policy} />
      </div>
    );
  }
  return (
    <div className="small">
      <Badge bg="secondary">Policy link not opened</Badge>
    </div>
  );
}

const LOCATION_FAILURE_REASONS = {
  DENIED: "Location refused",
  UNAVAILABLE: "Phone could not find its location",
  TIMEOUT: "Location took too long",
  UNSUPPORTED: "Browser cannot share location",
  UNKNOWN: "Location failed",
};

// What to tell a customer who is stuck, going by the browser that refused.
function locationFixHint(browser, reason) {
  if (reason === "TIMEOUT") return "Move near a window or step outside, then tap Try again.";
  if (browser.inApp) {
    const app = browser.browser === "an app" ? "the app" : browser.browser;
    return `Open the link in ${browser.ios ? "Safari" : "Chrome"}, not inside ${app}.`;
  }
  if (browser.ios && browser.browser === "Safari") {
    return "Settings → Privacy & Security → Location Services ON → Safari Websites → While Using the App; then aA → Website Settings → Location → Allow.";
  }
  if (browser.ios) {
    return `Settings → Privacy & Security → Location Services ON → ${browser.browser} → While Using the App, or open the link in Safari.`;
  }
  if (browser.android) {
    return "Turn the phone's Location ON, then allow Location for the site (icon left of the web address → Permissions).";
  }
  return "Open the link on their phone.";
}

// The last time the Trip Details page could not get the customer's location: why, in which
// browser, and what to tell them. Hover for the browser's own message and full details.
function PolicyLocationFailure({ policy }) {
  if (!policy?.locationFailedAt) return null;
  const browser = describeBrowser(policy.locationFailureUserAgent);
  const count = Number(policy.locationFailureCount || 1);
  const details = [policy.locationFailureMessage, policy.locationFailureUserAgent].filter(Boolean).join("\n");
  return (
    <div className="mt-1" title={details || undefined}>
      <div className="fw-semibold text-danger">
        <i className="bi bi-geo-alt me-1" aria-hidden="true" />
        {LOCATION_FAILURE_REASONS[policy.locationFailureReason] || LOCATION_FAILURE_REASONS.UNKNOWN}
        {count > 1 ? ` ×${count}` : ""} · {formatDateTime(policy.locationFailedAt)}
      </div>
      <div>{browser.label}</div>
      <div className="text-muted">Tell them: {locationFixHint(browser, policy.locationFailureReason)}</div>
    </div>
  );
}

function customerPolicyFilterLabel(policy) {
  if (policy?.status === "ACCEPTED") return "Policy accepted";
  if (policy?.status === "OPENED") return "Policy opened, not accepted";
  return "Policy link not opened";
}

function formatHotelAcceptedAt(value) {
  if (!value) return "—";
  const text = String(value);
  const date = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(text) ? text : `${text}Z`);
  if (Number.isNaN(date.getTime())) return formatDateTime(value);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date).replace(",", "");
}
function statusBadgeVariant(status) {
  if (status === "COMPLETED") return "success";
  if (status === "ONGOING") return "warning";
  if (status === "UPCOMING") return "primary";
  if (status === "CANCELLED") return "danger";
  return "secondary";
}
function splitSnapshotList(value) {
  if (!value || value === "None") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
function roomRateHotelLabel(rate) {
  if (!rate) return "";
  return rate.hotelCode || rate.hotelName || "";
}
function roomRateHotelDisplayLabel(rate) {
  if (!rate) return "";
  return [rate.hotelCity, rate.hotelName, rate.hotelCode].filter(Boolean).join(" - ");
}
function roomRateCategoryLabel(rate) {
  if (!rate) return "";
  return rate.roomCategoryName || "";
}
function roomRateSummary(rate) {
  return buildRoomSummary(roomRateHotelLabel(rate), roomRateCategoryLabel(rate));
}
function buildRoomSummary(hotel, category) {
  return [
    hotel,
    category,
  ]
    .filter(Boolean)
    .join(" - ");
}
function parseRoomSummary(value) {
  if (!value) return { hotel: "", category: "" };
  const text = String(value).trim();
  const separator = text.lastIndexOf(" - ");
  if (separator < 0) return { hotel: text, category: "" };
  return {
    hotel: text.slice(0, separator).trim(),
    category: text.slice(separator + 3).trim(),
  };
}
function findSelectedRoomRate(rates, summary) {
  if (!summary) return null;
  const selected = parseRoomSummary(summary);
  const selectedHotel = String(selected.hotel || "").toLowerCase();
  const selectedCategory = String(selected.category || "").toLowerCase();
  const categoryMatches = (rate) => {
    const category = String(roomRateCategoryLabel(rate) || "").toLowerCase();
    return !selectedCategory || category === selectedCategory;
  };
  return (
    rates.find((rate) => roomRateSummary(rate) === summary) ||
    rates.find(
      (rate) =>
        roomRateHotelLabel(rate) === selected.hotel &&
        roomRateCategoryLabel(rate) === selected.category
    ) ||
    bestHotelLabelMatch(rates, selectedHotel, categoryMatches) ||
    bestRoomRateTextMatch(rates, summary) ||
    null
  );
}
// How well one rate's hotel labels match the hotel name saved on the booking. Partial overlap is
// scored by how much of the two labels actually coincide, so a short code such as "Homestay"
// cannot beat an exact hit on another hotel's "Homestay with pool".
function hotelLabelMatchScore(rate, selectedHotel) {
  if (!selectedHotel) return 0;
  const labels = [
    roomRateHotelLabel(rate),
    roomRateHotelDisplayLabel(rate),
    rate?.hotelName,
    rate?.hotelCode,
  ].filter(Boolean).map((item) => String(item).toLowerCase());
  return labels.reduce((best, label) => {
    if (label === selectedHotel) return Math.max(best, 100);
    if (!label.includes(selectedHotel) && !selectedHotel.includes(label)) return best;
    const overlap = Math.min(label.length, selectedHotel.length);
    const span = Math.max(label.length, selectedHotel.length);
    return Math.max(best, Math.round((90 * overlap) / span));
  }, 0);
}
// Picks the closest hotel rather than the first one that merely contains the saved text. A matching
// room category only breaks ties between equally good hotel matches.
function bestHotelLabelMatch(rates = [], selectedHotel = "", categoryMatches = () => false) {
  let best = null;
  let bestScore = 0;
  rates.forEach((rate) => {
    const hotelScore = hotelLabelMatchScore(rate, selectedHotel);
    if (!hotelScore) return;
    const score = hotelScore * 2 + (categoryMatches(rate) ? 1 : 0);
    if (score > bestScore) {
      best = rate;
      bestScore = score;
    }
  });
  return best;
}
function bestRoomRateTextMatch(rates = [], summary = "") {
  const selected = parseRoomSummary(summary);
  const hotelTokens = normalizedTextTokens(selected.hotel);
  const allTokens = normalizedTextTokens(summary);
  let best = null;
  let bestScore = 0;
  rates.forEach((rate) => {
    const text = normalizedSearchText([
      roomRateHotelLabel(rate),
      roomRateHotelDisplayLabel(rate),
      roomRateCategoryLabel(rate),
    ].join(" "));
    const hotelScore = hotelTokens.filter((token) => text.includes(token)).length;
    const totalScore = allTokens.filter((token) => text.includes(token)).length;
    const score = hotelScore * 3 + totalScore;
    if (score > bestScore) {
      best = rate;
      bestScore = score;
    }
  });
  return bestScore > 0 ? best : null;
}
function normalizedSearchText(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
function normalizedTextTokens(value) {
  return Array.from(new Set(
    normalizedSearchText(value)
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length > 2 && !["with", "and", "the", "for"].includes(token))
  ));
}
function parseNightRoomSnapshot(value) {
  const result = {};
  splitSnapshotList(value).forEach((item, index) => {
    const match = item.match(/^Night\s+(\d+)\s*:\s*(.+)$/i);
    if (match) {
      result[Number(match[1])] = match[2].trim();
    } else if (item) {
      result[index + 1] = item;
    }
  });
  return result;
}
function parseQuoteSnapshot(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

// Parsed once per snapshot, not on every render of a long bookings table.
const travellerBreakdownCache = new Map();

/**
 * Adults, kids with their ages, and payable travellers from a booking's saved quote. Payable is the
 * quote's own chargeable count (adults plus kids above the free age); older snapshots without it
 * fall back to the same rule using the saved age limit. Null when the booking has no quote.
 */
function bookingTravellerBreakdown(booking) {
  const raw = booking?.quoteSnapshot;
  if (!raw || typeof raw !== "string") return null;
  if (travellerBreakdownCache.has(raw)) return travellerBreakdownCache.get(raw);
  const snapshot = parseQuoteSnapshot(raw);
  let breakdown = null;
  if (snapshot) {
    const travellers = snapshot.travellers || {};
    const request = snapshot.quoteRequest || {};
    const adultsValue = travellers.adults ?? request.adults ?? snapshot.adultsCount;
    if (adultsValue != null) {
      const kidAges = (travellers.kidAges ?? request.kidAges ?? snapshot.kidsAges ?? [])
        .map((age) => Number(age))
        .filter((age) => Number.isFinite(age));
      const adults = Math.max(0, Number(adultsValue) || 0);
      const kids = Math.max(0, Number(travellers.kids ?? request.kids ?? snapshot.kidsCount ?? kidAges.length) || 0);
      let payable = snapshot.chargeablePersons != null ? Number(snapshot.chargeablePersons) : null;
      if (payable == null && snapshot.kidPerPersonAgeThreshold != null) {
        const threshold = Number(snapshot.kidPerPersonAgeThreshold);
        payable = adults + kidAges.filter((age) => age > threshold).length;
      }
      breakdown = { adults, kids, kidAges, payable: Number.isFinite(payable) ? payable : null };
    }
  }
  if (travellerBreakdownCache.size > 500) travellerBreakdownCache.clear();
  travellerBreakdownCache.set(raw, breakdown);
  return breakdown;
}
function customizationAdjustmentFromSnapshot(snapshot) {
  if (snapshot?.customizationRequired === false) return 0;
  const amount = Number(snapshot?.customizationPrice || 0);
  if (!amount) return 0;
  return String(snapshot?.customizationPriceAdjustment || "ADD").toUpperCase() === "REDUCE"
    ? -amount
    : amount;
}
function customizationAdjustmentFromForm(form) {
  if (!form?.customizationRequired) return 0;
  const amount = Number(form?.customizationPrice || 0);
  if (!amount) return 0;
  return String(form?.customizationPriceAdjustment || "ADD").toUpperCase() === "REDUCE"
    ? -amount
    : amount;
}
function customizationDescriptionFromBookingDescription(value) {
  const text = String(value || "");
  const match = text.match(/(?:^|\n)\s*Customization:\s*([^\n]+)/i);
  return match ? match[1].trim() : "";
}
function adjustedBookingTotal(booking) {
  const savedTotal = booking?.totalPrice ?? booking?.total_price;
  if (savedTotal != null && Number(savedTotal) > 0) return Number(savedTotal);
  const snapshot = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  if (booking?.dynamicPricing || booking?.dynamic_pricing) {
    const corrected = correctedBookingTotalFromBookingRow(booking, snapshot);
    if (corrected > 0) return corrected;
  }
  const adjustment = customizationAdjustmentFromSnapshot(snapshot);
  if (!adjustment) return savedTotal == null ? null : Number(savedTotal);
  const baseTotal = Number(snapshot?.roundedTotalPrice || snapshot?.finalTripPrice || 0);
  const adjusted = baseTotal > 0 ? Math.max(0, baseTotal + adjustment) : 0;
  return adjusted > 0 ? adjusted : (savedTotal == null ? null : Number(savedTotal));
}
function correctedBookingTotalFromBookingRow(booking, snapshot) {
  if (!booking || !snapshot) return 0;
  const serverTotal = Number(snapshot.roundedTotalPrice || snapshot.finalTripPrice || 0);
  if (serverTotal > 0) {
    return Math.max(0, serverTotal + customizationAdjustmentFromSnapshot(snapshot));
  }
  // Fallback for older snapshots with no server total: rebuild it from what we owe the vendors.
  const hotel = Number(booking.hotelVendorPayable ?? booking.hotel_vendor_payable ?? 0);
  const transport = Number(booking.transportVendorPayable ?? booking.transport_vendor_payable ?? 0);
  const addons = addonTotalFromQuote(snapshot);
  const externalVendor = Number(snapshot?.externalVendorFacilityCost || 0);
  const profit = Number(snapshot?.profitAmount || 0);
  const adjustment = customizationAdjustmentFromSnapshot(snapshot);
  const baseTotal = hotel + transport + addons + externalVendor + profit;
  if (baseTotal <= 0) return 0;
  const travellers = quoteTravellersForRounding(snapshot, booking.noOfTravellers || booking.no_of_travellers || 1);
  const roundedBaseTotal = roundBookingPerPerson(baseTotal / travellers, snapshot?.roundingType) * travellers;
  return Math.max(0, roundedBaseTotal + adjustment);
}
function hasSavedCalculationDetails(snapshot) {
  if (!snapshot) return false;
  if (Number(snapshot.roundedTotalPrice || snapshot.finalTripPrice || 0) > 0) return true;
  if (Number(snapshot.baseTripCost || snapshot.vehicleCost || snapshot.totalRoomCostAcrossAllNights || 0) > 0) return true;
  return Array.isArray(snapshot.roomStayCosts) && snapshot.roomStayCosts.length > 0;
}
function normalizedArrayKey(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value ?? ""))
    .join("|");
}
// Add-on option choices, order-free. One naming neither an option nor a head count is the default.
function addonOptionsKey(choices = []) {
  return (Array.isArray(choices) ? choices : [])
    .filter((choice) => choice?.optionId || choice?.people)
    .map((choice) => `${choice.addonId}:${choice.optionId ?? ""}:${choice.people ?? ""}`)
    .sort()
    .join("|");
}
// Fixed price pickup extension charges: the first vehicle's, then each added vehicle's by vehicle.
function extensionVehicleAmountsKey(firstAmount, additionalVehicles) {
  return normalizedArrayKey([
    Number(firstAmount || 0),
    ...(additionalVehicles || []).map((item) => `${Number(item?.vehicleId)}:${Number(item?.extensionVehicleAmount || 0)}`).sort(),
  ]);
}
function normalizedSelectionKey(selections = []) {
  return (Array.isArray(selections) ? selections : [])
    .map((selection) => [
      selection?.roomRateId ?? "",
      selection?.roomCategoryId ?? "",
      Number(selection?.units || 0),
    ].join(":"))
    .sort()
    .join("|");
}
function normalizedRoomRateNightSelectionKey(selections = []) {
  return (Array.isArray(selections) ? selections : [])
    .map((selection) => [
      selection?.roomRateId ?? "",
      Number(selection?.nightNumber || 1),
    ].join(":"))
    .sort()
    .join("|");
}
function normalizedHotelFacilityKey(selections = []) {
  return (Array.isArray(selections) ? selections : [])
    .filter((selection) => selection?.selected !== false)
    .map((selection) => [
      selection?.roomRateId ?? selection?.extensionStayKey ?? "",
      selection?.facilityId ?? "",
    ].join(":"))
    .sort()
    .join("|");
}
function normalizedExtensionStayKey(stays = []) {
  return (Array.isArray(stays) ? stays : [])
    .map((stay) => [
      stay?.position || "",
      Number(stay?.nightIndex || 1),
      stay?.scope || "BOTH",
      stay?.hotelId || "",
      normalizedSelectionKey((stay?.roomCategorySelections || []).map((selection) => ({
        ...selection,
        roomRateId: stay?.hotelId || "",
      }))),
    ].join(":"))
    .sort()
    .join("|");
}
function roomSelectionStateFromSnapshot(snapshot) {
  const roomUnitsByRate = {};
  const roomCategoryIdsByRate = {};
  const roomCategoryUnitsByKey = roomCategoryUnitsFromSnapshot(snapshot);
  (snapshot?.selectedRoomRateIds || []).forEach((roomRateId, index) => {
    roomUnitsByRate[String(roomRateId)] = Number(snapshot?.selectedRoomUnits?.[index] || 1);
  });
  (snapshot?.roomStayCosts || []).forEach((stay) => {
    if (!stay?.roomRateId) return;
    roomUnitsByRate[String(stay.roomRateId)] = roomUnitsFromAllocation(stay.roomAllocation) || 1;
    if (stay.roomCategoryId) {
      roomCategoryIdsByRate[String(stay.roomRateId)] = String(stay.roomCategoryId);
    }
  });
  (snapshot?.selectedRoomCategorySelections || []).forEach((selection) => {
    if (!selection?.roomRateId) return;
    roomUnitsByRate[String(selection.roomRateId)] = Math.max(1, Number(selection.units || 1));
    if (selection.roomCategoryId) {
      roomCategoryIdsByRate[String(selection.roomRateId)] = String(selection.roomCategoryId);
    }
  });
  return { roomUnitsByRate, roomCategoryIdsByRate, roomCategoryUnitsByKey };
}
// allowSuggestedUnits: the saved summary text carries hotel and room category names but no unit
// counts, so units can only be guessed from headcount. That guess is reasonable when seeding a
// brand new booking and wrong on a saved one, where it replaces what was actually booked.
function roomSelectionStateFromSummary(summary, quoteOptions, travellerCount = 1, allowSuggestedUnits = true) {
  const selectedRoomRateIdsByNight = {};
  const selectedRoomCityByNight = {};
  const roomUnitsByRate = {};
  const roomCategoryIdsByRate = {};
  const roomCategoryUnitsByKey = {};
  const selectedNightRooms = parseNightRoomSnapshot(summary);
  Object.entries(selectedNightRooms).forEach(([night, nightSummary]) => {
    String(nightSummary)
      .split(/\s+\|\s+|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((roomSummary) => {
      const configuredNightRates = (quoteOptions?.roomRates || []).filter((rate) => Number(rate.nightNumber || 1) === Number(night));
      const rate = findSelectedRoomRate(configuredNightRates, roomSummary) || findSelectedRoomRate(quoteOptions?.roomRates || [], roomSummary);
      if (!rate?.id) return;
      if (rate.hotelCity) {
        selectedRoomCityByNight[String(night)] = rate.hotelCity;
      }
      const selected = parseRoomSummary(roomSummary);
      const categories = roomCategoriesForRate(quoteOptions, rate);
      const category = categoryForSavedRoomSummary(categories, selected.category);
      selectedRoomRateIdsByNight[String(night)] = Array.from(new Set([
        ...(selectedRoomRateIdsByNight[String(night)] || []),
        String(rate.id),
      ]));
      roomUnitsByRate[String(rate.id)] = Math.max(1, Number(roomUnitsByRate[String(rate.id)] || 1));
      if (category?.id) {
        roomCategoryIdsByRate[String(rate.id)] = String(category.id);
        if (allowSuggestedUnits) {
          const suggestedUnits = suggestedRoomCategoryUnits(category, travellerCount);
          roomCategoryUnitsByKey[roomCategoryUnitKey(rate.id, category.id)] = suggestedUnits;
          roomUnitsByRate[String(rate.id)] = suggestedUnits;
        }
      }
    });
  });
  return { selectedRoomRateIdsByNight, selectedRoomCityByNight, roomUnitsByRate, roomCategoryIdsByRate, roomCategoryUnitsByKey };
}
function categoryForSavedRoomSummary(categories = [], categoryName = "") {
  if (!categories.length) return null;
  const selectedCategory = String(categoryName || "").trim().toLowerCase();
  if (!selectedCategory && categories.length === 1) return categories[0];
  const selectedTokens = normalizedTextTokens(selectedCategory);
  return (
    categories.find((option) => String(option.categoryName || "").trim().toLowerCase() === selectedCategory) ||
    categories.find((option) => String(option.roomCategoryName || "").trim().toLowerCase() === selectedCategory) ||
    categories.find((option) => String(option.categoryName || "").trim().toLowerCase().includes(selectedCategory)) ||
    categories.find((option) => selectedCategory.includes(String(option.categoryName || "").trim().toLowerCase())) ||
    categories.find((option) => selectedTokens.some((token) => normalizedSearchText(option.categoryName || option.roomCategoryName || "").includes(token))) ||
    (categories.length === 1 ? categories[0] : null)
  );
}
function suggestedRoomCategoryUnits(category, travellerCount = 1) {
  const maxMembers = Math.max(1, Number(category?.maxMemberCount || category?.memberCount || 1));
  const maximumUnits = Math.max(1, Number(category?.maximumUnits || 1));
  return Math.min(maximumUnits, Math.max(1, Math.ceil(Math.max(1, Number(travellerCount || 1)) / maxMembers)));
}
function bookingBaseHotelNightCount(snapshot, quoteOptions) {
  const explicitBase = Number(
    snapshot?.baseDurationNights ??
    snapshot?.quoteRequest?.baseDurationNights ??
    quoteOptions?.durationNights ??
    0
  );
  if (explicitBase > 0) return explicitBase;
  return Math.max(
    0,
    ...(Array.isArray(snapshot?.roomStayCosts)
      ? snapshot.roomStayCosts.map((stay) => Number(stay?.nightNumber || 0))
      : [])
  );
}
// Nights the booking has no stay for. The quote result is the priced truth; the saved request is
// the fallback for snapshots written before the result carried it.
function noStayNightsFromSnapshot(snapshot) {
  const nights = Array.isArray(snapshot?.noStayNights)
    ? snapshot.noStayNights
    : snapshot?.quoteRequest?.noStayNights || [];
  return Array.from(new Set(nights.map(Number).filter((night) => night > 0))).sort((a, b) => a - b);
}
function selectedRoomRateIdsByNightFromSnapshot(snapshot) {
  const result = {};
  [
    ...(snapshot?.selectedRoomRateNightSelections || []),
    ...(snapshot?.quoteRequest?.selectedRoomRateNightSelections || []),
  ].forEach((selection) => {
    const night = Number(selection?.nightNumber || 0);
    const roomRateId = selection?.roomRateId;
    if (!night || !roomRateId) return;
    const key = String(night);
    result[key] = Array.from(new Set([...(result[key] || []), String(roomRateId)]));
  });
  (snapshot?.selectedRoomRateIds || []).forEach((roomRateId) => {
    const stay = (snapshot?.roomStayCosts || []).find((item) => String(item.roomRateId) === String(roomRateId));
    const night = Number(stay?.nightNumber || 0);
    if (!night || !roomRateId) return;
    const key = String(night);
    result[key] = Array.from(new Set([...(result[key] || []), String(roomRateId)]));
  });
  (snapshot?.roomStayCosts || []).forEach((stay) => {
    const night = Number(stay?.nightNumber || 0);
    if (!night || !stay?.roomRateId) return;
    const key = String(night);
    result[key] = Array.from(new Set([...(result[key] || []), String(stay.roomRateId)]));
  });
  return result;
}
function selectedRoomCityByNightFromSnapshot(snapshot, quoteOptions) {
  const result = {
    ...(snapshot?.selectedRoomCityByNight || {}),
    ...(snapshot?.quoteRequest?.selectedRoomCityByNight || {}),
  };
  const roomRates = quoteOptions?.roomRates || [];
  const applyRateCity = (roomRateId, nightNumber) => {
    const night = Number(nightNumber || 0);
    if (!night || !roomRateId || result[String(night)]) return;
    const rate = roomRates.find((item) => String(item.id) === String(roomRateId));
    if (rate?.hotelCity) result[String(night)] = rate.hotelCity;
  };
  (snapshot?.selectedRoomRateNightSelections || []).forEach((selection) =>
    applyRateCity(selection?.roomRateId, selection?.nightNumber)
  );
  (snapshot?.quoteRequest?.selectedRoomRateNightSelections || []).forEach((selection) =>
    applyRateCity(selection?.roomRateId, selection?.nightNumber)
  );
  (snapshot?.roomStayCosts || []).forEach((stay) =>
    applyRateCity(stay?.roomRateId, stay?.nightNumber)
  );
  return result;
}
function roomCategoryUnitsFromSnapshot(snapshot) {
  const unitsByKey = {};
  (snapshot?.roomStayCosts || []).forEach((stay) => {
    if (!stay?.roomRateId || !stay?.roomCategoryId) return;
    unitsByKey[roomCategoryUnitKey(stay.roomRateId, stay.roomCategoryId)] =
      roomUnitsFromAllocation(stay.roomAllocation) || 1;
  });
  (snapshot?.selectedRoomCategorySelections || []).forEach((selection) => {
    if (!selection?.roomRateId || !selection?.roomCategoryId) return;
    unitsByKey[roomCategoryUnitKey(selection.roomRateId, selection.roomCategoryId)] =
      Math.max(1, Number(selection.units || 1));
  });
  return unitsByKey;
}
function roomCategoryUnitKey(roomRateId, categoryId) {
  return `${roomRateId}:${categoryId}`;
}
function bookingDurationLabel(snapshot) {
  // finalDuration* is post-extension; base/plain figures are the pre-extension length.
  const nights = Number(snapshot?.finalDurationNights ?? snapshot?.durationNights ?? snapshot?.baseDurationNights ?? 0);
  const days = Number(snapshot?.finalDurationDays ?? snapshot?.durationDays ?? snapshot?.baseDurationDays ?? 0);
  if (!nights && !days) return "";
  return `${nights} ${nights === 1 ? "Night" : "Nights"} & ${days} ${days === 1 ? "Day" : "Days"}`;
}
function hotelSummaryFromQuoteSnapshot(snapshot) {
  if (!Array.isArray(snapshot?.roomStayCosts)) return "";
  const nights = new Map();
  [...snapshot.roomStayCosts, ...(snapshot.extensionStayCosts || [])].forEach((stay) => {
    const night = Number(stay?.nightNumber || 0);
    if (!night) return;
    const summary = buildRoomSummary(
      stay.hotelCode || stay.hotelName || "",
      stay.roomCategoryName || ""
    );
    if (summary) nights.set(night, [...(nights.get(night) || []), summary]);
  });
  return Array.from(nights.entries())
    .sort(([left], [right]) => left - right)
    .map(([night, summaries]) => `Night ${night}: ${Array.from(new Set(summaries)).join(" | ")}`)
    .join(", ");
}
function hotelSearchTextFromQuoteSnapshot(snapshot) {
  const stays = [
    ...(Array.isArray(snapshot?.roomStayCosts) ? snapshot.roomStayCosts : []),
    ...(Array.isArray(snapshot?.extensionStayCosts) ? snapshot.extensionStayCosts : []),
  ];
  return stays
    .flatMap((stay) => [
      stay?.hotelName,
      stay?.hotelCode,
      stay?.hotelCity,
      stay?.city,
      stay?.roomCategoryName,
    ])
    .filter(Boolean)
    .join(" ");
}
function hotelSummaryFromRoomRateIds(snapshot, quoteOptions) {
  const roomRates = quoteOptions?.roomRates || [];
  const nights = new Map();
  (snapshot?.roomStayCosts || []).forEach((stay) => {
    const night = Number(stay?.nightNumber || 0);
    const rate = roomRates.find((item) => String(item.id) === String(stay?.roomRateId));
    if (!night || !rate) return;
    const category = roomCategoryForSelection(quoteOptions, rate, stay?.roomCategoryId);
    const summary = buildRoomSummary(roomRateHotelLabel(rate), category?.categoryName || stay?.roomCategoryName || "");
    if (summary) nights.set(night, [...(nights.get(night) || []), summary]);
  });
  (snapshot?.selectedRoomRateIds || []).forEach((roomRateId) => {
    const rate = roomRates.find((item) => String(item.id) === String(roomRateId));
    const mappedNight = [
      ...(snapshot?.selectedRoomRateNightSelections || []),
      ...(snapshot?.quoteRequest?.selectedRoomRateNightSelections || []),
    ].find((selection) => String(selection?.roomRateId) === String(roomRateId))?.nightNumber;
    const night = Number(mappedNight || rate?.nightNumber || 0);
    if (!night || !rate) return;
    const selection = (snapshot?.selectedRoomCategorySelections || []).find(
      (item) => String(item.roomRateId) === String(roomRateId)
    );
    const category = roomCategoryForSelection(quoteOptions, rate, selection?.roomCategoryId);
    const summary = buildRoomSummary(roomRateHotelLabel(rate), category?.categoryName || "");
    if (summary) nights.set(night, [...(nights.get(night) || []), summary]);
  });
  return Array.from(nights.entries())
    .sort(([left], [right]) => left - right)
    .map(([night, summaries]) => `Night ${night}: ${Array.from(new Set(summaries)).join(" | ")}`)
    .join(", ");
}
function roomCategoriesForRate(quoteOptions, rate) {
  if (!rate) return [];
  const hotel = (quoteOptions?.availableHotels || []).find(
    (item) => String(item.id) === String(rate.hotelId)
  );
  return (hotel?.roomCategories || []).filter((category) => category.active !== false);
}
function roomCategoryForSelection(quoteOptions, rate, categoryId) {
  return roomCategoriesForRate(quoteOptions, rate).find(
    (category) => String(category.id) === String(categoryId)
  ) || null;
}
function hasBookingAddons(booking) {
  const summary = String(booking?.addonSummarySnapshot ?? booking?.addon_summary_snapshot ?? "").trim();
  if (summary && summary.toLowerCase() !== "none") return true;
  const quote = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  return Array.isArray(quote?.selectedAddons) && quote.selectedAddons.length > 0;
}

// The driver, the hotels and any e-pass decide readiness - they are what can sink a trip (a
// missing e-pass stops the vehicle at the checkpoint). Add-ons and hotel facilities are ticked off
// in the Trip column but never hold the booking back.
function bookingPreparationReady(booking) {
  const driversAssigned = booking?.allDriversAssigned ?? booking?.driverAssignedToTrip;
  return Boolean(driversAssigned && booking?.hotelsBlocked) && !bookingEpassesPending(booking);
}

// [owner, items] pairs for an owner-grouped dropdown; owners and items in alphabetical order.
function groupedByOwner(items, ownerOf) {
  const groups = (items || []).reduce((map, item) => {
    const owner = ownerOf(item);
    (map[owner] = map[owner] || []).push(item);
    return map;
  }, {});
  return Object.entries(groups)
    .sort(([ownerA], [ownerB]) => ownerA.localeCompare(ownerB))
    .map(([owner, list]) => [
      owner,
      [...list].sort((a, b) => String(a.name || a.registrationNumber || "").localeCompare(String(b.name || b.registrationNumber || ""))),
    ]);
}

// A booking's vehicles added on request, added up - e.g. what their transport vendors are owed.
function extraVehicleTotal(booking, key) {
  return (Array.isArray(booking?.extraVehicles) ? booking.extraVehicles : [])
    .reduce((total, extra) => total + Number(extra?.[key] || 0), 0);
}

function bookingAddonNames(booking) {
  const fromSummary = splitSnapshotList(booking?.addonSummarySnapshot ?? booking?.addon_summary_snapshot);
  if (fromSummary.length) return fromSummary;
  const quote = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  return (Array.isArray(quote?.selectedAddons) ? quote.selectedAddons : [])
    .map((addon) => addon?.name)
    .filter(Boolean);
}

/**
 * Hotel facilities the hotel staff must be reminded about, night by night and hotel by hotel:
 * those IO Trips pays for separately (Included externally) and the paid extras the customer opted
 * for. Facilities the hotel simply includes are left out - there is nothing to arrange.
 */
function bookingFacilityChecklist(booking) {
  const snapshot = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  const stays = [
    ...(Array.isArray(snapshot?.roomStayCosts) ? snapshot.roomStayCosts : []),
    ...(Array.isArray(snapshot?.extensionStayCosts) ? snapshot.extensionStayCosts : []),
  ];
  const groups = new Map();
  stays.forEach((stay, index) => {
    const night = Math.max(1, Number(stay?.nightNumber) || index + 1);
    const hotelName = stay?.hotelName || stay?.hotelCode || "Hotel";
    const hotelKey = String(stay?.hotelId ?? hotelName.toLowerCase());
    const selectedExternal = (stay?.selectedHotelFacilities || [])
      .filter((facility) => facility?.selected !== false && facility?.name && facility?.costBehavior === "INCLUDED_EXTERNAL");
    const external = selectedExternal.length
      ? selectedExternal
      : (stay?.facilities || []).filter((facility) =>
          facility?.name && facility?.available === true && facilityCostBehavior(facility) === "INCLUDED_EXTERNAL");
    const items = [
      ...external.map((facility) => ({ name: facility.name, kind: "Arrange externally" })),
      ...selectedOptionalPaidHotelFacilities(stay, snapshot).map((facility) => ({ name: facility.name, kind: "Customer opted" })),
    ];
    if (!items.length) return;
    const groupKey = `${night}|${hotelKey}`;
    const group = groups.get(groupKey) || {
      key: groupKey,
      night,
      date: booking?.travelDate ? formatDate(addIsoDays(booking.travelDate, night - 1)) : "",
      hotelName,
      items: [],
    };
    items.forEach((item) => {
      const key = `${groupKey}|${String(item.name).trim().toLowerCase()}`;
      if (!group.items.some((existing) => existing.key === key)) group.items.push({ ...item, key });
    });
    groups.set(groupKey, group);
  });
  return Array.from(groups.values()).sort((a, b) => a.night - b.night || a.hotelName.localeCompare(b.hotelName));
}

function TripArrangementChecklist({ booking, busy, onChange }) {
  const addonNames = bookingAddonNames(booking);
  const facilityGroups = bookingFacilityChecklist(booking);
  if (!addonNames.length && !facilityGroups.length) return null;
  const checked = new Set(Array.isArray(booking.facilityChecks) ? booking.facilityChecks : []);
  const toggleFacility = (key, on) => {
    const next = new Set(checked);
    if (on) next.add(key);
    else next.delete(key);
    onChange({ facilityChecks: Array.from(next) });
  };
  return (
    <div className="d-flex flex-column gap-1 mt-2 small">
      {addonNames.length > 0 && (
        <Form.Check
          type="checkbox"
          id={`booking-${booking.id}-addons`}
          label={<span><span className="fw-semibold">Add-ons:</span> {addonNames.join(", ")}</span>}
          checked={Boolean(booking.addonsArranged)}
          disabled={busy}
          onChange={(event) => onChange({ addonsArranged: event.target.checked })}
        />
      )}
      {facilityGroups.map((group) => (
        <div key={group.key}>
          <div className="fw-semibold">
            Night {group.night}{group.date ? ` - ${group.date}` : ""} · {group.hotelName}
          </div>
          {group.items.map((item) => (
            <Form.Check
              key={item.key}
              type="checkbox"
              id={`booking-${booking.id}-facility-${item.key}`}
              label={<span>{item.name} <span className="text-muted">({item.kind})</span></span>}
              checked={checked.has(item.key)}
              disabled={busy}
              onChange={(event) => toggleFacility(item.key, event.target.checked)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
function bookingChangeDetails(before, after, draftQuote) {
  if (!before || !after) return [];
  const fields = [
    ["Adults", Number(before.adults || 0), Number(after.adults || 0)],
    ["Kids", Number(before.kids || 0), Number(after.kids || 0)],
    ["Kid ages", before.kidAges || [], after.kidAges || []],
    ["Kid vehicle seats", before.kidVehicleSeatNeeded || [], after.kidVehicleSeatNeeded || []],
    ["Trip start date", before.travelDate || "", after.travelDate || ""],
    ["Vehicle pickup date/time", before.pickupDateTime || "", after.pickupDateTime || ""],
    ["Vehicle drop date/time", before.dropDateTime || "", after.dropDateTime || ""],
    ["Pickup", before.pickupLabel || "", after.pickupLabel || ""],
    ["Pickup map link", before.pickupGoogleMapLink || "", after.pickupGoogleMapLink || ""],
    ["Refreshment map link", before.refreshmentGoogleMapLink || "", after.refreshmentGoogleMapLink || ""],
    ["Vehicle", before.vehicleNameSnapshot || "", after.vehicleNameSnapshot || ""],
    ["Vehicle AC", before.acTypeSnapshot || "", after.acTypeSnapshot || ""],
    ["Hotel / Rooms", before.hotelSummarySnapshot || "", after.hotelSummarySnapshot || ""],
    ["Room units", before.roomUnitsByRate || {}, after.roomUnitsByRate || {}],
    ["Room categories", before.roomCategoryIdsByRate || {}, after.roomCategoryIdsByRate || {}],
    ["Room category units", before.roomCategoryUnitsByKey || {}, after.roomCategoryUnitsByKey || {}],
    ["Add-ons", before.addonSummarySnapshot || "", after.addonSummarySnapshot || ""],
    ["Trip extension required", Boolean(before.tripExtensionRequired), Boolean(after.tripExtensionRequired)],
    ["Trip extension pre-nights", Number(before.extraPreNights || 0), Number(after.extraPreNights || 0)],
    ["Trip extension post-nights", Number(before.extraPostNights || 0), Number(after.extraPostNights || 0)],
    ["Trip extension stays", before.extensionStays || [], after.extensionStays || []],
    ["Trip extension note", before.tripExtensionNote || "", after.tripExtensionNote || ""],
    ["Customization required", Boolean(before.customizationRequired), Boolean(after.customizationRequired)],
    ["Customization details", before.customizationDescription || "", after.customizationDescription || ""],
    ["Customization amount", Number(before.customizationPrice || 0), Number(after.customizationPrice || 0)],
    ["Customization type", before.customizationPriceAdjustment || "ADD", after.customizationPriceAdjustment || "ADD"],
    ["Total price", Number(before.totalPrice || 0), Number(after.totalPrice || draftQuote?.roundedTotalPrice || draftQuote?.finalTripPrice || 0)],
    ["Advance paid", Number(before.advancePaid || 0), Number(after.advancePaid || 0)],
    ["Cashback per adult", Number(before.cashbackAmountPerPerson || 0), Number(after.cashbackAmountPerPerson || 0)],
    ["Total cashback", Number(before.totalCashbackAmount || 0), Number(after.totalCashbackAmount || 0)],
    ["Cashback review video", Boolean(before.cashbackReviewVideoReceived), Boolean(after.cashbackReviewVideoReceived)],
    ["Cashback travel photos", Boolean(before.cashbackTravelPhotosReceived), Boolean(after.cashbackTravelPhotosReceived)],
    ["Cashback Google review", Boolean(before.cashbackGoogleReviewReceived), Boolean(after.cashbackGoogleReviewReceived)],
    ["Cashback paid", Boolean(before.cashbackPaid), Boolean(after.cashbackPaid)],
    ["Cashback paid amount", Number(before.cashbackPaidAmount || 0), Number(after.cashbackPaidAmount || 0)],
    ["Can ask for review", Boolean(before.cashbackReviewRequestAllowed), Boolean(after.cashbackReviewRequestAllowed)],
    ["Review follow-up note", before.cashbackReviewRequestNote || "", after.cashbackReviewRequestNote || ""],
    ["Driver assigned to Trip", Boolean(before.driverAssignedToTrip), Boolean(after.driverAssignedToTrip)],
    ["Hotels blocked", Boolean(before.hotelsBlocked), Boolean(after.hotelsBlocked)],
    ["Add-ons arranged", Boolean(before.addonsArranged), Boolean(after.addonsArranged)],
    ["Breakfast arranged", Boolean(before.breakfastArranged), Boolean(after.breakfastArranged)],
    ["Coupon", before.couponId || null, after.couponId || null],
    ["Status", before.tripStatus || "", after.tripStatus || ""],
    ["Assigned vehicle", before.assignedVehicleId || null, after.assignedVehicleId || null],
    ["Driver", before.driverId || null, after.driverId || null],
    ["Booking type", before.bookingType || "", after.bookingType || ""],
    ["Price option", before.priceListId || null, after.priceListId || null],
    ["Custom price", Number(before.pricePerPerson || 0), Number(after.pricePerPerson || 0)],
    ["Description", before.description || "", after.description || ""],
    ["Notes", before.bookingNotes || "", after.bookingNotes || ""],
    ["Travellers", before.travellers || [], after.travellers || []],
    ["Day-wise tracking plan", before.dayWisePlanDays || [], after.dayWisePlanDays || []],
  ];
  return fields
    .filter(([, previous, updated]) => stableJson(previous) !== stableJson(updated))
    .map(([label]) => label);
}

const REVISION_DIRECT_FIELDS = [
  ["Customer", "customerName"],
  ["Trip", "tripTitle"],
  ["Trip start date", "travelDate", "date"],
  ["Vehicle pickup date/time", "pickupDateTime", "datetime"],
  ["Vehicle drop date/time", "dropDateTime", "datetime"],
  ["Travellers", "travellersCount"],
  ["Booking type", "bookingType"],
  ["Pickup", "pickupLabel"],
  ["Vehicle", "vehicle"],
  ["Vehicle AC", "acType"],
  ["Add-ons", "addonSummary"],
  ["Customization details", "description"],
  ["Notes", "bookingNotes"],
  ["Travelling contact name", "travellingContactName"],
  ["Travelling contact phone", "travellingContactPhone"],
  ["Customer payments received", "advancePaid", "money"],
  ["Total price", "totalPrice", "money"],
  ["Cashback per adult", "cashbackAmountPerPerson", "money"],
  ["Total cashback", "totalCashbackAmount", "money"],
  ["Can ask for review", "cashbackReviewRequestAllowed", "boolean"],
  ["Review follow-up note", "cashbackReviewRequestNote"],
  ["Coupon", "couponId"],
  ["Status", "tripStatus"],
  ["Driver assigned to Trip", "driverAssignedToTrip", "boolean"],
  ["Hotels blocked", "hotelsBlocked", "boolean"],
  ["Add-ons arranged", "addonsArranged", "boolean"],
  ["Breakfast arranged", "breakfastArranged", "boolean"],
  ["Assigned vehicle", "assignedVehicleName"],
  ["Driver", "driverName"],
];

function revisionChangeLabels(entry) {
  const raw = Array.isArray(entry?.changes)
    ? entry.changes
    : String(entry?.changedFields || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
  return raw.map(revisionFieldLabel);
}

function revisionFieldLabel(field) {
  const labels = {
    customerName: "Customer",
    tripTitle: "Trip",
    travelDate: "Trip start date",
    pickupDateTime: "Vehicle pickup date/time",
    dropDateTime: "Vehicle drop date/time",
    travellersCount: "Travellers",
    pricePerPerson: "Custom price",
    advancePaid: "Customer payments received",
    bookingNotes: "Notes",
    travellingContactName: "Travelling contact name",
    travellingContactPhone: "Travelling contact phone",
    pickupLabel: "Pickup",
    acType: "Vehicle AC",
    hotelSummary: "Hotel / Rooms",
    addonSummary: "Add-ons",
    quoteSnapshot: "Dynamic booking details",
    dayWisePlan: "Day-wise tracking plan",
    totalPrice: "Total price",
    cashbackAmountPerPerson: "Cashback per adult",
    totalCashbackAmount: "Total cashback",
    cashbackReviewRequestAllowed: "Can ask for review",
    cashbackReviewRequestNote: "Review follow-up note",
    tripStatus: "Status",
    driverAssignedToTrip: "Driver assigned to Trip",
    hotelsBlocked: "Hotels blocked",
    addonsArranged: "Add-ons arranged",
    breakfastArranged: "Breakfast arranged",
    assignedVehicleName: "Assigned vehicle",
    driverName: "Driver",
  };
  return labels[field] || String(field || "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (char) => char.toUpperCase());
}

function bookingRevisionDiffDetails(entry) {
  const previous = parseSnapshotObject(entry?.previousSnapshot);
  const updated = parseSnapshotObject(entry?.updatedSnapshot);
  if (!previous || !updated || (!Object.keys(previous).length && !Object.keys(updated).length)) return [];
  const details = [];
  const addDetail = (label, previousValue, updatedValue) => {
    const previousText = revisionDisplayValue(previousValue);
    const updatedText = revisionDisplayValue(updatedValue);
    if (previousText !== updatedText) details.push({ label, previous: previousText, updated: updatedText });
  };

  addDetail("Hotel / Room details", revisionRooms(previous), revisionRooms(updated));
  addDetail("Dynamic quote total", revisionQuoteTotal(previous), revisionQuoteTotal(updated));
  addDetail("Dynamic vehicle cost", moneyOrDash(readPath(previous, "quoteSnapshot.vehicleCost")), moneyOrDash(readPath(updated, "quoteSnapshot.vehicleCost")));
  addDetail("Dynamic add-ons", revisionQuoteAddons(previous), revisionQuoteAddons(updated));

  REVISION_DIRECT_FIELDS.forEach(([label, key, type]) => {
    addDetail(label, revisionTypedValue(previous[key], type), revisionTypedValue(updated[key], type));
  });
  return details;
}

function parseSnapshotObject(value) {
  if (!value) return {};
  if (typeof value === "string") return parseQuoteSnapshot(value) || {};
  return value;
}

function readPath(value, path) {
  return String(path).split(".").reduce((current, key) => current == null ? undefined : current[key], value);
}

function revisionTypedValue(value, type) {
  if (type === "money") return moneyOrDash(value);
  if (type === "date") return formatDate(value);
  if (type === "datetime") return formatDateTime(value);
  if (type === "boolean") return value ? "Yes" : "No";
  return value;
}

function revisionDisplayValue(value) {
  if (value == null || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.map(revisionDisplayValue).join("; ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function moneyOrDash(value) {
  const amount = Number(value || 0);
  return amount ? `₹${amount.toLocaleString("en-IN")}` : "-";
}

function revisionQuoteTotal(snapshot) {
  return moneyOrDash(readPath(snapshot, "quoteSnapshot.roundedTotalPrice") || readPath(snapshot, "quoteSnapshot.finalTripPrice"));
}

function revisionQuoteAddons(snapshot) {
  const addons = readPath(snapshot, "quoteSnapshot.selectedAddons");
  if (!Array.isArray(addons) || !addons.length) return "-";
  return addons.map((addon) => addon?.name || addon?.addonName || "").filter(Boolean).join(", ") || "-";
}

function revisionRooms(snapshot) {
  const stays = readPath(snapshot, "quoteSnapshot.roomStayCosts");
  if (!Array.isArray(stays) || !stays.length) return snapshot?.hotelSummary || "-";
  return stays.map((stay, index) => {
    const night = stay?.nightNumber || index + 1;
    const city = stay?.hotelCity ? `${stay.hotelCity} - ` : "";
    const hotel = stay?.hotelName || stay?.hotelCode || "Hotel";
    const room = stay?.roomCategoryName || stay?.roomLabel || "Room";
    const units = stay?.roomAllocation?.totalUnitsProvided || stay?.units || "";
    const cost = moneyOrDash(stay?.roomPrice);
    return `Night ${night}: ${city}${hotel} - ${room}${units ? ` (${units} unit${Number(units) === 1 ? "" : "s"})` : ""}${cost !== "-" ? ` - ${cost}` : ""}`;
  }).join("; ");
}
function stableJson(value) {
  if (Array.isArray(value)) return JSON.stringify(value.map(stableObject));
  if (value && typeof value === "object") return JSON.stringify(stableObject(value));
  return JSON.stringify(value);
}

function normalizeDayWisePlan(plan) {
  const days = Array.isArray(plan)
    ? plan
    : Array.isArray(plan?.days)
      ? plan.days
      : Array.isArray(plan?.dayWisePlan)
        ? plan.dayWisePlan
        : [];
  return days.map((day, index) => ({
    key: day?.key || day?.dayKey || `day_${index}`,
    day: day?.day || day?.dayLabel || `Day ${index + 1}`,
    title: day?.title || day?.dayTitle || day?.route || "",
    locations: Array.isArray(day?.locations)
      ? day.locations.map((location, locationIndex) => ({
          key: location?.key || location?.placeKey || `place_${index}_${locationIndex}`,
          locationName: location?.locationName || location?.name || location?.title || location?.label || "",
          googleMapLink: location?.googleMapLink || location?.mapLink || location?.googleMapsUrl || location?.url || "",
          arrivalRadiusM: location?.arrivalRadiusM ?? "",
        }))
      : [],
  }));
}

function pickupGoogleMapLinkFromPlan(plan) {
  if (!plan || Array.isArray(plan)) return "";
  return plan.pickupGoogleMapLink || plan.pickupMapLink || plan.pickupGoogleMapsUrl || plan.pickupLocationMapLink || "";
}

function refreshmentGoogleMapLinkFromPlan(plan) {
  if (!plan || Array.isArray(plan)) return "";
  return plan.refreshmentGoogleMapLink || "";
}

function buildBookingDayWisePlan(days = [], pickupGoogleMapLink = "", refreshmentGoogleMapLink = "") {
  const normalizedDays = days
    .map((day, dayIndex) => ({
      key: day.key || `day_${dayIndex}`,
      day: (day.day || `Day ${dayIndex + 1}`).trim(),
      title: (day.title || "").trim(),
      locations: (day.locations || [])
        .map((location, locationIndex) => withPlaceCircle({
          key: location.key || `place_${dayIndex}_${locationIndex}`,
          locationName: (location.locationName || "").trim(),
          googleMapLink: (location.googleMapLink || "").trim(),
        }, location.arrivalRadiusM))
        .filter((location) => location.locationName || location.googleMapLink),
    }))
    .filter((day) => day.day || day.title || day.locations.length);
  return {
    pickupGoogleMapLink: (pickupGoogleMapLink || "").trim(),
    refreshmentGoogleMapLink: (refreshmentGoogleMapLink || "").trim(),
    days: normalizedDays,
  };
}

function clonePlanWithStableKeys(days = []) {
  return (days || []).map((day, dayIndex) => ({
    key: day?.key || newPlanKey("day"),
    day: day?.day || `Day ${dayIndex + 1}`,
    title: day?.title || "",
    locations: (day?.locations || []).map((location) => ({
      key: location?.key || newPlanKey("place"),
      locationName: location?.locationName || "",
      googleMapLink: location?.googleMapLink || "",
      arrivalRadiusM: location?.arrivalRadiusM ?? "",
    })),
  }));
}

function hasDynamicQuoteOptions(options) {
  return Boolean(
    options &&
      (
        (Array.isArray(options.pickups) && options.pickups.length) ||
        (Array.isArray(options.vehicles) && options.vehicles.length) ||
        (Array.isArray(options.roomRates) && options.roomRates.length) ||
        (Array.isArray(options.availableHotels) && options.availableHotels.length) ||
        (Array.isArray(options.addons) && options.addons.length)
      )
  );
}

function newPlanKey(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
function stableObject(value) {
  if (Array.isArray(value)) return value.map(stableObject);
  if (!value || typeof value !== "object") return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = stableObject(value[key]);
      return result;
    }, {});
}
function addonTotalFromQuote(quote) {
  const explicitTotal = quote?.addonsCost ?? quote?.addonCost ?? quote?.mandalpattiJeepCost;
  if (explicitTotal !== undefined && explicitTotal !== null && explicitTotal !== "") {
    return Math.max(0, Number(explicitTotal || 0));
  }
  const selectedAddons = Array.isArray(quote?.selectedAddons) ? quote.selectedAddons : [];
  const selectedTotal = selectedAddons.reduce(
    (total, addon) => total + Number(addon?.selectedCost ?? addon?.cost ?? 0),
    0
  );
  if (selectedTotal > 0) return selectedTotal;
  const base = Number(quote?.baseTripCost || 0);
  const rooms = Number(quote?.totalRoomCostAcrossAllNights || 0);
  const vehicle = Number(quote?.vehicleCost || 0);
  const breakfast = Number(quote?.coorgBreakfastCost || 0);
  return Math.max(0, base - rooms - vehicle - breakfast);
}
function addonNamesForCalculation(quote, fallbackSummary) {
  const selected = Array.isArray(quote?.selectedAddons) ? quote.selectedAddons : [];
  // "Bus (AC)" from the quote; the saved summary only knows "Bus", so that one is not listed twice.
  const selectedNames = selected
    .map((addon) => addon?.displayName || addon?.name || addon?.addonName)
    .filter(Boolean);
  const plainNames = new Set(selected.map((addon) => addon?.name || addon?.addonName).filter(Boolean));
  const fallbackNames = splitSnapshotList(fallbackSummary).filter((name) => name && name !== "None" && !plainNames.has(name));
  return Array.from(new Set([...(selectedNames || []), ...(fallbackNames || [])])).join(", ");
}
function totalHotelPayableForCalculation(quote) {
  return hotelRoomCostBreakdown(quote).reduce((sum, hotel) => sum + Number(hotel.cost || 0), 0);
}
function hotelFacilityItemsForBooking(rate = null, stay = null) {
  const items = [];
  const upsert = (facility) => {
    const name = facility?.name;
    if (!name) return;
    const existing = items.find((item) => item.name === name);
    if (existing) {
      existing.available = existing.available || facility.available === true;
      existing.costBehavior = mergeFacilityCostBehavior(existing, facility);
      existing.optionalWithCost = existing.optionalWithCost || facilityCostBehavior(facility) === "OPTIONAL_PAID";
      existing.extraCost = Number(existing.extraCost || 0) || Number(facility.extraCost || 0);
      existing.pricingType = existing.pricingType || facility.pricingType || "PER_ACTIVITY";
      existing.id = existing.id || facility.id;
      return;
    }
    items.push({
      id: facility.id,
      name,
      available: facility.available === true,
      optionalWithCost: facilityCostBehavior(facility) === "OPTIONAL_PAID",
      costBehavior: facilityCostBehavior(facility),
      pricingType: facility.pricingType || "PER_ACTIVITY",
      extraCost: Number(facility.extraCost || 0),
    });
  };

  [rate?.facilities, stay?.facilities].forEach((facilities) => {
    (facilities || [])
      .filter((facility) => facility?.name && facility?.active !== false)
      .forEach(upsert);
  });

  const addFlag = (label, value) => upsert({ name: label, available: value === true });
  addFlag("Breakfast", stay?.breakfast ?? rate?.breakfast);
  addFlag("Dinner", stay?.dinner ?? rate?.dinner);
  addFlag("Campfire", stay?.campfire ?? rate?.campfire);
  addFlag("Swimming Pool", stay?.swimmingPool ?? rate?.swimmingPool);

  return items;
}
function facilityCostBehavior(facility) {
  if (facility?.costBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  if (facility?.costBehavior === "OPTIONAL_PAID" || facility?.optionalWithCost === true) return "OPTIONAL_PAID";
  if (Number(facility?.extraCost || 0) > 0) return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}
function mergeFacilityCostBehavior(existing, next) {
  const existingBehavior = facilityCostBehavior(existing);
  const nextBehavior = facilityCostBehavior(next);
  if (existingBehavior === "OPTIONAL_PAID" || nextBehavior === "OPTIONAL_PAID") return "OPTIONAL_PAID";
  if (existingBehavior === "INCLUDED_EXTERNAL" || nextBehavior === "INCLUDED_EXTERNAL") return "INCLUDED_EXTERNAL";
  return "INCLUDED_BY_HOTEL";
}
function hotelFacilityKeysFromSnapshot(snapshot) {
  return Object.fromEntries(
    hotelFacilitySelectionsFromSnapshot(snapshot)
      .map((selection) => [`${selection.roomRateId}:${selection.facilityId}`, true])
  );
}
// Extension-night facility selections (e.g. Campfire on a post-trip home stay) have no roomRateId
// to key off - the saved request tags them with the stay's stayKey via extensionStayKey instead.
// Re-attach those onto each restored stay's selectedFacilityKeys so the checkboxes (and the price
// they drive) match what was actually booked.
function extensionStaysFromSnapshot(snapshot) {
  const stays = Array.isArray(snapshot?.quoteRequest?.extensionStays)
    ? snapshot.quoteRequest.extensionStays
    : [];
  const facilities = Array.isArray(snapshot?.quoteRequest?.selectedHotelFacilities)
    ? snapshot.quoteRequest.selectedHotelFacilities
    : [];
  return stays.map((stay) => {
    const selectedFacilityKeys = Object.fromEntries(
      facilities
        .filter(
          (facility) =>
            facility?.extensionStayKey &&
            facility.extensionStayKey === stay?.stayKey &&
            facility?.facilityId != null &&
            facility?.selected !== false
        )
        .map((facility) => [facility.facilityId, true])
    );
    return { ...stay, selectedFacilityKeys };
  });
}
function hotelFacilitySelectionsFromKeys(keys = {}, selectedRoomRateIds = []) {
  const allowedRates = new Set((selectedRoomRateIds || []).map((id) => String(id)));
  return Object.entries(keys || {})
    .filter(([, selected]) => selected === true)
    .map(([key]) => {
      const [roomRateId, facilityId] = key.split(":");
      return { roomRateId, facilityId };
    })
    .filter((selection) => selection.roomRateId && selection.facilityId)
    .filter((selection) => allowedRates.size === 0 || allowedRates.has(String(selection.roomRateId)))
    .map((selection) => ({
      roomRateId: Number(selection.roomRateId),
      facilityId: Number(selection.facilityId),
      selected: true,
    }));
}
function correctedBookingCalculationTotal(quote, customizationAdjustment = 0) {
  if (!quote) return 0;
  const adjustment = Number(customizationAdjustment || 0);
  // The server total is the single source of truth: it is the figure the customer was quoted, the
  // one the lead-confirmation receipt prints and the one stored on the booking. Re-deriving it here
  // drifted from the server - the local sum below leaves out meal cost and rounds over a different
  // traveller count than the server does - which is how one booking showed a different total on
  // every screen. The local sum stays only as a fallback for quotes that carry no server total.
  const serverTotal = Number(quote.roundedTotalPrice || quote.finalTripPrice || 0);
  if (serverTotal > 0) return Math.max(0, serverTotal + adjustment);
  const profit = Number(quote?.profitAmount || 0);
  const baseTotal = correctedBookingCostBeforeProfit(quote) + profit;
  if (baseTotal <= 0) return 0;
  const travellers = quoteTravellersForRounding(quote, 1);
  const roundedBaseTotal = roundBookingPerPerson(baseTotal / travellers, quote?.roundingType) * travellers;
  return Math.max(0, roundedBaseTotal + adjustment);
}
function correctedBookingCostBeforeProfit(quote) {
  if (!quote) return 0;
  const hotel = totalHotelPayableForCalculation(quote);
  const vehicle = Number(quote?.vehicleCost || 0);
  const addons = addonTotalFromQuote(quote);
  const externalVendor = Number(quote?.externalVendorFacilityCost || 0);
  return Math.max(0, hotel + vehicle + addons + externalVendor);
}
function correctedBookingPerPersonTotal(quote, total, fallbackTravellers = 1) {
  const travellers = quoteTravellersForRounding(quote, fallbackTravellers);
  return Math.round(Number(total || 0) / travellers);
}
function quoteTravellersForRounding(quote, fallbackTravellers = 1) {
  return Math.max(1, Number(quote?.totalTravellers || fallbackTravellers || 1));
}
function roundBookingPerPerson(value, roundingType = "NEAREST_100") {
  const amount = Number(value || 0);
  if (amount <= 0) return 0;
  const type = String(roundingType || "NEAREST_100").toUpperCase();
  if (type === "NONE") return Math.round(amount);
  const roundedHundred = Math.ceil(amount / 100) * 100;
  if (type === "NEAREST_99") return Math.max(0, roundedHundred - 1);
  return roundedHundred;
}
function hotelFacilitySelectionsFromSnapshot(snapshot, selectedRoomRateIds = []) {
  const allowedRates = new Set((selectedRoomRateIds || []).map((id) => String(id)));
  const selections = new Map();
  const addSelection = (roomRateId, facilityId, selected = true) => {
    if (!selected || roomRateId == null || facilityId == null) return;
    if (allowedRates.size > 0 && !allowedRates.has(String(roomRateId))) return;
    const key = `${roomRateId}:${facilityId}`;
    selections.set(key, {
      roomRateId: Number(roomRateId),
      facilityId: Number(facilityId),
      selected: true,
    });
  };

  (snapshot?.quoteRequest?.selectedHotelFacilities || []).forEach((selection) => {
    addSelection(selection?.roomRateId, selection?.facilityId, selection?.selected !== false);
  });

  (snapshot?.roomStayCosts || []).forEach((stay) => {
    (stay?.selectedHotelFacilities || []).forEach((facility) => {
      const behavior = String(facility?.costBehavior || "").toUpperCase();
      if (behavior && behavior !== "OPTIONAL_PAID") return;
      addSelection(stay?.roomRateId, facility?.id, facility?.selected !== false);
    });
  });

  return Array.from(selections.values());
}
function hotelRoomCostBreakdown(quote) {
  const hotels = new Map();
  const roomStayCosts = quote?.roomStayCosts || quote;
  const allStayCosts = [
    ...(Array.isArray(roomStayCosts) ? roomStayCosts : []),
    ...(Array.isArray(quote?.extensionStayCosts) ? quote.extensionStayCosts : []),
  ];
  allStayCosts.forEach((stay) => {
    const key = String(
      stay.hotelId ?? `${stay.hotelCity || ""}:${stay.hotelName || stay.hotelCode || ""}`
    );
    const existing = hotels.get(key) || {
      key,
      label:
        [stay.hotelCity, stay.hotelName || stay.hotelCode].filter(Boolean).join(" - ") ||
        "Hotel",
      cost: 0,
      selectedFacilities: [],
    };
    existing.cost += Number(stay.roomPrice || 0) + optionalPaidFacilityCostForStay(stay, quote);
    existing.selectedFacilities = Array.from(new Set([
      ...existing.selectedFacilities,
      ...selectedHotelFacilityLabels(stay, quote),
    ]));
    hotels.set(key, existing);
  });
  return Array.from(hotels.values());
}
function optionalPaidFacilityCostForStay(stay, quote = {}) {
  const explicit = Math.max(0, Number(stay?.optionalHotelFacilityCost || 0) - Number(stay?.externalVendorFacilityCost || 0));
  if (explicit > 0) return explicit;
  const selectedFromStay = (stay?.selectedHotelFacilities || [])
    .filter((facility) => facility?.selected !== false && facility?.costBehavior !== "INCLUDED_EXTERNAL")
    .reduce((sum, facility) => sum + Number(facility?.extraCost || 0), 0);
  if (selectedFromStay > 0) return selectedFromStay;
  const selectedIds = (quote?.quoteRequest?.selectedHotelFacilities || [])
    .filter((item) => item?.selected !== false && String(item?.roomRateId) === String(stay?.roomRateId))
    .map((item) => String(item.facilityId));
  if (!selectedIds.length) return 0;
  const travellers = Math.max(1, Number(quote?.totalTravellers || 0) || (Number(quote?.quoteRequest?.adults || 0) + Number(quote?.quoteRequest?.kids || 0)));
  return (stay?.facilities || [])
    .filter((facility) => facility?.id != null && selectedIds.includes(String(facility.id)) && facility?.costBehavior === "OPTIONAL_PAID")
    .reduce((sum, facility) => {
      const amount = Number(facility.extraCost || 0);
      return sum + (facility.pricingType === "PER_PERSON" ? amount * travellers : amount);
    }, 0);
}
function externalVendorFacilityBreakdown(quote) {
  const labels = [];
  [...(quote?.roomStayCosts || []), ...(quote?.extensionStayCosts || [])].forEach((stay) => {
    externalVendorFacilityLabels(stay).forEach((label) => labels.push(label));
  });
  return Array.from(new Set(labels));
}

function externalVendorFacilityLabels(stay) {
  return (stay?.selectedHotelFacilities || [])
    .filter((facility) => facility?.selected !== false && facility?.name && facility?.costBehavior === "INCLUDED_EXTERNAL")
    .map((facility) => {
      const amount = Number(facility.extraCost || 0);
      const vendor = facility.externalVendorName ? ` - ${facility.externalVendorName}` : "";
      const suffix = amount > 0 ? ` Rs. ${amount.toLocaleString("en-IN")}${facility.pricingType === "PER_PERSON" ? " / person / night" : " / night"}` : "";
      return `${facility.name}${suffix}${vendor}`;
    });
}
function externalVendorPaymentDetails(formOrBooking, quoteOptions = null, externalVendors = []) {
  const snapshot = parseQuoteSnapshot(formOrBooking?.quoteSnapshot ?? formOrBooking?.quote_snapshot);
  const travelDate = formOrBooking?.travelDate ?? formOrBooking?.travel_date;
  const rows = [];
  (snapshot?.roomStayCosts || []).forEach((stay, index) => {
    const configuredFacilities = facilityOptionsForStay(stay, snapshot, quoteOptions);
    const selectedExternalFacilities = (stay?.selectedHotelFacilities || [])
      .filter((facility) => facility?.selected !== false && facility?.name && facility?.costBehavior === "INCLUDED_EXTERNAL")
      .map((facility) => enrichExternalVendorFacility(facility, configuredFacilities));
    const fallbackExternalFacilities = selectedExternalFacilities.length
      ? []
      : configuredFacilities.filter((facility) =>
          facility?.name &&
          facility?.available === true &&
          facilityCostBehavior(facility) === "INCLUDED_EXTERNAL"
        );
    [...selectedExternalFacilities, ...fallbackExternalFacilities]
      .forEach((facility) => {
        const enrichedFacility = enrichExternalVendorFacilityFromMaster(facility, stay, externalVendors);
        const amount = Number(facility.extraCost || 0);
        rows.push({
          key: `${stay?.roomRateId || index}-${enrichedFacility.id || enrichedFacility.name}`,
          vendorName: enrichedFacility.externalVendorName || "External vendor",
          vendorMeta: [enrichedFacility.externalVendorType, enrichedFacility.externalVendorCity].filter(Boolean).join(" - "),
          contactName: enrichedFacility.externalVendorContactName || "",
          contactNumber: enrichedFacility.externalVendorContactNumber || "",
          upiNumber: enrichedFacility.externalVendorUpiNumber || "",
          upiScannerUrl: enrichedFacility.externalVendorUpiScannerUrl || "",
          bankAccountHolder: enrichedFacility.externalVendorBankAccountHolder || "",
          bankAccountNumber: enrichedFacility.externalVendorBankAccountNumber || "",
          bankIfsc: enrichedFacility.externalVendorBankIfsc || "",
          facilityName: enrichedFacility.name,
          stayLabel: [
            stay?.nightNumber ? `Night ${stay.nightNumber}` : "",
            travelDate && stay?.nightNumber ? formatDate(addIsoDays(travelDate, Number(stay.nightNumber || 1) - 1)) : "",
            stay?.hotelCity || stay?.city || "",
            stay?.hotelName || stay?.hotelCode || "",
            stay?.roomCategoryName || stay?.roomLabel || "",
          ].filter(Boolean).join(" | "),
          amount,
          pricingType: enrichedFacility.pricingType,
        });
      });
  });
  return rows;
}
function enrichExternalVendorFacilityFromMaster(facility, stay, externalVendors = []) {
  if (facility?.externalVendorName && facility.externalVendorName !== "External vendor") {
    return facility;
  }
  const city = String(stay?.hotelCity || stay?.city || facility?.externalVendorCity || "").trim().toLowerCase();
  const candidates = (externalVendors || []).filter((vendor) =>
    vendor?.active !== false &&
    (!city || String(vendor.city || "").trim().toLowerCase() === city)
  );
  const preferred = candidates.find((vendor) =>
    String(vendor.vendorType || "").toUpperCase().includes("RESTAURANT")
  ) || candidates[0];
  if (!preferred) return facility;
  return {
    ...facility,
    externalVendorId: facility.externalVendorId ?? preferred.id,
    externalVendorName: facility.externalVendorName && facility.externalVendorName !== "External vendor" ? facility.externalVendorName : preferred.name,
    externalVendorCity: facility.externalVendorCity || preferred.city,
    externalVendorType: facility.externalVendorType || preferred.vendorType,
    externalVendorContactName: facility.externalVendorContactName || preferred.contactName,
    externalVendorContactNumber: facility.externalVendorContactNumber || preferred.contactNumber,
    externalVendorUpiNumber: facility.externalVendorUpiNumber || preferred.upiNumber,
    externalVendorUpiScannerUrl: facility.externalVendorUpiScannerUrl || preferred.upiScannerUrl,
    externalVendorBankAccountHolder: facility.externalVendorBankAccountHolder || preferred.bankAccountHolder,
    externalVendorBankAccountNumber: facility.externalVendorBankAccountNumber || preferred.bankAccountNumber,
    externalVendorBankIfsc: facility.externalVendorBankIfsc || preferred.bankIfsc,
  };
}
function facilityOptionsForStay(stay, snapshot = {}, quoteOptions = null) {
  const fromStay = Array.isArray(stay?.facilities) ? stay.facilities : [];
  const fromSnapshotRate = (snapshot?.roomStayCosts || [])
    .find((item) => String(item?.roomRateId) === String(stay?.roomRateId))
    ?.facilities || [];
  const fromQuoteOptionsRate = (quoteOptions?.roomRates || [])
    .find((rate) => String(rate?.id) === String(stay?.roomRateId))
    ?.facilities || [];
  return [...fromStay, ...fromSnapshotRate, ...fromQuoteOptionsRate];
}
function enrichExternalVendorFacility(facility, configuredFacilities = []) {
  const configured = configuredFacilities.find((item) =>
    (facility?.id != null && item?.id != null && String(item.id) === String(facility.id)) ||
    (facility?.name && item?.name && String(item.name).toLowerCase() === String(facility.name).toLowerCase())
  );
  if (!configured) return facility;
  return {
    ...facility,
    externalVendorId: facility.externalVendorId ?? configured.externalVendorId,
    externalVendorName: facility.externalVendorName || configured.externalVendorName,
    externalVendorCity: facility.externalVendorCity || configured.externalVendorCity,
    externalVendorType: facility.externalVendorType || configured.externalVendorType,
    externalVendorContactName: facility.externalVendorContactName || configured.externalVendorContactName,
    externalVendorContactNumber: facility.externalVendorContactNumber || configured.externalVendorContactNumber,
    externalVendorUpiNumber: facility.externalVendorUpiNumber || configured.externalVendorUpiNumber,
    externalVendorUpiScannerUrl: facility.externalVendorUpiScannerUrl || configured.externalVendorUpiScannerUrl,
    externalVendorBankAccountHolder: facility.externalVendorBankAccountHolder || configured.externalVendorBankAccountHolder,
    externalVendorBankAccountNumber: facility.externalVendorBankAccountNumber || configured.externalVendorBankAccountNumber,
    externalVendorBankIfsc: facility.externalVendorBankIfsc || configured.externalVendorBankIfsc,
  };
}

function addIsoDays(value, days) {
  if (!value) return "";
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day + Number(days || 0));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function isChikmagalurJeepAddon(addon) {
  const name = String(addon?.name || "").toLowerCase();
  return (name.includes("chikmagalur") || name.includes("chikmangalur")) && name.includes("jeep");
}
function extensionStaysRequestPayload(extensionStays) {
  return (extensionStays || [])
    .filter((stay) => stay.hotelId && stay.roomCategoryId)
    .map(({ selectedFacilityKeys, ...stay }) => ({
      ...stay,
      scope: stay.scope || "BOTH",
      nightIndex: Math.max(1, Number(stay.nightIndex || 1)),
      hotelId: Number(stay.hotelId),
      roomCategorySelections: (stay.roomCategorySelections || [])
        .filter((selection) => selection.roomCategoryId && Number(selection.units || 0) > 0)
        .map((selection) => ({
          roomCategoryId: Number(selection.roomCategoryId),
          units: Math.max(1, Number(selection.units || 1)),
        })),
      roomCategoryId: Number(stay.roomCategoryId),
      units: Math.max(1, Number(stay.units || 1)),
    }));
}
function withPayableAmountLine(text, amount) {
  if (!text) return text;
  return `${text}\n\nAmount Payable: INR ${Number(amount || 0).toLocaleString("en-IN")}`;
}
// The driver guide video goes right after "Your trip has been assigned." so it is the message's
// first link and WhatsApp shows its thumbnail. Messages without that line (no driver assigned yet)
// are left as they are.
const DRIVER_ASSIGNED_LINE = "Your trip has been assigned.\n";
function withDriverGuideVideo(text, videoUrl) {
  if (!text || !videoUrl || !text.includes(DRIVER_ASSIGNED_LINE)) return text;
  return text.replace(
    DRIVER_ASSIGNED_LINE,
    `${DRIVER_ASSIGNED_LINE}\n*Please watch this video before the trip:*\n${videoUrl}\n`,
  );
}
// The Driver App part of the Copy Driver Message, at the end: where to install the app, the videos
// showing how - every language that has one, each under its name in its own script so the driver
// opens theirs - and this trip's link, which opens the trip in the app. Each link on its own line
// so WhatsApp makes it tappable. A vehicle with no trip link of its own (vehicle 2 onwards of a
// group) gets the app and the videos only. Like the guide video, only for messages to an assigned driver.
function withDriverAppSection(text, { appVideos, tripUrl }) {
  if (!text || !text.includes(DRIVER_ASSIGNED_LINE)) return text;
  const lines = [
    "*IO TRIPS DRIVER APP*",
    "Keep location sharing ON in the app for the whole trip.",
    "",
    "*1. Install the app (first time only):*",
    DRIVER_APP_DOWNLOAD_URL,
  ];
  const videos = appVideos || [];
  if (videos.length) {
    lines.push("", "*2. How to install and use the app - watch in your language:*");
    videos.forEach(({ label, url }) => lines.push(`${label}:`, url));
  }
  if (tripUrl) lines.push("", `*${videos.length ? 3 : 2}. Open this trip in the app:*`, tripUrl);
  return `${text.trimEnd()}\n\n${lines.join("\n")}`;
}
function hotelPayableForKey(key, summaries) {
  if (!Array.isArray(summaries)) return null;
  const found =
    summaries.find((s) => s.hotelId != null && String(s.hotelId) === String(key)) ||
    summaries.find((s) => `${s.city || ""}:${s.hotelName || ""}`.toLowerCase() === String(key).toLowerCase());
  return found ? Number(found.totalPayable || 0) : null;
}
function bookingVehicleDates(b) {
  const snapshot = parseQuoteSnapshot(b?.quoteSnapshot ?? b?.quote_snapshot);
  const baseNights = b?.priceList?.durationNights ?? b?.durationNights;
  const { start, end } = vehicleTripDates(b?.travelDate, snapshot, baseNights);
  // Hotel check-in and checkout, outside the vehicle dates when an extra night is hotel only.
  const stay = tripStayDates(b?.travelDate, snapshot, baseNights);
  return { vehicleStartDate: start || b?.travelDate || "", vehicleEndDate: end, stayStartDate: stay.start, stayEndDate: stay.end };
}
function timeOfDateTime(value, fallback) {
  const match = String(value || "").match(/T(\d{2}:\d{2})/);
  return match ? match[1] : fallback;
}
// The drop the driver is told: the saved time, moved to the vehicle's last day when an extra night
// keeps the vehicle past it. Mirrors effectiveDropDateTime on the backend.
function bookingDropDateTime(b) {
  const saved = toDateTimeInputValue(b?.raw?.dropDateTime ?? b?.raw?.drop_date_time);
  if (!saved || !b?.vehicleEndDate) return saved;
  const expected = `${b.vehicleEndDate}${saved.slice(10)}`;
  return expected > saved ? expected : saved;
}
function bookingTravelEndDate(b) {
  if (b?.vehicleEndDate) return b.vehicleEndDate;
  if (!b?.travelDate) return "";
  const drop = b.raw?.dropDateTime ?? b.raw?.drop_date_time ?? b.dropDateTime ?? b.drop_date_time;
  if (drop) return String(drop).slice(0, 10);
  const nights = Number(b.raw?.priceList?.durationNights ?? b.raw?.durationNights ?? 0);
  if (nights > 0) return addIsoDays(b.travelDate, nights);
  return "";
}
function formatRoomAllocation(stay) {
  const units = roomUnitsFromAllocation(stay?.roomAllocation) || Number(stay?.units || 0);
  return units > 0 ? `Total Units Provided: ${units}` : "";
}
const DOCUMENT_NO_STAY_TEXT = "No stay (own arrangement)";
// One saved stay as a document line, { night, text }; the day label is added by
// bookingDocumentDayLines.
function stayCostDocumentLine(stay, snapshot) {
  // The hotel's display name (code) only - receipts and policies never carry the real name.
  const hotel = stay?.hotelCode || "Hotel";
  const room = stay?.roomCategoryName || stay?.roomLabel || stay?.categoryName || "Room";
  const selection = (snapshot?.selectedRoomCategorySelections || []).find((item) =>
    String(item?.roomRateId) === String(stay?.roomRateId) &&
    (!stay?.roomCategoryId || String(item?.roomCategoryId) === String(stay.roomCategoryId))
  );
  const units = Number(selection?.units || 0) || Number(stay?.units || 0) || roomUnitsFromAllocation(stay?.roomAllocation);
  return {
    night: Number(stay?.nightNumber || 0),
    text: withDocumentFacilityDetails(
      `${hotel} - ${room}${units > 0 ? ` (${units} unit${units === 1 ? "" : "s"})` : ""}`,
      documentFacilityLabelsForStay(stay, snapshot)
    ),
  };
}
// Customer documents count days from the first night away: an extra pre-night is Day 1 and the
// package's own Day 1 moves down, and each line carries its check-in date rather than "Night 1" or
// "Extra pre-night 1". An extra night with no hotel is vehicle only, so it reads "No stay". Lines
// without a night (old bookings with only a summary) are printed as they are.
function bookingDocumentDayLines(lines, { travelDate, preNights, postNights, baseNights }) {
  const pre = Math.max(0, Number(preNights) || 0);
  const post = Math.max(0, Number(postNights) || 0);
  const base = Number(baseNights || 0);
  const nightLines = lines.filter((line) => typeof line === "object" && line.night);
  const extraNightsWithoutStay = nightLines.length === lines.length
    ? [
        ...Array.from({ length: pre }, (_, index) => -(index + 1)),
        ...Array.from({ length: post }, (_, index) => base + index + 1),
      ].filter((night) => !nightLines.some((line) => line.night === night))
    : [];
  return [
    ...lines.map((line) => (typeof line === "object" ? line : { night: 0, text: line })),
    ...extraNightsWithoutStay.map((night) => ({ night, noStay: true, text: DOCUMENT_NO_STAY_TEXT })),
  ]
    .sort((left, right) => left.night - right.night)
    .map((line) => (line.night ? `${tripDayLabel(line.night, travelDate, pre, line.noStay)}: ${line.text}` : line.text));
}
function bookingDocumentRoomLabels(snapshot, fallbackSummary) {
  const labels = [];
  // Base and extension nights together, ordered by night, each labelled so the customer can see
  // which property they are in on which night rather than an unlabelled run-on list.
  const stays = [
    ...(Array.isArray(snapshot?.roomStayCosts) ? snapshot.roomStayCosts : []),
    ...(Array.isArray(snapshot?.extensionStayCosts) ? snapshot.extensionStayCosts : []),
  ].sort((left, right) => Number(left?.nightNumber || 0) - Number(right?.nightNumber || 0));
  if (stays.length) {
    stays.forEach((stay) => labels.push(stayCostDocumentLine(stay, snapshot)));
  }
  if (!labels.length && Array.isArray(snapshot?.selectedRoomCategorySelections)) {
    snapshot.selectedRoomCategorySelections.forEach((selection) => {
      const stay = (snapshot?.roomStayCosts || []).find((item) => String(item?.roomRateId) === String(selection?.roomRateId));
      const city = stay?.hotelCity || stay?.city || "";
      const hotel = stay?.hotelCode || "Hotel";
      const room = stay?.roomCategoryName || stay?.roomLabel || stay?.categoryName || `Room ${selection?.roomCategoryId || ""}`.trim();
      const units = Number(selection?.units || 0);
      labels.push(withDocumentFacilityDetails(
        `${hotel}${city ? ` (${city})` : ""} - ${room}${units > 0 ? ` (${units} unit${units === 1 ? "" : "s"})` : ""}`,
        documentFacilityLabelsForStay(stay, snapshot)
      ));
    });
  }
  const fallback = splitSnapshotList(fallbackSummary).filter(Boolean);
  return labels.length ? labels : fallback;
}
function withDocumentFacilityDetails(label, facilityLabels = []) {
  const details = Array.from(new Set((facilityLabels || []).filter(Boolean)));
  return details.length ? `${label} | Facilities: ${details.join(", ")}` : label;
}
function documentFacilityLabelsForStay(stay, snapshot = {}) {
  return [
    ...selectedHotelFacilityLabels(stay, snapshot),
    ...externalVendorFacilityLabels(stay),
  ];
}
function liveBookingDocumentFacilityLabels(rate, selectedFacilityKeys = {}) {
  return hotelFacilityItemsForBooking(rate)
    .filter((facility) => facility?.id != null && selectedFacilityKeys?.[`${rate?.id}:${facility.id}`] === true)
    .map(hotelFacilityCostLabel);
}
function customerContactEmail(email) {
  const value = String(email || "").trim();
  return value.toLowerCase().endsWith("@iotrips.local") ? "" : value;
}

function hotelWhatsappMessages(booking) {
  const snapshot = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  const confirmations = Array.isArray(booking?.hotelConfirmations ?? booking?.hotel_confirmations)
    ? (booking.hotelConfirmations ?? booking.hotel_confirmations)
    : [];
  const confirmationsByKey = new Map(confirmations.map((item) => [String(item.groupKey ?? item.group_key ?? ""), item]));
  const stays = [
    ...(Array.isArray(snapshot?.roomStayCosts) ? snapshot.roomStayCosts : []),
    ...(Array.isArray(snapshot?.extensionStayCosts) ? snapshot.extensionStayCosts : []),
  ];
  if (!stays.length) return [];
  const grouped = new Map();
  stays.forEach((stay, index) => {
    const hotelKey = String(
      stay?.hotelId ?? `${stay?.hotelCity || stay?.city || ""}:${stay?.hotelName || stay?.hotelCode || ""}`.toLowerCase()
    );
    const stayDate = stayDateForHotelMessage(booking?.travelDate, stay?.nightNumber, index);
    const key = hotelKey;
    const group = grouped.get(key) || {
      key,
      hotelName: stay?.hotelName || "Hotel",
      city: stay?.hotelCity || stay?.city || "",
      stayDates: [],
      rooms: [],
    };
    if (stayDate && !group.stayDates.includes(stayDate)) group.stayDates.push(stayDate);
    const units = roomUnitsFromAllocation(stay?.roomAllocation) || Number(stay?.units || 0) || 1;
    group.rooms.push({
      stayDate,
      roomCategoryId: stay?.roomCategoryId ?? null,
      category: stay?.roomCategoryName || stay?.roomLabel || stay?.categoryName || "Room",
      units,
      optionalFacilities: selectedOptionalPaidHotelFacilityLabels(stay, snapshot),
      breakfast: stay?.breakfast === true,
      dinner: stay?.dinner === true,
      campfire: stay?.campfire === true,
      swimmingPool: stay?.swimmingPool === true,
    });
    grouped.set(key, group);
  });
  return Array.from(grouped.values()).map((group) => {
    const confirmation = confirmationsByKey.get(String(group.key)) || {};
    return {
      key: group.key,
      hotelName: group.hotelName,
      city: group.city,
      dateLabel: hotelDateLabel(group.stayDates),
      rooms: group.rooms,
      accepted: Boolean(confirmation.accepted),
      acceptedAt: confirmation.acceptedAt ?? confirmation.accepted_at ?? null,
      acceptedVia: confirmation.acceptedVia ?? null,
      acceptedByName: confirmation.acceptedByName ?? null,
      acceptedLatitude: confirmation.acceptedLatitude ?? null,
      acceptedLongitude: confirmation.acceptedLongitude ?? null,
      caretakerName: confirmation.caretakerName ?? confirmation.caretaker_name ?? "",
      caretakerNumber: confirmation.caretakerNumber ?? confirmation.caretaker_number ?? "",
      ownerName: confirmation.ownerName ?? confirmation.owner_name ?? "",
      ownerNumber: confirmation.ownerNumber ?? confirmation.owner_number ?? "",
      text: hotelWhatsappText(booking, group, confirmation),
      thankYouText: hotelThankYouWhatsappText(booking, group, confirmation),
    };
  });
}
function roomNumbersForStay(confirmation, room) {
  const assignments = confirmation?.roomAssignments ?? confirmation?.room_assignments ?? [];
  if (!Array.isArray(assignments) || room?.roomCategoryId == null) return [];
  const match = assignments.find(
    (entry) =>
      String(entry?.roomCategoryId ?? entry?.room_category_id ?? "") === String(room.roomCategoryId) &&
      String(entry?.stayDate ?? entry?.stay_date ?? "") === String(room.stayDate),
  );
  return (match?.roomNumbers ?? match?.room_numbers ?? []).filter(Boolean);
}

function hotelWhatsappText(booking, group, confirmation = {}) {
  const roomLines = group.rooms.map((room, index) => {
    const inclusions = [
      room.breakfast ? "Breakfast" : "",
      room.dinner ? "Dinner" : "",
      room.campfire ? "Campfire" : "",
      room.swimmingPool ? "Swimming Pool" : "",
    ].filter(Boolean);
    const optionalText = (room.optionalFacilities || []).length ? ` | Selected facilities: ${room.optionalFacilities.join(", ")}` : "";
    // Hotels that gave us their room numbers are expecting to see them on this line.
    const assigned = roomNumbersForStay(confirmation, room);
    const roomText = assigned.length ? ` - Room ${assigned.join(", ")}` : "";
    return `${index + 1}. ${formatDate(room.stayDate)} - ${room.category} - ${room.units} unit${Number(room.units) === 1 ? "" : "s"}${roomText}${inclusions.length ? ` (${inclusions.join(", ")})` : ""}${optionalText}`;
  });
  const confirmationUrl = confirmation.confirmationUrl ?? confirmation.confirmation_url ?? "";
  const guestLabel = bookingGuestLabel(booking);
  return [
    `Dear ${group.hotelName || "Hotel"} Team,`,
    "",
    "Please reserve the below rooms for IO Trips booking.",
    "",
    `Booking ID: #${booking?.id || "-"}`,
    `Customer: ${booking?.userName || "-"}`,
    `Guests: ${guestLabel}`,
    `Driver Number: ${booking?.driverPhone || "-"}`,
    `Stay Date: ${hotelDateLabel(group.stayDates)}`,
    ...(group.city ? [`City: ${group.city}`] : []),
    "",
    "Required Rooms:",
    ...roomLines,
    "",
    "Please confirm availability and reservation.",
    ...(confirmationUrl ? [
      "",
      "Acceptance Confirmation:",
      "Please click the below URL to accept this booking. Once accepted, this booking cannot be denied in future.",
      "Please switch ON Location on your phone before opening this link.",
      confirmationUrl,
    ] : []),
    "",
    "Regards,",
    "IO Trips",
  ].join("\n");
}
function selectedHotelFacilityLabels(stay, snapshot = {}) {
  const facilitiesById = new Map((stay?.facilities || []).map((facility) => [String(facility?.id || ""), facility]));
  const selectedFromStay = (stay?.selectedHotelFacilities || [])
    .filter((facility) => facility?.selected !== false && facility?.name)
    .map((facility) => hotelFacilityCostLabel(facilitiesById.get(String(facility.id || "")) || facility));
  if (selectedFromStay.length) return selectedFromStay;
  const selectedIds = (snapshot?.quoteRequest?.selectedHotelFacilities || [])
    .filter((item) => item?.selected !== false && String(item?.roomRateId) === String(stay?.roomRateId))
    .map((item) => String(item.facilityId));
  if (!selectedIds.length) return [];
  return (stay?.facilities || [])
    .filter((facility) => facility?.id != null && selectedIds.includes(String(facility.id)) && facility?.name)
    .map(hotelFacilityCostLabel);
}
function selectedOptionalPaidHotelFacilityLabels(stay, snapshot = {}) {
  return selectedOptionalPaidHotelFacilities(stay, snapshot).map(hotelFacilityCostLabel);
}

function selectedOptionalPaidHotelFacilities(stay, snapshot = {}) {
  const facilitiesById = new Map((stay?.facilities || []).map((facility) => [String(facility?.id || ""), facility]));
  const selectedFromStay = (stay?.selectedHotelFacilities || [])
    .filter((facility) => facility?.selected !== false && facility?.name)
    .map((facility) => facilitiesById.get(String(facility.id || "")) || facility)
    .filter((facility) => facilityCostBehavior(facility) === "OPTIONAL_PAID");
  if (selectedFromStay.length) return selectedFromStay;
  const selectedIds = (snapshot?.quoteRequest?.selectedHotelFacilities || [])
    .filter((item) => item?.selected !== false && String(item?.roomRateId) === String(stay?.roomRateId))
    .map((item) => String(item.facilityId));
  if (!selectedIds.length) return [];
  return (stay?.facilities || [])
    .filter((facility) =>
      facility?.id != null &&
      selectedIds.includes(String(facility.id)) &&
      facility?.name &&
      facilityCostBehavior(facility) === "OPTIONAL_PAID"
    );
}

function hotelFacilityCostLabel(facility) {
  const amount = Number(facility?.extraCost || 0);
  const perPerson = facility?.pricingType === "PER_PERSON";
  const suffix = amount > 0 ? ` +INR ${amount.toLocaleString("en-IN")}${perPerson ? " / person / night" : " / night"}` : "";
  return `${facility?.name || "Facility"}${suffix}`;
}
function kidsAgeSuffix(kidAges) {
  const ages = (kidAges || []).map((age) => String(age ?? "").trim()).filter(Boolean);
  if (!ages.length) return "";
  return ` (age${ages.length === 1 ? "" : "s"} ${ages.join(", ")})`;
}

function bookingGuestLabel(booking) {
  const snapshot = parseQuoteSnapshot(booking?.quoteSnapshot ?? booking?.quote_snapshot);
  const snapshotTravellers = snapshot?.travellers || snapshot?.quoteRequest || {};
  const adults = Number(booking?.adults ?? booking?.numberOfAdults ?? snapshotTravellers.adults ?? booking?.noOfTravellers ?? 0) || 0;
  const kids = Number(booking?.kids ?? booking?.numberOfKids ?? snapshotTravellers.kids ?? 0) || 0;
  const kidAges = Array.isArray(booking?.kidAges) ? booking.kidAges : snapshotTravellers.kidAges;
  const total = Number(booking?.noOfTravellers ?? snapshotTravellers.total ?? adults + kids) || adults + kids;
  const parts = [];
  if (adults > 0) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`);
  if (kids > 0) parts.push(`${kids} kid${kids === 1 ? "" : "s"}${kidsAgeSuffix(kidAges)}`);
  if (parts.length) return `${parts.join(", ")} (Total ${total})`;
  return total > 0 ? `${total} guest${total === 1 ? "" : "s"}` : "-";
}
function hotelThankYouWhatsappText(booking, group, confirmation = {}) {
  const roomLines = group.rooms.map((room, index) => {
    const inclusions = [
      room.breakfast ? "Breakfast" : "",
      room.dinner ? "Dinner" : "",
      room.campfire ? "Campfire" : "",
      room.swimmingPool ? "Swimming Pool" : "",
    ].filter(Boolean);
    const optionalText = (room.optionalFacilities || []).length ? ` | Selected facilities: ${room.optionalFacilities.join(", ")}` : "";
    return `${index + 1}. ${formatDate(room.stayDate)} - ${room.category} - ${room.units} unit${Number(room.units) === 1 ? "" : "s"}${inclusions.length ? ` (${inclusions.join(", ")})` : ""}${optionalText}`;
  });
  return [
    `Dear ${group.hotelName || "Hotel"} Team,`,
    "",
    "Thank you for accepting and confirming the IO Trips booking.",
    "",
    `Booking ID: #${booking?.id || "-"}`,
    `Customer: ${booking?.userName || "-"}`,
    `Driver Number: ${booking?.driverPhone || "-"}`,
    `Stay Date: ${hotelDateLabel(group.stayDates)}`,
    ...(group.city ? [`City: ${group.city}`] : []),
    ...(confirmation?.acceptedAt || confirmation?.accepted_at ? [`Accepted At: ${formatHotelAcceptedAt(confirmation.acceptedAt ?? confirmation.accepted_at)}`] : []),
    "",
    "Confirmed Rooms:",
    ...roomLines,
    "",
    "Please keep the rooms blocked as accepted. This booking has been accepted and cannot be denied in future.",
    "",
    "Regards,",
    "IO Trips",
  ].join("\n");
}
function hotelDateLabel(stayDates) {
  const dates = Array.from(new Set((stayDates || []).filter(Boolean))).sort();
  if (!dates.length) return "Date -";
  if (dates.length === 1) return formatDate(dates[0]);
  return dates.map(formatDate).join(", ");
}
function stayDateForHotelMessage(travelDate, nightNumber, index) {
  if (!travelDate) return "";
  const night = Number(nightNumber);
  if (Number.isFinite(night) && night !== 0) {
    return addIsoDays(travelDate, night > 0 ? night - 1 : night);
  }
  return addIsoDays(travelDate, index);
}
// The quote stores one row per night per room type, where `units` is how many rooms of that type
// are held that night. Summing them gives room-nights, which is what the Trip and Hotel WhatsApp
// columns report - a 2-night stay holding 2 rooms is 4 room-nights, not 4 rooms.
//
// Display only. The parsed "Night N: HOTEL - Category" snapshot format that restores the booking
// editor's room picks must stay exactly as it is, so nothing here feeds back into it.
function stayRoomEntries(snapshot) {
  const stays = [
    ...(Array.isArray(snapshot?.roomStayCosts) ? snapshot.roomStayCosts : []),
    ...(Array.isArray(snapshot?.extensionStayCosts) ? snapshot.extensionStayCosts : []),
  ];
  return stays.map((stay, index) => ({
    night: `Night ${stay?.nightNumber ?? index + 1}`,
    category: stay?.roomCategoryName || stay?.roomLabel || stay?.categoryName || "Room",
    units: roomUnitsFromAllocation(stay?.roomAllocation) || Number(stay?.units || 0) || 1,
  }));
}
// Reports what is held per night, never a total across nights: 2 rooms for 2 nights is
// "2 Units x 2 nights", not "4 Units". Summing them read as four rooms.
//
// Nights whose room mix is identical collapse into one line, which is the common case. When the mix
// genuinely differs night to night, each night is listed rather than averaged or added up.
function roomUnitsLabel(entries) {
  const nights = new Map();
  (entries || []).forEach((entry) => {
    const units = Number(entry?.units || 0);
    if (units <= 0) return;
    const night = String(entry?.night ?? "");
    if (!nights.has(night)) nights.set(night, new Map());
    const byCategory = nights.get(night);
    const category = entry?.category || "Room";
    byCategory.set(category, (byCategory.get(category) || 0) + units);
  });
  if (!nights.size) return "";
  const perNight = Array.from(nights.entries()).map(([night, byCategory]) => ({
    night,
    mix: Array.from(byCategory.entries())
      .map(([category, units]) => `${category} - ${units} Unit${units === 1 ? "" : "s"}`)
      .join(", "),
  }));
  const distinctMixes = new Set(perNight.map((item) => item.mix));
  if (distinctMixes.size === 1) {
    return perNight.length === 1 ? perNight[0].mix : `${perNight[0].mix} x ${perNight.length} nights`;
  }
  return perNight.map((item) => `${item.night}: ${item.mix}`).join("; ");
}
function roomUnitsFromAllocation(allocation) {
  if (!allocation) return 0;
  const optionRooms = Array.isArray(allocation.roomOptions)
    ? allocation.roomOptions.reduce((total, option) => total + Number(option.rooms || 0), 0)
    : 0;
  return optionRooms
    || Number(allocation.rooms2 || 0)
    + Number(allocation.rooms3 || 0)
    + Number(allocation.rooms4 || 0);
}
